// src/components/accordion-list/AccordionItem.tsx
import styled2 from "@emotion/styled";
import { ResizeObserver } from "@juggle/resize-observer";
import get from "lodash.get";
import React3, { useEffect as useEffect3, useState as useState3 } from "react";
import { animated as animated3, useSpring as useSpring3 } from "react-spring";
import useMeasure from "react-use-measure";

// src/hooks/useReveal.tsx
import React, {
  useCallback,
  Children,
  cloneElement,
  useEffect as useEffect2,
  useRef,
  useState as useState2,
  createContext,
  useContext,
  useMemo
} from "react";
import { useInView } from "react-intersection-observer";
import { animated, useSpring, useSprings, useTrail } from "react-spring";

// src/hooks/useMedia.ts
import { useTheme } from "@emotion/react";
import { useState, useEffect } from "react";
var parseQuery = (q, theme2) => {
  switch (q) {
    case "lg":
      return `(min-width: ${theme2.breakpoints.lg.width}px)`;
    case "ipadPro":
      return `(min-width: ${theme2.breakpoints.ipadPro.width}px)`;
    case "md":
      return `(min-width:${theme2.breakpoints.md.width}px) and (max-width: ${theme2.breakpoints.ipadPro.width - 1}px)`;
    case "sm":
      return `(max-width: ${theme2.breakpoints.md.width - 1}px)`;
    case "print":
      return "print";
  }
  return q;
};
function useMedia(rawQuery, defaultState = false) {
  const [state, setState] = useState(defaultState);
  const theme2 = useTheme();
  const query = parseQuery(rawQuery, theme2);
  useEffect(() => {
    if (typeof window !== "undefined") {
      let mounted = true;
      const mql = window.matchMedia(query);
      const onChange = () => {
        if (!mounted) {
          return;
        }
        setState(!!mql.matches);
      };
      mql.addListener(onChange);
      setState(mql.matches);
      return () => {
        mounted = false;
        mql.removeListener(onChange);
      };
    }
  }, [query]);
  return state;
}
function useCustomMedia(rawQuery, defaultState = false) {
  const [state, setState] = useState(defaultState);
  const [isFirstRender, setIsFirstRender] = useState(true);
  useEffect(() => {
    if (typeof window != "undefined") {
      const mql = window.matchMedia(rawQuery);
      const handleResize = () => {
        setState(!!mql.matches);
      };
      if (isFirstRender) {
        handleResize();
        setIsFirstRender(false);
      }
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [rawQuery]);
  return state;
}

// src/hooks/useReveal.tsx
var threshold = 0;
var triggerOnce = true;
var inViewConfig = {
  threshold,
  triggerOnce,
  rootMargin: "0px 0px 50px 0px"
};
var RevealContext = createContext({
  enabled: true
});
var getConfig = (inView, isPrint, config2) => ({
  opacity: inView || isPrint ? 1 : 0,
  y: inView || isPrint ? 0 : 50,
  from: { opacity: isPrint ? 1 : 0, y: isPrint ? 0 : 50 },
  delay: config2?.delay ?? 150,
  config: {
    tension: 100,
    ...config2
  }
});
function useReveal(config2) {
  const { enabled } = useContext(RevealContext);
  const [aboveFold, setAboveFold] = useState2(false);
  const [inViewRef, inView] = useInView(inViewConfig);
  const isPrint = useMedia("print");
  const style = useSpring(
    getConfig(enabled === true && (inView || aboveFold), isPrint, config2)
  );
  const ref = useRef();
  const setRefs = useCallback(
    (node) => {
      if (!ref.current) {
        ref.current = node;
        inViewRef(node);
      }
    },
    [inViewRef, ref]
  );
  useEffect2(() => {
    if (ref.current) {
      const { y } = ref.current.getBoundingClientRect();
      if (y <= window.innerHeight) {
        setAboveFold(true);
      }
    }
  }, [ref]);
  return { ref: setRefs, style };
}
function useRevealTrail(children, config2 = {}) {
  const ref = useRef();
  const isPrint = useMedia("print");
  const { enabled } = useContext(RevealContext);
  const [aboveFold, setAboveFold] = useState2(false);
  const childs = Children.toArray(children);
  const [inViewRef, inView] = useInView(inViewConfig);
  const trail = useTrail(childs.length, {
    ...getConfig(enabled === true && (inView || aboveFold), isPrint),
    ...config2
  });
  const items = useMemo(
    () => trail.map((props, index) => {
      return /* @__PURE__ */ React.createElement(animated.div, { key: index, style: props }, childs[index]);
    }),
    [childs, trail]
  );
  const setRefs = useCallback(
    (node) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );
  useEffect2(() => {
    const y = ref.current?.getBoundingClientRect().y;
    if (y !== void 0 && y <= window.innerHeight) {
      setAboveFold(true);
    }
  }, [ref]);
  const itemsWithStyleProp = useMemo(
    () => trail.map((style, index) => {
      return cloneElement(childs[index], {
        ...childs[index].props,
        style
      });
    }),
    [childs, trail]
  );
  return { ref: setRefs, items, itemsWithStyleProp, trail };
}
function useRevealTrailByRow(children, columns, config2 = {}) {
  const ref = useRef();
  const isPrint = useMedia("print");
  const { enabled } = useContext(RevealContext);
  const [aboveFold, setAboveFold] = useState2(false);
  const childs = Children.toArray(children);
  const [inViewRef, inView] = useInView(inViewConfig);
  const isActive = enabled === true && (inView || aboveFold);
  const cols = Math.max(1, columns);
  const baseDelay = config2?.delay ?? 150;
  const rowDelay = 200;
  const itemDelay = 80;
  const springConfigs = useMemo(
    () => childs.map((_, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      return {
        opacity: isActive || isPrint ? 1 : 0,
        y: isActive || isPrint ? 0 : 50,
        from: { opacity: isPrint ? 1 : 0, y: isPrint ? 0 : 50 },
        delay: isActive ? baseDelay + row * rowDelay + col * itemDelay : 0,
        config: {
          tension: 100,
          ...config2
        }
      };
    }),
    [childs.length, cols, isActive, isPrint, baseDelay, config2]
  );
  const springs = useSprings(childs.length, springConfigs);
  const items = useMemo(
    () => springs.map((props, index) => {
      return /* @__PURE__ */ React.createElement(animated.div, { key: index, style: props }, childs[index]);
    }),
    [childs, springs]
  );
  const setRefs = useCallback(
    (node) => {
      ref.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );
  useEffect2(() => {
    const y = ref.current?.getBoundingClientRect().y;
    if (y !== void 0 && y <= window.innerHeight) {
      setAboveFold(true);
    }
  }, [ref]);
  const itemsWithStyleProp = useMemo(
    () => springs.map((style, index) => {
      return cloneElement(childs[index], {
        ...childs[index].props,
        style
      });
    }),
    [childs, springs]
  );
  return { ref: setRefs, items, itemsWithStyleProp, springs };
}

// src/styles/base.ts
import { css as css3 } from "@emotion/react";

// src/styles/typography.tsx
import { css as css2 } from "@emotion/react";

// src/styles/theme.ts
var theme = {
  colors: {
    primary: "#20446A",
    secondary: "#FF8316",
    secondaryAlt: "#FEA425",
    ternary: "#0084A0",
    foreground: "#1E3D5A",
    background: "#FFFFFF",
    backgroundAlt: "#F4F7F7",
    // primary1
    focus: "#3f95fd",
    negative: "#FF0000",
    positive: "#00A651",
    warning: "#FF8800"
  },
  shades: {
    primary1: "#F4F7F7",
    primary2: "#CEE1EA",
    primary3: "#6195CC",
    primary4: "#39699B",
    primary5: "#163e63",
    secondary1: "#FFF7F0",
    secondary2: "#FBE4D3",
    secondary3: "#FFCAA1",
    secondary4: "#FFAF70",
    secondary5: "#F5952F",
    ternary1: "#F0FCFE",
    ternary2: "#CEF2F8",
    ternary3: "#A3E5EE",
    ternary4: "#72DCEB",
    ternary5: "#00B3BF"
  },
  gradients: {
    primary: "linear-gradient(to bottom right, #15324C, #2F6183)",
    secondary: "linear-gradient(to bottom right, #CEF2F8, #F0FCFE)",
    aukakronur: "linear-gradient(-225deg, rgb(240, 252, 254) 0%, rgb(206, 242, 248) 38%, rgb(163, 229, 238) 71%, rgb(114, 220, 235) 100%);"
  },
  font: {
    family: "LB Centro Sans, sans-serif",
    familySerif: "LB Centro Serif, serif",
    familySerifCond: "LB Centro Serif Cond, serif",
    familySlab: "LB Centro Slab, serif"
  },
  graphs: [
    "#1E69B0",
    "#FF8316",
    "#0084A0",
    "#FFD500",
    "#00C078",
    "#890FB4",
    "#A5C3DF",
    "#FFCDA2",
    "#99CED9",
    "#FFEE99",
    "#99E6C9",
    "#D09FE1"
  ],
  gutter: 20,
  segmentPadding: 90,
  gridColumnCount: 12,
  baseline: 4,
  pageWidth: 1180,
  pageLimit: function pageLimit({ theme: theme2 }) {
    return theme2.pageWidth + theme2.breakpoints.lg.gutter * 2;
  },
  useGridOverlay: false,
  headerHeight: 80,
  headerHeightMobile: 60,
  containerGutter: 34,
  breakpoints: {
    sm: { width: 414, gutter: 36 },
    //  mobile
    md: { width: 768, gutter: 50 },
    //  tablet
    ipadPro: { width: 1024, gutter: 50 },
    //  ipad pro
    lg: { width: 1080, gutter: 100 }
    // desktop
  }
};

// src/styles/devices.ts
var device = {
  mobile: `(min-width: ${theme.breakpoints.sm.width}px)`,
  tablet: `(min-width: ${theme.breakpoints.md.width}px)`,
  desktop: `(min-width: ${theme.breakpoints.lg.width}px)`,
  ipadPro: `(min-width: ${theme.breakpoints.ipadPro.width}px)`,
  pageLimit: `(min-width: ${theme.pageLimit({ theme })}px)`
};

// src/styles/mixins.ts
import { css } from "@emotion/react";

// src/utils/shouldForwardProp.ts
import isPropValid from "@emotion/is-prop-valid";
var shouldForwardProp = (tag) => typeof tag === "string" && tag.charCodeAt(0) > 96 ? isPropValid : (key) => key !== "theme" && key !== "innerRef";

// src/utils/mapThemeColorStringToValue.ts
var mapThemeColorStringToValue = (theme2, colorTheme) => {
  switch (colorTheme) {
    case "secondary":
      return theme2.colors.secondary;
    case "secondaryAlt":
      return theme2.colors.secondaryAlt;
    case "ternary":
      return theme2.colors.ternary;
    case "foreground":
      return theme2.colors.foreground;
    case "background":
      return theme2.colors.background;
    case "backgroundAlt":
      return theme2.colors.backgroundAlt;
    case "focus":
      return theme2.colors.focus;
    case "negative":
      return theme2.colors.negative;
    case "positive":
      return theme2.colors.positive;
    case "warning":
      return theme2.colors.warning;
    case "primary":
    default:
      return theme2.colors.primary;
  }
};

// src/utils/mapThemeShadeStringToValue.ts
var mapStringToThemeColorBaseOptions = (colorTheme) => {
  if (colorTheme === "secondary" || colorTheme === "ternary") {
    return colorTheme;
  } else {
    return "primary";
  }
};
var mapThemeShadeStringToValue = (theme2, colorTheme, variant) => {
  const mappedColorTheme = mapStringToThemeColorBaseOptions(colorTheme);
  switch (mappedColorTheme) {
    case "secondary":
      return getSecondaryShade(theme2, variant);
    case "ternary":
      return getTernaryShade(theme2, variant);
    case "primary":
    default:
      return getPrimaryShade(theme2, variant);
  }
};
var getPrimaryShade = (theme2, variant) => {
  switch (variant) {
    case 2:
      return theme2.shades.primary2;
    case 3:
      return theme2.shades.primary3;
    case 4:
      return theme2.shades.primary4;
    case 5:
      return theme2.shades.primary5;
    case 1:
    default:
      return theme2.shades.primary1;
  }
};
var getSecondaryShade = (theme2, variant) => {
  switch (variant) {
    case 2:
      return theme2.shades.secondary2;
    case 3:
      return theme2.shades.secondary3;
    case 4:
      return theme2.shades.secondary4;
    case 5:
      return theme2.shades.secondary5;
    case 1:
    default:
      return theme2.shades.secondary1;
  }
};
var getTernaryShade = (theme2, variant) => {
  switch (variant) {
    case 2:
      return theme2.shades.ternary2;
    case 3:
      return theme2.shades.ternary3;
    case 4:
      return theme2.shades.ternary4;
    case 5:
      return theme2.shades.ternary5;
    case 1:
    default:
      return theme2.shades.ternary1;
  }
};

// src/utils/replaceIcelandicCharacters.ts
var chars = {
  \u00E1: "a",
  \u00F0: "d",
  \u00E9: "e",
  \u00ED: "i",
  \u00F3: "o",
  \u00FA: "u",
  \u00FD: "y",
  \u00FE: "th",
  \u00E6: "ae",
  \u00F6: "o"
  // tood: add Uppercase mapping if needed
};
var toNonIcelandicLowerCase = (text) => {
  return text.toLowerCase().replace(/[áðéíóúýþæö]/g, (m) => chars[m]);
};

// src/styles/mixins.ts
function responseFont(minSize, size, limit) {
  return (props) => {
    const { theme: theme2 } = props;
    limit = limit ?? theme2.pageLimit({ theme: theme2 });
    const baseline = theme2.breakpoints.sm.width;
    const fontMultiplier = (size - minSize) / ((limit ?? 0) - baseline);
    const fontBaseline = minSize - fontMultiplier * baseline;
    return css`
      font-size: ${minSize}px;

      @media (min-width: ${theme2.breakpoints.sm.width}px) {
        font-size: calc(${fontMultiplier} * 100vw + (${fontBaseline}px));
      }

      @media (min-width: ${limit}px) {
        font-size: ${size}px;
      }
    `;
  };
}
var dynamicInput = () => css`
  position: absolute;
  left: -9999px;
`;
function mobileGutterMargin({ theme: theme2 }) {
  const minGutter = 16;
  const baseline = 320;
  const gutterMultiplier = (theme2.breakpoints.sm.gutter - minGutter) / (theme2.breakpoints.sm.width - baseline);
  const gutterBaseline = minGutter - gutterMultiplier * baseline;
  return css`
    @media (max-width: ${theme2.breakpoints.sm.width}px) {
      margin-left: calc(
        -1 * (${gutterMultiplier} * 100vw + (${gutterBaseline}px))
      );
      margin-right: calc(
        -1 * (${gutterMultiplier} * 100vw + (${gutterBaseline}px))
      );
    }
  `;
}
function mobileGutterPadding({ theme: theme2 }) {
  const minGutter = 16;
  const baseline = 320;
  const gutterMultiplier = (theme2.breakpoints.sm.gutter - minGutter) / (theme2.breakpoints.sm.width - baseline);
  const gutterBaseline = minGutter - gutterMultiplier * baseline;
  return css`
    @media (max-width: ${theme2.breakpoints.sm.width}px) {
      padding-left: calc(${gutterMultiplier} * 100vw + (${gutterBaseline}px));
      padding-right: calc(${gutterMultiplier} * 100vw + (${gutterBaseline}px));
    }
  `;
}
function container({ theme: theme2 }) {
  return css`
    flex-grow: 1;
    margin: 0 auto;
    padding-left: ${theme2.breakpoints.sm.gutter}px;
    padding-right: ${theme2.breakpoints.sm.gutter}px;
    max-width: ${theme2.pageWidth + theme2.breakpoints.sm.gutter * 2}px;

    ${mobileGutterPadding({ theme: theme2 })}

    @media (min-width: ${theme2.breakpoints.md.width}px) {
      padding-left: ${theme2.breakpoints.md.gutter}px;
      padding-right: ${theme2.breakpoints.md.gutter}px;
      max-width: ${theme2.pageWidth + theme2.breakpoints.md.gutter * 2}px;
    }

    @media (min-width: ${theme2.breakpoints.lg.width}px) {
      padding-left: ${theme2.breakpoints.lg.gutter}px;
      padding-right: ${theme2.breakpoints.lg.gutter}px;
      max-width: ${theme2.pageWidth + theme2.breakpoints.lg.gutter * 2}px;
    }
  `;
}
function fit() {
  return css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  `;
}
function sronly() {
  return css`
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  `;
}
function smallTable(background) {
  return (props) => {
    const { theme: theme2 } = props;
    const wrapperBgColor = background === "Gray" ? theme2.colors.backgroundAlt : theme2.colors.background;
    const underLineBorder = `3px solid ${theme2.colors.foreground}`;
    return css`
      background-color: ${wrapperBgColor};
      table {
        width: 100%;
        text-align: right;
        th {
          padding: 9px 0;
          font-family: ${theme2.font.familySerifCond};
          font-size: 18px;
          line-height: ${24 / 18};
          font-weight: 500;
          text-align: left;
          border-bottom: ${underLineBorder};
        }
        td {
          padding: 6px 0 7px;
          border-bottom: 1px solid rgba(30, 61, 90, 0.1);
          font-size: 14px;
          line-height: ${18 / 14};
          &:first-child {
            text-align: left;
          }
        }
        tbody {
          tr:first-child td {
            padding-top: 12px;
          }
          tr:last-child td {
            padding-bottom: 14px;
            border-bottom: 1px solid transparent;
            border-color: ${theme2.colors.primary};
          }
        }
        tbody:last-child tr:last-child td {
          border-bottom-color: transparent;
        }
        strong {
          font-weight: 500;
        }
        b {
          font-weight: 500;
        }
      }
    `;
  };
}
function zebraTable(background, colorTheme) {
  return (props) => {
    const { theme: theme2 } = props;
    const wrapperBgColor = background === "Gray" ? theme2.colors.backgroundAlt : theme2.colors.background;
    const themeColor = mapThemeColorStringToValue(theme2, colorTheme);
    const shadeColor = mapThemeShadeStringToValue(theme2, colorTheme);
    const tdBgColor = background === "Gray" && colorTheme === "primary" ? "#fff" : shadeColor;
    return css`
      overflow-x: auto;
      background-color: ${wrapperBgColor};

      table {
        width: 100%;
        text-align: right;
        position: relative;
        border-collapse: separate;

        th {
          padding: 12px 10px;
          font-family: ${theme2.font.familySerifCond};
          border-bottom: 2px solid ${themeColor};

          position: sticky;
          top: 0;
          z-index: 2;
          background-color: #fff;
          white-space: nowrap;

          &:last-child {
            padding-right: 0;
          }

          &:first-child {
            text-align: left;
            left: 0;
            z-index: 3;
          }
        }

        td {
          padding: 17px 10px;
          border-bottom: 1px solid ${themeColor};
          background-color: ${wrapperBgColor};

          &:first-child {
            position: sticky;
            left: 0;
            text-align: left;
          }

          &:last-of-type {
            text-align: right;
          }
        }

        tr:nth-of-type(2n) {
          td {
            background-color: ${tdBgColor};
          }
        }

        tfoot {
          tr {
            td {
              padding: 22px 10px;
              font-weight: 500;
              border: none;
            }
            td:first-child {
              text-align: left;
            }
          }
        }
      }
    `;
  };
}
function standardTable(background, noHeadingUnderline) {
  return (props) => {
    const { theme: theme2 } = props;
    const wrapperBgColor = background === "Gray" ? theme2.colors.backgroundAlt : theme2.colors.background;
    const underLineSpace = noHeadingUnderline ? "30px" : "inherit";
    const underLineBorder = noHeadingUnderline ? "2px solid transparent" : `2px solid ${theme2.colors.foreground}`;
    const underLineDisplay = noHeadingUnderline ? "inline-block" : "none";
    return css`
      background-color: ${wrapperBgColor};

      table {
        width: 100%;
        text-align: right;

        th {
          padding: 12px 0;
          padding-bottom: ${underLineSpace};
          font-family: ${theme2.font.familySerifCond};

          border-bottom: ${underLineBorder};

          &:first-child {
            position: relative;
            text-align: left;
            font-size: 30px;
            font-weight: 500;

            &:after {
              content: '';
              display: ${underLineDisplay};
              position: absolute;
              bottom: 10px;
              left: 0;
              display: inline-block;
              height: 1px;
              width: 27px;
              background-color: currentColor;
            }
          }
        }

        td {
          padding: 7px 0;

          &:first-child {
            text-align: left;
          }
        }

        tbody {
          tr:first-child td {
            padding-top: 14px;
          }

          tr:last-child td {
            padding-bottom: 14px;
            border-bottom: 1px solid transparent;
            border-color: ${theme2.colors.primary};
          }
        }

        tbody:last-child tr:last-child td {
          border-bottom-color: transparent;
        }

        strong {
          font-weight: 500;
        }
      }
    `;
  };
}
function stickyFirstColumn() {
  return (props) => {
    const { theme: theme2 } = props;
    return css`
      -ms-overflow-style: none;
      scrollbar-width: none;
      ::-webkit-scrollbar {
        display: none;
      }

      table {
        width: 100%;
        position: relative;
        border-collapse: separate;
        tr {
          left: 0;
        }
        th {
          position: sticky;
          z-index: 2;
          background-color: #fff;

          &:first-of-type {
            left: -${theme2.containerGutter}px;
            z-index: 3;
            max-width: 180px;
            box-shadow: 5px 10px 8px #e8ebee;
            @media ${device.desktop} {
              box-shadow: none;
            }
          }
          /* Mix svo box-shadow fari ekki yfir th borderinn */
          &:first-of-type:after {
            content: '';
            background: rgb(32, 68, 106);
            height: 3px;
            display: block;
            width: 120%;
            position: Absolute;
            bottom: -3px;
            left: 0;
            z-index: 4;
          }
        }

        td {
          &:first-of-type {
            position: sticky !important;
            left: -${theme2.containerGutter}px;
            z-index: 5 !important;
            background-color: #fff;
            box-shadow: 5px 10px 8px #e8ebee;

            @media ${device.desktop} {
              box-shadow: none;
            }
          }
        }
      }
    `;
  };
}

// src/styles/typography.tsx
var sizes = {
  /**18*/
  bodySize: 18,
  /**18*/
  bodySizeMin: 18,
  /**16*/
  bodyMediumSize: 16,
  /**24*/
  bodyLineHeight: 24,
  /**14*/
  bodySmallSize: 14,
  /**13*/
  bodySmallSizeMin: 13,
  /**20*/
  bodySmallLineHeight: 20,
  /**14*/
  bodySmallBoldSize: 20,
  /**13*/
  bodySmallBoldSizeMin: 20,
  /**20*/
  bodySmallBoldLineHeight: 26,
  /**22*/
  bodyEditorialSize: 22,
  /**18*/
  bodyEditorialSizeMin: 18,
  /**32*/
  bodyEditorialLineHeight: 32,
  /**134*/
  displaySize: 134,
  /**80*/
  displaySizeMin: 80,
  /**154*/
  displayLineHeihght: 154,
  /**72*/
  heroSmallSize: 72,
  /**32*/
  heroSmallSizeMin: 32,
  /**88*/
  heroSmallLineHeight: 88,
  /**84*/
  h1Size: 84,
  /**40*/
  h1SizeMin: 40,
  /**104*/
  h1LineHeight: 104,
  /**56*/
  h2BigSize: 56,
  /**32*/
  h2BigSizeMin: 32,
  /**70*/
  h2BigLineHeight: 70,
  /**44*/
  h2Size: 44,
  /**30*/
  h2SizeMin: 30,
  /**54*/
  h2LineHeight: 54,
  /**28*/
  h3Size: 28,
  /**26*/
  h3SizeMin: 26,
  /**36*/
  h3LineHeight: 36,
  /**22*/
  h4Size: 22,
  /**20*/
  h4SizeMin: 20,
  /**32*/
  h4LineHeight: 32,
  /**18*/
  h5Size: 18,
  /**16*/
  h5SizeMin: 16,
  /**24*/
  h5LineHeight: 24,
  /**32*/
  p1Size: 32,
  /**28*/
  p1SizeMin: 28,
  /**40*/
  p1LineHeight: 40,
  /**24*/
  p2Size: 24,
  /**20*/
  p2SizeMin: 20,
  /**32*/
  p2LineHeight: 32,
  /**16*/
  numbersSize: 16,
  /**14*/
  numbersSizeMin: 14,
  /**24*/
  numbersLineHeight: 24,
  /**30*/
  numbers2Size: 18,
  /**26*/
  numbers2SizeMin: 18,
  /**40*/
  numbers2LineHeight: 24,
  /**24 */
  numbers2MSize: 24,
  /**26*/
  numbers2MSizeMin: 20,
  /**40*/
  numbers2MLineHeight: 40,
  /**40*/
  numbers4BigSize: 40,
  /**32*/
  numbers4BigSizeMin: 32,
  /**70*/
  numbers4BigLineHeight: 70,
  /**40*/
  numbersMSize: 40,
  /**32*/
  numbersMSizeMin: 32,
  /**48*/
  numbersMLineHeight: 48,
  /**60*/
  numbersBigSize: 60,
  /**42*/
  numbersBigSizeMin: 42,
  /**70*/
  numbersBigLineHeight: 70,
  /**84*/
  numbersBiggestSize: 84,
  /**52*/
  numbersBiggestSizeMin: 52,
  /**104*/
  numbersBiggestLineHeight: 104,
  /**16*/
  numbersTableSize: 16,
  /**14**/
  numbersTableSizeMin: 16,
  /**40*/
  numbersTableLineHeight: 24,
  /**16*/
  chartsSize: 16,
  /**14*/
  chartsSizeMin: 14,
  /**24*/
  chartsLineHeight: 24,
  /**24*/
  inputSize: 24,
  /**20*/
  inputSizeMin: 20,
  /**32*/
  inputSizeHeight: 32,
  /**28*/
  input2Size: 22,
  /**20*/
  input2SizeMin: 20,
  /**32*/
  input2SizeHeight: 28,
  /**14*/
  labelSize: 12,
  /**12*/
  labelSizeMin: 10,
  /**20*/
  labelLineHeight: 20
};
var createTypographyStyle = (fontFamily, fontWeight, responseFonts, lineHeight, letterSpacing, otherStyles = []) => css2`
  font-family: ${fontFamily};
  font-weight: ${fontWeight};
  ${responseFonts}
  ${otherStyles.length > 0 && otherStyles.join(";\n")}
  line-height: ${lineHeight};
  letter-spacing: ${letterSpacing}px;
`;
var body = (props) => createTypographyStyle(
  props.theme.font.family,
  "normal",
  responseFont(sizes.bodySizeMin, sizes.bodySize)(props),
  sizes.bodyLineHeight / sizes.bodySize,
  0.28
);
var bodyMedium = (props) => createTypographyStyle(
  props.theme.font.family,
  500,
  responseFont(sizes.bodyMediumSize, sizes.bodyMediumSize)(props),
  sizes.bodyLineHeight / sizes.bodySize,
  0.27
);
var bodyBold = (props) => createTypographyStyle(
  props.theme.font.family,
  "bold",
  responseFont(sizes.bodyMediumSize, sizes.bodyMediumSize)(props),
  sizes.bodyLineHeight / sizes.bodySize,
  0.25
);
var bodySmall = (props) => createTypographyStyle(
  props.theme.font.family,
  "normal",
  responseFont(sizes.bodySmallSizeMin, sizes.bodySmallSize)(props),
  sizes.bodySmallLineHeight / sizes.bodySmallSize,
  0.2
);
var bodySmallBold = (props) => createTypographyStyle(
  props.theme.font.family,
  500,
  responseFont(sizes.bodySmallBoldSizeMin, sizes.bodySmallBoldSize)(props),
  sizes.bodySmallBoldLineHeight / sizes.bodySmallBoldSize,
  0.36
);
var bodyEditorial = (props) => createTypographyStyle(
  props.theme.font.familySerif,
  "normal",
  responseFont(sizes.bodyEditorialSizeMin, sizes.bodyEditorialSize)(props),
  sizes.bodyEditorialLineHeight / sizes.bodyEditorialSize,
  0.25
);
var display = (props) => createTypographyStyle(
  props.theme.font.familySerifCond,
  500,
  responseFont(sizes.displaySizeMin, sizes.displaySize)(props),
  sizes.displayLineHeihght / sizes.displaySize,
  0.5
);
var heroSmall = (props) => createTypographyStyle(
  props.theme.font.familySerifCond,
  500,
  responseFont(sizes.heroSmallSizeMin, sizes.heroSmallSize)(props),
  sizes.heroSmallLineHeight / sizes.heroSmallSize,
  0.5
);
var h1 = (props) => createTypographyStyle(
  props.theme.font.familySerifCond,
  500,
  responseFont(sizes.h1SizeMin, sizes.h1Size)(props),
  sizes.h1LineHeight / sizes.h1Size,
  0,
  ["text-wrap:balance;"]
);
var h2Big = (props) => createTypographyStyle(
  props.theme.font.familySerifCond,
  500,
  responseFont(sizes.h2BigSizeMin, sizes.h2BigSize)(props),
  sizes.h2BigLineHeight / sizes.h2BigSize,
  0.5
);
var h2 = (props) => createTypographyStyle(
  props.theme.font.familySerifCond,
  500,
  responseFont(sizes.h2SizeMin, sizes.h2Size)(props),
  sizes.h2LineHeight / sizes.h2Size,
  0.5
);
var h3 = (props) => createTypographyStyle(
  props.theme.font.familySerifCond,
  500,
  responseFont(sizes.h3SizeMin, sizes.h3Size)(props),
  sizes.h3LineHeight / sizes.h3Size,
  0.5
);
var h4 = (props) => createTypographyStyle(
  props.theme.font.familySerifCond,
  500,
  responseFont(sizes.h4SizeMin, sizes.h4Size)(props),
  sizes.h4LineHeight / sizes.h4Size,
  0.5
);
var h5 = (props) => createTypographyStyle(
  props.theme.font.familySerifCond,
  500,
  responseFont(sizes.h5SizeMin, sizes.h5Size)(props),
  sizes.h5LineHeight / sizes.h5Size,
  0.5
);
var p1 = (props) => createTypographyStyle(
  props.theme.font.family,
  "normal",
  responseFont(sizes.p1SizeMin, sizes.p1Size)(props),
  sizes.p1LineHeight / sizes.p1Size,
  0.25
);
var p2 = (props) => createTypographyStyle(
  props.theme.font.family,
  "normal",
  responseFont(sizes.p2SizeMin, sizes.p2Size)(props),
  sizes.p2LineHeight / sizes.p2Size,
  0.25
);
var numbers = (props) => createTypographyStyle(
  props.theme.font.familySlab,
  500,
  responseFont(sizes.numbersSizeMin, sizes.numbersSize)(props),
  sizes.numbersLineHeight / sizes.numbersSize,
  0
);
var numbers2 = (props) => createTypographyStyle(
  props.theme.font.family,
  "normal",
  responseFont(sizes.numbers2SizeMin, sizes.numbers2Size)(props),
  sizes.numbers2LineHeight / sizes.numbers2Size,
  0.2
);
var numbers4Big = (props) => createTypographyStyle(
  props.theme.font.familySlab,
  500,
  responseFont(sizes.numbers4BigSizeMin, sizes.numbers4BigSize)(props),
  sizes.numbers4BigLineHeight / sizes.numbers4BigSize,
  -0.6
);
var numbersMedium = (props) => createTypographyStyle(
  props.theme.font.familySlab,
  "bold",
  responseFont(sizes.numbersMSizeMin, sizes.numbersMSize)(props),
  sizes.numbersMLineHeight / sizes.numbersMSize,
  0
);
var numbers2Medium = (props) => createTypographyStyle(
  props.theme.font.familySlab,
  500,
  responseFont(sizes.numbers2MSizeMin, sizes.numbers2MSize)(props),
  sizes.numbers2MLineHeight / sizes.numbers2MSize,
  0.2
);
var numbersBig = (props) => createTypographyStyle(
  props.theme.font.familySlab,
  "bold",
  responseFont(sizes.numbersBigSizeMin, sizes.numbersBigSize)(props),
  sizes.numbersBigLineHeight / sizes.numbersBigSize,
  0
);
var numbersBiggest = (props) => createTypographyStyle(
  props.theme.font.familySlab,
  "bold",
  responseFont(sizes.numbersBiggestSizeMin, sizes.numbersBiggestSize)(props),
  sizes.numbersBiggestLineHeight / sizes.numbersBiggestSize,
  0
);
var tableNumbers = (props) => createTypographyStyle(
  props.theme.font.family,
  "normal",
  responseFont(sizes.numbersTableSize, sizes.numbersTableSize)(props),
  sizes.numbersTableLineHeight / sizes.numbersTableSize,
  0
);
var charts = (props) => createTypographyStyle(
  props.theme.font.family,
  500,
  responseFont(sizes.chartsSizeMin, sizes.chartsSize)(props),
  sizes.chartsLineHeight / sizes.chartsSize,
  0
);
var input = (props) => createTypographyStyle(
  props.theme.font.familySlab,
  500,
  responseFont(sizes.inputSizeMin, sizes.inputSize)(props),
  sizes.inputSizeHeight / sizes.inputSize,
  0.5,
  ["color: rgba(21, 50, 76, 1)"]
);
var input2 = (props) => createTypographyStyle(
  props.theme.font.family,
  500,
  responseFont(sizes.input2SizeMin, sizes.input2Size)(props),
  sizes.input2SizeHeight / sizes.input2Size,
  0.58,
  ["color: rgb(22, 62, 99)"]
);
var label = (props) => createTypographyStyle(
  props.theme.font.family,
  "normal",
  responseFont(sizes.labelSizeMin, sizes.labelSize)(props),
  sizes.labelLineHeight / sizes.labelSize,
  0.2
);
var listStyle = () => css2`
  position: relative;
  padding-left: 30px;
  margin-bottom: 28px;

  @media ${device.tablet} {
    padding-left: 40px;
  }

  &:before {
    content: '';
    display: inline-block;

    position: absolute;
    top: 6px;
    left: 0;

    margin-right: 20px;

    height: 8px;
    width: 8px;

    border-radius: 50%;

    background: currentColor;

    @media ${device.tablet} {
      left: 10px;
    }

    @media ${device.desktop} {
      top: 10px;
    }
  }
`;
var heroListStyle = (props) => css2`
  ${responseFont(sizes.bodySizeMin, sizes.bodySize)(props)}
  line-height: ${sizes.bodyEditorialLineHeight / sizes.bodySize};

  position: relative;
  padding-left: 30px;
  margin-bottom: 5px;

  @media ${device.tablet} {
    padding-left: 40px;
  }

  &:before {
    content: '';
    display: inline-block;

    position: absolute;
    top: 12px;
    left: 0;

    margin-right: 20px;

    height: 2px;
    width: 11px;

    background: currentColor;

    @media ${device.tablet} {
      left: 10px;
    }
  }
`;
var olListStyle = () => css2`
  position: relative;

  padding-left: 30px;
  margin-bottom: 28px;

  @media ${device.tablet} {
    padding-left: 0px;
  }

  &:before {
    content: counter(custom-counter) '. ';
    display: inline-block;
    position: relative;
    top: 0;
    left: 0;
    font-weight: bold;

    margin-right: 20px;

    @media ${device.tablet} {
      left: 10px;
    }
  }
`;
var typography = {
  body,
  bodyMedium,
  bodyBold,
  bodySmall,
  bodySmallBold,
  bodyEditorial,
  display,
  heroSmall,
  h1,
  h2Big,
  h2,
  h3,
  h4,
  h5,
  p1,
  p2,
  numbers,
  numbers2,
  numbers2Medium,
  numbers4Big,
  numbersMedium,
  numbersBig,
  numbersBiggest,
  tableNumbers,
  listStyle,
  heroListStyle,
  input,
  input2,
  label,
  olListStyle,
  charts,
  sizes
};

// src/styles/base.ts
var base = (theme2) => css3`
  html,
  body {
    min-height: 100vh;
    font-family: ${theme2.font.family}, sans-serif;
    font-weight: 400;
    color: ${theme2.colors.foreground};
    max-width: 100%;
    margin: 0;
  }

  html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    box-sizing: border-box;
    background-color: ${theme2.colors.primary};
  }
  body {
    -webkit-overflow-scrolling: touch;
    /* overflow-x: hidden; */
    /* always show the vertical scrollbar so that content doesn't jump */
    /* overflow-y: scroll; */
  }
  #atlwdg-trigger {
    display: none !important;
  }
  svg {
    transform: translate3d(0px, 0px, 0px);
  }
  .lazyloading {
    filter: blur(10px);
  }

  .lazyloaded {
    filter: blur(0px);
  }

  .no-js .lazyloading {
    filter: blur(0px);
  }

  .no-js [style*='opacity:0'] {
    opacity: 1 !important;
  }

  .no-js [style*='transform'] {
    transform: none !important;
  }

  .grecaptcha-badge {
    visibility: hidden;
  }

  /* inherited from <html> */
  *,
  *::before,
  *::after {
    box-sizing: inherit;
  }

  body {
    font-size: ${sizes.bodySize}px;
    position: relative;
    margin: 0;
    font-family: ${theme2.font.family}, sans-serif;
    line-height: ${sizes.bodyLineHeight / sizes.bodySize};
    /* iOS on orientation change */
    text-size-adjust: 100%;
    color: ${theme2.colors.foreground};
    @media (min-width: ${theme2.breakpoints.lg.width}) {
      font-size: ${sizes.bodySize};
    }
    background-color: white;
  }

  html.is-scroll-disabled body {
    height: 100vh;
    overflow: hidden;
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    opacity: 1;
  }

  /* placeholders */
  input,
  textarea,
  select {
    &::placeholder {
      opacity: 1;
      color: ${theme2.colors.primary};
    }
  }
  a,
  input,
  select,
  button {
    outline: none;
  }
  a
  {
    &:focus-visible{
      outline: 0.1rem solid -webkit-focus-ring-color;
      outline-offset: -0.1rem;
    }
  }

  button
  {
    &:focus-visible{
      outline: 0.2rem solid -webkit-focus-ring-color;
      outline-offset: -0.2rem;
    }  
  }
  button[type='submit']:focus-visible{
      outline: 0.2rem solid #163E63;
      outline-offset: -0.2rem;
    }
  ::selection {
    background: ${theme2.shades.secondary1};
  }

  input[type='text']::selection {
    background: ${theme2.shades.secondary3};
  }
  sub,
  sup {
    font-size: 14px;
    line-height: 14px;
  }
`;

// src/styles/fonts.ts
import { css as css4 } from "@emotion/react";
var fonts = css4`
  @font-face {
    font-family: 'LB Centro Serif';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-bold-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-bold-webfont.woff')
        format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Serif';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-medium-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-medium-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
    ascent-override: 103%;
  }
  @font-face {
    font-family: 'LB Centro Serif';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-regular-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-regular-webfont.woff')
        format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Serif';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-mediumitalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-mediumitalic-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
    ascent-override: 103%;
  }
  @font-face {
    font-family: 'LB Centro Serif';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-bolditalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-bolditalic-webfont.woff')
        format('woff');
    font-weight: 700;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Serif';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-italic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserif-italic-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Slab';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-bolditalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-bolditalic-webfont.woff')
        format('woff');
    font-weight: 700;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Slab';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-regular-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-regular-webfont.woff')
        format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
    ascent-override: 101%;
  }
  @font-face {
    font-family: 'LB Centro Slab';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-medium-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-medium-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Slab';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-italic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-italic-webfont.woff')
        format('woff');
    font-weight: 400;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Slab';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-mediumitalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-mediumitalic-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Slab';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-bold-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbslab-bold-webfont.woff')
        format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-regular-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-regular-webfont.woff')
        format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
    ascent-override: 101%;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-mediumitalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-mediumitalic-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-italic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-italic-webfont.woff')
        format('woff');
    font-weight: 400;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-thinitalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-thinitalic-webfont.woff')
        format('woff');
    font-weight: 100;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-thin-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-thin-webfont.woff')
        format('woff');
    font-weight: 100;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-bolditalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-bolditalic-webfont.woff')
        format('woff');
    font-weight: 700;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-bold-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-bold-webfont.woff')
        format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-lightitalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-lightitalic-webfont.woff')
        format('woff');
    font-weight: 300;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-medium-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-medium-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Sans';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-light-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbsans-light-webfont.woff')
        format('woff');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Serif Cond';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-mediumitalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-mediumitalic-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
    ascent-override: 103%;
  }
  @font-face {
    font-family: 'LB Centro Serif Cond';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-italic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-italic-webfont.woff')
        format('woff');
    font-weight: 400;
    font-style: italic;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Serif Cond';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-medium-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-medium-webfont.woff')
        format('woff');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Serif Cond';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-bold-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-bold-webfont.woff')
        format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Serif Cond';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-regular-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-regular-webfont.woff')
        format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'LB Centro Serif Cond';
    src:
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-bolditalic-webfont.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/lb/lbserifcond-bolditalic-webfont.woff')
        format('woff');
    font-weight: 700;
    font-style: italic;
    font-display: swap;
  }
`;

// src/styles/legacyFonts.ts
import { css as css5 } from "@emotion/react";
var legacyFonts = css5`
  @font-face {
    font-family: 'LB Centro Serif';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-mediumitalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-mediumitalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-mediumitalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-mediumitalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-mediumitalic.ttf')
        format('truetype');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bolditalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bolditalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bolditalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bolditalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bolditalic.ttf')
        format('truetype');
    font-weight: bold;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-italic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-italic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-italic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-italic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-italic.ttf')
        format('truetype');
    font-weight: normal;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Slab';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bolditalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bolditalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bolditalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bolditalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bolditalic.ttf')
        format('truetype');
    font-weight: bold;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-regular.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-regular.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-regular.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-regular.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-regular.ttf')
        format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Slab';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-regular.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-regular.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-regular.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-regular.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-regular.ttf')
        format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-regular.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-regular.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-regular.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-regular.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-regular.ttf')
        format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif Cond';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-mediumitalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-mediumitalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-mediumitalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-mediumitalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-mediumitalic.ttf')
        format('truetype');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-medium.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-medium.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-medium.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-medium.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-medium.ttf')
        format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-mediumitalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-mediumitalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-mediumitalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-mediumitalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-mediumitalic.ttf')
        format('truetype');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Slab';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-medium.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-medium.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-medium.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-medium.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-medium.ttf')
        format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif Cond';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-italic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-italic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-italic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-italic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-italic.ttf')
        format('truetype');
    font-weight: normal;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif Cond';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-medium.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-medium.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-medium.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-medium.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-medium.ttf')
        format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif Cond';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bold.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bold.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bold.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bold.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bold.ttf')
        format('truetype');
    font-weight: bold;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-italic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-italic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-italic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-italic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-italic.ttf')
        format('truetype');
    font-weight: normal;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Slab';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-italic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-italic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-italic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-italic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-italic.ttf')
        format('truetype');
    font-weight: normal;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thinitalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thinitalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thinitalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thinitalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thinitalic.ttf')
        format('truetype');
    font-weight: 100;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif Cond';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-regular.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-regular.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-regular.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-regular.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-regular.ttf')
        format('truetype');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thin.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thin.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thin.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thin.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-thin.ttf')
        format('truetype');
    font-weight: 100;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bolditalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bolditalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bolditalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bolditalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bolditalic.ttf')
        format('truetype');
    font-weight: bold;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bold.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bold.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bold.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bold.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-bold.ttf')
        format('truetype');
    font-weight: bold;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-lightitalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-lightitalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-lightitalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-lightitalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-lightitalic.ttf')
        format('truetype');
    font-weight: 300;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bold.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bold.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bold.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bold.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserif-bold.ttf')
        format('truetype');
    font-weight: bold;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Serif Cond';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bolditalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bolditalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bolditalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bolditalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroserifcond-bolditalic.ttf')
        format('truetype');
    font-weight: bold;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Slab';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-mediumitalic.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-mediumitalic.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-mediumitalic.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-mediumitalic.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-mediumitalic.ttf')
        format('truetype');
    font-weight: 500;
    font-style: italic;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-medium.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-medium.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-medium.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-medium.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-medium.ttf')
        format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Slab';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bold.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bold.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bold.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bold.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentroslab-bold.ttf')
        format('truetype');
    font-weight: bold;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'LB Centro Sans';
    src: url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-light.eot');
    src:
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-light.eot?#iefix')
        format('embedded-opentype'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-light.woff2')
        format('woff2'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-light.woff')
        format('woff'),
      url('https://cdn.landsbankinn.is/fonts/centro/lbcentrosans-light.ttf')
        format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }
`;

// src/styles/reset.ts
import { css as css6 } from "@emotion/react";
var reset = css6`
  html,
  body,
  div,
  span,
  applet,
  object,
  iframe,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  hr,
  p,
  blockquote,
  pre,
  a,
  abbr,
  acronym,
  address,
  big,
  cite,
  code,
  del,
  dfn,
  em,
  img,
  ins,
  kbd,
  q,
  s,
  samp,
  small,
  strike,
  strong,
  sub,
  sup,
  tt,
  var,
  b,
  u,
  i,
  center,
  dl,
  dt,
  dd,
  ol,
  ul,
  li,
  fieldset,
  form,
  label,
  legend,
  table,
  caption,
  tbody,
  tfoot,
  thead,
  tr,
  th,
  td,
  article,
  aside,
  canvas,
  details,
  embed,
  figure,
  figcaption,
  footer,
  header,
  hgroup,
  menu,
  nav,
  output,
  ruby,
  section,
  summary,
  time,
  mark,
  audio,
  video,
  button {
    margin: 0;
    padding: 0;
    font-family: inherit;
    font-style: inherit;
    font-size: inherit;
    font-weight: inherit;
    border: 0;
    border-radius: 0;
    background-color: transparent;
    color: inherit;
    text-decoration: none;
  }
  ol,
  ul {
    list-style: none;
  }
  blockquote,
  q {
    quotes: none;
  }
  blockquote:before,
  blockquote:after,
  q:before,
  q:after {
    content: '';
    content: none;
  }
  table {
    border-collapse: collapse;
    border-spacing: 0;
  }
  button,
  input,
  textarea,
  select {
    font-family: inherit;
    font-style: inherit;
    font-size: inherit;
    font-weight: inherit;
    border: 0;
    appearance: none;
    &:focus:not(:focus-visible) {
      outline: none;
    }
  }

  fieldset {
    border: 0;
    margin: 0;
    min-width: 0;
    padding: 0.01em 0 0 0;
  }

  .blur-up {
    -webkit-filter: blur(5px);
    filter: blur(5px);
    transition: filter 400ms, -webkit-filter 400ms;
    width: 200px;
    overflow: hidden;
  }

  .blur-up.lazyloaded {
    -webkit-filter: blur(0);
    filter: blur(0);
  }

  .js-focus-visible :focus:not(.focus-visible) {
    outline: none;
  }

  .js-focus-visible .focus-visible {
    outline: 1px dashed #666;
  }
`;

// src/components/buttons/ButtonPlus.tsx
import styled from "@emotion/styled";
import React2 from "react";
import { animated as animated2, useSpring as useSpring2 } from "react-spring";
var Div = styled.div`
  height: 40px;
  width: 40px;
  min-height: 40px;
  min-width: 40px;
  position: relative;
`;
var PlusLine = styled(animated2.line, {
  shouldForwardProp: (prop) => prop !== "isHover" && prop !== "isOpen"
})`
  shape-rendering: crispedges;

  transform-origin: 50% 50%;
  transform: rotate(0deg);
  opacity: 1;
  ${(props) => props.isHover || props.isOpen ? "stroke:rgb(255,255,255)" : "stroke:rgb(30,60,90)"};
`;
var MinusLine = styled(animated2.line, {
  shouldForwardProp: (prop) => prop !== "isHover" && prop !== "isOpen"
})`
  shape-rendering: crispedges;

  transform-origin: 50% 50%;
  transform: rotate(0deg);

  ${(props) => props.isHover || props.isOpen ? "stroke:rgb(255,255,255)" : "stroke:rgb(30,60,90)"};
`;
var Box = styled.rect`
  transition: all 0.4s linear;
  ${(props) => props.isHover || props.isOpen ? "fill:rgb(30,60,90)" : "fill:rgb(255,255,255)"};
`;
var ButtonPlus = ({ isOpen, isHover }) => {
  const plusLineRotate = useSpring2({
    transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
    config: { mass: 10, tension: 2e3, friction: 200, duration: 300 }
  });
  const minusLineRotate = useSpring2({
    opacity: isOpen ? 0 : 1,
    transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
    config: { mass: 10, tension: 2e3, friction: 200, duration: 300 }
  });
  return /* @__PURE__ */ React2.createElement(Div, null, /* @__PURE__ */ React2.createElement("svg", { id: "e2kt2k1630ka1", viewBox: "0 0 40 40" }, /* @__PURE__ */ React2.createElement("g", { id: "e2kt2k1630ka2" }, /* @__PURE__ */ React2.createElement(
    "path",
    {
      id: "e2kt2k1630ka3",
      d: "M39,1L39,39L1,39L1,1L39,1M40,0L0,0L0,40L40,40L40,0Z",
      fill: "rgb(30,60,90)",
      stroke: "none",
      strokeWidth: "1"
    }
  )), /* @__PURE__ */ React2.createElement("g", { id: "e2kt2k1630ka4" }, /* @__PURE__ */ React2.createElement(
    "line",
    {
      id: "e2kt2k1630ka5",
      x1: "20",
      y1: "13.500000",
      x2: "20",
      y2: "26.500000",
      fill: "none",
      stroke: "rgb(30,60,90)",
      strokeWidth: "1",
      strokeMiterlimit: "10"
    }
  )), /* @__PURE__ */ React2.createElement("g", { id: "e2kt2k1630ka6" }, /* @__PURE__ */ React2.createElement(
    "line",
    {
      id: "e2kt2k1630ka7",
      x1: "26.500000",
      y1: "20",
      x2: "13.500000",
      y2: "20",
      fill: "none",
      stroke: "rgb(30,60,90)",
      strokeWidth: "1",
      strokeMiterlimit: "10"
    }
  )), /* @__PURE__ */ React2.createElement("g", { id: "e2kt2k1630ka8" }, /* @__PURE__ */ React2.createElement(
    Box,
    {
      isOpen,
      isHover,
      id: "e2kt2k1630ka9",
      width: "39",
      height: "39",
      rx: "0",
      ry: "0",
      transform: "matrix(1 0 0 1 0.50000000000000 0.50000000000000)",
      stroke: "none",
      strokeWidth: "1",
      strokeOpacity: "0"
    }
  ), /* @__PURE__ */ React2.createElement(
    "path",
    {
      id: "e2kt2k1630ka10",
      d: "M39,1L39,39L1,39L1,1L39,1M40,0L0,0L0,40L40,40L40,0Z",
      fill: "rgb(30,60,90)",
      stroke: "none",
      strokeWidth: "1"
    }
  )), /* @__PURE__ */ React2.createElement("g", { id: "e2kt2k1630ka11", opacity: "0" }, /* @__PURE__ */ React2.createElement(
    "line",
    {
      id: "e2kt2k1630ka12",
      x1: "26.500000",
      y1: "20",
      x2: "13.500000",
      y2: "20",
      fill: "none",
      stroke: "rgb(30,60,90)",
      strokeWidth: "1",
      strokeMiterlimit: "10"
    }
  )), /* @__PURE__ */ React2.createElement(
    MinusLine,
    {
      id: "e2kt2k1630ka13",
      x1: "20",
      y1: "13.500000",
      x2: "20",
      y2: "26.500000",
      fill: "none",
      stroke: "rgb(30,60,90)",
      strokeWidth: "1",
      strokeMiterlimit: "10",
      style: minusLineRotate,
      isHover,
      isOpen
    }
  ), /* @__PURE__ */ React2.createElement(
    PlusLine,
    {
      id: "e2kt2k1630ka14",
      x1: "26.500000",
      y1: "20",
      x2: "13.500000",
      y2: "20",
      fill: "none",
      stroke: "rgb(30,60,90)",
      strokeWidth: "1",
      strokeMiterlimit: "10",
      style: plusLineRotate,
      isHover,
      isOpen
    }
  )));
};

// src/components/accordion-list/AccordionItem.tsx
var Item = styled2.div`
  ${(props) => !props.lineSeperator && `border-bottom: 1px solid ${props.theme.colors.primary};`};

  ${(props) => props.isOpen && !props.lineSeperator && "border-color: transparent"};

  transition: border-color 200ms ease-in-out;
`;
var Header = styled2.button`
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: relative;
  padding: ${(props) => `25px ${props.theme.breakpoints.sm.gutter}px`};
  width: 100%;

  text-align: left;

  background-color: transparent;
  appearance: none;
  cursor: pointer;

  ${(props) => props.lineSeperator && `border-bottom: 1px solid ${props.theme.colors.primary};`}

  @media ${device.tablet} {
    padding: 27px 45px 27px 0;
  }
`;
var Content = styled2(animated3.div, {
  shouldForwardProp: shouldForwardProp("div")
})`
  padding: ${(props) => props.isFullWidth ? "0" : `0 ${props.theme.breakpoints.sm.gutter}px`};
  height: 0px;
  overflow: hidden;
  ${mobileGutterPadding}

  @media ${device.tablet} {
    padding: ${(props) => props.isFullWidth ? "0" : "0 45px 0 0"};
  }
`;
var Heading = styled2.h3`
  ${typography.h4};
  margin-right: 40px;
  scroll-margin-top: 125px;
`;
var AccordionMarkdown = styled2(animated3.div, {
  shouldForwardProp: shouldForwardProp("div")
})`
  position: relative;
  padding: ${(props) => props.lineSeperator ? `0 ${props.theme.containerGutter}px` : "40px 0"};
  ${(props) => props.contentSpacing && "padding: 40px 20px 60px;"};
  width: 100%;
  opacity: 0;

  @media ${device.mobile} {
    padding: ${(props) => props.lineSeperator ? `0 ${props.theme.breakpoints.sm.gutter}px` : "40px 0"};
  }

  @media ${device.tablet} {
    padding: ${(props) => props.lineSeperator ? "0" : "40px 0"};
  }

  @media (min-width: 600px) {
    ${(props) => !props.isFullWidth && !props.isMarkdownFullWidth && `width: 80%;`}
  }
  @media ${device.desktop} {
    ${(props) => props.contentSpacing && "padding: 43px 43px 60px;"};
  }

  &:before {
    content: '';
    display: ${(props) => props.lineSeperator ? "none" : "block"};
    position: absolute;
    top: 0;
    left: ${(props) => props.isFullWidth ? "20px" : "0"};

    height: 1px;
    width: 27px;

    background-color: ${(props) => props.theme.colors.foreground};

    @media ${device.tablet} {
      left: 0;
    }
  }
  ${(props) => props.markdownPadding && "padding:" + props.markdownPadding}
`;
var AccordionItem = ({
  index,
  title,
  content,
  isFullWidth,
  lineSeperator,
  contentSpacing,
  onMouseEnter,
  onMouseLeave,
  isMarkdownFullWidth,
  markdownPadding,
  onClose,
  onOpen,
  isOpen
}) => {
  const [hover, setHover] = useState3(false);
  const [copyRef, copySize] = useMeasure({ polyfill: ResizeObserver });
  let scrollToId = title;
  if (scrollToId !== void 0) {
    if (scrollToId.includes(". ")) scrollToId = scrollToId.split(". ")[1];
    scrollToId = scrollToId?.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/g, "").replaceAll(" ", "_");
    scrollToId = toNonIcelandicLowerCase(scrollToId);
  }
  useEffect3(() => {
    if (window?.location?.hash && window.location.hash.substring(1) == scrollToId && onOpen && index !== void 0) {
      onOpen(index);
    }
  }, []);
  const setHeight = useSpring3({
    height: isOpen ? `${get(copySize, "height", 0)}px` : "0px",
    visability: isOpen ? "visible" : "hidden"
  });
  const fadeIn = useSpring3({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? "translateY(0px)" : "translateY(10px)"
  });
  const onEnter = (e) => {
    setHover(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };
  const onLeave = (e) => {
    setHover(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };
  return /* @__PURE__ */ React3.createElement(Item, { lineSeperator, isOpen }, /* @__PURE__ */ React3.createElement(
    Header,
    {
      onClick: () => {
        if (onClose && onOpen && index !== void 0) {
          return isOpen ? onClose(index) : onOpen(index);
        }
      },
      role: "button",
      "aria-expanded": isOpen ? true : false,
      "aria-controls": "content",
      isFullWidth,
      lineSeperator,
      onMouseLeave: onLeave,
      onMouseEnter: onEnter
    },
    /* @__PURE__ */ React3.createElement(Heading, { id: scrollToId }, title),
    /* @__PURE__ */ React3.createElement(ButtonPlus, { isOpen: !!isOpen, isHover: hover })
  ), /* @__PURE__ */ React3.createElement(
    Content,
    {
      className: isOpen ? "open" : "",
      style: { ...setHeight, visibility: isOpen ? "visible" : "hidden" },
      id: "content",
      isFullWidth
    },
    /* @__PURE__ */ React3.createElement(
      AccordionMarkdown,
      {
        ref: copyRef,
        isFullWidth,
        isMarkdownFullWidth,
        lineSeperator,
        contentSpacing,
        markdownPadding,
        style: { ...fadeIn }
      },
      /* @__PURE__ */ React3.createElement(RevealContext.Provider, { value: { enabled: !!isOpen } }, content)
    )
  ));
};

// src/components/accordion-list/AccordionList.tsx
import styled5 from "@emotion/styled";
import React6, { Children as Children2, useState as useState4 } from "react";

// src/components/reveal/Reveal.tsx
import React4 from "react";
import { animated as animated4 } from "react-spring";
function Reveal({
  children,
  style = {},
  config: config2,
  ...rest
}) {
  const reveal = useReveal(config2);
  return /* @__PURE__ */ React4.createElement(
    animated4.div,
    {
      ref: reveal.ref,
      style: {
        ...style,
        ...reveal.style
      },
      ...rest
    },
    children
  );
}

// src/components/segment/Segment.tsx
import styled4 from "@emotion/styled";
import React5 from "react";

// src/components/container/Container.tsx
import styled3 from "@emotion/styled";
var Container = styled3.div`
  position: relative;
  ${container}
`;

// src/components/segment/Segment.tsx
var SegmentHost = styled4.section`
  background-color: ${(props) => props.theme.colors.background};
  padding-top: ${(props) => props.paddingTopMobile !== void 0 ? props.paddingTopMobile : props.spaceGutter}px;
  padding-bottom: ${(props) => props.paddingBottomMobile !== void 0 ? props.paddingBottomMobile : props.spaceGutter}px;
  ${(props) => props.isGray && `background-color: ${props.theme.colors.backgroundAlt}`};
  ${(props) => props.isGray && `margin-bottom: ${props.spaceGutter}px`};

  &:last-of-type {
    padding-bottom: ${(props) => props.spaceGutter * 2}px;
  }

  margin-top: -1px;
  margin-bottom: -1px;

  @media (min-width: ${(props) => props.theme.breakpoints.md.width}px) {
    padding-top: ${(props) => props.paddingTop !== void 0 ? props.paddingTop / props.theme.pageLimit(props) * 100 : props.spaceGutter * 2 / props.theme.pageLimit(props) * 100}%;
    padding-bottom: ${(props) => props.paddingTop !== void 0 && props.paddingBottom !== void 0 ? props.paddingBottom / props.theme.pageLimit(props) * 100 : props.spaceGutter * 2 / props.theme.pageLimit(props) * 100}%;
    ${(props) => props.isGray && `margin-bottom: ${props.spaceGutter * 2}px`};

    &:last-of-type {
      padding-bottom: ${(props) => props.spaceGutter * 3}px;
    }
  }

  @media (min-width: ${(props) => props.theme.pageLimit}px) {
    padding-top: ${(props) => props.paddingTop !== void 0 ? props.paddingTop : props.spaceGutter * 2}px;
    padding-bottom: ${(props) => props.paddingBottom !== void 0 ? props.paddingBottom : props.spaceGutter * 2}px;
    ${(props) => props.isGray && `margin-bottom: ${props.spaceGutter * 2}px`};
  }
`;
var Segment = ({
  children,
  isGray,
  isArticle,
  container: container2 = true,
  paddingTop,
  paddingBottom,
  paddingBottomMobile,
  paddingTopMobile,
  ...rest
}) => {
  const spaceGutter = isArticle ? 18 : 36;
  return /* @__PURE__ */ React5.createElement(
    SegmentHost,
    {
      isGray,
      paddingTop,
      paddingBottom,
      paddingBottomMobile,
      paddingTopMobile,
      spaceGutter,
      ...rest
    },
    container2 ? /* @__PURE__ */ React5.createElement(Container, null, children) : children
  );
};

// src/components/accordion-list/AccordionList.tsx
var Host = styled5(Reveal)`
  margin: ${(props) => `0 -${props.theme.breakpoints.sm.gutter}px`};
  ${mobileGutterMargin}
  @media ${device.tablet} {
    margin: 0;
  }
`;
var Title = styled5.h2`
  ${typography.h2}
  flex: 1;

  margin-bottom: 40px;
  margin-left: ${(props) => props.theme.breakpoints.sm.gutter}px;

  @media ${device.tablet} {
    margin-left: 0;
  }
`;
var List = styled5.div``;
var Top = styled5.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
`;
var Categories = styled5.div`
  padding-left: ${(props) => `${props.theme.breakpoints.sm.gutter}px`};
  margin-right: ${(props) => `${props.theme.breakpoints.sm.gutter}px`};
  margin-left: auto;
  padding-bottom: ${(props) => props.theme.breakpoints.sm.gutter * 2}px;

  @media ${device.tablet} {
    margin-right: 45px;
    padding-bottom: 40px;
  }
`;
var Category = styled5.button`
  border-bottom: 1px solid;
  border-bottom-color: ${(props) => props.active ? "#000" : "transparent"};
  margin-right: 20px;
  cursor: pointer;

  &:last-child {
    margin-right: 0;
  }
`;
var AccordionList = ({
  children,
  title,
  firstOpen = false,
  singleDisplay = false
}) => {
  const [activeIndexes, setActiveIndexes] = useState4(firstOpen ? [0] : []);
  const onOpen = (i) => {
    if (singleDisplay) {
      setActiveIndexes([i]);
    } else {
      setActiveIndexes((val) => [...val, i]);
    }
  };
  const onClose = (i) => {
    setActiveIndexes((val) => [...val.filter((n) => n !== i)]);
  };
  const childs = Children2.toArray(children).map(
    (el, index) => React6.cloneElement(el, {
      ...el.props,
      index,
      isOpen: activeIndexes.includes(index),
      onOpen,
      onClose
    })
  );
  const categories = childs.reduce((acc, child) => {
    const category = (
      /*         Prismic.text(child?.props?.category?.props?.value) || */
      child?.props?.category
    );
    if (category && typeof category === "string" && !acc.includes(category)) {
      acc.push(category);
    }
    return acc;
  }, []);
  const [selectedCategory, setSelectedCategory] = useState4(null);
  const filteredChildren = childs.filter((child) => {
    if (child?.props) {
      const category = (
        /*         Prismic.text(child?.props?.category?.props?.value) || */
        child?.props?.category
      );
      if (category === selectedCategory || selectedCategory === null) {
        return true;
      }
      return false;
    }
    return true;
  });
  return /* @__PURE__ */ React6.createElement(Segment, null, /* @__PURE__ */ React6.createElement(Host, null, /* @__PURE__ */ React6.createElement(Top, null, title && /* @__PURE__ */ React6.createElement(Title, null, title), categories.length > 0 && typeof categories[0] === "string" && /* @__PURE__ */ React6.createElement(Categories, null, /* @__PURE__ */ React6.createElement(
    Category,
    {
      onClick: () => setSelectedCategory(null),
      active: selectedCategory === null
    },
    "Allt"
  ), categories.map((category) => {
    return /* @__PURE__ */ React6.createElement(
      Category,
      {
        key: category,
        onClick: () => setSelectedCategory(category),
        active: selectedCategory === category
      },
      category
    );
  }))), /* @__PURE__ */ React6.createElement(List, null, filteredChildren)));
};

// src/components/buttons/ArrowButton.tsx
import styled6 from "@emotion/styled";
import React7, { forwardRef, useState as useState5, useEffect as useEffect4 } from "react";
import { animated as animated5, useSpring as useSpring4 } from "react-spring";
var ArrowLink = styled6.a`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  color: ${(props) => props.isWhite ? "#fff" : props.theme.colors.foreground};
  max-width: 100%;
`;
var Icon = styled6(animated5.span, {
  shouldForwardProp: shouldForwardProp("div")
})`
  position: relative;
  z-index: 1;
  overflow: hidden;
  display: flex;
  border: 1px solid
    ${(props) => props.isWhite ? "#fff" : props.theme.colors.foreground};
  width: ${(props) => props.isSmall ? "29px" : "40px"};
  height: ${(props) => props.isSmall ? "29px" : "40px"};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  color: ${(props) => props.isWhite && "#fff"};
`;
var SmallSvg = styled6.svg`
  position: relative;
  z-index: 3;
  left: ${(props) => props.isArrowDown ? "0px" : "1px"};
  ${(props) => props.isArrowDown && "transform: rotate(90deg)"};
`;
var LargeSvg = styled6.svg`
  position: relative;
  z-index: 2;
`;
var ArrowPath = styled6.path`
  fill: ${(props) => props.isWhite && !props.hover ? "#FFF" : props.isWhite && props.hover ? props.theme.colors.primary : props.hover ? "#fff" : props.theme.colors.primary};
  transition: fill 200ms ease-in-out;
`;
var ArrowStroke = styled6.path`
  stroke: ${(props) => props.hover ? "#fff" : props.theme.colors.primary};
  transition: stroke 200ms ease-in-out;
`;
var Text = styled6.span`
  ${(props) => props.hideText && sronly}
  ${typography.body}
  margin-top: 2px;
  margin-left: 15px;
  padding-right: 15px;
  font-weight: 500;
  line-height: calc(29 / 18);
  color: ${(props) => props.isWhite && "#fff"};
`;
var ArrowButton = forwardRef(function ArrowButton2(props, ref) {
  const {
    title,
    notLink,
    isSmall,
    isArrowDown,
    isWhite,
    hover,
    hideText,
    onMouseEnter,
    onMouseLeave,
    as,
    ...rest
  } = props;
  const [isHover, setHover] = useState5(hover || false);
  const backgroundFade = useSpring4({
    backgroundColor: isHover ? "rgba(32,68,106,1)" : "rgba(32,68,106,0)"
  });
  const isWhiteFade = useSpring4({
    backgroundColor: isHover ? "rgba(255,255,255,1)" : "rgba(255,255,255,0)"
  });
  const onEnter = (e) => {
    if (hover === true) return;
    setHover(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };
  const onLeave = (e) => {
    if (hover === true) return;
    setHover(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };
  useEffect4(() => {
    if (hover === true) {
      setHover(true);
    } else {
      setHover(false);
    }
    return () => {
      setHover(false);
    };
  }, [hover]);
  const content = /* @__PURE__ */ React7.createElement(React7.Fragment, null, /* @__PURE__ */ React7.createElement(
    Icon,
    {
      isSmall,
      isWhite,
      style: isWhite ? isWhiteFade : backgroundFade
    },
    isSmall ? /* @__PURE__ */ React7.createElement(
      SmallSvg,
      {
        xmlns: "http://www.w3.org/2000/svg",
        width: "6",
        height: "10",
        viewBox: "0 0 6 10",
        isArrowDown
      },
      /* @__PURE__ */ React7.createElement(
        ArrowStroke,
        {
          hover: isHover,
          fill: "none",
          fillRule: "evenodd",
          stroke: "#1E3D5A",
          d: "M-.005.005l5.32 4.655-5.32 4.655"
        }
      )
    ) : /* @__PURE__ */ React7.createElement(LargeSvg, { width: "16", height: "9", xmlns: "http://www.w3.org/2000/svg" }, /* @__PURE__ */ React7.createElement("g", { fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React7.createElement("path", { stroke: "currentColor", d: "M-10.5-15.5h39v39h-39z" }), /* @__PURE__ */ React7.createElement(
      ArrowPath,
      {
        hover: isHover,
        d: "M6.841-.012l9 4.5-9 4.5V4.987H.207v-1h6.634v-4z",
        fill: "currentColor",
        fillRule: "nonzero",
        isWhite
      }
    )))
  ), title && /* @__PURE__ */ React7.createElement(Text, { hideText, isWhite }, title));
  if (notLink) {
    return /* @__PURE__ */ React7.createElement(ArrowLink, { as: "div", ref, ...rest }, content);
  }
  return /* @__PURE__ */ React7.createElement(
    ArrowLink,
    {
      as,
      ref,
      title,
      ...rest,
      onMouseEnter: onEnter,
      onMouseLeave: onLeave,
      isWhite
    },
    content
  );
});

// src/components/buttons/Button.tsx
import styled8 from "@emotion/styled";
import { darken } from "polished";
import React9, { useState as useState6, forwardRef as forwardRef2 } from "react";
import { animated as animated6, useSpring as useSpring5 } from "react-spring";

// src/components/loader/LoadingCircles.tsx
import styled7 from "@emotion/styled";
import React8 from "react";
var Host2 = styled7.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;

  @keyframes fadedelay {
    0%,
    80%,
    100% {
      transform: scale(0);
      -webkit-transform: scale(0);
    }
    40% {
      transform: scale(1);
      -webkit-transform: scale(1);
    }
  }

  @keyframes bouncedelay {
    0% {
      transform: translateY(0px);
    }
    35% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
    70% {
      transform: translateY(3px);
    }
    85% {
      transform: translateY(0px);
    }
  }
`;
var Circle = styled7.div`
  float: left;
  height: ${({ size }) => size};

  width: ${({ size }) => size};
  border-radius: 100%;

  ${({ animation }) => animation === "fade" && "animation: fadedelay 1s ease-in-out infinite both"};

  ${({ animation }) => animation === "bounce" && "animation: bouncedelay 1.4s ease-in-out infinite"};

  &:not(:first-of-type) {
    margin-left: ${({ size }) => size === "10px" ? "6px" : "32px"};
  }
`;
var CircleOne = styled7(Circle)`
  animation-delay: -0.32s;

  ${({ color }) => color === "blue" && "background-color: #bfe1f2"};

  ${({ color, theme: theme2 }) => color === "multi" && `background-color: ${theme2.colors.primary}`};
`;
var CircleTwo = styled7(Circle)`
  animation-delay: -0.16s;

  ${({ color }) => color === "blue" && "background-color: #9fd6f1"};

  ${({ color, theme: theme2 }) => color === "multi" && `background-color: ${theme2.colors.secondary}`};
`;
var CircleThree = styled7(Circle)`
  ${({ color }) => color === "blue" && "background-color: #85d7fb"};

  ${({ color, theme: theme2 }) => color === "multi" && `background-color: ${theme2.colors.ternary}`};
`;
function LoadingCircles({ color, animation, size }) {
  return /* @__PURE__ */ React8.createElement(Host2, null, /* @__PURE__ */ React8.createElement(CircleOne, { size, animation, color }), /* @__PURE__ */ React8.createElement(CircleTwo, { size, animation, color }), /* @__PURE__ */ React8.createElement(CircleThree, { size, animation, color }));
}

// src/components/buttons/Button.tsx
var getBg = (props) => {
  return props["databg"] ? typeof props["databg"] === "function" ? props["databg"](props) : props["databg"] : props.theme.colors.secondary;
};
var Btn = styled8.button`
  appearance: none;
  border: none;
  background: none;
  background-color: ${(props) => props.bordered ? "none" : getBg(props)};
  border: ${(props) => props.bordered ? `1px solid ${getBg(props)}` : ""};
  ${typography.h5};
  position: relative;
  font-family: ${(props) => props.theme.font.family};
  color: ${(props) => props.bordered ? getBg(props) : "#ffffff"};
  min-height: ${(props) => props.isSmall ? "40px" : "64px"};
  padding: ${(props) => props.isSmall ? "10px 20px" : "0 38px"};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  width: 100%;
  min-width: ${(props) => props.isSmall ? "80px" : "220px"};
  ${(props) => props.isSmall && "max-width: 80px"};

  text-decoration: none;

  display: flex;
  align-items: center;
  justify-content: ${(props) => props.icon ? "space-between" : "center"};

  &:focus {
    outline: 1px dashed #666;
  }
`;
var BtnText = styled8.div`
  position: relative;
  z-index: 7;
  font-weight: 500;
  flex: 1;
  text-align: ${(props) => props.icon ? "left" : "center"};
`;
var BtnBackground = styled8(animated6.div, {
  shouldForwardProp: shouldForwardProp("div")
})`
  position: absolute;
  top: 0;
  left: -10px;
  right: -10px;
  bottom: 0;
`;
var BtnBg1 = styled8(BtnBackground)`
  background-color: ${(props) => darken(-0.05, getBg(props))};
`;
var BtnBg2 = styled8(BtnBackground)`
  background-color: ${(props) => darken(-0.1, getBg(props))};
`;
var Icon2 = styled8.div`
  display: block;
  position: static;
  right: 32px;
  z-index: 12;
  margin-left: ${(props) => props.iconAlign === "right" ? 15 : 0}px;
  margin-right: ${(props) => props.iconAlign === "left" ? 10 : 0}px;
`;
var Button = forwardRef2(function ButtonInner({
  title,
  bg,
  bordered = false,
  onMouseEnter,
  as,
  icon,
  isSmall,
  loading,
  iconAlign = "right",
  ...rest
}, ref) {
  const isMobile = useMedia("sm");
  const [activated, setActivated] = useState6(false);
  const [hover, setHover] = useState6(false);
  const props = useSpring5({
    from: {
      red: "-100%",
      blue: "-110%"
    },
    red: hover ? "0%" : "100%",
    blue: hover ? "0%" : "110%",
    immediate: !activated,
    reset: hover,
    config: {
      mass: 3,
      tension: 440,
      friction: 80
    }
  });
  if (ref) {
  }
  const onEnter = (e) => {
    setHover(true);
    setActivated(true);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };
  const onLeave = (e) => {
    setHover(false);
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  };
  const HostButton = as ? Btn.withComponent(as) : Btn;
  return /* @__PURE__ */ React9.createElement(
    HostButton,
    {
      ...rest,
      databg: bg,
      bordered,
      onMouseEnter: isMobile ? void 0 : onEnter,
      onMouseLeave: onLeave,
      icon,
      isSmall,
      title
    },
    !!icon && iconAlign === "left" && /* @__PURE__ */ React9.createElement(Icon2, { iconAlign }, icon),
    /* @__PURE__ */ React9.createElement(BtnText, { icon }, loading ? /* @__PURE__ */ React9.createElement(LoadingCircles, { color: "blue", size: "10px", animation: "fade" }) : title),
    !!icon && iconAlign === "right" && /* @__PURE__ */ React9.createElement(Icon2, { iconAlign }, icon),
    /* @__PURE__ */ React9.createElement(
      BtnBg1,
      {
        databg: bg,
        style: {
          opacity: bordered ? 0.05 : 1,
          transform: props.red.to((x) => `translate3d(${x},0,0)`)
        }
      }
    ),
    /* @__PURE__ */ React9.createElement(
      BtnBg2,
      {
        databg: bg,
        style: {
          opacity: bordered ? 0.05 : 1,
          transform: props.blue.to((x) => `translate3d(${x},0,0)`)
        }
      }
    )
  );
});

// src/components/buttons/CardButton.tsx
import styled9 from "@emotion/styled";
import React10, { forwardRef as forwardRef3 } from "react";
var Button2 = styled9.div`
  flex: 1;

  display: flex;
  border: none;
  background: none;
  width: 100%;

  cursor: pointer;

  ${typography.h5}
  font-family: ${(props) => props.theme.font.family};
  color: ${(props) => props.theme.colors.primary};

  padding-top: 12px;
  padding-bottom: 12px;

  align-items: center;

  > svg {
    flex-shrink: 0;
  }
`;
var ButtonLink = styled9(Button2)`
  &:hover {
    text-decoration: underline;
  }
`;
var CardButton = forwardRef3(function CardButton2(props, ref) {
  const { title, notLink, as = "a", ...rest } = props;
  const content = /* @__PURE__ */ React10.createElement(React10.Fragment, null, /* @__PURE__ */ React10.createElement(
    "svg",
    {
      width: "16",
      height: "12",
      xmlns: "http://www.w3.org/2000/svg",
      style: { marginTop: -4, marginRight: 10 }
    },
    /* @__PURE__ */ React10.createElement("g", { fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React10.createElement("g", { stroke: "currentColor" }, /* @__PURE__ */ React10.createElement("path", { d: "M9.395 1.665l5.32 4.655-5.32 4.655" }), /* @__PURE__ */ React10.createElement("path", { strokeLinejoin: "round", d: "M14.05 6.32H.75" })))
  ), /* @__PURE__ */ React10.createElement("span", null, title));
  if (notLink) {
    return /* @__PURE__ */ React10.createElement(Button2, { ref, ...rest }, content);
  }
  return /* @__PURE__ */ React10.createElement(ButtonLink, { as, ref, title, ...rest }, content);
});

// src/components/buttons/ExpandArrow.tsx
import styled10 from "@emotion/styled";
import React11 from "react";
import { animated as animated7, useSpring as useSpring6 } from "react-spring";
var Path = styled10(animated7.path)`
  transform-origin: center center;
  transform: rotate(0deg);
`;
var ExpandArrow = ({ isOpen }) => {
  const rotate = useSpring6({
    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)"
  });
  return /* @__PURE__ */ React11.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "23", height: "13" }, /* @__PURE__ */ React11.createElement(
    Path,
    {
      style: rotate,
      fill: "#1E3D5A",
      fillRule: "evenodd",
      d: "M21.112 0l1.01 1.01L12.07 11.061l.05.05-1.01 1.01-.05-.05-.05.05L10 11.112l.05-.051L0 1.01 1.01 0l10.05 10.05L21.112 0z"
    }
  ));
};

// src/components/buttons/FormButton.tsx
import styled11 from "@emotion/styled";
import React12 from "react";
var MyButton = styled11(Button)`
  min-height: 55px;
  max-height: 55px;
`;
var FormButton = function FormButton2(props) {
  const { ...rest } = props;
  return /* @__PURE__ */ React12.createElement(MyButton, { ...rest });
};

// src/components/buttons/IconLink.tsx
import styled31 from "@emotion/styled";
import React190, { forwardRef as forwardRef4, useRef as useRef2 } from "react";
import { animated as animated9 } from "react-spring";

// src/components/icons/index.tsx
import React189 from "react";

// src/components/icons/arsskyrsla/Anaegjuvogin.tsx
import React13 from "react";
function Anaegjuvogin() {
  return /* @__PURE__ */ React13.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "92",
      height: "139",
      viewBox: "0 0 92 139"
    },
    /* @__PURE__ */ React13.createElement(
      "g",
      {
        id: "\xC1rssk\xFDrsla/icon/\xC1n\xE6gjuvogin",
        fill: "#163E63",
        fillRule: "evenodd",
        stroke: "none",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-1",
          d: "M3.52 129.285h2.046l-1.003-6.343h-.04l-1.003 6.343zm1.268-10.067H3.09l1.616-2.946h2.762l-2.68 2.946zm-2.087 15.55H.246l2.72-14.772h3.152l2.721 14.772H6.384l-.45-3.273H3.151l-.45 3.273z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-3",
          d: "M9.08524468 134.768387L9.08524468 119.995796 11.4383559 119.995796 14.5279507 128.445835 14.5684856 128.445835 14.5684856 119.995796 16.8604964 119.995796 16.8604964 134.768387 14.6507476 134.768387 11.4177904 125.540436 11.3766594 125.540436 11.3766594 134.768387z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-5",
          d: "M23.099 129.08h-1.655l1.655-5.423v5.423zm5.98-6.874v-2.21h-7.205l-4.767 14.772h2.578l1.063-3.478h2.35v3.478h5.97v-2.21h-3.646v-4.337h2.377v-2.21h-2.377v-3.805h3.658z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-7",
          d: "M33.31 127.28h4.153v7.559h-1.228l-.47-1.175c-.758.9-1.371 1.309-2.558 1.309-2.005 0-3.396-1.432-3.396-3.785v-7.612c0-2.087 1.227-3.785 3.723-3.785 2.538 0 3.806 1.841 3.806 4.051v.941h-2.414c0-2.21-.45-2.782-1.453-2.782-.614 0-1.125.368-1.125 1.616v7.591c0 .941.307 1.555 1.186 1.555 1.023 0 1.392-.572 1.392-2.046v-1.432h-1.617v-2.005z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-9",
          d: "M37.946 132.763h.532c.982 0 1.33-.409 1.33-1.227v-11.54h2.537v11.826c0 1.882-.757 3.03-3.56 3.03h-.84v-2.089z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-11",
          d: "M43.871 119.996h2.537v11.15c0 1.085.43 1.617 1.35 1.617.922 0 1.351-.532 1.351-1.616v-11.151h2.537v11.048c0 2.538-1.473 3.929-3.887 3.929-2.415 0-3.888-1.391-3.888-3.929v-11.048z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-13",
          d: "M63.27 131.126c0 1.105.326 1.637 1.35 1.637 1.022 0 1.35-.532 1.35-1.637v-7.488c0-1.105-.328-1.637-1.35-1.637-1.024 0-1.35.532-1.35 1.637v7.488zm-2.538-7.55c0-2.087 1.227-3.785 3.887-3.785s3.888 1.698 3.888 3.785v7.612c0 2.087-1.228 3.785-3.888 3.785s-3.887-1.698-3.887-3.785v-7.612z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-15",
          d: "M73.234 127.28h4.154v7.488H76.16l-.47-1.104c-.758.9-1.372 1.309-2.559 1.309-2.005 0-3.396-1.432-3.396-3.785v-7.612c0-2.087 1.228-3.785 3.724-3.785 2.537 0 3.806 1.841 3.806 4.051v.941H74.85c0-2.21-.45-2.782-1.452-2.782-.614 0-1.126.368-1.126 1.616v7.591c0 .941.307 1.555 1.187 1.555 1.023 0 1.391-.572 1.391-2.046v-1.432h-1.616v-2.005z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-17",
          d: "M79.0865927 134.768178L81.6238993 134.768178 81.6238993 119.995588 79.0865927 119.995588z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-19",
          d: "M83.1994853 134.768387L83.1994853 119.995796 85.5525966 119.995796 88.6421913 128.445835 88.6827263 128.445835 88.6827263 119.995796 90.9747371 119.995796 90.9747371 134.768387 88.7649883 134.768387 85.532031 125.540436 85.4909 125.540436 85.4909 134.768387z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-21",
          d: "M16.16 113.787h2.537V99.014H16.16v14.773zm1.514-15.55h-1.699l1.617-2.947h2.762l-2.68 2.946z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-23",
          d: "M24.897 102.656c-.102-1.268-.778-1.637-1.35-1.637-.82 0-1.27.532-1.27 1.453 0 2.517 5.157 3.724 5.157 7.734 0 2.435-1.637 3.786-4.01 3.786-2.353 0-3.704-1.883-3.806-4.092l2.414-.348c.102 1.493.696 2.23 1.514 2.23.88 0 1.474-.47 1.474-1.35 0-2.926-5.157-3.704-5.157-7.94 0-2.352 1.432-3.682 3.929-3.682 2.067 0 3.294 1.493 3.52 3.58l-2.415.266z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-25",
          d: "M28.8254485 113.786951L28.8254485 99.0143599 31.3627551 99.0143599 31.3627551 111.577202 35.3524634 111.577202 35.3524634 113.786951z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-27",
          d: "M36.0895729 113.786951L36.0895729 99.0143599 42.8827471 99.0143599 42.8827471 101.224109 38.6265814 101.224109 38.6265814 105.029621 41.9003716 105.029621 41.9003716 107.23937 38.6265814 107.23937 38.6265814 111.577202 43.1688759 111.577202 43.1688759 113.786951z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-29",
          d: "M43.5376244 113.786951L43.5376244 99.0143599 45.8907356 99.0143599 48.9803304 107.464399 49.0211634 107.464399 49.0211634 99.0143599 51.3125781 99.0143599 51.3125781 113.786951 49.1028293 113.786951 45.8701701 104.558999 45.8290391 104.558999 45.8290391 113.786951z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-31",
          d: "M57.574 102.656c-.102-1.268-.777-1.637-1.35-1.637-.819 0-1.269.532-1.269 1.453 0 2.517 5.156 3.724 5.156 7.734 0 2.435-1.636 3.786-4.01 3.786-2.353 0-3.703-1.883-3.805-4.092l2.414-.348c.102 1.493.695 2.23 1.514 2.23.88 0 1.473-.47 1.473-1.35 0-2.926-5.156-3.704-5.156-7.94 0-2.352 1.432-3.682 3.929-3.682 2.066 0 3.294 1.493 3.519 3.58l-2.415.266z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-33",
          d: "M61.0939502 113.786951L61.0939502 99.0143599 63.6312569 99.0143599 63.6312569 104.477333 63.6720898 104.477333 66.3318953 99.0143599 68.8692019 99.0143599 66.1274325 104.518166 69.2170273 113.786951 66.6388877 113.786951 64.5722033 106.91211 64.5313703 106.91211 63.6312569 108.549006 63.6312569 113.786951z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-35",
          d: "M72.184 108.303h2.046l-1.002-6.343h-.041l-1.003 6.343zm-.818 5.484H68.91l2.722-14.773h3.15l2.722 14.773h-2.455l-.45-3.274h-2.783l-.45 3.274z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-38",
          d: "M0 138.78799L54.5805563 138.78799 54.5805563 137.595787 0 137.595787z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-39",
          d: "M57.9157449 138.78799L91.2974367 138.78799 91.2974367 137.595787 57.9157449 137.595787z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-40",
          d: "M60.2120179 120.010192L56.2410868 138.191888 52.2704538 120.010192z"
        }
      ),
      /* @__PURE__ */ React13.createElement(
        "path",
        {
          id: "Fill-41",
          d: "M68.8241367 4.97379915e-14L45.3562113 88.9926097 21.8885839 4.97379915e-14z"
        }
      )
    )
  );
}

// src/components/icons/arsskyrsla/FyrstaSaeti.tsx
import React14 from "react";
function FyrstaSaeti() {
  return /* @__PURE__ */ React14.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "112",
      height: "112",
      viewBox: "0 0 112 112"
    },
    /* @__PURE__ */ React14.createElement(
      "g",
      {
        id: "\xC1rssk\xFDrsla/icon/FyrstaS\xE6ti",
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React14.createElement(
        "g",
        {
          id: "gaming-trophy-check",
          stroke: "#163E63",
          strokeWidth: "2",
          transform: "translate(20 20)"
        },
        /* @__PURE__ */ React14.createElement(
          "path",
          {
            id: "Path",
            d: "M47.7484337 9.79644578L33.1550602 28.2301807 25.4743373 20.5494578"
          }
        ),
        /* @__PURE__ */ React14.createElement(
          "path",
          {
            id: "Path",
            d: "M8.697 41.386a12.29 12.29 0 01-8.028-11.53v-7.681a3.072 3.072 0 013.072-3.073h6.301M63.303 41.386a12.29 12.29 0 008.028-11.53v-7.681a3.072 3.072 0 00-3.072-3.073h-6.301"
          }
        ),
        /* @__PURE__ */ React14.createElement(
          "path",
          {
            id: "Path",
            d: "M63.595 3.818a3.072 3.072 0 00-3.072-3.15H11.477a3.073 3.073 0 00-3.072 3.15C9.47 47.488 32.839 35.01 32.839 51.36c-.17 9.47-4.096 12.98-10.824 13.687a3.229 3.229 0 00-2.894 3.211 3.072 3.072 0 003.072 3.072h27.614a3.072 3.072 0 003.072-3.072c0-1.653-1.25-3.039-2.894-3.21-6.728-.707-10.655-4.219-10.824-13.688 0-16.35 23.368-3.874 24.434-47.543z"
          }
        )
      )
    )
  );
}

// src/components/icons/arsskyrsla/Greidslumat.tsx
import React15 from "react";
function Greidslumat() {
  return /* @__PURE__ */ React15.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "112",
      height: "112",
      viewBox: "0 0 112 112"
    },
    /* @__PURE__ */ React15.createElement(
      "g",
      {
        id: "\xC1rssk\xFDrsla/icon/Grei\xF0slumat",
        fill: "none",
        fillRule: "evenodd",
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2"
      },
      /* @__PURE__ */ React15.createElement("path", { id: "Path", d: "M11 80L100 80" }),
      /* @__PURE__ */ React15.createElement("path", { id: "Path", d: "M13 30L97 47" }),
      /* @__PURE__ */ React15.createElement("path", { id: "Path", d: "M90.089 46L89.911 79" }),
      /* @__PURE__ */ React15.createElement("path", { id: "Path-Copy-3", d: "M60 80L60 58 75 58 75 80" }),
      /* @__PURE__ */ React15.createElement("path", { id: "Path-Copy-5", d: "M75 41.1597189L75 35 90 35 90 45" }),
      /* @__PURE__ */ React15.createElement(
        "path",
        {
          id: "Path-Copy-4",
          d: "M26.25 75.4289551L26.25 54.25 41.25 54.25 41.25 75.75",
          transform: "rotate(90 33.75 65)"
        }
      ),
      /* @__PURE__ */ React15.createElement("path", { id: "Path", d: "M23 32L23 79" })
    )
  );
}

// src/components/icons/arsskyrsla/NewDocument.tsx
import React16 from "react";
function NewDocument() {
  return /* @__PURE__ */ React16.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "49",
      height: "49",
      viewBox: "0 0 49 49",
      style: { marginLeft: 21 }
    },
    /* @__PURE__ */ React16.createElement(
      "g",
      {
        id: "\xC1rssk\xFDrsla",
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React16.createElement(
        "g",
        {
          id: "Fylgiskj\xF6l-\xE1rssk\xFDrslu",
          stroke: "#20446A",
          strokeWidth: "2",
          transform: "translate(-152 -142)"
        },
        /* @__PURE__ */ React16.createElement("g", { id: "Group", transform: "translate(130 120.25)" }, /* @__PURE__ */ React16.createElement(
          "path",
          {
            id: "Combined-Shape",
            d: "M59.743 23l10.256 10.255L70 69.49H23.511V23h36.232z"
          }
        ), /* @__PURE__ */ React16.createElement(
          "path",
          {
            id: "Path",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            d: "M58.9 23.132v9.916c0 .599.515 1.084 1.15 1.084h8.847"
          }
        ), /* @__PURE__ */ React16.createElement("g", { id: "Atoms/Takkar/\xD6r-\xE1n-leggs-h\xE6gri", transform: "rotate(90 5 51)" }, /* @__PURE__ */ React16.createElement(
          "path",
          {
            id: "Path-Copy-3",
            d: "M-3.9382716 14.9111111L5.5441358 4.07407407 15.0265432 14.9111111",
            transform: "rotate(90 5.544 9.493)"
          }
        )), /* @__PURE__ */ React16.createElement(
          "path",
          {
            id: "Line",
            strokeLinecap: "square",
            d: "M46.5 36.146L46.507 55.282"
          }
        ))
      )
    )
  );
}

// src/components/icons/arsskyrsla/Sjalfsafgreidsla.tsx
import React17 from "react";
function Sjalfsafgreidsla() {
  return /* @__PURE__ */ React17.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "112",
      height: "112",
      viewBox: "0 0 112 112"
    },
    /* @__PURE__ */ React17.createElement(
      "g",
      {
        id: "\xC1rssk\xFDrsla/icon/Sj\xE1lfsafgrei\xF0sla",
        fill: "none",
        fillRule: "evenodd",
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2"
      },
      /* @__PURE__ */ React17.createElement("g", { id: "desktop-monitor-approve", transform: "translate(27 21)" }, /* @__PURE__ */ React17.createElement("circle", { id: "Oval", cx: "51.5", cy: "18.5", r: "18" }), /* @__PURE__ */ React17.createElement(
        "path",
        {
          id: "Path",
          d: "M59.522 13.265l-8.715 11.622a2.253 2.253 0 01-3.393.24l-4.5-4.5"
        }
      ), /* @__PURE__ */ React17.createElement("path", { id: "Path", d: "M12.5 48.5L63.5 48.5" }), /* @__PURE__ */ React17.createElement(
        "path",
        {
          id: "Shape",
          d: "M63.5 39.5v15a3 3 0 01-3 3H12.625M.5 27V15.5a3 3 0 013-3h24"
        }
      ), /* @__PURE__ */ React17.createElement(
        "path",
        {
          id: "Path",
          d: "M23 69.5a20.937 20.937 0 004.5-12M41 69.5a20.937 20.937 0 01-4.5-12"
        }
      ), /* @__PURE__ */ React17.createElement("path", { id: "Path", d: "M18.5 69.5L45.5 69.5" })),
      /* @__PURE__ */ React17.createElement("g", { id: "smart-house-phone-lock-1", transform: "translate(15 48)" }, /* @__PURE__ */ React17.createElement(
        "rect",
        {
          id: "Rectangle",
          width: "24",
          height: "42",
          x: "0.5",
          y: "0.5",
          rx: "3"
        }
      ), /* @__PURE__ */ React17.createElement("path", { id: "Path", d: "M0.5 6.5L24.5 6.5" }), /* @__PURE__ */ React17.createElement("path", { id: "Path", d: "M0.5 36.5L24.5 36.5" }))
    )
  );
}

// src/components/icons/arsskyrsla/Timapantanir.tsx
import React18 from "react";
function Timapantanir() {
  return /* @__PURE__ */ React18.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "112",
      height: "112",
      viewBox: "0 0 112 112"
    },
    /* @__PURE__ */ React18.createElement(
      "g",
      {
        id: "\xC1rssk\xFDrsla/icon/T\xEDmapantanir",
        fill: "none",
        fillRule: "evenodd",
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2"
      },
      /* @__PURE__ */ React18.createElement("g", { id: "calendar-check-1", transform: "translate(20 15)" }, /* @__PURE__ */ React18.createElement("circle", { id: "Oval", cx: "60.979", cy: "60.983", r: "21.25" }), /* @__PURE__ */ React18.createElement(
        "path",
        {
          id: "Path",
          d: "M70.45 54.803L60.16 68.523a2.66 2.66 0 01-4.006.283l-5.312-5.312"
        }
      ), /* @__PURE__ */ React18.createElement("path", { id: "Path", d: "M18.479 0.774L18.479 18.483" }), /* @__PURE__ */ React18.createElement("path", { id: "Path", d: "M53.896 0.774L53.896 18.483" }), /* @__PURE__ */ React18.createElement("path", { id: "Path", d: "M0.771 25.566L71.604 25.566" })),
      /* @__PURE__ */ React18.createElement(
        "path",
        {
          id: "Path",
          d: "M52.646 79.52H24.312a3.542 3.542 0 01-3.541-3.54V26.395a3.542 3.542 0 013.541-3.542h63.75a3.542 3.542 0 013.542 3.542v23.02"
        }
      )
    )
  );
}

// src/components/icons/Atm.tsx
import React19 from "react";
var Atm = () => {
  return /* @__PURE__ */ React19.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "23",
      height: "17",
      fill: "none",
      viewBox: "0 0 23 17"
    },
    /* @__PURE__ */ React19.createElement(
      "path",
      {
        stroke: "#00B3BF",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M16.597 6.3h3.79a.875.875 0 00.875-.875v-3.5a.875.875 0 00-.875-.875H2.012a.875.875 0 00-.875.875v3.5c0 .483.392.875.875.875H5.81"
      }
    ),
    /* @__PURE__ */ React19.createElement(
      "path",
      {
        stroke: "#00B3BF",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M17.762 12.857a.437.437 0 01-.437.443H5.081a.437.437 0 01-.437-.443c.047-3.81.716-5.507 2.181-9.182h8.75c1.473 3.675 2.141 5.373 2.188 9.182z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React19.createElement(
      "path",
      {
        stroke: "#00B3BF",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M5.516 15.05h11.375M4.203 3.675h14.875m-7.874 2.187v-.875"
      }
    )
  );
};

// src/components/icons/Clock.tsx
import React20 from "react";
var Clock = () => {
  return /* @__PURE__ */ React20.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "23",
      height: "23",
      fill: "none",
      viewBox: "0 0 23 23"
    },
    /* @__PURE__ */ React20.createElement(
      "path",
      {
        stroke: "#FF8316",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M1.137 11.2a10.063 10.063 0 1020.125 0 10.063 10.063 0 00-20.125 0z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React20.createElement(
      "path",
      {
        stroke: "#FF8316",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M16.45 16.45l-4.325-4.323"
      }
    ),
    /* @__PURE__ */ React20.createElement(
      "path",
      {
        stroke: "#FF8316",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M9.887 11.2a1.313 1.313 0 102.625 0 1.313 1.313 0 00-2.625 0v0z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React20.createElement(
      "path",
      {
        stroke: "#FF8316",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M11.2 6.388v3.5"
      }
    )
  );
};

// src/components/icons/CorporateTWP.tsx
import React21 from "react";
function CorporateTWP() {
  return /* @__PURE__ */ React21.createElement(
    "svg",
    {
      width: "46px",
      height: "46px",
      viewBox: "13 15 60 60",
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React21.createElement("title", null, "icon/Fyrirt\xE6ki"),
    /* @__PURE__ */ React21.createElement(
      "g",
      {
        id: "icon/Fyrirt\xE6ki",
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      },
      /* @__PURE__ */ React21.createElement(
        "g",
        {
          id: "Group-4",
          transform: "translate(15, 21)",
          stroke: "#FFAF70",
          strokeWidth: "2"
        },
        /* @__PURE__ */ React21.createElement(
          "line",
          {
            x1: "1.40909091",
            y1: "49.3181818",
            x2: "60.5909091",
            y2: "49.3181818",
            id: "Line-9"
          }
        ),
        /* @__PURE__ */ React21.createElement(
          "polyline",
          {
            id: "Path",
            points: "5.63636364 48.8044508 5.63636364 19.7272727 31 19.7272727 31 48.8044508"
          }
        ),
        /* @__PURE__ */ React21.createElement(
          "polyline",
          {
            id: "Path-Copy",
            points: "31 48.2907197 31 0 56.3636364 0 56.3636364 48.2907197"
          }
        ),
        /* @__PURE__ */ React21.createElement(
          "line",
          {
            x1: "18.3181818",
            y1: "32.4090909",
            x2: "18.3181818",
            y2: "38.0454545",
            id: "Line-10"
          }
        ),
        /* @__PURE__ */ React21.createElement(
          "line",
          {
            x1: "43.6818182",
            y1: "32.4090909",
            x2: "43.6818182",
            y2: "38.0454545",
            id: "Line-10-Copy"
          }
        ),
        /* @__PURE__ */ React21.createElement(
          "line",
          {
            x1: "43.6818182",
            y1: "12.6818182",
            x2: "43.6818182",
            y2: "18.3181818",
            id: "Line-10-Copy-2"
          }
        )
      )
    )
  );
}

// src/components/icons/DocumentInfo.tsx
import React22 from "react";
var DocumentInfo = () => /* @__PURE__ */ React22.createElement(
  "svg",
  {
    width: "48",
    height: "48",
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  },
  /* @__PURE__ */ React22.createElement("g", { clipPath: "url(#clip0_25368_13493)" }, /* @__PURE__ */ React22.createElement(
    "path",
    {
      d: "M34.314 1.58734C33.939 1.21227 33.4303 1.00156 32.8998 1.00156H1.00146V47.0016H47.0015V15.1032C47.0015 14.5728 46.7908 14.064 46.4157 13.689L34.314 1.58734Z",
      stroke: "#20446A",
      style: {
        stroke: "color(display-p3 0.1255 0.2667 0.4157)",
        strokeOpacity: 1
      },
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ), /* @__PURE__ */ React22.createElement(
    "path",
    {
      d: "M33.7275 2V12.2748C33.7275 13.3793 34.623 14.2748 35.7275 14.2748H46",
      stroke: "#20446A",
      style: {
        stroke: "color(display-p3 0.1255 0.2667 0.4157)",
        strokeOpacity: 1
      },
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ), /* @__PURE__ */ React22.createElement(
    "path",
    {
      d: "M24 23L24 35",
      stroke: "#20446A",
      style: {
        stroke: "color(display-p3 0.1255 0.2667 0.4157)",
        strokeOpacity: 1
      },
      strokeWidth: "2",
      strokeLinecap: "round"
    }
  ), /* @__PURE__ */ React22.createElement(
    "circle",
    {
      cx: "24",
      cy: "18.5",
      r: "1.5",
      fill: "#20446A",
      style: {
        fill: "color(display-p3 0.1255 0.2667 0.4157)",
        fillOpacity: 1
      }
    }
  )),
  /* @__PURE__ */ React22.createElement("defs", null, /* @__PURE__ */ React22.createElement("clipPath", { id: "clip0_25368_13493" }, /* @__PURE__ */ React22.createElement(
    "rect",
    {
      width: "48",
      height: "48",
      fill: "white",
      style: {
        fill: "color(display-p3 0.1255 0.2667 0.4157)",
        fillOpacity: 1
      }
    }
  )))
);

// src/components/icons/DownArrow.tsx
import { useTheme as useTheme2 } from "@emotion/react";
import React23 from "react";
var DownArrow = () => {
  const theme2 = useTheme2();
  return /* @__PURE__ */ React23.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "22",
      height: "22",
      fill: "none",
      viewBox: "0 0 22 22"
    },
    /* @__PURE__ */ React23.createElement(
      "path",
      {
        stroke: theme2.colors.ternary,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.909",
        d: "M11 14.553V6"
      }
    ),
    /* @__PURE__ */ React23.createElement(
      "circle",
      {
        cx: "11",
        cy: "11.001",
        r: "10",
        stroke: theme2.colors.ternary,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.909"
      }
    ),
    /* @__PURE__ */ React23.createElement(
      "path",
      {
        stroke: theme2.colors.ternary,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.909",
        d: "M15 11.553l-4 4-4-4"
      }
    )
  );
};

// src/components/icons/homepage/Bilalan.tsx
import styled12 from "@emotion/styled";
import React24 from "react";
var Host3 = styled12.div`
  height: 64px;
  width: 64px;
`;
var Bilalan = () => /* @__PURE__ */ React24.createElement(Host3, null, /* @__PURE__ */ React24.createElement("object", { type: "image/svg+xml", data: "/svgs/Bilalan.svg", tabIndex: -1 }, "B\xEDlal\xE1n"));

// src/components/icons/homepage/Fyrirtaeki.tsx
import styled13 from "@emotion/styled";
import React25 from "react";
var Div2 = styled13.div`
  height: 64px;
  width: 64px;
`;
var Fyrirtaeki = () => /* @__PURE__ */ React25.createElement(Div2, null, /* @__PURE__ */ React25.createElement("object", { type: "image/svg+xml", data: "/svgs/Fyrirtaeki.svg", tabIndex: -1 }, "Fyrirt\xE6ki"));

// src/components/icons/homepage/FyrirtaekiBlue.tsx
import styled14 from "@emotion/styled";
import React26 from "react";
var Div3 = styled14.div`
  height: 64px;
  width: 64px;
`;
var FyrirtaekiBlue = () => /* @__PURE__ */ React26.createElement(Div3, null, /* @__PURE__ */ React26.createElement("object", { type: "image/svg+xml", data: "/svgs/Fyrirtaeki_Blue.svg", tabIndex: -1 }, "Fyrirt\xE6ki"));

// src/components/icons/homepage/Ibudalan.tsx
import styled15 from "@emotion/styled";
import React27 from "react";
var Div4 = styled15.div`
  height: 64px;
  width: 64px;
`;
var Ibudalan = () => /* @__PURE__ */ React27.createElement(Div4, null, /* @__PURE__ */ React27.createElement("object", { type: "image/svg+xml", data: "/svgs/Ibudalan.svg", tabIndex: -1 }, "\xCDb\xFA\xF0al\xE1n"));

// src/components/icons/homepage/Kreditkort.tsx
import styled16 from "@emotion/styled";
import React28 from "react";
var Div5 = styled16.div`
  height: 64px;
  width: 64px;
`;
var Kreditkort = () => /* @__PURE__ */ React28.createElement(Div5, null, /* @__PURE__ */ React28.createElement("object", { type: "image/svg+xml", data: "/svgs/Kreditkort.svg", tabIndex: -1 }, "Kreditkort"));

// src/components/icons/homepage/Lifeyrissparnadur.tsx
import styled17 from "@emotion/styled";
import React29 from "react";
var Div6 = styled17.div`
  height: 220px;
  width: 220px;
`;
var Lifeyrissparnadur = () => /* @__PURE__ */ React29.createElement(Div6, null, /* @__PURE__ */ React29.createElement("object", { type: "image/svg+xml", data: "/svgs/Lifeyrissparnadur.svg" }, "L\xEDfeyrissparna\xF0ur"));

// src/components/icons/homepage/Radgjof.tsx
import styled18 from "@emotion/styled";
import React30 from "react";
var Div7 = styled18.div`
  height: 64px;
  width: 64px;
`;
var Radgjof = () => /* @__PURE__ */ React30.createElement(Div7, null, /* @__PURE__ */ React30.createElement("object", { type: "image/svg+xml", data: "/svgs/Panta_Radgjof.svg", tabIndex: -1 }, "Panta r\xE1\xF0gj\xF6f"));

// src/components/icons/homepage/Sereignasparnadur.tsx
import React31 from "react";
var Sereignasparnadur = () => {
  return /* @__PURE__ */ React31.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      "data-name": "Layer 1",
      viewBox: "0 0 64 64",
      height: "64",
      width: "64"
    },
    /* @__PURE__ */ React31.createElement("g", { "data-name": "icon/fors\xED\xF0a/s\xE9reignasparna\xF0ur" }, /* @__PURE__ */ React31.createElement("g", { "data-name": "icon/L\xEDfeyrir" }, /* @__PURE__ */ React31.createElement(
      "g",
      {
        stroke: "#20446a",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1.6",
        "data-name": "Group"
      },
      /* @__PURE__ */ React31.createElement(
        "path",
        {
          fill: "#cef2f8",
          fillRule: "evenodd",
          d: "M33.892 34.208s-7.079-12.534 6.6-17.32c.001 0 9.447 12.826-6.6 17.32z",
          "data-name": "Stroke-4"
        }
      ),
      /* @__PURE__ */ React31.createElement(
        "path",
        {
          fill: "#cef2f8",
          fillRule: "evenodd",
          d: "M31.7 40.978s-3.363-9.791-14.261-6.992c0 0 1.561 12.366 14.261 6.992z",
          "data-name": "Stroke-6"
        }
      ),
      /* @__PURE__ */ React31.createElement(
        "path",
        {
          fill: "none",
          d: "M33.634 34.084s-4.6 9.178 1.75 13.4",
          "data-name": "Stroke-8"
        }
      ),
      /* @__PURE__ */ React31.createElement(
        "path",
        {
          fill: "none",
          d: "M32.281 38.3s2.414-9.977-4.72-12.663",
          "data-name": "Stroke-8-Copy"
        }
      ),
      /* @__PURE__ */ React31.createElement(
        "path",
        {
          fill: "none",
          d: "M44.308 47.546c-6.616-2.408-12.474-.163-15.095 7.039",
          "data-name": "Path-Copy"
        }
      )
    )))
  );
};

// src/components/icons/homepage/Sparnadur.tsx
import styled19 from "@emotion/styled";
import React32 from "react";
var Div8 = styled19.div`
  height: 64px;
  width: 64px;
`;
var Sparnadur = () => /* @__PURE__ */ React32.createElement(Div8, null, /* @__PURE__ */ React32.createElement("object", { type: "image/svg+xml", data: "/svgs/Sparnadur.svg", tabIndex: -1 }, "Sparna\xF0ur"));

// src/components/icons/homepage/Tryggingar.tsx
import styled20 from "@emotion/styled";
import React33 from "react";
var Div9 = styled20.div`
  height: 64px;
  width: 64px;
`;
var Tryggingar = () => /* @__PURE__ */ React33.createElement(Div9, null, /* @__PURE__ */ React33.createElement("object", { type: "image/svg+xml", data: "/svgs/Tryggingar.svg", tabIndex: -1 }, "Tryggingar"));

// src/components/icons/Info.tsx
import React34 from "react";
function Info(props) {
  return /* @__PURE__ */ React34.createElement("svg", { viewBox: "0 0 32 32", ...props }, /* @__PURE__ */ React34.createElement(
    "g",
    {
      fill: "none",
      fillRule: "evenodd",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    },
    /* @__PURE__ */ React34.createElement("path", { d: "M31 15.742c.021 8.334-6.666 15.135-15 15.256A14.779 14.779 0 0 1 1 16.262C.977 7.926 7.664 1.122 16 1.002a14.78 14.78 0 0 1 15 14.74Zm-15 2.992V9.189" }),
    /* @__PURE__ */ React34.createElement("path", { d: "M15.988 21.46a.334.334 0 0 0-.329.348.346.346 0 0 0 .345.334h0a.335.335 0 0 0 .329-.347.345.345 0 0 0-.333-.334h-.007" })
  ));
}

// src/components/icons/Mail.tsx
import React35 from "react";
var Mail = () => {
  return /* @__PURE__ */ React35.createElement(
    "svg",
    {
      version: "1.1",
      id: "Layer_1",
      xmlns: "http://www.w3.org/2000/svg",
      x: "0px",
      y: "0px",
      viewBox: "0 0 30 30",
      width: "30",
      height: "30"
    },
    /* @__PURE__ */ React35.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M15,0C6.7,0,0,6.7,0,15c0,8.3,6.7,15,15,15c8.3,0,15-6.7,15-15C30,6.7,23.3,0,15,0z M23.5,8.1\n	c0,0-0.1,0.1-0.1,0.1l-8.1,6.8c-0.2,0.2-0.5,0.2-0.6,0L6.6,8.3c0,0-0.1-0.1-0.1-0.1H23.5z M24.1,20.9c0,0.6-0.4,1-1,1H6.9\n	c-0.6,0-1-0.4-1-1V9c0,0,0,0,0,0l8.1,6.8c0.3,0.2,0.6,0.4,1,0.4s0.7-0.1,1-0.4L24,9c0,0,0,0,0,0V20.9z"
      }
    )
  );
};

// src/components/icons/PersonalTWP.tsx
import React36 from "react";
function PersonalTWP() {
  return /* @__PURE__ */ React36.createElement(
    "svg",
    {
      width: "46px",
      height: "46px",
      viewBox: "17 15 57 57",
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React36.createElement("title", null, "icon/Einstaklingur"),
    /* @__PURE__ */ React36.createElement(
      "g",
      {
        id: "icon/Einstaklingur",
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      },
      /* @__PURE__ */ React36.createElement(
        "g",
        {
          id: "Group-16",
          transform: "translate(25, 19)",
          stroke: "#20446A",
          strokeWidth: "2"
        },
        /* @__PURE__ */ React36.createElement("g", { id: "Group-5" }, /* @__PURE__ */ React36.createElement(
          "path",
          {
            d: "M33.5528956,12.605243 C33.5528956,19.5663051 27.9315891,25.210486 20.9987384,25.210486 C14.0658878,25.210486 8.44458132,19.5663051 8.44458132,12.605243 C8.44458132,5.64418092 14.0658878,0 20.9987384,0 C27.9315891,0 33.5528956,5.64418092 33.5528956,12.605243 Z",
            id: "Stroke-1"
          }
        ), /* @__PURE__ */ React36.createElement(
          "path",
          {
            d: "M0.228175187,50.6153846 C0.228175187,39.0937077 -3.69581335,26.3502919 21.0014424,26.3502919 C45.6986982,26.3502919 41.7713297,39.0937077 41.7713297,50.6153846",
            id: "Stroke-3"
          }
        ))
      )
    )
  );
}

// src/components/icons/points/AirContract.tsx
import React37 from "react";
var AirContract = () => {
  return /* @__PURE__ */ React37.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "55",
      height: "48",
      viewBox: "0 0 55 48"
    },
    /* @__PURE__ */ React37.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React37.createElement("g", { stroke: "#20446A", strokeWidth: "2", transform: "translate(-1050 -414)" }, /* @__PURE__ */ React37.createElement("g", { transform: "translate(1031 396)" }, /* @__PURE__ */ React37.createElement("g", { transform: "translate(20 19)" }, /* @__PURE__ */ React37.createElement("path", { d: "M6.627 24.988l-2.782-2.874a12.828 12.828 0 01-2.462-14.95A13.217 13.217 0 0122.4 3.795l3.62 3.302 3.622-3.302a13.215 13.215 0 0121.016 3.367 12.835 12.835 0 01-2.46 14.951l-3.767 3.887" }), /* @__PURE__ */ React37.createElement("path", { d: "M6.945 25.316L6.627 24.988" }), /* @__PURE__ */ React37.createElement("path", { d: "M31.622 13.367l12.675 12.675a3.251 3.251 0 01-4.597 4.597l-10.478-10.48" }), /* @__PURE__ */ React37.createElement("path", { d: "M39.684 30.655a3.25 3.25 0 11-4.597 4.597L24.609 24.773" }), /* @__PURE__ */ React37.createElement("path", { d: "M35.065 35.274a3.251 3.251 0 01-4.597 4.597l-10.48-10.479" }), /* @__PURE__ */ React37.createElement("path", { d: "M30.45 39.888a3.25 3.25 0 11-4.595 4.597l-1.248-1.247" }), /* @__PURE__ */ React37.createElement("path", { d: "M33.356 11.224L26.74 17.84a6.055 6.055 0 01-8.562 0 2 2 0 010-2.828l7.795-7.795" }), /* @__PURE__ */ React37.createElement("path", { d: "M23.958 43.478a3.052 3.052 0 11-4.315-4.317 3.052 3.052 0 014.315 4.317z" }), /* @__PURE__ */ React37.createElement("path", { d: "M19.505 39.025a3.052 3.052 0 11-4.315-4.318 3.052 3.052 0 014.315 4.318z" }), /* @__PURE__ */ React37.createElement("path", { d: "M15.052 34.571a3.052 3.052 0 11-4.316-4.315 3.052 3.052 0 014.316 4.315z" }), /* @__PURE__ */ React37.createElement("path", { d: "M10.598 30.118a3.05 3.05 0 11-4.315-4.315 3.05 3.05 0 114.315 4.315z" }))))
    )
  );
};

// src/components/icons/points/Bicycle.tsx
import React38 from "react";
var Bicycle = () => {
  return /* @__PURE__ */ React38.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "62",
      height: "39",
      viewBox: "0 0 62 39"
    },
    /* @__PURE__ */ React38.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React38.createElement("g", { stroke: "#20446A", strokeWidth: "2", transform: "translate(-145 -422)" }, /* @__PURE__ */ React38.createElement("g", { transform: "translate(130 396)" }, /* @__PURE__ */ React38.createElement("g", { transform: "translate(16 27)" }, /* @__PURE__ */ React38.createElement("path", { d: "M.404 25.896c0 5.68 4.605 10.285 10.285 10.285s10.285-4.604 10.285-10.285c0-5.68-4.605-10.285-10.285-10.285S.404 20.216.404 25.896zM38.973 25.896c0 5.68 4.605 10.285 10.285 10.285s10.285-4.604 10.285-10.285c0-5.68-4.605-10.285-10.285-10.285s-10.285 4.605-10.285 10.285z" }), /* @__PURE__ */ React38.createElement("path", { d: "M27.402 25.896L42.017 6.24 22.26 13.04l-9.64 10.712a1.286 1.286 0 00.957 2.144h13.825l-8.356-20.57" }), /* @__PURE__ */ React38.createElement("path", { d: "M49.2580076 25.896268L40.2585954 1.4692918 45.4011167 0.183661475" }), /* @__PURE__ */ React38.createElement("path", { d: "M15.832 5.326L23.545 5.326" }))))
    )
  );
};

// src/components/icons/points/Car.tsx
import React39 from "react";
var Car = () => {
  return /* @__PURE__ */ React39.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "75",
      height: "45",
      viewBox: "0 0 75 45"
    },
    /* @__PURE__ */ React39.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React39.createElement("g", { stroke: "#20446A", strokeWidth: "2", transform: "translate(-136 -151)" }, /* @__PURE__ */ React39.createElement("g", { transform: "translate(130 132)" }, /* @__PURE__ */ React39.createElement("g", { transform: "translate(6.393 19.339)" }, /* @__PURE__ */ React39.createElement("g", { transform: "translate(.607 .661)" }, /* @__PURE__ */ React39.createElement("path", { d: "M1.951 14.388s-3.769-3.852-.83-9.533l1.883.776S6.662-2.682 16.64.892c0 0 2.604 16.434-14.688 13.496z" }), /* @__PURE__ */ React39.createElement(
        "path",
        {
          strokeLinecap: "round",
          d: "M.683 18.428s.544-6.617 8.045-11.058"
        }
      )), /* @__PURE__ */ React39.createElement("g", { strokeLinecap: "round", transform: "translate(8.607 15.661)" }, /* @__PURE__ */ React39.createElement("g", null, /* @__PURE__ */ React39.createElement("g", null, /* @__PURE__ */ React39.createElement("path", { d: "M3.029 8.606H48.14l13.831 4.61a3.798 3.798 0 012.599 3.604v5.477a2.738 2.738 0 01-2.738 2.738h-8.968m-9.49 0h-24.27M9 25.035H5.577" }), /* @__PURE__ */ React39.createElement("path", { d: "M48.14 8.606l-9.095-6.822A6.964 6.964 0 0034.866.391H19.98h0" }), /* @__PURE__ */ React39.createElement("path", { d: "M64.57 16.82L59.093 16.82" }), /* @__PURE__ */ React39.createElement("path", { d: "M1.591 14.082L7.067 14.082" }), /* @__PURE__ */ React39.createElement("path", { d: "M20.022.391H10.65a3.809 3.809 0 00-2.338.97L2.206 9.165a6.964 6.964 0 00-1.195 6.257l2.339 7.946a2.321 2.321 0 002.227 1.666h3.068" }), /* @__PURE__ */ React39.createElement("path", { d: "M34.449 14.082L31.711 14.082" }), /* @__PURE__ */ React39.createElement("path", { d: "M42.664 22.297a5.476 5.476 0 1010.953 0 5.476 5.476 0 00-10.953 0zM8.645 22.297a5.476 5.476 0 1010.953 0 5.476 5.476 0 00-10.953 0z" })), /* @__PURE__ */ React39.createElement("path", { d: "M24.5 8L24.5 1" }), /* @__PURE__ */ React39.createElement("path", { d: "M13 8L16 1" }))))))
    )
  );
};

// src/components/icons/points/Contract.tsx
import React40 from "react";
var Contract = () => {
  return /* @__PURE__ */ React40.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 44 46" }, /* @__PURE__ */ React40.createElement(
    "path",
    {
      stroke: "#FFAF70",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M37.021 39.205l-7.449 1.49m.813-8.414l-3.514 1.443a2.275 2.275 0 01-1.862-4.148l3.513-1.706a3.142 3.142 0 012.48-.143l6.413 2.454"
    }
  ), /* @__PURE__ */ React40.createElement(
    "path",
    {
      stroke: "#FFAF70",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M16.166 40.813h2.655l4.417 3.357c.463.519 1.252.58 1.79.14l6.635-5.321a1.297 1.297 0 00.2-1.8l-3.645-4.015m-3.501-3.404l-.474-.391a2.79 2.79 0 00-2.683-.341l-5.393 2.295"
    }
  ), /* @__PURE__ */ React40.createElement(
    "path",
    {
      stroke: "#FFAF70",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M10.479 42.708h3.792a1.896 1.896 0 001.895-1.895v-9.48a1.896 1.896 0 00-1.895-1.895h-3.792v13.27zm32.23 0h-3.792a1.896 1.896 0 01-1.896-1.895v-9.48c0-1.047.849-1.895 1.896-1.895h3.791v13.27z",
      clipRule: "evenodd"
    }
  ), /* @__PURE__ */ React40.createElement(
    "path",
    {
      stroke: "#FFAF70",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M30.385 1H6.687A5.687 5.687 0 001 6.688v36.968c0 .524.424.948.948.948h4.74m0-36.021h13.27M6.688 14.27h13.27m-13.27 5.688h6.635"
    }
  ), /* @__PURE__ */ React40.createElement(
    "path",
    {
      stroke: "#FFAF70",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M25.646 24.698V5.74A4.74 4.74 0 0130.386 1c2.616 0 4.74 2.121 4.74 5.688h-9.48"
    }
  ));
};

// src/components/icons/points/Document.tsx
import React41 from "react";
var Document = () => {
  return /* @__PURE__ */ React41.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 44 42" }, /* @__PURE__ */ React41.createElement(
    "path",
    {
      stroke: "#72DCEB",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M1 1h17.063v5.688H1V1z",
      clipRule: "evenodd"
    }
  ), /* @__PURE__ */ React41.createElement(
    "path",
    {
      stroke: "#72DCEB",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M4.792 6.688h17.062v5.687H4.791V6.687zm-1.896 5.687h17.063v5.688H2.896v-5.688z",
      clipRule: "evenodd"
    }
  ), /* @__PURE__ */ React41.createElement(
    "path",
    {
      stroke: "#72DCEB",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M1 18.063h17.063v5.687H1v-5.688zm1.896 5.687h17.063v5.688H2.896V23.75z",
      clipRule: "evenodd"
    }
  ), /* @__PURE__ */ React41.createElement(
    "path",
    {
      stroke: "#72DCEB",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M1 29.438h17.063v5.687H1v-5.688zm1.896 5.687h17.063v5.688H2.896v-5.688zM23.75 23.75h17.063v5.688H23.75V23.75z",
      clipRule: "evenodd"
    }
  ), /* @__PURE__ */ React41.createElement(
    "path",
    {
      stroke: "#72DCEB",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M25.646 29.438h17.063v5.687H25.646v-5.688zm-1.896 5.687h17.063v5.688H23.75v-5.688z",
      clipRule: "evenodd"
    }
  ));
};

// src/components/icons/points/Download.tsx
import React42 from "react";
function Download() {
  return /* @__PURE__ */ React42.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "49",
      height: "49",
      viewBox: "0 0 49 49"
    },
    /* @__PURE__ */ React42.createElement(
      "g",
      {
        id: "\xC1rssk\xFDrsla",
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React42.createElement(
        "g",
        {
          id: "Fylgiskj\xF6l-\xE1rssk\xFDrslu",
          stroke: "#20446A",
          strokeWidth: "2",
          transform: "translate(-152 -142)"
        },
        /* @__PURE__ */ React42.createElement("g", { id: "Group", transform: "translate(130 120.25)" }, /* @__PURE__ */ React42.createElement(
          "path",
          {
            id: "Combined-Shape",
            d: "M59.743 23l10.256 10.255L70 69.49H23.511V23h36.232z"
          }
        ), /* @__PURE__ */ React42.createElement(
          "path",
          {
            id: "Path",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            d: "M58.9 23.132v9.916c0 .599.515 1.084 1.15 1.084h8.847"
          }
        ), /* @__PURE__ */ React42.createElement("g", { id: "Atoms/Takkar/\xD6r-\xE1n-leggs-h\xE6gri", transform: "rotate(90 5 51)" }, /* @__PURE__ */ React42.createElement(
          "path",
          {
            id: "Path-Copy-3",
            d: "M-3.9382716 14.9111111L5.5441358 4.07407407 15.0265432 14.9111111",
            transform: "rotate(90 5.544 9.493)"
          }
        )), /* @__PURE__ */ React42.createElement(
          "path",
          {
            id: "Line",
            strokeLinecap: "square",
            d: "M46.5 36.146L46.507 55.282"
          }
        ))
      )
    )
  );
}

// src/components/icons/points/ElectonicCar.tsx
import React43 from "react";
var ElectonicCar = () => {
  return /* @__PURE__ */ React43.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "76",
      height: "46",
      viewBox: "0 0 76 46"
    },
    /* @__PURE__ */ React43.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "#20446A",
        strokeLinejoin: "round",
        strokeWidth: "2"
      },
      /* @__PURE__ */ React43.createElement("g", { strokeLinecap: "round" }, /* @__PURE__ */ React43.createElement("path", { d: "M13.029 25.606H58.14l13.831 4.61a3.798 3.798 0 012.599 3.604v5.477a2.738 2.738 0 01-2.738 2.738h-8.968m-9.873 0H28.875m-9.75 0h-3.548M58.14 25.606l-9.095-6.822a6.964 6.964 0 00-4.179-1.393H29.98h0M74.57 33.82h-5.477m-57.502-2.738h5.476" }), /* @__PURE__ */ React43.createElement("path", { d: "M30.022 17.391H20.65a3.809 3.809 0 00-2.338.97l-6.105 7.805a6.964 6.964 0 00-1.195 6.257l2.339 7.946a2.321 2.321 0 002.227 1.666h3.068m25.803-10.953h-2.738m10.953 8.215a5.476 5.476 0 1010.953 0 5.476 5.476 0 00-10.953 0z" }), /* @__PURE__ */ React43.createElement("path", { d: "M18.645 39.297a5.476 5.476 0 1010.953 0 5.476 5.476 0 00-10.953 0zM34.5 25v-7M23 25l3-7" })),
      /* @__PURE__ */ React43.createElement("path", { d: "M11.964 10.132L19.084 1 4.966 8.894l3.561 3.561L1 22.111l14.714-8.228z" })
    )
  );
};

// src/components/icons/points/Food.tsx
import React44 from "react";
var Food = () => {
  return /* @__PURE__ */ React44.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "29",
      height: "47",
      viewBox: "0 0 29 47"
    },
    /* @__PURE__ */ React44.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React44.createElement("g", { stroke: "#20446A", strokeWidth: "2", transform: "translate(-762 -413)" }, /* @__PURE__ */ React44.createElement("g", { transform: "translate(730 396)" }, /* @__PURE__ */ React44.createElement("g", { transform: "translate(33 17)" }, /* @__PURE__ */ React44.createElement("path", { d: "M20.329 25.566h4.825a.965.965 0 00.965-.965C25.99 9.546 22.941 1.303 20.33 1.303v44.394-20.131zM.818 1.303v6.875c0 2.278 2.842 2.864 6.156 2.864 3.314 0 5.844-.586 5.844-2.864V1.303" }), /* @__PURE__ */ React44.createElement("path", { d: "M7 1L6.82 45.697" }))))
    )
  );
};

// src/components/icons/points/Foot.tsx
import React45 from "react";
var Foot = () => {
  return /* @__PURE__ */ React45.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "32",
      height: "47",
      viewBox: "0 0 32 47"
    },
    /* @__PURE__ */ React45.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React45.createElement("g", { stroke: "#20446A", strokeWidth: "2", transform: "translate(-760 -150)" }, /* @__PURE__ */ React45.createElement("g", { transform: "translate(730 132)" }, /* @__PURE__ */ React45.createElement("g", { transform: "translate(31 19)" }, /* @__PURE__ */ React45.createElement("path", { d: "M10.567 44.697a6.622 6.622 0 01-6.624-6.625V19.605a9.65 9.65 0 0119.301 0v1.505a4.285 4.285 0 01-4.284 4.285 4.287 4.287 0 00-3.98 5.878l1.737 4.34a6.624 6.624 0 01-6.15 9.084zM21.314 4.164a3.86 3.86 0 107.72 0 3.86 3.86 0 00-7.72 0zM13.594 2.233a1.93 1.93 0 103.86 0 1.93 1.93 0 00-3.86 0zM5.873 3.199a1.93 1.93 0 103.86 0 1.93 1.93 0 00-3.86 0zM.083 8.024a1.93 1.93 0 103.86 0 1.93 1.93 0 00-3.86 0z" }))))
    )
  );
};

// src/components/icons/points/Invoice.tsx
import React46 from "react";
var Invoice = () => {
  return /* @__PURE__ */ React46.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 46 47" }, /* @__PURE__ */ React46.createElement(
    "path",
    {
      stroke: "#20446A",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M8.438 1.959l14.329 8.277-5.216 9.029a.948.948 0 01-1.295.346L2.75 11.81a.948.948 0 01-.347-1.295l4.742-8.208a.948.948 0 011.294-.347zm14.328 26.338l-1.841 5.919a.947.947 0 01-1.187.624l-9.559-2.973a.948.948 0 01-.624-1.187l1.559-5.012a.948.948 0 011.185-.624l10.467 3.253zM43.21 17.153L30.53 27.1a.948.948 0 01-1.33-.16l-6.437-8.205L36.188 8.203a.948.948 0 011.331.161l5.852 7.458a.948.948 0 01-.161 1.33z",
      clipRule: "evenodd"
    }
  ), /* @__PURE__ */ React46.createElement(
    "path",
    {
      stroke: "#20446A",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M22.766 39.62V3.6"
    }
  ), /* @__PURE__ */ React46.createElement(
    "path",
    {
      stroke: "#20446A",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M9.846 10.491a2.37 2.37 0 104.74 0 2.37 2.37 0 00-4.74 0h0zm20.837 7.237a2.37 2.37 0 104.74 0 2.37 2.37 0 00-4.74 0z",
      clipRule: "evenodd"
    }
  ), /* @__PURE__ */ React46.createElement(
    "path",
    {
      stroke: "#20446A",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M17.184 45.308l-1.473-4.44a.948.948 0 01.9-1.247h12.536a.948.948 0 01.9 1.247l-1.48 4.44"
    }
  ));
};

// src/components/icons/points/Paper.tsx
import React47 from "react";
var Paper = () => {
  return /* @__PURE__ */ React47.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "36",
      height: "47",
      viewBox: "0 0 36 47"
    },
    /* @__PURE__ */ React47.createElement(
      "path",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "#20446A",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2",
        d: "M17.286 31.67a7.292 7.292 0 01-11.913-4.42l-.087-.625a6.773 6.773 0 01-.1-12.458l.1-.563a5.208 5.208 0 015.208-5.208l.52-.521a6.77 6.77 0 1113.543 0l.52.52a5.208 5.208 0 015.209 5.21l.625.818a6.773 6.773 0 01-.59 12.192l-.12.625a7.292 7.292 0 01-11.876 4.462l-1.04-.031zm.5-17.024v31.25m0-19.792a8.333 8.333 0 008.333-8.333m-8.333 4.167a6.25 6.25 0 01-6.25-6.25"
      }
    )
  );
};

// src/components/icons/points/Pcaf.tsx
import React48 from "react";
var Pcaf = () => {
  return /* @__PURE__ */ React48.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "48",
      height: "47",
      viewBox: "0 0 48 47"
    },
    /* @__PURE__ */ React48.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "#20446A",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2"
      },
      /* @__PURE__ */ React48.createElement("path", { d: "M1.308 28.452h7.836v15.672H1.308zm7.836 12.733c21.017 7.008 13.266 7.08 37.22-4.897a5.413 5.413 0 00-5.877-1.959L31.8 37.213" }),
      /* @__PURE__ */ React48.createElement("path", { d: "M9.144 30.411h5.877a12.91 12.91 0 018.815 3.918h5.877a1.99 1.99 0 110 3.918H18.938m-5.187-14.504c-3.822-7.604-.878-16.868 6.633-20.87 7.511-4.002 16.843-1.278 21.022 6.136 4.178 7.414 1.676 16.808-5.636 21.161" }),
      /* @__PURE__ */ React48.createElement("path", { d: "M39.339 6.146a7.787 7.787 0 01-3.409 5.105c-2.724-.682-5.45 1.36-4.77 1.36.68 0 1.372 2.726 1.372 2.726.681.682 2.045 0 2.045 0 1.702 1.702-2.727 4.772-2.727 5.452 0 .68 1.706 1.022.682 2.045a5.844 5.844 0 01-2.044 1.371c0 3.407-3.408 3.407-4.088 3.407-.68 0-2.045-2.727-2.045-3.407 0-.68.684-1.371.684-2.043 0-.672-1.372-2.043-1.372-2.043 0-2.727-2.043-2.045-4.09-2.045-2.047 0-2.043-2.737-2.043-2.737s0-4.768 3.408-5.448c3.409-.68 3.405.68 3.405.68 1.371 1.371 4.088 0 5.452 0 0 0 .682-4.77-.682-4.088-1.363.681-2.584.152-2.725-1.372-.205-2.123 5.946-3.398 5.946-3.398" })
    )
  );
};

// src/components/icons/points/Wallet.tsx
import React49 from "react";
var Wallet = () => {
  return /* @__PURE__ */ React49.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 46 46" }, /* @__PURE__ */ React49.createElement(
    "path",
    {
      stroke: "#6195CC",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M9.091 18.062l-1.706-5.286a1.896 1.896 0 011.223-2.385l28.875-9.3a1.896 1.896 0 012.382 1.224l4.648 14.435a1.896 1.896 0 01-1.223 2.387l-10.06 3.24M8.841 17.288l4.283-8.35m28.198-2.113l-8.349-4.28m10.093 9.695l-4.28 8.349"
    }
  ), /* @__PURE__ */ React49.createElement(
    "path",
    {
      stroke: "#6195CC",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M28.4 9.991l-5.699 1.837a1.596 1.596 0 00.598 3.113l5.308-.354a1.596 1.596 0 01.597 3.113l-1.124.362m-3.29-6.907l-.58-1.805m9.02 25.774v5.688a3.792 3.792 0 01-3.792 3.791H4.791A3.792 3.792 0 011 40.813V21.854a3.792 3.792 0 013.792-3.792h24.645a3.792 3.792 0 013.792 3.791v5.688M6.695 18.062v26.541"
    }
  ), /* @__PURE__ */ React49.createElement(
    "path",
    {
      stroke: "#6195CC",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M26.594 27.541a3.792 3.792 0 100 7.583h6.635a1.896 1.896 0 001.896-1.896v-3.791a1.896 1.896 0 00-1.896-1.896h-6.635z",
      clipRule: "evenodd"
    }
  ), /* @__PURE__ */ React49.createElement(
    "path",
    {
      stroke: "#6195CC",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1.896",
      d: "M30.867 30.859a.474.474 0 110 .948.474.474 0 010-.948"
    }
  ));
};

// src/components/icons/related/HouseLine.tsx
import React50 from "react";
var HouseLine = () => {
  return /* @__PURE__ */ React50.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 156 174" }, /* @__PURE__ */ React50.createElement("g", { fill: "none", fillRule: "evenodd", stroke: "#03ADC4", strokeWidth: "2.676" }, /* @__PURE__ */ React50.createElement("path", { d: "M2.005 80.11L78.883 2l75.639 76.809m-48.36-65.092h26.04v29.942M2.005 133.734c7.44-8.782 20.666-13.173 39.679-13.173 28.52 0 48.359 23.712 73.158 23.712 16.533 0 29.76-7.026 39.68-21.077M2.005 161.631c7.44-8.782 20.666-13.173 39.679-13.173 28.52 0 48.359 23.712 73.158 23.712 16.533 0 29.76-7.026 39.68-21.078" }), /* @__PURE__ */ React50.createElement("path", { d: "M66.223 45.24h24.082v25.107H66.223z" })));
};

// src/components/icons/Service.tsx
import React51 from "react";
var Service = () => {
  return /* @__PURE__ */ React51.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "23",
      height: "22",
      fill: "none",
      viewBox: "0 0 23 22"
    },
    /* @__PURE__ */ React51.createElement(
      "path",
      {
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M14.7 19.513h.875c1.692 0 3.063-.934 3.063-2.625v-1.899"
      }
    ),
    /* @__PURE__ */ React51.createElement(
      "path",
      {
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M17.763 8.138a3.5 3.5 0 011.167 6.8.875.875 0 01-1.167-.824V8.138zm-13.125 0a3.5 3.5 0 00-1.168 6.8.875.875 0 001.168-.824V8.138z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React51.createElement(
      "path",
      {
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M17.762 9.45V7.7a6.563 6.563 0 00-13.125 0v1.75"
      }
    ),
    /* @__PURE__ */ React51.createElement(
      "path",
      {
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "0.875",
        d: "M10.762 18.638h3.5c.242 0 .438.196.438.437v.875a.438.438 0 01-.438.438h-3.5a.875.875 0 01-.875-.875v0c0-.484.392-.875.875-.875z",
        clipRule: "evenodd"
      }
    )
  );
};

// src/components/icons/socialMedia/Facebook.tsx
import React52 from "react";
var Facebook = () => {
  return /* @__PURE__ */ React52.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      "data-name": "Layer 1",
      height: "30",
      width: "30",
      viewBox: "0 0 30 30"
    },
    /* @__PURE__ */ React52.createElement(
      "path",
      {
        fill: "#163e63",
        d: "M15 0a15 15 0 00-2.344 29.818V19.336H8.848V15h3.808v-3.3c0-3.759 2.24-5.836 5.666-5.836a23.073 23.073 0 013.358.293v3.687h-1.892a2.167 2.167 0 00-2.444 2.342V15H21.5l-.665 4.336h-3.491v10.482A15 15 0 0015 0z"
      }
    )
  );
};

// src/components/icons/socialMedia/Instagram.tsx
import React53 from "react";
function Instagram(props) {
  return /* @__PURE__ */ React53.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 30 30",
      height: "30",
      ...props
    },
    /* @__PURE__ */ React53.createElement("style", null, `.insta { fill:#163E63; }`),
    /* @__PURE__ */ React53.createElement(
      "path",
      {
        className: "insta",
        d: "M21.66,11.679c-0.038-0.799-0.172-1.232-0.284-1.519c-0.149-0.383-0.327-0.655-0.615-0.941\n				c-0.288-0.286-0.56-0.463-0.943-0.611c-0.289-0.112-0.723-0.244-1.521-0.279c-0.864-0.038-1.124-0.046-3.31-0.041\n				c-2.186,0.005-2.445,0.013-3.309,0.054c-0.798,0.038-1.231,0.172-1.52,0.285C9.776,8.776,9.503,8.953,9.217,9.241\n				c-0.286,0.288-0.463,0.56-0.611,0.943c-0.111,0.289-0.244,0.723-0.278,1.521c-0.038,0.863-0.046,1.122-0.041,3.309\n				S8.3,17.46,8.34,18.323c0.037,0.799,0.172,1.232,0.285,1.521c0.149,0.382,0.326,0.655,0.614,0.941\n				c0.288,0.285,0.561,0.463,0.943,0.611c0.289,0.111,0.723,0.244,1.521,0.278h0c0.864,0.037,1.122,0.046,3.309,0.041\n				c2.187-0.005,2.446-0.013,3.31-0.054c0.798-0.038,1.231-0.172,1.519-0.285c0.382-0.149,0.655-0.327,0.941-0.614\n				s0.464-0.56,0.611-0.943c0.111-0.289,0.244-0.723,0.279-1.522c0.038-0.863,0.046-1.123,0.042-3.309\n				C21.71,12.802,21.701,12.543,21.66,11.679z M15.008,19.205c-2.322,0.004-4.209-1.874-4.213-4.197\n				c-0.004-2.323,1.875-4.209,4.197-4.214c2.323-0.005,4.21,1.875,4.214,4.197C19.21,17.315,17.33,19.201,15.008,19.205z\n				 M19.365,11.604c-0.543,0.001-0.984-0.438-0.984-0.981c-0.001-0.543,0.438-0.983,0.981-0.984c0,0,0,0,0,0\n				c0.543-0.001,0.984,0.438,0.984,0.981C20.347,11.162,19.908,11.603,19.365,11.604z"
      }
    ),
    /* @__PURE__ */ React53.createElement(
      "path",
      {
        className: "insta",
        d: "M14.995,12.27c-1.508,0.003-2.728,1.228-2.725,2.736c0,0,0,0,0,0.001c0.003,1.508,1.228,2.727,2.736,2.724\n				c1.508-0.003,2.728-1.228,2.725-2.736C17.727,13.487,16.503,12.267,14.995,12.27z"
      }
    ),
    /* @__PURE__ */ React53.createElement(
      "path",
      {
        className: "insta",
        d: "M15,0C6.716,0,0,6.716,0,15c0,8.284,6.716,15,15,15c8.284,0,15-6.716,15-15C30,6.716,23.284,0,15,0z\n				 M23.147,18.362c-0.038,0.871-0.175,1.467-0.377,1.988c-0.208,0.54-0.488,0.997-0.942,1.453s-0.91,0.737-1.449,0.947\n				c-0.521,0.203-1.112,0.343-1.986,0.384s-1.154,0.051-3.377,0.056c-2.224,0.004-2.504-0.005-3.376-0.043\n				c-0.872-0.038-1.468-0.175-1.988-0.377c-0.541-0.209-0.997-0.488-1.453-0.942c-0.456-0.454-0.737-0.91-0.947-1.449\n				c-0.204-0.52-0.343-1.115-0.385-1.987c-0.042-0.874-0.052-1.153-0.056-3.377s0.004-2.505,0.043-3.376\n				c0.039-0.872,0.175-1.468,0.377-1.989c0.208-0.539,0.488-0.996,0.942-1.452c0.454-0.456,0.91-0.737,1.449-0.947\n				c0.52-0.204,1.115-0.344,1.987-0.385c0.874-0.043,1.152-0.052,3.376-0.056c2.224-0.004,2.505,0.005,3.377,0.043\n				c0.872,0.038,1.467,0.175,1.989,0.377c0.539,0.208,0.997,0.487,1.452,0.942c0.456,0.455,0.737,0.91,0.947,1.449\n				c0.204,0.521,0.343,1.114,0.384,1.987c0.041,0.873,0.051,1.152,0.056,3.376S23.185,17.487,23.147,18.362z"
      }
    )
  );
}

// src/components/icons/socialMedia/LinkedIn.tsx
import React54 from "react";
function LinkedIn(props) {
  return /* @__PURE__ */ React54.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      height: "30",
      viewBox: "0 0 30 30",
      ...props
    },
    /* @__PURE__ */ React54.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M15,0C6.716,0,0,6.716,0,15c0,8.284,6.716,15,15,15c8.284,0,15-6.716,15-15C30,6.716,23.284,0,15,0z\n			 M8.997,20.795v-8.533h2.652l0,8.533H8.997z M10.324,11.096c-0.849,0-1.538-0.688-1.538-1.537c0-0.849,0.688-1.538,1.537-1.538\n			c0.849,0,1.538,0.688,1.538,1.537S11.173,11.096,10.324,11.096z M21.582,20.795h-2.65v-4.149c0-0.989-0.018-2.264-1.378-2.264\n			c-1.38,0-1.591,1.078-1.591,2.192v4.221h-2.65v-8.533h2.544v1.166h0.036c0.519-0.887,1.483-1.416,2.51-1.378\n			c2.686,0,3.181,1.768,3.181,4.064V20.795z"
      }
    )
  );
}

// src/components/icons/socialMedia/TikTok.tsx
import React55 from "react";
function TikTok(props) {
  return /* @__PURE__ */ React55.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 30 30",
      height: "30",
      ...props
    },
    /* @__PURE__ */ React55.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M15,0C6.716,0,0,6.716,0,15c0,8.284,6.716,15,15,15c8.284,0,15-6.716,15-15C30,6.716,23.284,0,15,0zM22.273,13.551c-1.487,0-2.865-0.465-3.99-1.254v5.696c0,2.845-2.368,5.159-5.279,5.159c-1.125,0-2.168-0.347-3.025-0.935c-0.001,0-0.001-0.001-0.002-0.001c-1.36-0.934-2.252-2.478-2.252-4.223c0-2.844,2.368-5.159,5.279-5.159c0.242,0,0.478,0.019,0.711,0.05l0,2.862c-0.225-0.069-0.463-0.109-0.711-0.109c-1.329,0-2.411,1.057-2.411,2.356c0,0.905,0.525,1.691,1.292,2.085c0,0,0,0,0,0c0.335,0.172,0.715,0.271,1.118,0.271c1.299,0,2.358-1.01,2.406-2.267l0.005-11.232h2.868c0,0.243,0.024,0.48,0.068,0.711h0c0.202,1.068,0.851,1.984,1.749,2.557c0,0,0.001,0.001,0.001,0.001c0.625,0.398,1.371,0.631,2.172,0.631v0.624c0,0,0,0,0,0V13.551z"
      }
    )
  );
}

// src/components/icons/socialMedia/TwitterX.tsx
import React56 from "react";
var TwitterX = () => {
  return /* @__PURE__ */ React56.createElement(
    "svg",
    {
      version: "1.1",
      id: "Layer_1",
      xmlns: "http://www.w3.org/2000/svg",
      x: "0px",
      y: "0px",
      viewBox: "0 0 30 30",
      height: "30",
      width: "30"
    },
    /* @__PURE__ */ React56.createElement(
      "polygon",
      {
        fill: "#163E63",
        points: "15.1,13.8 11.4,8.5 9.4,8.5 14,15.1 14.6,15.9 14.6,15.9 18.6,21.6 20.6,21.6 15.7,14.6 	"
      }
    ),
    /* @__PURE__ */ React56.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M15,0C6.7,0,0,6.7,0,15c0,8.3,6.7,15,15,15c8.3,0,15-6.7,15-15C30,6.7,23.3,0,15,0z M18,22.5l-4-5.8l-5,5.8\n		H7.7l5.7-6.7L7.7,7.5H12l3.8,5.5l4.7-5.5h1.3l-5.5,6.3h0l5.9,8.6H18z"
      }
    )
  );
};

// src/components/icons/tiles/ElectricCar.tsx
import React57 from "react";
var ElectricCar = () => {
  return /* @__PURE__ */ React57.createElement(
    "svg",
    {
      width: "112",
      height: "112",
      viewBox: "0 0 112 112",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M78.5205 68.6005V63.2105C78.5205 61.9005 77.0705 59.7105 77.0705 59.7105L73.5605 53.7205H38.8405L35.3305 59.7105C35.3305 59.7105 33.8805 61.9005 33.8805 63.2105V68.6005C33.8805 71.3405 36.1005 73.5605 38.8405 73.5605H73.5605C76.3005 73.5605 78.5205 71.3405 78.5205 68.6005Z",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M65.0205 38.8405C66.9505 38.7205 68.7704 39.7105 69.7304 41.3805L73.5605 53.7205H38.8405L42.6705 41.3805C43.6205 39.7005 45.4505 38.7205 47.3805 38.8405H65.0205Z",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M56.2004 19.0005C76.8604 18.8905 93.7004 35.5405 93.8104 56.2005C93.9204 76.8605 77.2704 93.7005 56.6104 93.8105C35.9504 93.9205 19.1104 77.2705 19.0004 56.6105C18.9704 50.4105 20.4704 44.3105 23.3804 38.8405",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M38.8405 73.5605V78.5205",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M73.5604 73.5605V78.5205",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M34.1804 39.1406C31.2704 42.0506 26.5604 42.0506 23.6604 39.1406C20.7604 36.2306 20.7504 31.5206 23.6604 28.6206L28.9204 23.3606L39.4404 33.8806L34.1804 39.1406Z",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M30.6704 25.1106L34.1804 21.6106",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M37.6904 32.1306L41.1904 28.6206",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M73 62.4106H67C65.8954 62.4106 65 63.3061 65 64.4106V64.9106",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round"
      }
    ),
    /* @__PURE__ */ React57.createElement(
      "path",
      {
        d: "M39 62.4106H45C46.1046 62.4106 47 63.3061 47 64.4106V64.9106",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round"
      }
    )
  );
};

// src/components/icons/tiles/Landsbankinn.tsx
import React58 from "react";
var LandsbankinnIcon = () => {
  return /* @__PURE__ */ React58.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "1.506 4.101 25.518 24.9",
      width: "25.518",
      height: "24.9"
    },
    /* @__PURE__ */ React58.createElement("g", { fill: "#163e63", transform: "matrix(1, 0, 0, 1, 1.506219, 4.001299)" }, /* @__PURE__ */ React58.createElement("path", { d: "M23.3 25c1.9 0 2.5-1.4 2.1-2.6-.5-1.4-4-11.8-4-11.8s-3.7 11-4.1 12.5c-.7 2.3-3.9 1.4-3.2-1.1.7-2.5 5.3-16.7 5.8-18.4.7-2.3 1.8-3.4 4.1-3.5H12.8c-2 0-3.3 1.4-3.8 3S3.5 20.3 3 21.8c-.6 1.7-1.8 2.8-3 3.2h23.3" }))
  );
};

// src/components/icons/tiles/Phone.tsx
import React59 from "react";
var Phone = () => {
  return /* @__PURE__ */ React59.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "112",
      height: "112",
      viewBox: "0 0 112 112"
    },
    /* @__PURE__ */ React59.createElement(
      "g",
      {
        id: "\xC1rssk\xFDrsla/icon/Appi\xF0",
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React59.createElement(
        "g",
        {
          id: "Group",
          stroke: "#163E63",
          strokeWidth: "2",
          transform: "translate(34 18)"
        },
        /* @__PURE__ */ React59.createElement("path", { id: "Path", d: "M44 63.5L0 63.5" }),
        /* @__PURE__ */ React59.createElement(
          "path",
          {
            id: "Path",
            d: "M21.5 68.75a.75.75 0 01.75.75h0a.75.75 0 01-.75.75h0a.75.75 0 01-.75-.75h0a.75.75 0 01.75-.75"
          }
        ),
        /* @__PURE__ */ React59.createElement("path", { id: "Path", d: "M0 0L44 0 44 76 0 76z" })
      )
    )
  );
};

// src/components/icons/tiles/Spotify.tsx
import React60 from "react";
var BlueSpotify = () => {
  return /* @__PURE__ */ React60.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      "data-name": "Layer 1",
      viewBox: "0 0 558.425 167.487"
    },
    /* @__PURE__ */ React60.createElement(
      "path",
      {
        fill: "#163e63",
        d: "M83.743 0a83.744 83.744 0 1083.744 83.742A83.743 83.743 0 0083.743 0zm38.406 120.787a5.215 5.215 0 01-7.177 1.737c-19.665-12.019-44.417-14.733-73.567-8.075a5.218 5.218 0 11-2.324-10.174c31.9-7.292 59.263-4.154 81.336 9.334a5.221 5.221 0 011.732 7.178zm10.247-22.8a6.533 6.533 0 01-8.981 2.15c-22.5-13.833-56.822-17.841-83.447-9.759a6.528 6.528 0 11-3.794-12.492c30.415-9.23 68.226-4.76 94.074 11.125a6.53 6.53 0 012.152 8.973zm.884-23.744c-26.994-16.031-71.52-17.5-97.289-9.684a7.832 7.832 0 01-4.548-14.99c29.582-8.979 78.756-7.245 109.832 11.2a7.833 7.833 0 01-8 13.472zM227.836 77.309c-14.458-3.448-17.036-5.868-17.036-10.952 0-4.8 4.523-8.037 11.249-8.037 6.52 0 12.985 2.455 19.764 7.509a.944.944 0 00.714.175.932.932 0 00.625-.387l7.061-9.952a.948.948 0 00-.181-1.288 42.713 42.713 0 00-27.769-9.62c-15.612 0-26.516 9.369-26.516 22.774 0 14.376 9.406 19.465 25.662 23.394 13.836 3.187 16.172 5.857 16.172 10.63 0 5.29-4.722 8.578-12.321 8.578-8.44 0-15.324-2.844-23.025-9.512a.993.993 0 00-.695-.227.941.941 0 00-.65.335l-7.916 9.42a.939.939 0 00.094 1.313 47.19 47.19 0 0031.872 12.225c16.823 0 27.694-9.193 27.694-23.42.003-12.023-7.181-18.673-24.798-22.958zM290.7 63.049c-7.292 0-13.273 2.872-18.206 8.757v-6.624a.948.948 0 00-.946-.948H258.6a.948.948 0 00-.946.948v73.6a.949.949 0 00.946.949h12.947a.949.949 0 00.946-.949v-23.23a23.257 23.257 0 0018.206 8.24c13.549 0 27.265-10.43 27.265-30.368S304.247 63.049 290.7 63.049zm12.21 30.375c0 10.153-6.255 17.238-15.209 17.238-8.853 0-15.532-7.407-15.532-17.238s6.679-17.237 15.532-17.237c8.81 0 15.209 7.247 15.209 17.237zM353.121 63.049c-17.45 0-31.121 13.437-31.121 30.592 0 16.969 13.576 30.264 30.9 30.264 17.511 0 31.222-13.391 31.222-30.481.007-17.033-13.61-30.375-31.001-30.375zm0 47.719c-9.282 0-16.279-7.457-16.279-17.344 0-9.928 6.755-17.134 16.065-17.134 9.34 0 16.385 7.457 16.385 17.351 0 9.927-6.801 17.127-16.171 17.127zM421.391 64.234h-14.246V49.667a.948.948 0 00-.946-.948h-12.945a.95.95 0 00-.949.948v14.567h-6.226a.946.946 0 00-.943.948v11.127a.947.947 0 00.943.949h6.226v28.791c0 11.635 5.79 17.534 17.212 17.534a23.607 23.607 0 0012.128-3.018.943.943 0 00.478-.821v-10.595a.949.949 0 00-1.371-.85 16.321 16.321 0 01-7.6 1.834c-4.154 0-6.007-1.886-6.007-6.113V77.258h14.246a.948.948 0 00.945-.949V65.182a.947.947 0 00-.945-.948zM471.029 64.29V62.5c0-5.263 2.017-7.61 6.544-7.61a21.854 21.854 0 017.3 1.346.946.946 0 001.245-.9V44.425a.947.947 0 00-.67-.908 35.524 35.524 0 00-10.761-1.547c-11.958 0-18.278 6.735-18.278 19.467v2.74h-6.22a.952.952 0 00-.95.948v11.184a.953.953 0 00.95.949h6.22v44.409a.946.946 0 00.943.949h12.947a.95.95 0 00.949-.949V77.258h12.082l18.518 44.4c-2.1 4.665-4.169 5.593-6.991 5.593a14.942 14.942 0 01-7.139-2.024.968.968 0 00-.754-.071.954.954 0 00-.56.511l-4.389 9.627a.942.942 0 00.408 1.225 27.263 27.263 0 0013.827 3.54c9.56 0 14.844-4.453 19.5-16.433l22.461-58.041a.946.946 0 00-.879-1.292h-13.475a.951.951 0 00-.9.636l-13.807 39.437L494.03 64.9a.947.947 0 00-.885-.61z"
      }
    ),
    /* @__PURE__ */ React60.createElement(
      "rect",
      {
        width: "14.844",
        height: "58.382",
        x: "428.357",
        y: "64.234",
        fill: "#163e63",
        rx: "0.949"
      }
    ),
    /* @__PURE__ */ React60.createElement(
      "path",
      {
        fill: "#163e63",
        d: "M435.845 38.515a9.285 9.285 0 109.285 9.285 9.285 9.285 0 00-9.285-9.285zM549.267 82.394a9.136 9.136 0 119.158-9.164 9.075 9.075 0 01-9.158 9.164zm.047-17.365a8.227 8.227 0 108.154 8.2 8.148 8.148 0 00-8.154-8.2zm2.02 9.138l2.578 3.609h-2.173l-2.32-3.31h-2v3.31H545.6v-9.564h4.265c2.222 0 3.684 1.137 3.684 3.05a2.852 2.852 0 01-2.215 2.905zm-1.534-4.315h-2.373v3.025h2.373c1.184 0 1.891-.579 1.891-1.513-.003-.984-.71-1.512-1.891-1.512z"
      }
    )
  );
};

// src/components/icons/tiles/Tree.tsx
import React61 from "react";
var Tree = () => {
  return /* @__PURE__ */ React61.createElement(
    "svg",
    {
      width: "112",
      height: "112",
      viewBox: "0 0 112 112",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React61.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M57.0699 59.763L50.3332 51.1596C51.5436 52.4023 53.5322 52.4284 54.7749 51.218C56.0175 50.0075 56.0436 48.0189 54.8332 46.7763L45.9666 36.0863C49.0666 39.1663 53.2599 34.8863 50.1766 31.4196L40.4232 21.0463C39.7934 20.3785 38.9161 20 37.9982 20C37.0803 20 36.203 20.3785 35.5732 21.0463L25.8232 31.4263C22.7399 34.8863 26.9332 39.1663 30.0299 36.093L21.1632 46.7763C20.3802 47.5808 20.0858 48.7418 20.391 49.8221C20.6962 50.9025 21.5545 51.7379 22.6427 52.0138C23.7309 52.2897 24.8835 51.9641 25.6666 51.1596L18.9266 59.763C18.0283 60.6607 17.7585 62.0107 18.2427 63.1848C18.7268 64.3588 19.8699 65.1261 21.1399 65.1296H54.8566C56.1265 65.1261 57.2696 64.3588 57.7538 63.1848C58.238 62.0107 57.9682 60.6607 57.0699 59.763Z",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React61.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M92.1968 53.3896L84.6401 43.2629C87.2801 46.1829 90.4334 41.9296 88.2268 38.8529L80.6668 28.6963C80.0447 27.8455 79.0541 27.3425 78.0001 27.3425C76.9462 27.3425 75.9555 27.8455 75.3334 28.6963L67.7901 38.8529C65.5834 41.9163 68.7368 46.1863 71.3801 43.2629L63.8201 53.3896C63.0987 54.2006 62.8529 55.3293 63.1718 56.3669C63.4906 57.4044 64.3278 58.2003 65.3801 58.4663H90.6368C91.6891 58.2003 92.5263 57.4044 92.8451 56.3669C93.1639 55.3293 92.9182 54.2006 92.1968 53.3896Z",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React61.createElement(
      "path",
      {
        d: "M37.9999 65.1331V78.4664",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React61.createElement(
      "path",
      {
        d: "M77.9999 58.4663V68.4663",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React61.createElement(
      "path",
      {
        d: "M18 80.0463C35.5334 75.8202 54.0318 80.1793 67.8333 91.7897",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React61.createElement(
      "path",
      {
        d: "M54.9033 76.0164C66.7759 68.6383 81.1903 66.5539 94.6667 70.2664",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  );
};

// src/components/icons/tiles/Visa.tsx
import React62 from "react";
var Visa = () => {
  return /* @__PURE__ */ React62.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "87",
      height: "69",
      fill: "none",
      viewBox: "0 0 87 69"
    },
    /* @__PURE__ */ React62.createElement(
      "path",
      {
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2",
        d: "M78.138 11h7.609v56.667H9.08v-9.525"
      }
    ),
    /* @__PURE__ */ React62.createElement(
      "path",
      {
        fillRule: "evenodd",
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2",
        d: "M1.08 1h76.667v56.667H1.08V1z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React62.createElement(
      "path",
      {
        stroke: "#163E63",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2",
        d: "M15.924 22.667h4.27l1.207 12a1.493 1.493 0 002.833.493L30.4 22.303M36.444 22.563l-2.707 13.54M48.84 22.407s-4.583-1.2-6.147 1.406c-1.563 2.607.887 4.27 2.5 5.26 1.614.99 3.387 2.5 2.917 4.27-.47 1.77-2.447 4.377-7.667 2.554M52.734 36.103l6.267-12.63a1.453 1.453 0 012.75.527l1 11.897M54.727 32.093h7.76"
      }
    )
  );
};

// src/components/icons/tiles/Youtube.tsx
import React63 from "react";
var BlueYoutube = () => {
  return /* @__PURE__ */ React63.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      "data-name": "Layer 1",
      viewBox: "0 0 380.925 85.039"
    },
    /* @__PURE__ */ React63.createElement(
      "path",
      {
        fill: "#163e63",
        d: "M176.336 77.364a13.234 13.234 0 01-5.126-7.558 49.466 49.466 0 01-1.521-13.64v-7.732a44.825 44.825 0 011.738-13.814 13.858 13.858 0 015.429-7.6 17.589 17.589 0 019.687-2.389q5.909 0 9.47 2.432a13.947 13.947 0 015.212 7.6 46.618 46.618 0 011.651 13.77v7.732a47.42 47.42 0 01-1.607 13.683 13.43 13.43 0 01-5.213 7.558q-3.606 2.388-9.773 2.389-6.343.005-9.947-2.431zm13.464-8.341q1-2.605 1-8.513V43.916a25.687 25.687 0 00-1-8.384 3.574 3.574 0 00-3.518-2.649 3.524 3.524 0 00-3.432 2.649 25.66 25.66 0 00-1 8.384V60.51q0 5.908.956 8.513a3.493 3.493 0 003.475 2.607 3.568 3.568 0 003.519-2.607zM360.856 56.292v2.694a70.748 70.748 0 00.3 7.688 7.286 7.286 0 001.26 3.736 3.586 3.586 0 002.954 1.173 3.718 3.718 0 003.692-2.085q1-2.085 1.086-6.95l10.338.608a16.272 16.272 0 01.087 1.911q0 7.386-4.039 11.034t-11.421 3.649q-8.86 0-12.424-5.561t-3.562-17.2v-9.3q0-11.988 3.693-17.505t12.641-5.517q6.168 0 9.469 2.259a12.121 12.121 0 014.648 7.037 50.844 50.844 0 011.347 13.2v9.122zm1.52-22.414a7.336 7.336 0 00-1.216 3.692 71.921 71.921 0 00-.3 7.776v3.823h8.774v-3.823a65.036 65.036 0 00-.346-7.776 7.455 7.455 0 00-1.26-3.735 3.484 3.484 0 00-2.824-1.086 3.418 3.418 0 00-2.828 1.129zM147.142 55.3L133.5 6.036h11.9l4.778 22.327q1.826 8.256 2.693 14.074h.348q.606-4.17 2.693-13.987l4.952-22.414h11.9L158.957 55.3v23.627h-11.815zM241.586 25.672v53.255H232.2l-1.043-6.516h-.26q-3.82 7.389-11.465 7.389-5.3 0-7.819-3.475t-2.519-10.86V25.672h11.989v39.094a11.935 11.935 0 00.782 5.083 2.7 2.7 0 002.606 1.52 5.314 5.314 0 003-.956 5.917 5.917 0 002.129-2.433V25.672zM303.082 25.672v53.255H293.7l-1.043-6.516h-.26q-3.824 7.389-11.469 7.389-5.3 0-7.819-3.475t-2.519-10.86V25.672h11.989v39.094a11.935 11.935 0 00.782 5.083 2.7 2.7 0 002.606 1.52 5.314 5.314 0 003-.956 5.917 5.917 0 002.129-2.433V25.672z"
      }
    ),
    /* @__PURE__ */ React63.createElement(
      "path",
      {
        fill: "#163e63",
        d: "M274.164 15.681h-11.9v63.246h-11.73V15.681h-11.9V6.037h35.532zM342.761 34.185q-1.088-5.037-3.519-7.3a9.453 9.453 0 00-6.689-2.258 11.1 11.1 0 00-6.168 1.867 12.7 12.7 0 00-4.431 4.909h-.088V3.344h-11.553v75.582h9.9l1.217-5.038h.26a10.169 10.169 0 004.17 4.257 12.369 12.369 0 006.169 1.563 9.44 9.44 0 008.948-5.6q2.866-5.6 2.867-17.506v-8.43a69.992 69.992 0 00-1.083-13.987zm-10.99 21.722a67.376 67.376 0 01-.478 9.122 9.865 9.865 0 01-1.607 4.692 3.715 3.715 0 01-3.04 1.39 5.561 5.561 0 01-2.737-.7 5.238 5.238 0 01-2.042-2.085V38.1a7.577 7.577 0 012.085-3.562 4.61 4.61 0 013.215-1.39 3.242 3.242 0 012.823 1.433 11.03 11.03 0 011.39 4.822 89.865 89.865 0 01.391 9.643zM118.946 13.279a15.213 15.213 0 00-10.74-10.74C98.732 0 60.742 0 60.742 0S22.753 0 13.279 2.539a15.213 15.213 0 00-10.74 10.74C0 22.753 0 42.52 0 42.52s0 19.766 2.539 29.24a15.214 15.214 0 0010.74 10.74c9.474 2.538 47.463 2.538 47.463 2.538s37.99 0 47.464-2.538a15.214 15.214 0 0010.74-10.741c2.539-9.474 2.539-29.24 2.539-29.24s0-19.766-2.539-29.24zM48.594 60.742V24.3l31.561 18.22z"
      }
    )
  );
};

// src/components/icons/Web.tsx
import React64 from "react";
var Web = () => /* @__PURE__ */ React64.createElement(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    fill: "none",
    viewBox: "0 0 24 24"
  },
  /* @__PURE__ */ React64.createElement(
    "path",
    {
      stroke: "#00B3BF",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M.5 4.5h20M3.5 2.25a.25.25 0 100 .5.25.25 0 000-.5v0M5.5 2.25a.25.25 0 100 .5.25.25 0 000-.5v0M7.5 2.25a.25.25 0 100 .5.25.25 0 000-.5v0"
    }
  ),
  /* @__PURE__ */ React64.createElement(
    "path",
    {
      stroke: "#00B3BF",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M7.5 16.5h-5a2 2 0 01-2-2v-12a2 2 0 012-2h16a2 2 0 012 2v6"
    }
  ),
  /* @__PURE__ */ React64.createElement(
    "path",
    {
      stroke: "#00B3BF",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M16.5 23.5c-2-1.5-3-4.5-3-7s1-5.5 3-7M16.5 23.5c2-1.5 3-4.5 3-7s-1-5.5-3-7M10.174 13.5h12.652M10.174 19.5h12.652M9.5 16.5h14"
    }
  ),
  /* @__PURE__ */ React64.createElement(
    "circle",
    {
      cx: "16.5",
      cy: "16.5",
      r: "7",
      stroke: "#00B3BF",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  )
);

// src/components/icons/Youtube.tsx
import React65 from "react";
var Youtube = () => {
  return /* @__PURE__ */ React65.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      "data-name": "Layer 1",
      height: "30",
      width: "30",
      viewBox: "0 0 30 30"
    },
    /* @__PURE__ */ React65.createElement(
      "path",
      {
        fill: "#163e63",
        d: "M14.968 0a15 15 0 1015 15 15 15 0 00-15-15zm9.52 19.814a2.5 2.5 0 01-1.757 1.768C21.182 22 14.968 22 14.968 22s-6.214 0-7.768-.418a2.5 2.5 0 01-1.756-1.768A26.147 26.147 0 015.032 15a26.147 26.147 0 01.416-4.814A2.5 2.5 0 017.2 8.418C8.754 8 14.968 8 14.968 8s6.214 0 7.763.418a2.5 2.5 0 011.757 1.768A26.209 26.209 0 0124.9 15a26.209 26.209 0 01-.412 4.814z"
      }
    ),
    /* @__PURE__ */ React65.createElement("path", { fill: "#163e63", d: "M12.935 17.955L18.129 15l-5.194-2.955v5.91z" })
  );
};

// src/components/icons/Appstore.tsx
import React66 from "react";
var Appstore = () => {
  return /* @__PURE__ */ React66.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "135", height: "40" }, /* @__PURE__ */ React66.createElement(
    "path",
    {
      fill: "#A6A6A6",
      d: "M130.197 40H4.729A4.74 4.74 0 010 35.267V4.726A4.733 4.733 0 014.729 0h125.468C132.803 0 135 2.12 135 4.726v30.541c0 2.605-2.197 4.733-4.803 4.733z"
    }
  ), /* @__PURE__ */ React66.createElement("path", { d: "M134.032 35.268a3.83 3.83 0 01-3.834 3.83H4.729a3.835 3.835 0 01-3.839-3.83V4.725A3.84 3.84 0 014.729.89h125.468a3.834 3.834 0 013.834 3.835l.001 30.543z" }), /* @__PURE__ */ React66.createElement(
    "path",
    {
      fill: "#FFF",
      d: "M30.128 19.784c-.029-3.223 2.639-4.791 2.761-4.864-1.511-2.203-3.853-2.504-4.676-2.528-1.967-.207-3.875 1.177-4.877 1.177-1.022 0-2.565-1.157-4.228-1.123-2.14.033-4.142 1.272-5.24 3.196-2.266 3.923-.576 9.688 1.595 12.859 1.086 1.553 2.355 3.287 4.016 3.226 1.625-.067 2.232-1.036 4.193-1.036 1.943 0 2.513 1.036 4.207.997 1.744-.028 2.842-1.56 3.89-3.127 1.255-1.78 1.759-3.533 1.779-3.623-.041-.014-3.387-1.291-3.42-5.154zm-3.2-9.478c.874-1.093 1.472-2.58 1.306-4.089-1.265.056-2.847.875-3.758 1.944-.806.942-1.526 2.486-1.34 3.938 1.421.106 2.88-.717 3.792-1.793zM53.645 31.504h-2.271l-1.244-3.909h-4.324l-1.185 3.909H42.41l4.284-13.308h2.646l4.305 13.308zm-3.89-5.549L48.63 22.48c-.119-.355-.342-1.191-.671-2.507h-.04a96.75 96.75 0 01-.632 2.507l-1.105 3.475h3.573zm14.907.633c0 1.632-.441 2.922-1.323 3.869-.79.843-1.771 1.264-2.942 1.264-1.264 0-2.172-.454-2.725-1.362h-.04v5.055H55.5V25.067c0-1.026-.027-2.079-.079-3.159h1.875l.119 1.521h.04c.711-1.146 1.79-1.718 3.238-1.718 1.132 0 2.077.447 2.833 1.342.758.896 1.136 2.074 1.136 3.535zm-2.172.078c0-.934-.21-1.704-.632-2.31-.461-.632-1.08-.948-1.856-.948-.526 0-1.004.176-1.431.523-.428.35-.708.807-.839 1.373-.066.264-.099.48-.099.65v1.6c0 .698.214 1.287.642 1.768s.984.721 1.668.721c.803 0 1.428-.31 1.875-.928.448-.619.672-1.435.672-2.449zm13.209-.078c0 1.632-.441 2.922-1.324 3.869-.789.843-1.77 1.264-2.941 1.264-1.264 0-2.172-.454-2.724-1.362h-.04v5.055h-2.132V25.067c0-1.026-.027-2.079-.079-3.159h1.875l.119 1.521h.04c.71-1.146 1.789-1.718 3.238-1.718 1.131 0 2.076.447 2.834 1.342.755.896 1.134 2.074 1.134 3.535zm-2.172.078c0-.934-.211-1.704-.633-2.31-.461-.632-1.078-.948-1.855-.948a2.22 2.22 0 00-1.432.523c-.428.35-.707.807-.838 1.373-.065.264-.099.48-.099.65v1.6c0 .698.214 1.287.64 1.768.428.48.984.721 1.67.721.803 0 1.428-.31 1.875-.928.448-.619.672-1.435.672-2.449zm14.512 1.106c0 1.132-.393 2.053-1.182 2.764-.867.777-2.074 1.165-3.625 1.165-1.432 0-2.58-.276-3.449-.829l.494-1.777c.936.566 1.963.85 3.082.85.803 0 1.428-.182 1.877-.544.447-.362.67-.848.67-1.454 0-.54-.184-.995-.553-1.364-.367-.369-.98-.712-1.836-1.029-2.33-.869-3.494-2.142-3.494-3.816 0-1.094.408-1.991 1.225-2.689.814-.699 1.9-1.048 3.258-1.048 1.211 0 2.217.211 3.02.632l-.533 1.738c-.75-.408-1.598-.612-2.547-.612-.75 0-1.336.185-1.756.553a1.58 1.58 0 00-.533 1.205c0 .526.203.961.611 1.303.355.316 1 .658 1.936 1.027 1.145.461 1.986 1 2.527 1.618.539.616.808 1.387.808 2.307zm7.049-4.264h-2.35v4.659c0 1.185.414 1.777 1.244 1.777.381 0 .697-.033.947-.099l.059 1.619c-.42.157-.973.236-1.658.236-.842 0-1.5-.257-1.975-.77-.473-.514-.711-1.376-.711-2.587v-4.837h-1.4v-1.6h1.4v-1.757l2.094-.632v2.389h2.35v1.602zm10.603 3.119c0 1.475-.422 2.686-1.264 3.633-.883.975-2.055 1.461-3.516 1.461-1.408 0-2.529-.467-3.365-1.401s-1.254-2.113-1.254-3.534c0-1.487.43-2.705 1.293-3.652.861-.948 2.023-1.422 3.484-1.422 1.408 0 2.541.467 3.396 1.402.818.907 1.226 2.078 1.226 3.513zm-2.212.069c0-.885-.189-1.644-.572-2.277-.447-.766-1.086-1.148-1.914-1.148-.857 0-1.508.383-1.955 1.148-.383.634-.572 1.405-.572 2.317 0 .885.189 1.644.572 2.276.461.766 1.105 1.148 1.936 1.148.814 0 1.453-.39 1.914-1.168.393-.645.591-1.412.591-2.296zm9.142-2.913a3.702 3.702 0 00-.672-.059c-.75 0-1.33.283-1.738.85-.355.5-.533 1.132-.533 1.895v5.035h-2.131l.02-6.574c0-1.106-.027-2.113-.08-3.021h1.857l.078 1.836h.059c.225-.631.58-1.139 1.066-1.52a2.578 2.578 0 011.541-.514c.197 0 .375.014.533.039v2.033zm9.535 2.469a5 5 0 01-.078.967h-6.396c.025.948.334 1.673.928 2.173.539.447 1.236.671 2.092.671.947 0 1.811-.151 2.588-.454l.334 1.48c-.908.396-1.98.593-3.217.593-1.488 0-2.656-.438-3.506-1.313-.848-.875-1.273-2.05-1.273-3.524 0-1.447.395-2.652 1.186-3.613.828-1.026 1.947-1.539 3.355-1.539 1.383 0 2.43.513 3.141 1.539.563.815.846 1.823.846 3.02zm-2.033-.553c.014-.632-.125-1.178-.414-1.639-.369-.593-.936-.889-1.699-.889-.697 0-1.264.289-1.697.869-.355.461-.566 1.014-.631 1.658h4.441zM49.05 10.009c0 1.177-.353 2.063-1.058 2.658-.653.549-1.581.824-2.783.824-.596 0-1.106-.026-1.533-.078V6.982c.557-.09 1.157-.136 1.805-.136 1.145 0 2.008.249 2.59.747.652.563.979 1.368.979 2.416zm-1.105.029c0-.763-.202-1.348-.606-1.756-.404-.407-.994-.611-1.771-.611-.33 0-.611.022-.844.068v4.889c.129.02.365.029.708.029.802 0 1.421-.223 1.857-.669s.656-1.096.656-1.95zm6.964.999c0 .725-.207 1.319-.621 1.785-.434.479-1.009.718-1.727.718-.692 0-1.243-.229-1.654-.689-.41-.459-.615-1.038-.615-1.736 0-.73.211-1.329.635-1.794s.994-.698 1.712-.698c.692 0 1.248.229 1.669.688.4.446.601 1.022.601 1.726zm-1.087.034c0-.435-.094-.808-.281-1.119-.22-.376-.533-.564-.94-.564-.421 0-.741.188-.961.564-.188.311-.281.69-.281 1.138 0 .435.094.808.281 1.119.227.376.543.564.951.564.4 0 .714-.191.94-.574.194-.317.291-.693.291-1.128zm8.943-2.352l-1.475 4.714h-.96l-.611-2.047a15.32 15.32 0 01-.379-1.523h-.019a11.15 11.15 0 01-.379 1.523l-.649 2.047h-.971l-1.387-4.714h1.077l.533 2.241c.129.53.235 1.035.32 1.513h.019c.078-.394.207-.896.389-1.503l.669-2.25h.854l.641 2.202c.155.537.281 1.054.378 1.552h.029c.071-.485.178-1.002.32-1.552l.572-2.202h1.029zm5.433 4.714H67.15v-2.7c0-.832-.316-1.248-.95-1.248a.946.946 0 00-.757.343 1.217 1.217 0 00-.291.808v2.796h-1.048v-3.366c0-.414-.013-.863-.038-1.349h.921l.049.737h.029c.122-.229.304-.418.543-.569.284-.176.602-.265.95-.265.44 0 .806.142 1.097.427.362.349.543.87.543 1.562v2.824zm2.89 0h-1.047V6.556h1.047v6.877zm6.17-2.396c0 .725-.207 1.319-.621 1.785-.434.479-1.01.718-1.727.718-.693 0-1.244-.229-1.654-.689-.41-.459-.615-1.038-.615-1.736 0-.73.211-1.329.635-1.794s.994-.698 1.711-.698c.693 0 1.248.229 1.67.688.4.446.601 1.022.601 1.726zm-1.088.034c0-.435-.094-.808-.281-1.119-.219-.376-.533-.564-.939-.564-.422 0-.742.188-.961.564-.188.311-.281.69-.281 1.138 0 .435.094.808.281 1.119.227.376.543.564.951.564.4 0 .713-.191.939-.574.195-.317.291-.693.291-1.128zm6.16 2.362h-.941l-.078-.543h-.029c-.322.433-.781.65-1.377.65-.445 0-.805-.143-1.076-.427a1.339 1.339 0 01-.369-.96c0-.576.24-1.015.723-1.319.482-.304 1.16-.453 2.033-.446V10.3c0-.621-.326-.931-.979-.931-.465 0-.875.117-1.229.349l-.213-.688c.438-.271.979-.407 1.617-.407 1.232 0 1.85.65 1.85 1.95v1.736c0 .471.023.846.068 1.124zm-1.088-1.62v-.727c-1.156-.02-1.734.297-1.734.95 0 .246.066.43.201.553a.733.733 0 00.512.184c.23 0 .445-.073.641-.218a.893.893 0 00.38-.742zm7.043 1.62h-.93l-.049-.757h-.029c-.297.576-.803.864-1.514.864-.568 0-1.041-.223-1.416-.669s-.562-1.025-.562-1.736c0-.763.203-1.381.611-1.853.395-.44.879-.66 1.455-.66.633 0 1.076.213 1.328.64h.02V6.556h1.049v5.607c0 .459.012.882.037 1.27zm-1.086-1.988v-.786a1.194 1.194 0 00-.408-.965 1.03 1.03 0 00-.701-.257c-.391 0-.697.155-.922.466-.223.311-.336.708-.336 1.193 0 .466.107.844.322 1.135.227.31.533.465.916.465.344 0 .619-.129.828-.388.202-.239.301-.527.301-.863zm10.049-.408c0 .725-.207 1.319-.621 1.785-.434.479-1.008.718-1.727.718-.691 0-1.242-.229-1.654-.689-.41-.459-.615-1.038-.615-1.736 0-.73.211-1.329.635-1.794s.994-.698 1.713-.698c.691 0 1.248.229 1.668.688.4.446.601 1.022.601 1.726zm-1.086.034c0-.435-.094-.808-.281-1.119-.221-.376-.533-.564-.941-.564-.42 0-.74.188-.961.564-.188.311-.281.69-.281 1.138 0 .435.094.808.281 1.119.227.376.543.564.951.564.4 0 .715-.191.941-.574.193-.317.291-.693.291-1.128zm6.721 2.362h-1.047v-2.7c0-.832-.316-1.248-.951-1.248-.311 0-.562.114-.756.343s-.291.499-.291.808v2.796h-1.049v-3.366c0-.414-.012-.863-.037-1.349h.92l.049.737h.029a1.53 1.53 0 01.543-.569c.285-.176.602-.265.951-.265.439 0 .805.142 1.096.427.363.349.543.87.543 1.562v2.824zm7.053-3.929h-1.154v2.29c0 .582.205.873.611.873.188 0 .344-.016.467-.049l.027.795c-.207.078-.479.117-.814.117-.414 0-.736-.126-.969-.378-.234-.252-.35-.676-.35-1.271V9.504h-.689v-.785h.689v-.864l1.027-.31v1.173h1.154v.786zm5.548 3.929h-1.049v-2.68c0-.845-.316-1.268-.949-1.268-.486 0-.818.245-1 .735a1.317 1.317 0 00-.049.377v2.835h-1.047V6.556h1.047v2.841h.02c.33-.517.803-.775 1.416-.775.434 0 .793.142 1.078.427.355.355.533.883.533 1.581v2.803zm5.723-2.58c0 .188-.014.346-.039.475h-3.143c.014.466.164.821.455 1.067.266.22.609.33 1.029.33.465 0 .889-.074 1.271-.223l.164.728c-.447.194-.973.291-1.582.291-.73 0-1.305-.215-1.721-.645-.418-.43-.625-1.007-.625-1.731 0-.711.193-1.303.582-1.775.406-.504.955-.756 1.648-.756.678 0 1.193.252 1.541.756.281.4.42.895.42 1.483zm-1-.271a1.411 1.411 0 00-.203-.805c-.182-.291-.459-.437-.834-.437a.995.995 0 00-.834.427 1.586 1.586 0 00-.311.815h2.182z"
    }
  ));
};

// src/components/icons/ArrowHouse.tsx
import React67 from "react";
var ArrowHouse = (props) => {
  return /* @__PURE__ */ React67.createElement(
    "svg",
    {
      width: "26",
      height: "33",
      style: {
        marginRight: "16px",
        marginLeft: "2px",
        overflow: "visible"
      }
    },
    /* @__PURE__ */ React67.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: props.selected ? "#00ADC4" : "#1E3D5A",
        strokeWidth: "2",
        opacity: props.selected ? "1" : "0.5"
      },
      /* @__PURE__ */ React67.createElement("path", { d: "M0 5.584h8.101v8.101M19.694 30.54l-5.728-5.728 5.728-5.728" }),
      /* @__PURE__ */ React67.createElement("path", { d: "M15.29 24.809h8.878V10.63L12.925 1.815M8.061 5.628l-6.38 5.003v14.178h7.666" })
    )
  );
};

// src/components/icons/Aukakronur.tsx
import React68 from "react";
var Aukakronur = () => {
  return /* @__PURE__ */ React68.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      xmlnsXlink: "http://www.w3.org/1999/xlink",
      width: "200",
      height: "215",
      viewBox: "0 0 200 215"
    },
    /* @__PURE__ */ React68.createElement("defs", null, /* @__PURE__ */ React68.createElement(
      "path",
      {
        id: "path-1",
        d: "M0.793793103 0.368694779L118.956724 0.368694779 118.956724 111.645701 0.793793103 111.645701z"
      }
    )),
    /* @__PURE__ */ React68.createElement("g", { fill: "none", fillRule: "evenodd", stroke: "none", strokeWidth: "1" }, /* @__PURE__ */ React68.createElement("g", { transform: "translate(-224 -371)" }, /* @__PURE__ */ React68.createElement("g", { transform: "translate(130 270)" }, /* @__PURE__ */ React68.createElement("g", { transform: "translate(94 101)" }, /* @__PURE__ */ React68.createElement("g", { transform: "translate(35.345 .495)" }, /* @__PURE__ */ React68.createElement("mask", { id: "mask-2", fill: "#fff" }, /* @__PURE__ */ React68.createElement("use", { xlinkHref: "#path-1" })), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M34.11 111.646c3.766-12.412 7.627-25.194 8.274-27.141l16.49-57.027h1.244c5.602 19.01 11.512 38.329 17.115 58.274l7.656 25.894h34.068L82.52.368H38.03C26.319 35.898 13.792 73.061.795 111.646H34.11z",
        mask: "url(#mask-2)"
      }
    )), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M135.865 164.807c5.273 16.813 10.298 33.6 15.601 49.824h36.404l-16.317-49.824h-35.688z"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M18.435 164.807a15253.827 15253.827 0 00-16.618 49.824h35.468l15.596-49.824H18.435z"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M12.776 129.853c-.188-.738-.443-1.474-.666-2.213h-.07l-.701 2.251c-.847 2.653-2.356 7.079-3.126 9.881h7.657a539.48 539.48 0 00-3.094-9.919zm5.262 16.887c-.403-1.29-.918-2.985-1.33-4.166H7.253c-.333 1.033-.92 2.8-1.29 4.166h2.502v2.802H.001v-2.802h2.06l7.622-22.493h5.042c2.395 7.303 5.046 15.23 7.437 22.493h2.062v2.802h-8.616v-2.802h2.43z"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M26.613 144.197v-9.844h-2.575v-2.84h6.514v12.02c0 2.03.256 3.171 2.616 3.171 1.176 0 3.165-.518 4.788-2.027v-10.324h-2.32v-2.84h6.256v15.227h2.54v2.324c-1.36.518-2.724.703-3.57.703-1.806 0-2.616-.555-2.8-2.326-2.17 1.99-4.528 2.508-6.073 2.508-4.564 0-5.377-2.802-5.377-5.752"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M56.693 131.547h7.804v2.658h-1.659l-4.822 4.13 5.23 8.557 2.354.327v2.104c-.924.33-2.173.48-2.946.48-.992 0-1.913-.15-2.652-1.366l-4.742-8.001c-.851.702-1.586 1.366-2.394 2.064v4.425h2.279v2.617h-8.686v-2.617h2.503v-20.98H46.2v-2.618h6.666v15.56l5.445-4.682h-1.618v-2.658z"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M74.627 141.429c-2.058.15-3.163 1.33-3.163 2.843 0 1.917.882 2.803 2.983 2.803 1.324 0 3.016-.776 4.192-1.883v-4.055l-4.012.292zm7.808 5.35h2.798l.217 2.212c-.993.552-3.126.775-4.122.775-1.435 0-2.429-.333-2.464-2.25-.036 0-.036-.037-.078-.037-2.61 2.357-5.004 2.395-6.072 2.395-4.307 0-5.336-3.095-5.336-5.197 0-2.914 1.986-5.053 6.993-5.384l4.342-.296v-1.069c0-2.692-.846-3.725-3.606-3.725-1.766 0-3.793.37-5.522.959a48.914 48.914 0 01-.736-2.507c2.06-.92 4.822-1.437 7.178-1.437 4.013 0 6.408 1.548 6.408 6.083v9.478z"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M97.487 131.547h7.805v2.658h-1.657l-4.825 4.13 5.23 8.557 2.353.327v2.104c-.919.33-2.169.48-2.945.48-.995 0-1.913-.15-2.648-1.366l-4.752-8.001c-.845.702-1.582 1.366-2.39 2.064v4.425h2.28v2.617H87.25v-2.617h2.505v-20.98h-2.76v-2.618h6.663v15.56l5.445-4.682h-1.616v-2.658z"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M108.388 131.512l6.26-.036v2.727c.036 0 .108.038.145.038 1.398-1.547 4.196-3.17 7.106-3.17v3.722c-3.018.222-5.78 1.293-7.18 2.14v9.845h2.721v2.764h-9.129v-2.764h2.504v-12.464h-2.427v-2.802z"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M131.217 129.853l-1.467-1.366 4.893-6.083 2.357 2.323-5.783 5.126zm.739 4.24c-3.644 0-4.564 3.32-4.564 6.526 0 3.357.663 6.304 4.454 6.304 3.534 0 4.344-3.206 4.344-6.304 0-3.02-.697-6.526-4.234-6.526zm-.368 15.746c-6.071 0-8.245-4.24-8.245-9.22 0-4.94 2.429-9.476 8.54-9.476 6.222 0 8.32 4.537 8.32 9.403 0 4.792-2.174 9.293-8.615 9.293z"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M159.878 137.117v9.624h2.723v2.323c-1.028.479-2.648.7-3.642.7-2.91 0-2.983-2.174-2.983-2.914v-8.994c0-2.434-.406-3.578-2.91-3.578-1.512 0-3.493.81-4.858 1.658v13.607h-3.902v-15.228h-2.577v-2.802h6.48v1.585l.182.036c2.064-1.329 4.197-2.026 6.004-2.026 5.19 0 5.483 3.907 5.483 6.009"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M165.647 144.197v-9.844h-2.575v-2.84h6.516v12.02c0 2.03.253 3.171 2.613 3.171 1.177 0 3.167-.518 4.788-2.027v-10.324h-2.325v-2.84h6.26v15.227h2.54v2.324c-1.357.518-2.725.703-3.568.703-1.807 0-2.615-.555-2.8-2.326-2.175 1.99-4.526 2.508-6.074 2.508-4.563 0-5.375-2.802-5.375-5.752"
      }
    ), /* @__PURE__ */ React68.createElement(
      "path",
      {
        fill: "#163E63",
        d: "M185.685 131.512l6.26-.036v2.727c.036 0 .106.038.144.038 1.4-1.547 4.195-3.17 7.103-3.17v3.722c-3.019.222-5.778 1.293-7.18 2.14v9.845h2.726v2.764h-9.128v-2.764h2.5v-12.464h-2.425v-2.802z"
      }
    )))))
  );
};

// src/components/icons/BoxClose.tsx
import React69 from "react";
function BoxClose() {
  return /* @__PURE__ */ React69.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "31",
      height: "31",
      viewBox: "0 0 31 31"
    },
    /* @__PURE__ */ React69.createElement("g", { fill: "none", fillRule: "evenodd", stroke: "none", strokeWidth: "1" }, /* @__PURE__ */ React69.createElement("g", { stroke: "#1E3D5A", transform: "translate(1 1)" }, /* @__PURE__ */ React69.createElement("g", null, /* @__PURE__ */ React69.createElement("path", { d: "M0 0H29V29H0z" })), /* @__PURE__ */ React69.createElement("path", { d: "M8 21L21 8" }), /* @__PURE__ */ React69.createElement("path", { d: "M8 21L21 8", transform: "matrix(-1 0 0 1 29 0)" })))
  );
}

// src/components/icons/BubbleClose.tsx
import React70 from "react";
var BubbleClose = () => /* @__PURE__ */ React70.createElement(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "60",
    height: "60",
    viewBox: "0 0 60 60"
  },
  /* @__PURE__ */ React70.createElement("g", { fill: "#FFF" }, /* @__PURE__ */ React70.createElement("circle", { cx: "30", cy: "30", r: "30", fillRule: "evenodd" }), /* @__PURE__ */ React70.createElement(
    "path",
    {
      fill: "none",
      stroke: "#1E3C5A",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M39.339 39.339L21.66 21.66m17.678 0L21.66 39.34"
    }
  ))
);

// src/components/icons/BubbleIcon.tsx
import React71 from "react";
var BubbleIcon = () => /* @__PURE__ */ React71.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "60", height: "60" }, /* @__PURE__ */ React71.createElement("g", { fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React71.createElement("circle", { cx: "30", cy: "30", r: "30", fill: "#FFF" }), /* @__PURE__ */ React71.createElement(
  "path",
  {
    stroke: "#1E3C5A",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    d: "M39.125 24.14h2.015a8.201 8.201 0 011.812 16.198v4.105a.559.559 0 01-.556.557.555.555 0 01-.395-.163l-4.296-4.296h-2.504a8.192 8.192 0 01-6.754-3.547",
    opacity: "0.5"
  }
), /* @__PURE__ */ React71.createElement(
  "path",
  {
    stroke: "#1E3C5A",
    strokeLinecap: "inherit",
    strokeLinejoin: "round",
    d: "M28.924 16h-7.528C15.654 16 11 20.654 11 26.396c0 4.951 3.463 9.09 8.098 10.136v5.204a.707.707 0 001.206.499l5.445-5.444h3.175c5.74 0 10.396-4.654 10.396-10.395C39.32 20.654 34.665 16 28.924 16z"
  }
)));

// src/components/icons/BubbleIconSmall.tsx
import React72 from "react";
var BubbleIconSmall = () => /* @__PURE__ */ React72.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "33", height: "36" }, /* @__PURE__ */ React72.createElement("g", { fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React72.createElement("path", { d: "M-4-2h40v40H-4z" }), /* @__PURE__ */ React72.createElement(
  "path",
  {
    stroke: "#1E3C5A",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    d: "M5.023 34.62C26.277 34.62 32 24.775 32 16.226 32 7.648 25.046.694 16.468.694S.936 7.648.936 16.226c0 5.485 2.845 10.303 7.138 13.066L5.023 34.62zm5.081-13.214a9.692 9.692 0 0013.706 0"
  }
)));

// src/components/icons/ButtonArrow.tsx
import styled21 from "@emotion/styled";
import React73 from "react";
var Svg = styled21.svg`
  transform: ${(props) => props.active ? "rotate(180deg)" : "none"};
  rect {
    fill: ${(props) => props.hover ? "#1E69B0" : "transparent"};
  }

  polyline {
    stroke: ${(props) => props.invert && !props.hover || props.active && !props.hover ? "#1E3D5A" : "#FFFFFF"};
  }
`;
function ButtonArrow(props) {
  const { arrowScale = "1" } = props;
  return /* @__PURE__ */ React73.createElement(
    Svg,
    {
      width: "28px",
      height: "28px",
      viewBox: "0 0 44 44",
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg",
      ...props
    },
    /* @__PURE__ */ React73.createElement(
      "g",
      {
        id: "Button-Arrow-big",
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd"
      },
      /* @__PURE__ */ React73.createElement(
        "rect",
        {
          id: "Rectangle",
          fill: "#1E69B0",
          x: "0",
          y: "-1.8189894e-12",
          width: `${props.width ?? "44px"}`,
          height: `${props.height ?? "44px"}`
        }
      ),
      /* @__PURE__ */ React73.createElement(
        "polyline",
        {
          id: "Path-Copy-3",
          stroke: "#1E3D5A",
          strokeWidth: `${props.arrowWidth ?? "1.68796992"}`,
          transform: `translate(22.000000, 22.500000) rotate(-540.000000) scale(${arrowScale},${arrowScale}) translate(-22.000000, -22.500000) `,
          points: "14 27 22 18 30 27"
        }
      )
    )
  );
}

// src/components/icons/Calendar.tsx
import React74 from "react";
var Calendar = () => {
  return /* @__PURE__ */ React74.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "34", height: "33" }, /* @__PURE__ */ React74.createElement(
    "g",
    {
      fill: "none",
      fillRule: "evenodd",
      stroke: "#1E3C5A",
      transform: "translate(-3 -3)"
    },
    /* @__PURE__ */ React74.createElement("rect", { width: "31.432", height: "29.27", x: "4.284", y: "6.176", rx: "2" }),
    /* @__PURE__ */ React74.createElement(
      "path",
      {
        strokeLinecap: "round",
        strokeLinejoin: "round",
        d: "M28.108 19.73l-8.156 7.306a1.081 1.081 0 01-1.41 0l-3.407-3.052m-10.676-9.66h31.082M12.027 3.986v6.622m16.216-6.622v6.622"
      }
    )
  ));
};

// src/components/icons/CalendarIconLarge.tsx
import React75 from "react";
var CalendarIconLarge = () => /* @__PURE__ */ React75.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "60", height: "61" }, /* @__PURE__ */ React75.createElement(
  "g",
  {
    fill: "none",
    fillRule: "evenodd",
    stroke: "#1E3C5A",
    transform: "translate(-7 -6)"
  },
  /* @__PURE__ */ React75.createElement("rect", { width: "59", height: "55", x: "7.5", y: "11", rx: "2" }),
  /* @__PURE__ */ React75.createElement(
    "path",
    {
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M52 36.5L36.912 50.017c-.72.644-1.889.644-2.608 0L28 44.369M8.25 26.5h57.5M22.25 7.375v12.25m30-12.25v12.25"
    }
  )
));

// src/components/icons/CancelContract.tsx
import React76 from "react";
function CancelContract(props) {
  return /* @__PURE__ */ React76.createElement("svg", { viewBox: "0 0 98 94", ...props }, /* @__PURE__ */ React76.createElement(
    "g",
    {
      transform: "translate(1 1)",
      stroke: "currentColor",
      strokeWidth: "2",
      fill: "none",
      fillRule: "evenodd",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    },
    /* @__PURE__ */ React76.createElement("path", { d: "M30.338 22.966V3.828A3.825 3.825 0 0 1 34.161 0h38.226l22.936 22.966V84.21a3.825 3.825 0 0 1-3.823 3.828H64.742" }),
    /* @__PURE__ */ React76.createElement("path", { d: "M72.985 0v19.188a3.837 3.837 0 0 0 3.836 3.837H96M85.846 38.601H55.385M86.523 69.076H70.954M86.523 54.177H61.477" }),
    /* @__PURE__ */ React76.createElement("circle", { cx: "26.5", cy: "65.5", r: "26.5" }),
    /* @__PURE__ */ React76.createElement("path", { d: "M7.764 84.236 45.24 46.76" })
  ));
}

// src/components/icons/CheckboxActive.tsx
import React77 from "react";
function CheckboxActive({ className, disabled, ...rest }) {
  return /* @__PURE__ */ React77.createElement(
    "svg",
    {
      viewBox: "0 0 28 28",
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg",
      className,
      ...rest
    },
    /* @__PURE__ */ React77.createElement(
      "g",
      {
        id: "4.-Radio-Button/Old/Check/1.-Check",
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd"
      },
      /* @__PURE__ */ React77.createElement(
        "rect",
        {
          id: "Rectangle-Copy-6",
          fill: disabled ? "#a0a0a0" : "#1E3D5A",
          x: "0",
          y: "0",
          width: "28",
          height: "28"
        }
      ),
      /* @__PURE__ */ React77.createElement(
        "polyline",
        {
          id: "Path-Copy-4",
          stroke: "#FFFFFF",
          strokeWidth: "2",
          transform: "translate(14.000000, 14.000000) scale(-1, 1) rotate(-180.000000) translate(-14.000000, -14.000000) ",
          points: "7 14.4615385 12.5 8 21 20"
        }
      )
    )
  );
}

// src/components/icons/Close.tsx
import React78 from "react";
var Close = ({ className }) => /* @__PURE__ */ React78.createElement(
  "svg",
  {
    width: "30px",
    height: "30px",
    viewBox: "0 0 30 30",
    version: "1.1",
    className
  },
  /* @__PURE__ */ React78.createElement("g", { stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React78.createElement("g", { id: "Menu-lvl-1", transform: "translate(-346.000000, -27.000000)" }, /* @__PURE__ */ React78.createElement("g", { id: "Group", transform: "translate(346.000000, 27.000000)" }, /* @__PURE__ */ React78.createElement(
    "g",
    {
      id: "Group-3",
      transform: "translate(8.000000, 8.000000)",
      stroke: "#FFFFFF",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    },
    /* @__PURE__ */ React78.createElement(
      "line",
      {
        x1: "0.388888889",
        y1: "0.388888889",
        x2: "13.6111111",
        y2: "13.6111111",
        id: "Line-4"
      }
    ),
    /* @__PURE__ */ React78.createElement(
      "line",
      {
        x1: "0.388888889",
        y1: "0.388888889",
        x2: "13.6111111",
        y2: "13.6111111",
        id: "Line-4",
        transform: "translate(7.000000, 7.000000) scale(1, -1) translate(-7.000000, -7.000000) "
      }
    )
  ))))
);

// src/components/icons/CloseIcon.tsx
import React79 from "react";
var CloseIcon = ({ color, height, width }) => /* @__PURE__ */ React79.createElement(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: `${width ?? 16}`,
    height: `${height ?? 14}`
  },
  /* @__PURE__ */ React79.createElement("g", { fill: "none", fillRule: "evenodd", opacity: "0.8" }, /* @__PURE__ */ React79.createElement("path", { d: "M-4-5h24v24H-4z" }), /* @__PURE__ */ React79.createElement(
    "path",
    {
      stroke: color ?? "#35669E",
      strokeLinecap: "inherit",
      strokeLinejoin: "inherit",
      d: "M8 7L1 0l7 7 7-7-7 7zm0 0l-7 7 7-7 7 7-7-7z"
    }
  ))
);

// src/components/icons/CloseThick.tsx
import { useResource } from "@landsbankinn/l16n";
import React80 from "react";

// src/components/dialog/Dialog.res.ts
var Dialog_res_default = {
  closeAriaLabel: {
    is: "Loka glugga",
    en: "Close window",
    pl: "Zamknij okno"
  }
};

// src/components/icons/CloseThick.tsx
var CloseThick = ({ className, ...rest }) => {
  const { r } = useResource();
  return /* @__PURE__ */ React80.createElement(
    "svg",
    {
      width: "12",
      height: "12",
      viewBox: "0 0 12 12",
      className,
      "aria-label": r(Dialog_res_default.closeAriaLabel),
      ...rest
    },
    /* @__PURE__ */ React80.createElement("g", { transform: "translate(-12 -11.936)", fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React80.createElement("g", { stroke: "currentColor", strokeWidth: "2" }, /* @__PURE__ */ React80.createElement("path", { d: "M13 13l10 10M23 13L13 23" })))
  );
};

// src/components/icons/CloseThin.tsx
import styled22 from "@emotion/styled";
import { useResource as useResource2 } from "@landsbankinn/l16n";
import React81 from "react";
var StyledPath = styled22.path`
  fill: none;
  stroke: #163e63;
  stroke-linecap: round;
  stroke-linejoin: round;
`;
var StyledSvg = styled22.svg`
  enable-background: new 0 0 28 28;
`;
var CloseThin = ({ className, ...rest }) => {
  const { r } = useResource2();
  return /* @__PURE__ */ React81.createElement(
    StyledSvg,
    {
      xmlns: "http://www.w3.org/2000/svg",
      x: "0px",
      y: "0px",
      viewBox: "0 0 28 28",
      className,
      "aria-label": r(Dialog_res_default.closeAriaLabel),
      ...rest
    },
    /* @__PURE__ */ React81.createElement("g", { transform: "translate(6.2218 6.221799999999997)" }, /* @__PURE__ */ React81.createElement(StyledPath, { d: "M0,0l15.6,15.6" }), /* @__PURE__ */ React81.createElement("g", { transform: "translate(0.00040000000035433914 0.0)" }, /* @__PURE__ */ React81.createElement(StyledPath, { className: "st0", d: "M15.6,0L0,15.6" })))
  );
};

// src/components/icons/Contact.tsx
import React82 from "react";
var Contact = () => /* @__PURE__ */ React82.createElement(
  "svg",
  {
    width: "135",
    height: "135",
    viewBox: "0 0 136 136",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  },
  /* @__PURE__ */ React82.createElement(
    "path",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M20.1973 9.27011V54.7723C20.1973 59.3374 23.8665 63.0424 28.3966 63.0424H77.1214C79.3229 63.0424 81.4301 63.9314 82.9716 65.5152L95.4921 78.3752C96.7753 79.6984 99.0014 78.7804 99.0014 76.9279V63.0424V45.6379V9.27011C99.0014 4.70088 95.3322 1 90.802 1H28.3966C23.8665 1 20.1973 4.70088 20.1973 9.27011Z",
      stroke: "#163E63",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ React82.createElement(
    "path",
    {
      opacity: "0.2",
      d: "M20 37H7.51694C3.91606 37 0.9995 39.9417 0.9995 43.5737V72.4814V86.3157V97.353C0.9995 98.8255 2.76899 99.5551 3.78897 98.5033L13.7411 88.2813C14.9664 87.0224 16.6414 86.3157 18.3913 86.3157H57.1212C60.7221 86.3157 63.6387 83.3707 63.6387 79.7421V63.5",
      stroke: "#163E63",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ React82.createElement(
    "path",
    {
      d: "M37 42.7018L50.0287 32.3657L56.0401 38.5284L74.3515 24L82 31.9034",
      stroke: "#163E63",
      strokeWidth: "2",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  )
);

// src/components/icons/Corporate.tsx
import React83 from "react";
function Corporate() {
  return /* @__PURE__ */ React83.createElement("svg", { width: "22px", height: "19px", viewBox: "0 0 22 19", version: "1.1" }, /* @__PURE__ */ React83.createElement(
    "g",
    {
      id: "Page-1",
      stroke: "none",
      strokeWidth: "1",
      fill: "none",
      fillRule: "evenodd",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    },
    /* @__PURE__ */ React83.createElement(
      "g",
      {
        id: "Fors\xED\xF0a-(Netbankar)-selected",
        transform: "translate(-1273.000000, -127.000000)"
      },
      /* @__PURE__ */ React83.createElement(
        "g",
        {
          id: "icon/top/login/unlock-copy",
          transform: "translate(1272.000000, 124.000000)",
          stroke: "#163E63"
        },
        /* @__PURE__ */ React83.createElement("g", null, /* @__PURE__ */ React83.createElement("line", { x1: "1.5", y1: "21.5", x2: "22.5", y2: "21.5", id: "Line-9" }), /* @__PURE__ */ React83.createElement("polyline", { id: "Path", points: "3 21 3 11 12 11 12 21" }), /* @__PURE__ */ React83.createElement(
          "polyline",
          {
            id: "Path-Copy",
            points: "12 21 12 4 21 4 21 21"
          }
        ), /* @__PURE__ */ React83.createElement("line", { x1: "7.5", y1: "15.5", x2: "7.5", y2: "17.5", id: "Line-10" }), /* @__PURE__ */ React83.createElement(
          "line",
          {
            x1: "16.5",
            y1: "15.5",
            x2: "16.5",
            y2: "17.5",
            id: "Line-10-Copy"
          }
        ), /* @__PURE__ */ React83.createElement(
          "line",
          {
            x1: "16.5",
            y1: "8.5",
            x2: "16.5",
            y2: "10.5",
            id: "Line-10-Copy-2"
          }
        ))
      )
    )
  ));
}

// src/components/icons/Datepicker.tsx
import styled23 from "@emotion/styled";
import React84 from "react";
var Svg2 = styled23.svg`
  & path {
    fill: transparent;
    ${({ active }) => active && "fill: #cee1ea;"};
  }

  & rect,
  & line {
    stroke: #bbc4cd;
    ${({ active, hover }) => (active || hover) && "stroke: #20446a"};
  }

  &:hover path {
    fill: #cee1ea;
  }
`;
function Datepicker({
  active,
  hover
}) {
  return /* @__PURE__ */ React84.createElement(
    Svg2,
    {
      width: "28px",
      height: "26px",
      viewBox: "0 0 28 26",
      version: "1.1",
      active,
      hover
    },
    /* @__PURE__ */ React84.createElement(
      "g",
      {
        id: "Gengi",
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd"
      },
      /* @__PURE__ */ React84.createElement("g", { id: "Artboard", transform: "translate(-144.000000, -71.000000)" }, /* @__PURE__ */ React84.createElement("g", { id: "Group", transform: "translate(145.000000, 72.000000)" }, /* @__PURE__ */ React84.createElement(
        "path",
        {
          d: "M2.18181818,2.18181818 L24,2.18181818 C25.2049849,2.18181818 26.1818182,3.15865145 26.1818182,4.36363636 L26.1818182,9.45454545 L26.1818182,9.45454545 L0,9.45454545 L0,4.36363636 C-1.47568091e-16,3.15865145 0.976833273,2.18181818 2.18181818,2.18181818 Z",
          id: "Rectangle-Copy-16"
        }
      ), /* @__PURE__ */ React84.createElement(
        "rect",
        {
          id: "Rectangle-Copy-17",
          stroke: "#20446A",
          strokeLinejoin: "round",
          x: "0",
          y: "2.18181818",
          width: "26.1818182",
          height: "21.8181818",
          rx: "2.18181818"
        }
      ), /* @__PURE__ */ React84.createElement(
        "line",
        {
          x1: "6.90909091",
          y1: "0.363636364",
          x2: "6.90909091",
          y2: "4",
          id: "Line-7-Copy-4",
          stroke: "#20446A",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ), /* @__PURE__ */ React84.createElement(
        "line",
        {
          x1: "19.2727273",
          y1: "0.363636364",
          x2: "19.2727273",
          y2: "4",
          id: "Line-7-Copy-5",
          stroke: "#20446A",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        }
      ), /* @__PURE__ */ React84.createElement(
        "line",
        {
          x1: "0.622314453",
          y1: "9.5",
          x2: "25.5",
          y2: "9.5",
          id: "Line-Copy",
          stroke: "#20446A",
          strokeLinecap: "square"
        }
      )))
    )
  );
}

// src/components/icons/DatepickerWithCheckmark.tsx
import React85 from "react";
function DatepickerWithCheckmark({ className }) {
  return /* @__PURE__ */ React85.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "220",
      height: "220",
      viewBox: "0 0 220 220",
      className
    },
    /* @__PURE__ */ React85.createElement(
      "g",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React85.createElement("g", { stroke: "#1E3D5A", strokeWidth: "3", transform: "translate(-911 -5321)" }, /* @__PURE__ */ React85.createElement("g", { transform: "translate(913 5323)" }, /* @__PURE__ */ React85.createElement("path", { d: "M0 19.5L216 19.5 216 216 0 216z" }), /* @__PURE__ */ React85.createElement("path", { d: "M46.5 0L46.5 46.5" }), /* @__PURE__ */ React85.createElement("path", { d: "M168 0L168 46.5" }), /* @__PURE__ */ React85.createElement("path", { d: "M0 66L216 66" }), /* @__PURE__ */ React85.createElement("path", { d: "M154.5 141c0 25.681-20.819 46.5-46.5 46.5S61.5 166.681 61.5 141 82.319 94.5 108 94.5" }), /* @__PURE__ */ React85.createElement("path", { d: "M88.5 136.289l15.274 11.283c2.008 1.482 4.843 1.156 6.45-.743L154.5 94.5" })))
    )
  );
}

// src/components/icons/ElectricCar.tsx
import React86 from "react";
function ElectricCar2({ active }) {
  return /* @__PURE__ */ React86.createElement(
    "svg",
    {
      width: "28px",
      height: "26px",
      viewBox: "0 0 28 26",
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React86.createElement("title", null, "icon/rafmagnsb\xEDll"),
    /* @__PURE__ */ React86.createElement(
      "g",
      {
        id: "B\xEDlal\xE1n",
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd"
      },
      /* @__PURE__ */ React86.createElement(
        "g",
        {
          id: "B\xEDlal\xE1nareiknir-5",
          transform: "translate(-577.000000, -1831.000000)"
        },
        /* @__PURE__ */ React86.createElement("g", { id: "Group-9", transform: "translate(130.000000, 1472.000000)" }, /* @__PURE__ */ React86.createElement("g", { id: "Group-6", transform: "translate(100.000000, 344.000000)" }, /* @__PURE__ */ React86.createElement("g", { id: "Group-4", transform: "translate(347.000000, 12.000000)" }, /* @__PURE__ */ React86.createElement("g", { id: "Group-38", transform: "translate(0.000000, 3.000000)" }, /* @__PURE__ */ React86.createElement(
          "polygon",
          {
            id: "Path-23",
            stroke: active ? theme.colors.secondary : theme.colors.primary,
            strokeWidth: "1.5",
            points: "21.75 4.5 15.75 15.75 19.5 15.75 19.5 22.5 26.25 11.25 21.75 11.25"
          }
        ), /* @__PURE__ */ React86.createElement(
          "path",
          {
            d: "M16.9837371,-6.71462885e-13 L18.5303301,1.54659299 L17.4696699,2.60725316 L16.362,1.5 L6.4695,1.5 L3.616,5.996 L17.2666982,5.9685168 L11.8133552,16.0961538 L4.81725,16.09575 L4.81730769,18.75 L-9.23705556e-14,18.75 L-9.23705556e-14,14.0192308 L0.00458719899,13.9364071 L0.687520102,7.790011 L1.627,6.331 L5.64537496,-6.71462885e-13 L16.9837371,-6.71462885e-13 Z",
            id: "Combined-Shape",
            fill: active ? theme.colors.secondary : theme.colors.primary
          }
        ), /* @__PURE__ */ React86.createElement(
          "polygon",
          {
            id: "Path-26",
            fill: "#FFFFFF",
            points: "3 9 3 10.5 3.75 11.25 9 11.25 6.75 9"
          }
        )))))
      )
    )
  );
}

// src/components/icons/ExclamationMark.tsx
import * as React87 from "react";
var ExclamationMark = () => /* @__PURE__ */ React87.createElement(
  "svg",
  {
    width: "30px",
    height: "30px",
    viewBox: "0 0 24 24",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    display: "block"
  },
  /* @__PURE__ */ React87.createElement(
    "g",
    {
      id: "Design-system-einkabanki",
      stroke: "none",
      strokeWidth: "1",
      fill: "none",
      fillRule: "evenodd"
    },
    /* @__PURE__ */ React87.createElement("g", { id: "Artboard", transform: "translate(-160.000000, -436.000000)" }, /* @__PURE__ */ React87.createElement("g", { id: "Group-11-Copy", transform: "translate(160.000000, 436.000000)" }, /* @__PURE__ */ React87.createElement(
      "path",
      {
        d: "M11.9985842,7.3662323 C12.2772727,7.3662323 12.5067809,7.14675179 12.5067809,6.85973877 L12.5067809,6.57272582 C12.5067809,6.28571281 12.2772727,6.0662323 11.9985842,6.0662323 C11.7198957,6.0662323 11.5067809,6.28571281 11.5067809,6.57272582 L11.5067809,6.85973877 C11.5067809,7.14675179 11.7198957,7.3662323 11.9985842,7.3662323 Z M12.016985,18.0662323 C12.3026992,18.0662323 12.5067809,17.8924872 12.5067809,17.6318694 L12.5067809,9.50059527 C12.5067809,9.2399775 12.2822912,9.0662323 11.9965769,9.0662323 C11.7108626,9.0662323 11.5067809,9.25735203 11.5067809,9.50059527 L11.5067809,17.6318694 C11.5067809,17.8924872 11.7312707,18.0662323 12.016985,18.0662323 Z",
        id: "Shape",
        fill: "#35669E",
        fillRule: "nonzero",
        transform: "translate(12.006781, 12.066232) rotate(-180.000000) translate(-12.006781, -12.066232) "
      }
    ), /* @__PURE__ */ React87.createElement(
      "circle",
      {
        id: "Oval",
        stroke: "#35669E",
        strokeWidth: "0.914256029",
        cx: "12",
        cy: "12",
        r: "11.542872"
      }
    )))
  )
);

// src/components/icons/ExpandPlus.tsx
import styled24 from "@emotion/styled";
import React88 from "react";
import { animated as animated8, useSpring as useSpring7 } from "react-spring";
function correctSize(size) {
  switch (size) {
    case "small":
      return "29px";
    case "medium":
      return "41px";
    case "large":
      return "96px";
    default:
      return "96px";
  }
}
function borderSize(size) {
  switch (size) {
    case "small":
      return "9px";
    case "medium":
      return "14px";
    case "large":
      return "30px";
    default:
      return "30px";
  }
}
var Div10 = styled24.div`
  border-radius: 100px;
  background: ${({ theme: theme2, currentTheme }) => mapThemeColorStringToValue(theme2, currentTheme)};
  height: ${({ size }) => correctSize(size)};
  width: ${({ size }) => correctSize(size)};
  display: flex;
  justify-content: center;
  align-items: center;
`;
var NotAnimatedSpan = styled24.span`
  height: ${({ size }) => borderSize(size)};
  display: inline-block;
  width: 1px;
  border-left: 1px solid #ffffff;
  margin-left: ${({ size }) => size === "small" || size === "medium" ? "-1px" : "0px"};
  transform-origin: center center;
  transform: rotate(90deg);
`;
var AnimatedSpan = styled24(animated8.span)`
  height: ${({ size }) => borderSize(size)};
  border-left: 1px solid #ffffff;
  width: 1px;
  display: inline-block;
  transform-origin: center center;
  transform: rotate(90deg);
`;
var ExpandPlus = ({ isOpen, theme: theme2, size }) => {
  const rotate = useSpring7({
    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)"
  });
  return /* @__PURE__ */ React88.createElement(Div10, { currentTheme: theme2 ?? "primary", size }, /* @__PURE__ */ React88.createElement(AnimatedSpan, { size, style: { ...rotate } }), /* @__PURE__ */ React88.createElement(NotAnimatedSpan, { size }));
};

// src/components/icons/GasCar.tsx
import React89 from "react";
function GasCar({ active }) {
  return /* @__PURE__ */ React89.createElement(
    "svg",
    {
      width: "27px",
      height: "24px",
      viewBox: "0 0 27 24",
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React89.createElement("title", null, "icon/olia"),
    /* @__PURE__ */ React89.createElement(
      "g",
      {
        id: "B\xEDlal\xE1n",
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd"
      },
      /* @__PURE__ */ React89.createElement(
        "g",
        {
          id: "B\xEDlal\xE1nareiknir-5",
          transform: "translate(-1060.000000, -1831.000000)"
        },
        /* @__PURE__ */ React89.createElement("g", { id: "Group-9", transform: "translate(130.000000, 1472.000000)" }, /* @__PURE__ */ React89.createElement("g", { id: "Group-6", transform: "translate(100.000000, 344.000000)" }, /* @__PURE__ */ React89.createElement("g", { id: "Group-36", transform: "translate(830.000000, 12.000000)" }, /* @__PURE__ */ React89.createElement("g", { id: "Bens\xEDn", transform: "translate(0.000000, 3.000000)" }, /* @__PURE__ */ React89.createElement("g", { id: "Group-38" }, /* @__PURE__ */ React89.createElement(
          "path",
          {
            d: "M16.9837371,-6.48370246e-13 L18.5303301,1.54659299 L17.4696699,2.60725316 L16.362,1.5 L6.4695,1.5 L3.616,5.996 L17.2933535,5.96846092 L16.6633375,7.08877784 C15.7983989,8.62684256 15.1013303,9.8797038 14.5724655,10.8467208 L14.5724655,10.8467208 L14.0471124,11.8164635 C13.7012643,12.4622578 13.3002912,13.2261996 12.8442964,14.1081103 L12.8442964,14.1081103 L11.824588,16.0961538 L4.81725,16.09575 L4.81730769,18.75 L-5.32907052e-14,18.75 L-5.32907052e-14,14.0192308 L0.00458719899,13.9364071 L0.687520102,7.790011 L1.627,6.331 L5.64537496,-6.48370246e-13 L16.9837371,-6.48370246e-13 Z",
            id: "Combined-Shape",
            fill: active ? theme.colors.secondary : theme.colors.primary,
            fillRule: "nonzero"
          }
        ), /* @__PURE__ */ React89.createElement(
          "polygon",
          {
            id: "Path-26",
            fill: "#FFFFFF",
            points: "3 9 3 10.5 3.75 11.25 9 11.25 6.75 9"
          }
        )), /* @__PURE__ */ React89.createElement(
          "g",
          {
            id: "Group-40",
            transform: "translate(15.750000, 6.000000)",
            stroke: active ? theme.colors.secondary : theme.colors.primary,
            strokeWidth: "1.5"
          },
          /* @__PURE__ */ React89.createElement(
            "path",
            {
              d: "M5.25,16.4991068 C8.58061826,16.4407555 11.040734,13.7492557 10.3973191,10.3119417 C9.96837587,8.02039913 8.151675,4.58308522 4.94721652,0 C2.14646935,4.58308522 0.531624127,8.02039913 0.102680866,10.3119417 C-0.540734026,13.7492557 1.91938174,16.557458 5.25,16.4991068 Z",
              id: "Oval"
            }
          )
        )))))
      )
    )
  );
}

// src/components/icons/GoBackArrow.tsx
import React90 from "react";
var GoBackArrow = () => /* @__PURE__ */ React90.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "12", height: "20" }, /* @__PURE__ */ React90.createElement("g", { fill: "none", fillRule: "evenodd", opacity: "0.85" }, /* @__PURE__ */ React90.createElement("path", { d: "M-6-2h24v24H-6z" }), /* @__PURE__ */ React90.createElement("path", { stroke: "#1E3C5A", d: "M12 1L0 10l12 9" })));

// src/components/icons/Headset.tsx
import React91 from "react";
var Headset = () => /* @__PURE__ */ React91.createElement("svg", { width: "32", height: "33", xmlns: "http://www.w3.org/2000/svg" }, /* @__PURE__ */ React91.createElement(
  "g",
  {
    stroke: "#1E3C5A",
    fill: "none",
    fillRule: "evenodd",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  },
  /* @__PURE__ */ React91.createElement("path", { d: "M4.968 24.86c0 2.859 3.066 5.03 7.398 5.63M4.736 10.155c0-6.374 5.016-8.83 11.203-8.83 6.187 0 11.203 2.456 11.203 8.83" }),
  /* @__PURE__ */ React91.createElement("path", { d: "M7.735 10.246a1.427 1.427 0 0 0-1.064-.418c-3.142.118-5.603 2.748-5.603 5.986v2.998c0 3.238 2.46 5.867 5.603 5.986h.054c.376 0 .738-.15 1.01-.419.284-.282.445-.672.445-1.079V11.326c0-.408-.161-.797-.445-1.08zM24.144 24.379c.284.282.666.434 1.062.418 3.143-.118 5.605-2.748 5.605-5.985v-2.998c0-3.238-2.462-5.867-5.605-5.986l-.053-.001c-.376 0-.738.15-1.01.42a1.519 1.519 0 0 0-.445 1.078V23.3c0 .407.16.797.446 1.079zM17.893 32.135h-3.908c-.703 0-1.279-.592-1.279-1.318v-.696c0-.724.576-1.317 1.279-1.317h3.908c.704 0 1.28.593 1.28 1.317v.696c0 .726-.576 1.318-1.28 1.318z" })
));

// src/components/icons/House.tsx
import React92 from "react";
var House = (props) => {
  return /* @__PURE__ */ React92.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "27",
      height: "28",
      style: {
        marginRight: "19px",
        marginLeft: "2px",
        minHeight: "33px"
      }
    },
    /* @__PURE__ */ React92.createElement(
      "path",
      {
        fill: "none",
        stroke: props.selected ? "#00ADC4" : "#1E3D5A",
        strokeWidth: "2",
        opacity: props.selected ? "1" : "0.5",
        d: "M12.976 2.34L1.339 11.261v14.35h23.273v-14.35z"
      }
    )
  );
};

// src/components/icons/HybridCar.tsx
import React93 from "react";
function HybridCar({ active }) {
  return /* @__PURE__ */ React93.createElement(
    "svg",
    {
      width: "30px",
      height: "26px",
      viewBox: "0 0 30 26",
      version: "1.1",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React93.createElement("title", null, "icon/vistv\xE6nn"),
    /* @__PURE__ */ React93.createElement(
      "g",
      {
        id: "B\xEDlal\xE1n",
        stroke: "none",
        strokeWidth: "1",
        fill: "none",
        fillRule: "evenodd"
      },
      /* @__PURE__ */ React93.createElement(
        "g",
        {
          id: "B\xEDlal\xE1nareiknir-5",
          transform: "translate(-840.000000, -1831.000000)"
        },
        /* @__PURE__ */ React93.createElement("g", { id: "Group-9", transform: "translate(130.000000, 1472.000000)" }, /* @__PURE__ */ React93.createElement("g", { id: "Group-6", transform: "translate(100.000000, 344.000000)" }, /* @__PURE__ */ React93.createElement("g", { id: "Group-36", transform: "translate(610.000000, 12.000000)" }, /* @__PURE__ */ React93.createElement("g", { id: "Vistv\xE6nn", transform: "translate(0.000000, 3.000000)" }, /* @__PURE__ */ React93.createElement("g", { id: "Group-38" }, /* @__PURE__ */ React93.createElement(
          "path",
          {
            d: "M16.9837371,-6.71462885e-13 L18.5303301,1.54659299 L17.4696699,2.60725316 L16.362,1.5 L6.4695,1.5 L3.616,5.996 L17.5907751,5.9678374 C15.239025,8.96534696 13.8585094,10.8213148 13.4492282,11.5357408 L13.4103533,11.6055749 C12.8886236,12.5745015 12.4489743,13.8751307 12.0986122,15.5038793 L12.0986122,15.5038793 L11.9712073,16.0961538 L4.81725,16.09575 L4.81730769,18.75 L-1.13686838e-13,18.75 L-1.13686838e-13,14.0192308 L0.00458719899,13.9364071 L0.687520102,7.790011 L1.664,6.273 L5.64537496,-6.71462885e-13 L16.9837371,-6.71462885e-13 Z",
            id: "Combined-Shape",
            fill: active ? theme.colors.secondary : theme.colors.primary,
            fillRule: "nonzero"
          }
        ), /* @__PURE__ */ React93.createElement(
          "polygon",
          {
            id: "Path-26",
            fill: "#FFFFFF",
            points: "3 9 3 10.5 3.75 11.25 9 11.25 6.75 9"
          }
        )), /* @__PURE__ */ React93.createElement(
          "g",
          {
            id: "Group-39",
            transform: "translate(22.435295, 14.491481) rotate(-345.000000) translate(-22.435295, -14.491481) translate(16.435295, 2.991481)",
            stroke: active ? theme.colors.secondary : theme.colors.primary,
            strokeWidth: "1.5"
          },
          /* @__PURE__ */ React93.createElement(
            "path",
            {
              d: "M0,12 C0,9.5 2,5.5 6,0 L6.29625,0.4096875 C10.09875,5.6990625 12,9.5625 12,12 C12,14.5 10,16.75 6,18.75 L6,18.75 C2,16.75 0,14.5 0,12 Z",
              id: "Combined-Shape"
            }
          ),
          /* @__PURE__ */ React93.createElement(
            "path",
            {
              d: "M6.7105671,10.6039308 C5.70181851,14.8443293 5.54988112,18.880538 6.7105671,22.6039308",
              id: "Path-24",
              transform: "translate(6.303301, 16.603931) rotate(-9.000000) translate(-6.303301, -16.603931) "
            }
          )
        )))))
      )
    )
  );
}

// src/components/icons/InfoBubble.tsx
import React94 from "react";
function InfoBubble() {
  return /* @__PURE__ */ React94.createElement(
    "svg",
    {
      width: "100",
      height: "100",
      viewBox: "0 0 100 100",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React94.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M1 11.2846V67.8707C1 73.5478 5.563 78.1553 11.1966 78.1553H71.7902C74.528 78.1553 77.1486 79.2609 79.0656 81.2304L94.6358 97.223C96.2316 98.8685 99 97.7269 99 95.4232V78.1553V56.5113V11.2846C99 5.60237 94.437 1 88.8034 1H11.1966C5.563 1 1 5.60237 1 11.2846Z",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React94.createElement(
      "path",
      {
        d: "M50.4186 35.6426V58.8314",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React94.createElement(
      "path",
      {
        d: "M50.4186 26.6431V26.7862",
        stroke: "#163E63",
        strokeWidth: "2",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  );
}

// src/components/icons/Language.tsx
import React95 from "react";
var Language = () => /* @__PURE__ */ React95.createElement(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "26",
    height: "25",
    fill: "none",
    viewBox: "0 0 26 25",
    id: "globe"
  },
  /* @__PURE__ */ React95.createElement("circle", { cx: "13", cy: "12.5", r: "12", stroke: "#fff" }),
  /* @__PURE__ */ React95.createElement(
    "path",
    {
      stroke: "#fff",
      d: "M17 12.5c0 3.41-.498 6.475-1.289 8.67-.395 1.099-.855 1.953-1.34 2.524-.487.572-.952.806-1.371.806-.419 0-.884-.234-1.37-.806-.486-.57-.946-1.425-1.341-2.524C9.498 18.974 9 15.91 9 12.5s.498-6.475 1.289-8.67c.395-1.099.855-1.953 1.34-2.524C12.116.734 12.581.5 13 .5c.419 0 .884.234 1.37.806.486.57.946 1.425 1.341 2.525C16.502 6.025 17 9.09 17 12.5z"
    }
  ),
  /* @__PURE__ */ React95.createElement(
    "path",
    {
      stroke: "#fff",
      strokeLinecap: "square",
      d: "M2.275 8.5h21.45m-21.45 8h21.45"
    }
  )
);

// src/components/icons/Lock.tsx
import React96 from "react";
var Lock = () => /* @__PURE__ */ React96.createElement(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "20",
    height: "27",
    fill: "none",
    viewBox: "0 0 20 27",
    id: "lock"
  },
  /* @__PURE__ */ React96.createElement(
    "path",
    {
      stroke: "#00B3BF",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M4 6.928C4 3.553 6.686.817 10 .817s6 2.736 6 6.11v3.89"
    }
  ),
  /* @__PURE__ */ React96.createElement(
    "path",
    {
      stroke: "#00B3BF",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M16 6.928c0-3.375-2.686-6.111-6-6.111s-6 2.736-6 6.11v3.89"
    }
  ),
  /* @__PURE__ */ React96.createElement(
    "path",
    {
      stroke: "#00B3BF",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M19 12.96c0-1.184-.948-2.143-2.118-2.143H3.118c-1.17 0-2.118.959-2.118 2.143v10.714c0 1.183.948 2.143 2.118 2.143h13.764c1.17 0 2.118-.96 2.118-2.143V12.96z",
      clipRule: "evenodd"
    }
  )
);

// src/components/icons/Ludur.tsx
import React97 from "react";
var Ludur = () => /* @__PURE__ */ React97.createElement(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "39",
    height: "29",
    viewBox: "0 0 39 29"
  },
  /* @__PURE__ */ React97.createElement(
    "g",
    {
      fill: "none",
      fillRule: "evenodd",
      stroke: "none",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      strokeWidth: "1"
    },
    /* @__PURE__ */ React97.createElement("g", { stroke: "#00B3BF", transform: "translate(-55 -451)" }, /* @__PURE__ */ React97.createElement("g", { transform: "translate(49 446)" }, /* @__PURE__ */ React97.createElement("g", { transform: "translate(5)" }, /* @__PURE__ */ React97.createElement("g", { transform: "translate(1.622 4.865)" }, /* @__PURE__ */ React97.createElement("g", null, /* @__PURE__ */ React97.createElement("g", { transform: "translate(0 1.79)" }, /* @__PURE__ */ React97.createElement("path", { d: "M0.63 21.039L23.602 0.488" }), /* @__PURE__ */ React97.createElement("path", { d: "M3.257 25.993L0 19.852" })), /* @__PURE__ */ React97.createElement("path", { d: "M32.527 19.105L2.628 26.597" }), /* @__PURE__ */ React97.createElement("g", { transform: "translate(22.56 .297)" }, /* @__PURE__ */ React97.createElement("path", { d: "M0.097 0.201L10.911 20.59" }), /* @__PURE__ */ React97.createElement("g", { transform: "translate(6.814 .498)" }, /* @__PURE__ */ React97.createElement("path", { d: "M7.367 5.296L4.787 6.664" }), /* @__PURE__ */ React97.createElement("path", { d: "M1.148 0.219L0.291 3.01" }), /* @__PURE__ */ React97.createElement("path", { d: "M8.081 13.291L5.289 12.435" })))), /* @__PURE__ */ React97.createElement("path", { d: "M16.844 23.382a4.352 4.352 0 11-8.518 1.795" })))))
  )
);

// src/components/icons/Notification.tsx
import React98 from "react";
function Notification(props) {
  return /* @__PURE__ */ React98.createElement("svg", { viewBox: "0 0 24 24", ...props }, /* @__PURE__ */ React98.createElement(
    "path",
    {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "m20.865 17.964-2.053-2.326V11a7 7 0 0 0-6.6-7.1 6.812 6.812 0 0 0-7.022 6.809v4.551a1 1 0 0 1-.251.662l-1.8 2.042a.536.536 0 0 0 .4.891h16.924a.536.536 0 0 0 .402-.891Z"
    }
  ), /* @__PURE__ */ React98.createElement(
    "path",
    {
      fill: "none",
      stroke: "currentColor",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M14.145 18.855a2.145 2.145 0 1 1-4.29 0M11 3.488h0a1 1 0 0 1 1-1h0a1 1 0 0 1 1 1h0"
    }
  ));
}

// src/components/icons/Personal.tsx
import React99 from "react";
function Personal() {
  return /* @__PURE__ */ React99.createElement("svg", { width: "17px", height: "20px", viewBox: "0 0 17 20", version: "1.1" }, /* @__PURE__ */ React99.createElement(
    "g",
    {
      id: "Page-1",
      stroke: "none",
      strokeWidth: "1",
      fill: "none",
      fillRule: "evenodd",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    },
    /* @__PURE__ */ React99.createElement(
      "g",
      {
        id: "Fors\xED\xF0a-(Netbankar)-selected",
        transform: "translate(-1276.000000, -80.000000)",
        stroke: "#163E63"
      },
      /* @__PURE__ */ React99.createElement("g", { id: "Group", transform: "translate(1272.000000, 78.000000)" }, /* @__PURE__ */ React99.createElement("g", { id: "Group-5", transform: "translate(4.000000, 3.000000)" }, /* @__PURE__ */ React99.createElement(
        "path",
        {
          d: "M12.8335944,4.60130719 C12.8335944,7.14231215 10.7732921,9.20261438 8.23228718,9.20261438 C5.69128222,9.20261438 3.63097999,7.14231215 3.63097999,4.60130719 C3.63097999,2.06030223 5.69128222,0 8.23228718,0 C10.7732921,0 12.8335944,2.06030223 12.8335944,4.60130719 Z",
          id: "Stroke-1"
        }
      ), /* @__PURE__ */ React99.createElement(
        "path",
        {
          d: "M0.619530618,18.869306 C0.619530618,14.6635342 -0.818676383,10.0117896 8.23327823,10.0117896 C17.2852328,10.0117896 15.845787,14.6635342 15.845787,18.869306",
          id: "Stroke-3"
        }
      )))
    )
  ));
}

// src/components/icons/PlayStore.tsx
import React100 from "react";
var PlayStore = () => {
  return /* @__PURE__ */ React100.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 5435.8 1604",
      width: "135",
      height: "40"
    },
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fillRule: "evenodd",
        d: "M5234.4 1604h-5033C90.4 1604 0 1513.6 0 1403.5v-1203C0 90 90.4 0 201.4 0h5033c110.9 0 201.4 90 201.4 200.5v1203c0 110.1-90.5 200.5-201.4 200.5z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fill: "#a6a6a6",
        fillRule: "evenodd",
        d: "M5234.4 32.1c93.1 0 169.3 75.7 169.3 168.4v1203c0 92.7-75.7 168.4-169.3 168.4h-5033c-93.1 0-169.3-75.7-169.3-168.4v-1203c0-92.7 75.7-168.4 169.3-168.4h5033zm0-32.1h-5033C90.4 0 0 90.4 0 200.5v1203C0 1514 90.4 1604 201.4 1604h5033c110.9 0 201.4-90 201.4-200.5v-1203C5435.8 90.4 5345.3 0 5234.4 0z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fill: "#fff",
        d: "M2863.6 530.6c-36.3 0-66.9-12.7-91.1-37.7-24-24.3-37.4-57.8-36.8-92 0-36.5 12.4-67.4 36.8-91.9 24.1-25 54.7-37.7 91-37.7 35.9 0 66.5 12.7 91.1 37.7 24.4 25.3 36.8 56.2 36.8 91.9-.4 36.6-12.8 67.5-36.8 91.9-24.1 25.2-54.7 37.8-91 37.8zm-1080.1 0c-35.5 0-66.3-12.5-91.5-37.2-25-24.6-37.7-55.7-37.7-92.4s12.7-67.8 37.7-92.4c24.7-24.7 55.5-37.2 91.5-37.2 17.6 0 34.7 3.5 51.1 10.6 16.1 6.9 29.2 16.3 38.9 27.8l2.4 2.9-27.1 26.6-2.8-3.3c-15.3-18.2-35.8-27.1-62.9-27.1-24.2 0-45.3 8.6-62.7 25.6-17.5 17.1-26.4 39.5-26.4 66.6s8.9 49.5 26.4 66.6c17.4 17 38.5 25.6 62.7 25.6 25.8 0 47.5-8.6 64.4-25.6 10-10 16.2-24 18.4-41.7H1779v-37.4h124.2l.5 3.4c.9 6.3 1.8 12.8 1.8 18.8 0 34.5-10.4 62.4-31 83-23.4 24.4-54 36.8-91 36.8zm1436.1-5.3h-38.3L3064 337.6l1 33.8v153.8h-38.3V276.7h43.7l1.2 1.9 110.3 176.8-1-33.7v-145h38.7v248.6zm-643.8 0H2537V314.1h-67.3v-37.4H2643v37.4h-67.3v211.2zm-137.7 0h-38.7V276.7h38.7v248.6zm-217.5 0h-38.7V314.1h-67.3v-37.4h173.3v37.4h-67.3v211.2zm-130.5-.4h-148.4V276.7h148.4v37.4h-109.6v68.2h98.9v37h-98.9v68.2h109.6v37.4zm710.8-57.7c17.3 17.3 38.3 26 62.7 26 25.1 0 45.6-8.5 62.7-26 17-17 25.6-39.3 25.6-66.2s-8.6-49.3-25.5-66.2c-17.3-17.3-38.4-26-62.7-26-25.1 0-45.6 8.5-62.6 26-17 17-25.6 39.3-25.6 66.2s8.5 49.3 25.4 66.2z"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M2732.1 872.4c-94.5 0-171.1 71.7-171.1 170.6 0 98 77.1 170.6 171.1 170.6 94.5 0 171.1-72.2 171.1-170.6 0-98.9-76.6-170.6-171.1-170.6zm0 273.6c-51.7 0-96.2-42.8-96.2-103.4 0-61.5 44.6-103.4 96.2-103.4 51.7 0 96.2 41.9 96.2 103.4.1 61-44.5 103.4-96.2 103.4zm-373.3-273.6c-94.5 0-171.1 71.7-171.1 170.6 0 98 77.1 170.6 171.1 170.6 94.5 0 171.1-72.2 171.1-170.6 0-98.9-76.7-170.6-171.1-170.6zm0 273.6c-51.7 0-96.2-42.8-96.2-103.4 0-61.5 44.6-103.4 96.2-103.4 51.7 0 96.2 41.9 96.2 103.4 0 61-44.5 103.4-96.2 103.4zm-444.2-221.5v72.2h173.3c-5.3 40.5-18.7 70.4-39.2 90.9-25.4 25.4-64.6 53-133.7 53-106.5 0-189.8-86-189.8-192.5s83.3-192.5 189.8-192.5c57.5 0 99.4 22.7 130.5 51.7l51.2-51.2c-43.2-41.4-100.7-73.1-181.3-73.1-146.1 0-268.7 119-268.7 264.7 0 146.1 122.5 264.7 268.7 264.7 78.9 0 138.1-25.8 184.9-74.4 47.7-47.7 62.8-115 62.8-169.3 0-16.9-1.3-32.1-4-45h-244.6c.1-.1.1.8.1.8zm1816.9 56.2C3717.2 942.4 3674 872 3585.4 872c-87.8 0-160.8 69.1-160.8 170.6 0 95.8 72.2 170.6 169.3 170.6 78 0 123.4-47.7 142.1-75.7l-57.9-38.8c-19.2 28.5-45.9 47.2-83.8 47.2-38.3 0-65.1-17.4-82.9-51.7l228.1-94.5c0 .1-8-19-8-19zm-232.6 57c-1.8-65.9 51.2-99.4 89.1-99.4 29.9 0 54.8 14.7 63.3 36.1l-152.4 63.3zM3313.6 1203h74.9V701.8h-74.9V1203zm-123-292.7h-2.7c-16.9-20.1-49-38.3-90-38.3-85.1 0-163.5 74.9-163.5 171.1 0 95.8 78 169.8 163.5 169.8 40.5 0 73.1-18.3 90-38.8h2.7v24.5c0 65.1-34.8 100.2-90.9 100.2-45.9 0-74.4-33-86-60.6l-65.1 27.2c18.7 45 68.6 100.7 151 100.7 87.8 0 162.2-51.7 162.2-177.8V882.2H3191v28.1h-.4zm-86 235.7c-51.7 0-94.9-43.2-94.9-102.9 0-60.2 43.2-103.8 94.9-103.8 51.2 0 90.9 44.1 90.9 103.8.5 59.7-39.6 102.9-90.9 102.9zm977.6-444.2h-179.1V1203h74.9v-189.8h104.3c82.9 0 164.4-60.1 164.4-155.5s-81.2-155.9-164.5-155.9zm2.2 241.4h-106.5v-172h106.5c56.1 0 87.8 46.3 87.8 86 0 39.3-32.1 86-87.8 86zm462.5-71.7c-54.4 0-110.5 24.1-133.7 76.6l66.4 27.6c14.3-27.6 40.5-37 68.2-37 38.8 0 78 23.2 78.9 64.6v5.3c-13.4-7.6-42.8-19.2-78-19.2-71.7 0-144.4 39.2-144.4 112.7 0 67.3 58.8 110.5 124.3 110.5 50.3 0 78-22.7 95.3-49h2.7v38.8h72.2v-192c0-89.4-66.4-138.9-151.9-138.9zm-9.4 274.5c-24.5 0-58.8-12-58.8-42.8 0-38.8 42.8-53.5 79.3-53.5 33 0 48.6 7.1 68.2 16.9-5.4 45-43.4 79-88.7 79.4zm424.7-263.8l-86 217.4h-2.7l-89.1-217.4h-80.6l133.7 303.9-76.2 168.9h78L5045 882.2h-82.8zM4288 1203h74.9V701.8H4288V1203z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "linearGradient",
      {
        id: "a",
        x1: "1682.108",
        x2: "1624.292",
        y1: "1339.478",
        y2: "1309.634",
        gradientTransform: "matrix(11.64 0 0 -22.55 -18705.596 30554.37)",
        gradientUnits: "userSpaceOnUse"
      },
      /* @__PURE__ */ React100.createElement("stop", { offset: "0", stopColor: "#00a0ff" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.007", stopColor: "#00a1ff" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.26", stopColor: "#00beff" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.512", stopColor: "#00d2ff" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.76", stopColor: "#00dfff" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "1", stopColor: "#00e3ff" })
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fill: "url(#a)",
        fillRule: "evenodd",
        d: "M418.4 302.1c-11.6 12.5-18.3 31.6-18.3 56.6v886.7c0 25 6.7 44.1 18.7 56.1l3.1 2.7 496.8-496.8v-11.1L421.5 299.4l-3.1 2.7z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "linearGradient",
      {
        id: "b",
        x1: "1712.662",
        x2: "1606.561",
        y1: "1274.838",
        y2: "1274.838",
        gradientTransform: "matrix(9.145 0 0 -7.7 -14305.538 10618.251)",
        gradientUnits: "userSpaceOnUse"
      },
      /* @__PURE__ */ React100.createElement("stop", { offset: "0", stopColor: "#ffe000" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.409", stopColor: "#ffbd00" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.775", stopColor: "orange" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "1", stopColor: "#ff9c00" })
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fill: "url(#b)",
        fillRule: "evenodd",
        d: "M1084 973.5L918.3 807.8v-11.6L1084 630.5l3.6 2.2 196 111.4c56.1 31.6 56.1 83.8 0 115.8l-196 111.4-3.6 2.2z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "linearGradient",
      {
        id: "c",
        x1: "1707.441",
        x2: "1646.682",
        y1: "1290.047",
        y2: "1211.223",
        gradientTransform: "matrix(15.02 0 0 -11.5775 -24650.229 15829.648)",
        gradientUnits: "userSpaceOnUse"
      },
      /* @__PURE__ */ React100.createElement("stop", { offset: "0", stopColor: "#ff3a44" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "1", stopColor: "#c31162" })
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fill: "url(#c)",
        fillRule: "evenodd",
        d: "M1087.6 971.3L918.3 802l-499.9 499.9c18.3 19.6 49 21.8 83.3 2.7l585.9-333.3",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "linearGradient",
      {
        id: "d",
        x1: "1660.636",
        x2: "1687.767",
        y1: "1365.668",
        y2: "1330.45",
        gradientTransform: "matrix(15.02 0 0 -11.5715 -24650.229 15809.992)",
        gradientUnits: "userSpaceOnUse"
      },
      /* @__PURE__ */ React100.createElement("stop", { offset: "0", stopColor: "#32a071" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.069", stopColor: "#2da771" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.476", stopColor: "#15cf74" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "0.801", stopColor: "#06e775" }),
      /* @__PURE__ */ React100.createElement("stop", { offset: "1", stopColor: "#00f076" })
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fill: "url(#d)",
        fillRule: "evenodd",
        d: "M1087.6 632.7L501.7 299.9c-34.3-19.6-65.1-16.9-83.3 2.7L918.3 802l169.3-169.3z",
        clipRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fillRule: "evenodd",
        d: "M1084 967.7l-581.9 330.6c-32.5 18.7-61.5 17.4-80.2.4l-3.1 3.1 3.1 2.7c18.7 16.9 47.7 18.3 80.2-.4L1088 971.3l-4-3.6z",
        clipRule: "evenodd",
        opacity: "0.2"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fillRule: "evenodd",
        d: "M1283.6 854.1l-200.1 113.6 3.6 3.6 196-111.4c28.1-16 41.9-37 41.9-57.9-1.7 19.2-16 37.4-41.4 52.1z",
        clipRule: "evenodd",
        opacity: "0.12"
      }
    ),
    /* @__PURE__ */ React100.createElement(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M501.7 305.7l781.9 444.2c25.4 14.3 39.7 33 41.9 52.1 0-20.9-13.8-41.9-41.9-57.9L501.7 299.9c-56.1-32.1-101.6-5.3-101.6 58.8v5.8c0-64.2 45.5-90.5 101.6-58.8z",
        clipRule: "evenodd",
        opacity: "0.25"
      }
    )
  );
};

// src/components/icons/Question.tsx
import React101 from "react";
var Question = () => {
  return /* @__PURE__ */ React101.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "25", height: "24" }, /* @__PURE__ */ React101.createElement("g", { fill: "none", fillRule: "evenodd", transform: "translate(.2)" }, /* @__PURE__ */ React101.createElement(
    "path",
    {
      fill: "#1E3C5A",
      d: "M10.71 13.63v-.477c0-.547.1-1.003.3-1.366.198-.363.546-.738 1.044-1.126.592-.468.974-.831 1.145-1.09.172-.259.258-.567.258-.925 0-.418-.14-.739-.418-.963-.279-.224-.68-.335-1.202-.335-.472 0-.91.067-1.313.201a9.323 9.323 0 00-1.179.485l-.627-1.313a6.569 6.569 0 013.246-.843c.975 0 1.749.238 2.32.716.573.477.859 1.137.859 1.977 0 .373-.055.705-.164.996a2.87 2.87 0 01-.496.832c-.222.264-.604.607-1.146 1.03-.462.363-.772.664-.929.903-.157.239-.235.56-.235.963v.335h-1.462zm-.305 2.38c0-.75.365-1.126 1.097-1.126.358 0 .631.098.82.295.19.196.284.473.284.832 0 .353-.096.633-.287.839-.192.206-.464.31-.817.31-.354 0-.625-.101-.814-.303-.189-.201-.283-.483-.283-.846z"
    }
  ), /* @__PURE__ */ React101.createElement(
    "rect",
    {
      width: "22.51",
      height: "22.51",
      x: "0.5",
      y: "0.5",
      stroke: "#1E3C5A",
      rx: "11.255"
    }
  )));
};

// src/components/icons/ScrollDownArrow.tsx
import React102 from "react";
var ScrollDownArrow = () => {
  return /* @__PURE__ */ React102.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "77",
      height: "46",
      fill: "none",
      viewBox: "0 0 77 46"
    },
    /* @__PURE__ */ React102.createElement("path", { stroke: "#fff", strokeWidth: "2", d: "M75.48 1L38.24 43.56 1 1" })
  );
};

// src/components/icons/Search.tsx
import React103 from "react";
var SearchIcon = (props) => /* @__PURE__ */ React103.createElement(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: props.width ? props.width : 26,
    height: props.height ? props.height : 26,
    fill: "none",
    viewBox: "0 0 26 26"
  },
  /* @__PURE__ */ React103.createElement(
    "path",
    {
      stroke: props.stroke ? props.stroke : "#fff",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M1 11.5C1 17.299 5.701 22 11.5 22S22 17.299 22 11.5 17.299 1 11.5 1 1 5.701 1 11.5z",
      clipRule: "evenodd"
    }
  ),
  /* @__PURE__ */ React103.createElement(
    "path",
    {
      stroke: props.stroke ? props.stroke : "#fff",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      d: "M25 25l-6-6"
    }
  )
);

// src/components/icons/Share.tsx
import React104 from "react";
var Share = () => {
  return /* @__PURE__ */ React104.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", width: "23", height: "24" }, /* @__PURE__ */ React104.createElement("g", { fill: "none", fillRule: "evenodd", stroke: "#1E3C5A" }, /* @__PURE__ */ React104.createElement("circle", { cx: "5.878", cy: "12.245", r: "5.378" }), /* @__PURE__ */ React104.createElement("circle", { cx: "19.102", cy: "3.429", r: "2.929" }), /* @__PURE__ */ React104.createElement("circle", { cx: "19.102", cy: "20.082", r: "2.929" }), /* @__PURE__ */ React104.createElement("path", { d: "M10.776 15.184l5.877 3.918m-5.877-9.796l5.877-3.918" })));
};

// src/components/icons/Shield.tsx
import React105 from "react";
var Shield = () => /* @__PURE__ */ React105.createElement(
  "svg",
  {
    width: "40",
    height: "40",
    viewBox: "0 0 40 40",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  },
  /* @__PURE__ */ React105.createElement("g", { opacity: "0.85" }, /* @__PURE__ */ React105.createElement(
    "path",
    {
      d: "M35.312 8.07283L35.2 7.61361L20.016 3.06888L4.832 7.61361L4.704 8.07283C4.24 9.83055 4 11.6516 4 13.5043C4 32.285 18.992 36.9722 20 37.2731C21.008 36.9722 36 32.285 36 13.5043C36 11.6516 35.76 9.83055 35.296 8.07283H35.312Z",
      stroke: "#1E3C5A",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ), /* @__PURE__ */ React105.createElement(
    "path",
    {
      d: "M20 27L20 16.5",
      stroke: "#163E63",
      strokeMiterlimit: "10",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ), /* @__PURE__ */ React105.createElement(
    "path",
    {
      d: "M20 13.528L20 13.472",
      stroke: "#1E3C5A",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ))
);

// src/components/icons/Skutla.tsx
import React106 from "react";
var Skutla = () => /* @__PURE__ */ React106.createElement(
  "svg",
  {
    width: "38",
    height: "28",
    viewBox: "0 0 39 28",
    xmlns: "http://www.w3.org/2000/svg"
  },
  /* @__PURE__ */ React106.createElement(
    "g",
    {
      fill: "none",
      fillRule: "evenodd",
      stroke: "#1E3C5A",
      strokeLinecap: "inherit",
      strokeLinejoin: "inherit",
      transform: "translate(1)"
    },
    /* @__PURE__ */ React106.createElement("polyline", { points: "22.61 16.399 23.297 17.121 23.87 17.773 30.877 25.735 37 .884 11.429 12.049 18.992 14.57 19.352 20.333 28.356 9.168 18.992 14.57" }),
    /* @__PURE__ */ React106.createElement(
      "path",
      {
        d: "M0.4019,0.2197 C0.4329,1.1997 0.4809,1.7177 0.6999,2.6507 L0.4019,0.2197 Z M2.355,6.2588 C2.943,7.1928 3.79,7.9458 4.684,8.6588 L2.355,6.2588 Z M7.9312,10.2432 C9.0602,10.5492 10.1352,10.6592 11.3512,10.6662 L7.9312,10.2432 Z M14.8018,9.8901 C15.9628,9.4111 16.8258,8.7921 17.7358,8.1101 L14.8018,9.8901 Z",
        transform: "translate(0 16)"
      }
    )
  )
);

// src/components/icons/SkutlaLarge.tsx
import React107 from "react";
var SkutlaLarge = () => /* @__PURE__ */ React107.createElement(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "69",
    height: "50",
    opacity: "0.85",
    viewBox: "0 0 39 28"
  },
  /* @__PURE__ */ React107.createElement(
    "g",
    {
      fill: "none",
      fillRule: "evenodd",
      stroke: "#1E3C5A",
      strokeLinecap: "inherit",
      strokeLinejoin: "inherit",
      transform: "translate(1)"
    },
    /* @__PURE__ */ React107.createElement("path", { d: "M22.61 16.399L23.297 17.121 23.87 17.773 30.877 25.735 37 0.884 11.429 12.049 18.992 14.57 19.352 20.333 28.356 9.168 18.992 14.57" }),
    /* @__PURE__ */ React107.createElement("path", { d: "M.402 16.22c.03.98.079 1.498.298 2.43l-.298-2.43zm1.953 6.039c.588.934 1.435 1.687 2.329 2.4l-2.329-2.4zm5.576 3.984c1.13.306 2.204.416 3.42.423l-3.42-.423zm6.87-.353c1.162-.479 2.025-1.098 2.935-1.78l-2.934 1.78z" })
  )
);

// src/components/icons/SmileIcon.tsx
import React108 from "react";
var SmileIcon = () => /* @__PURE__ */ React108.createElement("svg", { width: "119", height: "127", xmlns: "http://www.w3.org/2000/svg" }, /* @__PURE__ */ React108.createElement("g", { fill: "none", fillRule: "evenodd", opacity: ".15" }, /* @__PURE__ */ React108.createElement("path", { d: "M-15-7h144v144H-15z" }), /* @__PURE__ */ React108.createElement(
  "path",
  {
    d: "M86.387 78.533c-7.15 7.15-16.545 10.728-25.941 10.728-9.393 0-18.789-3.578-25.942-10.728a1.801 1.801 0 0 1 0-2.545 1.801 1.801 0 0 1 2.545 0c12.903 12.9 33.89 12.9 46.793 0a1.801 1.801 0 0 1 2.545 0 1.801 1.801 0 0 1 0 2.545M59.585.896C27.265.896.966 26.697.966 58.416c0 19.073 9.5 36.685 25.496 47.441l-10.31 17.658a1.766 1.766 0 0 0 .003 1.793c.331.551.929.893 1.584.893 90.67 0 100.461-47.405 100.461-67.784C118.2 26.697 91.902.896 59.585.896",
    fill: "#1E3C5A"
  }
)));

// src/components/icons/SuccessCheck.tsx
import React109 from "react";
function SuccessCheck() {
  return /* @__PURE__ */ React109.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 34 34",
      width: "32px",
      height: "32px",
      version: "1.1"
    },
    /* @__PURE__ */ React109.createElement("title", null, "Group"),
    /* @__PURE__ */ React109.createElement(
      "g",
      {
        id: "Proccess-ver\xF0br\xE9f",
        fill: "none",
        fillRule: "evenodd",
        stroke: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "1"
      },
      /* @__PURE__ */ React109.createElement(
        "g",
        {
          id: "Draga-til-baka-2",
          stroke: "#ffffff",
          transform: "translate(-152 -131)"
        },
        /* @__PURE__ */ React109.createElement("g", { id: "Group", transform: "translate(153 132)" }, /* @__PURE__ */ React109.createElement(
          "path",
          {
            id: "Path",
            d: "M 32 16 C 32 24.8366 24.8366 32 16 32 C 7.16344 32 0 24.8366 0 16 C 0 7.16344 7.16344 0 16 0"
          }
        ), /* @__PURE__ */ React109.createElement(
          "path",
          {
            id: "Path",
            d: "M 9.29032 14.379 L 14.546 18.2613 C 15.2369 18.7714 16.2123 18.659 16.7651 18.0057 L 32 0"
          }
        ))
      )
    )
  );
}

// src/components/icons/SwitchIcon.tsx
import React110 from "react";
function SwitchIcon(props) {
  return /* @__PURE__ */ React110.createElement("svg", { width: 14, height: 12, viewBox: "0 0 14 12", ...props }, /* @__PURE__ */ React110.createElement("defs", null, /* @__PURE__ */ React110.createElement("filter", { id: "prefix__a" }, /* @__PURE__ */ React110.createElement(
    "feColorMatrix",
    {
      in: "SourceGraphic",
      values: "0 0 0 0 0.117647 0 0 0 0 0.239216 0 0 0 0 0.352941 0 0 0 1.000000 0"
    }
  ))), /* @__PURE__ */ React110.createElement(
    "g",
    {
      transform: "translate(1 1)",
      filter: "url(#prefix__a)",
      fill: "none",
      fillRule: "evenodd",
      strokeLinejoin: "round"
    },
    /* @__PURE__ */ React110.createElement("g", { stroke: "#FFF" }, /* @__PURE__ */ React110.createElement("path", { d: "M7.834 0l2.304 1.94-2.304 1.94" }), /* @__PURE__ */ React110.createElement("path", { d: "M0 4.268V2.716c0-.428.413-.776.922-.776h9.216M3.686 10.09L1.382 8.148l2.304-1.94" }), /* @__PURE__ */ React110.createElement("path", { d: "M11.52 5.82v1.553c0 .428-.413.776-.922.776H1.382" }))
  ));
}

// src/components/icons/arrows/Arrow.tsx
import styled25 from "@emotion/styled";
import React111 from "react";
var Host4 = styled25.svg`
  stroke: currentColor;
  fill-rule: evenodd;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
  height: 19px;
  width: 17px;
`;
function Arrow({ className }) {
  return /* @__PURE__ */ React111.createElement(Host4, { viewBox: "0 0 15 18", className }, /* @__PURE__ */ React111.createElement("path", { d: "m1 9 7.78 7.78M1.53 9H14.5M8.78 1.22 1 9" }));
}

// src/components/icons/arrows/ArrowDown.tsx
import React112 from "react";
function ArrowDown(props) {
  return /* @__PURE__ */ React112.createElement(
    "svg",
    {
      width: 10,
      height: 11,
      viewBox: "0 0 10 11",
      xmlns: "http://www.w3.org/2000/svg",
      ...props
    },
    /* @__PURE__ */ React112.createElement("title", null, "Arrow down"),
    /* @__PURE__ */ React112.createElement(
      "g",
      {
        stroke: "red",
        strokeWidth: 1.152,
        fill: "none",
        fillRule: "evenodd",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      },
      /* @__PURE__ */ React112.createElement("path", { d: "M5.032.576v9.792M9.064 6.336l-4.032 4.032L1 6.336" })
    )
  );
}

// src/components/icons/arrows/ArrowUp.tsx
import React113 from "react";
function ArrowUp(props) {
  return /* @__PURE__ */ React113.createElement(
    "svg",
    {
      width: 10,
      height: 11,
      viewBox: "0 0 10 11",
      xmlns: "http://www.w3.org/2000/svg",
      ...props
    },
    /* @__PURE__ */ React113.createElement("title", null, "Arrow upp"),
    /* @__PURE__ */ React113.createElement(
      "g",
      {
        stroke: "#00A651",
        strokeWidth: 1.152,
        fill: "none",
        fillRule: "evenodd",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      },
      /* @__PURE__ */ React113.createElement("path", { d: "M4.968 10.424V.632M.936 4.664L4.968.632 9 4.664" })
    )
  );
}

// src/components/icons/arrows/SimpleArrow.tsx
import React114 from "react";
function SimpleArrow(props) {
  return /* @__PURE__ */ React114.createElement(
    "svg",
    {
      width: props.width ?? "16",
      height: props.height ?? "9",
      preserveAspectRatio: props.height || props.width ? "none" : "xMidYMid",
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 16 9"
    },
    /* @__PURE__ */ React114.createElement("g", { fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React114.createElement(
      "path",
      {
        d: "M6.841-.012l9 4.5-9 4.5V4.987H.207v-1h6.634v-4z",
        fill: "currentColor",
        fillRule: "nonzero"
      }
    ))
  );
}

// src/components/icons/flags/Flags.tsx
import React140 from "react";

// src/components/icons/flags/Aud.tsx
import React115 from "react";
function Aud(props) {
  return /* @__PURE__ */ React115.createElement(
    "svg",
    {
      "data-name": "b15c62ea-6519-4572-abc7-78331ce2f6ea",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React115.createElement("defs", null, /* @__PURE__ */ React115.createElement(
      "linearGradient",
      {
        id: "prefix__a",
        y1: 60.392,
        x2: 181.187,
        y2: 60.392,
        gradientUnits: "userSpaceOnUse"
      },
      /* @__PURE__ */ React115.createElement("stop", { offset: 0, stopColor: "#f6f4f4" }),
      /* @__PURE__ */ React115.createElement("stop", { offset: 0.45, stopColor: "#f6f4f4" }),
      /* @__PURE__ */ React115.createElement("stop", { offset: 0.8, stopColor: "#f6f4f4" }),
      /* @__PURE__ */ React115.createElement("stop", { offset: 1, stopColor: "#f6f4f4" })
    ), /* @__PURE__ */ React115.createElement(
      "linearGradient",
      {
        id: "prefix__b",
        y1: 42.126,
        x2: 110.7,
        y2: 42.126,
        xlinkHref: "#prefix__a"
      }
    )),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M40.112 84.252H1v26.074l39.112-26.074M1 16.332v26.074h39.112L1 16.332M79.309 1h-54.3l54.3 36.2V1M165 1h-54.3v36.2L165 1m214 0H190v14.666l-40.112 26.74H190v41.846h-40.112L190 110.993v15.665h-23.5l-55.8-37.2v37.2H79.309v-37.2l-55.8 37.2H1V283h378V1",
        fill: "#1966ca"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M54.8 84.252H40.112L1 110.326v9.792l53.8-35.866m24.506 0h-6.88L8.814 126.658h14.692l55.8-37.2v-5.206m38.262 0H110.7v5.209l55.8 37.2h14.688l-63.617-42.409m32.317 0H135.2l54.8 36.533v-9.792l-40.112-26.741M1 6.54v9.792l39.112 26.074H54.8L1 6.54m189-.666l-54.8 36.532h14.688L190 15.666V5.874M25.006 1H10.314l62.111 41.406h6.884V37.2L25.006 1m154.679 0H165l-54.3 36.2v5.206h6.879L179.685 1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M72.429 84.252H54.8L1 120.118v6.54h7.814l63.615-42.406m62.771 0h-17.629l63.615 42.406H190v-5.873l-54.8-36.533M10.314 1H1v5.54l53.8 35.866h17.625L10.314 1M190 1h-10.315l-62.11 41.406H135.2L190 5.874V1",
        fill: "#de0000"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M190 76.389h-85.2v50.269H85.2V76.389H1v7.863h78.309v42.406H110.7V84.252H190v-7.863M85.2 1h-5.891v41.406H1v7.863h84.2V1m25.5 0h-5.9v49.269H190v-7.863h-79.3V1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        fill: "#de0000",
        d: "M104.801 1H85.204v49.269H1v26.12h84.204v50.269h19.597V76.389H190v-26.12h-85.199V1"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M285 165.529l2.618 8.134 7.99-3.024-4.73 7.117 7.352 4.363-8.513.742 1.17 8.465L285 185.13l-5.891 6.196 1.174-8.465-8.513-.742 7.348-4.363-4.73-7.117 7.994 3.024 2.618-8.134zM237.502 90.322l2.614 8.138 7.994-3.028-4.73 7.121 7.352 4.36-8.517.741 1.174 8.466-5.887-6.192-5.891 6.192 1.174-8.466-8.514-.741 7.349-4.36-4.73-7.121 7.994 3.028 2.618-8.138zM285 38.867l2.618 8.138 7.99-3.028-4.73 7.121 7.352 4.359-8.513.742 1.17 8.465L285 58.468l-5.891 6.196 1.174-8.465-8.513-.742 7.348-4.359-4.73-7.121 7.994 3.028L285 38.867zM327.218 77.655l2.622 8.138 7.994-3.028-4.73 7.121 7.348 4.364-8.513.737 1.174 8.465-5.895-6.192-5.887 6.192 1.174-8.465-8.518-.737 7.353-4.364-4.731-7.121 7.995 3.028 2.614-8.138zM303.999 115.766l2.068 5.071 5.459.401-4.18 3.535 1.304 5.315-4.651-2.885-4.651 2.885 1.304-5.315-4.184-3.535 5.463-.401 2.068-5.071zM95 159.926l5.498 17.088 16.787-6.358-9.936 14.949 15.438 9.16-17.882 1.558 2.461 17.778L95 201.093l-12.362 13.008 2.461-17.778-17.887-1.558 15.439-9.16-9.932-14.949 16.787 6.358L95 159.926z"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M1 84.252H0v26.741l1-.667V84.252M0 15.666v26.74h1V16.332l-1-.666M79.309 0h-55.8l1.5 1h54.3V0M166.5 0h-55.8v1H165l1.5-1M380 0H190v1h189v282H1V126.658H0V284h380V0",
        fill: "#175ebd"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M1 110.326l-1 .667v9.792l1-.667v-9.792M0 5.874v9.792l1 .666V6.54l-1-.666M23.506 0H8.814l1.5 1h14.692l-1.5-1m157.68 0H166.5L165 1h14.687l1.5-1",
        fill: "url(#prefix__a)"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M1 120.118l-1 .667v5.873h1v-6.54M8.814 0H0v5.874l1 .666V1h9.314l-1.5-1M190 0h-8.814l-1.5 1H190V0",
        fill: "#ca0000"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M1 76.389H0v7.863h1v-7.863m0-33.983H0v7.863h1v-7.863M85.2 0h-5.891v1H85.2V0m25.5 0h-5.9v1h5.9V0",
        fill: "url(#prefix__b)"
      }
    ),
    /* @__PURE__ */ React115.createElement(
      "path",
      {
        d: "M1 50.269H0v26.12h1v-26.12M104.8 0H85.2v1h19.6V0",
        fill: "#ca0000"
      }
    )
  );
}

// src/components/icons/flags/Cad.tsx
import React116 from "react";
function Cad(props) {
  return /* @__PURE__ */ React116.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React116.createElement("path", { fill: "#fff", d: "M285.149 1H94.855v282h190.294V1" }),
    /* @__PURE__ */ React116.createElement(
      "path",
      {
        fill: "#de0000",
        d: "M94.855 1H1v282h93.855V1M379 1h-93.851v282H379V1"
      }
    ),
    /* @__PURE__ */ React116.createElement(
      "path",
      {
        d: "M115.731 143.49l33.793 29.285c6.2 5.372 1.392 10.858-1.58 16.05l38.777-4.773-1.192 38.78h8.353l-1.794-38.78 38.776 4.773c-2.784-5.563-6.135-12.332-.3-17.276l33.113-28.059c-3.173-1.274-6.908-1.732-7.655-5.306-1.269-6.087 3.469-18.646 5.822-25.4-6.7 2.71-15.479 4.8-21.169 4.22-3.115-.319-4.892-8.344-6.193-11.1l-17.432 18.459c-4.644 4.909-6.406 2.631-5.1-3.661l8.173-39.252-11.673 6.593c-3.434 1.947-4.564 2.152-6.5-1.824L189.7 61.168l-12.571 25.144c-1.946 3.893-2.138 3.984-5.808 1.972l-11.442-6.244 7.008 38.829c1.113 6.135-.061 8.369-4.922 3.032l-16.407-18c-1.589 4.22-3.579 9.544-6.559 11.333-3.579 1.191-13.725-2.182-20.881-4.172 2.313 6.668 7.75 18.153 6.781 24.4-.624 4.056-5.499 4.614-9.168 6.028z",
        fill: "#de0000",
        fillRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React116.createElement(
      "path",
      {
        d: "M285.149 283H94.855v1h190.294v-1m0-283H94.855v1h190.294V0",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React116.createElement(
      "path",
      {
        fill: "#ca0000",
        d: "M94.855 0H0v284h94.855v-1H1V1h93.855V0M380 0h-94.851v1H379v282h-93.851v1H380V0"
      }
    )
  );
}

// src/components/icons/flags/Chf.tsx
import React117 from "react";
function Chf(props) {
  return /* @__PURE__ */ React117.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React117.createElement("path", { fill: "#de0000", d: "M379 1H1v282h378V1" }),
    /* @__PURE__ */ React117.createElement(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M101.248 168.625h62.129v62.125h53.251v-62.125h62.124v-53.25h-62.124V53.25h-53.251v62.125h-62.129v53.25z"
      }
    ),
    /* @__PURE__ */ React117.createElement("path", { d: "M1 283V1h378v282H1M380 0H0v284h380V0", fill: "#ca0000" })
  );
}

// src/components/icons/flags/Cny.tsx
import React118 from "react";
function Cny(props) {
  return /* @__PURE__ */ React118.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React118.createElement("path", { fill: "#de0000", d: "M379 1H1v282h378V1" }),
    /* @__PURE__ */ React118.createElement(
      "path",
      {
        d: "M71.029 36.143l10.083 31.248 32.838-.061-26.6 19.248 10.206 31.207-26.527-19.35-26.529 19.35 10.2-31.207L28.108 67.33l32.833.061zm89.028 103.586l-10.45 3.539.179 11.03-6.6-8.844-10.429 3.573 6.372-9-6.627-8.818 10.532 3.282 6.341-9.027.133 11.031zm29.015-34.029l-9.053 6.311 3.242 10.541-8.793-6.657-9.023 6.341 3.615-10.424-8.824-6.622 11.031.219 3.573-10.439 3.2 10.557zm-2.6-49.048l-6.372 9.011 6.632 8.814L176.2 71.2l-6.336 9.032-.138-11.03-10.547-3.237 10.445-3.538-.182-11.027 6.6 8.844zM150.158 21.9l-.148 11.031 10.455 3.507-10.531 3.262-.107 11.031-6.362-9.012-10.527 3.313 6.607-8.839-6.4-8.982 10.444 3.548z",
        fill: "#ffd500",
        fillRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React118.createElement("path", { d: "M1 283V1h378v282H1M380 0H0v284h380V0", fill: "#ca0000" })
  );
}

// src/components/icons/flags/Czk.tsx
import React119 from "react";
function Czk(props) {
  return /* @__PURE__ */ React119.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React119.createElement(
      "polyline",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#DE0000",
        points: "0,284 380,284 380,142 0,142 0,284"
      }
    ),
    /* @__PURE__ */ React119.createElement(
      "polyline",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#FFFFFF",
        points: "0,142 380,142 380,0 0,0 0,142"
      }
    ),
    /* @__PURE__ */ React119.createElement(
      "polyline",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#1966CA",
        points: "213,142 0,0 0,284 213,142"
      }
    ),
    /* @__PURE__ */ React119.createElement(
      "polyline",
      {
        fill: "#CA0000",
        points: "380,142 379,142 379,283 1.5,283 0,284 380,284 380,142"
      }
    ),
    /* @__PURE__ */ React119.createElement(
      "polyline",
      {
        fill: "#E8EBEE",
        points: "380,0 0,0 1.5,1 379,1 379,142 380,142 380,0"
      }
    ),
    /* @__PURE__ */ React119.createElement("polyline", { fill: "#175EBD", points: "0,0 0,284 1.5,283 1,283 1,1 1.5,1 0,0" })
  );
}

// src/components/icons/flags/default.tsx
import React120 from "react";
function DefaultFlag(props) {
  return /* @__PURE__ */ React120.createElement("svg", { viewBox: "0 0 380 284", height: 50, width: 67, ...props }, /* @__PURE__ */ React120.createElement("switch", null, /* @__PURE__ */ React120.createElement("g", null, /* @__PURE__ */ React120.createElement("path", { fill: "#1966ca", d: "M379.5.6H.5v282.8h379V.6" }), /* @__PURE__ */ React120.createElement("path", { d: "M1 283V1h378v282H1M380 0H0v284h380V0", fill: "#175ebd" }))));
}

// src/components/icons/flags/Dkk.tsx
import React121 from "react";
function Dkk(props) {
  return /* @__PURE__ */ React121.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React121.createElement(
      "path",
      {
        d: "M132.806 168.271H1V283h131.806V168.271m246.194 0H185.918V283H379V168.271M132.806 1H1v114.025h131.806V1M379 1H185.918v114.025H379V1",
        fill: "#de0000"
      }
    ),
    /* @__PURE__ */ React121.createElement(
      "path",
      {
        fill: "#fff",
        d: "M185.918 1h-53.112v114.025H1v53.246h131.806V283h53.112V168.271H379v-53.246H185.918V1"
      }
    ),
    /* @__PURE__ */ React121.createElement(
      "path",
      {
        d: "M1 168.271H0V284h132.806v-1H1V168.271m379 0h-1V283H185.918v1H380V168.271M132.806 0H0v115.025h1V1h131.806V0M380 0H185.918v1H379v114.025h1V0",
        fill: "#ca0000"
      }
    ),
    /* @__PURE__ */ React121.createElement(
      "path",
      {
        d: "M185.918 283h-53.112v1h53.112v-1M1 115.025H0v53.246h1v-53.246m379 0h-1v53.246h1v-53.246M185.918 0h-53.112v1h53.112V0",
        fill: "#e8ebee"
      }
    )
  );
}

// src/components/icons/flags/Eur.tsx
import React122 from "react";
function Eur(props) {
  return /* @__PURE__ */ React122.createElement("svg", { height: 50, width: 67, viewBox: "0 0 380 284", ...props }, /* @__PURE__ */ React122.createElement("switch", null, /* @__PURE__ */ React122.createElement("g", null, /* @__PURE__ */ React122.createElement("path", { fill: "#1966ca", d: "M379.5.6H.5v282.8h379V.6" }), /* @__PURE__ */ React122.createElement(
    "path",
    {
      d: "M139.2 37.7l4.2 11.8 12.5.4-9.9 7.7 3.5 12-10.4-7-10.3 7 3.5-12-9.9-7.7 12.5-.4 4.3-11.8zM240 214.2l4.2 11.8 12.5.4-9.9 7.7 3.5 12L240 239l-10.3 7 3.5-12-9.9-7.7 12.5-.4 4.2-11.7zm-100.8 0l4.2 11.8 12.5.4-9.9 7.6 3.5 12-10.4-7-10.3 7 3.5-12-9.9-7.7 12.5-.4 4.3-11.7zM240 37.7l4.2 11.8 12.5.4-9.9 7.7 3.5 12-10.3-7-10.3 7 3.5-12-9.9-7.7 12.5-.4 4.2-11.8zm37.4 139.5l4.2 11.8 12.5.4-9.9 7.7 3.5 12-10.3-7-10.4 7 3.5-12-9.9-7.7 12.5-.4 4.3-11.8zm0-102.1l4.2 11.8 12.5.4-9.9 7.7 3.5 12-10.3-7-10.4 7 3.5-12-9.9-7.7 12.5-.4 4.3-11.8zM102.2 177.2l4.2 11.8 12.5.4-9.9 7.6 3.5 12-10.3-7-10.4 7 3.5-12-9.9-7.7L98 189l4.2-11.8zm189.3-50.6l4.2 11.8 12.5.4-9.9 7.7 3.5 12-10.3-7-10.4 7 3.5-12-9.9-7.7 12.5-.4 4.3-11.8zm-203 0l4.2 11.8 12.5.4-9.9 7.7 3.5 12-10.3-7-10.4 7 3.5-12-9.9-7.7 12.5-.4 4.3-11.8zm13.7-51.5l4.2 11.8 12.5.4L109 95l3.5 12-10.3-7-10.4 7 3.5-12-9.9-7.7 12.6-.4 4.2-11.8zm87.4 153l4.2 11.8 12.5.4-9.9 7.7 3.5 12-10.4-7-10.3 7 3.5-12-9.9-7.7 12.5-.4 4.3-11.8zm0-204l4.2 11.8 12.5.4-9.9 7.7 3.5 12-10.4-7-10.3 7 3.5-12-9.9-7.7 12.5-.4 4.3-11.8z",
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#ffd500"
    }
  ), /* @__PURE__ */ React122.createElement("path", { d: "M1 283V1h378v282H1M380 0H0v284h380V0", fill: "#175ebd" }))));
}

// src/components/icons/flags/Gbp.tsx
import React123 from "react";
function Gbp(props) {
  return /* @__PURE__ */ React123.createElement(
    "svg",
    {
      height: 50,
      width: 67,
      "data-name": "Layer 2",
      viewBox: "0 0 380 284",
      ...props
    },
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M225.362 201.426V283h122.246l-122.246-81.574m-71.308 0L31.813 283h122.241v-81.575M65 189.591H1V232.3l64-42.706m314.005 0h-64.581L379 232.684v-43.093M1 51.706v42.708h64L1 51.706m378-.387l-64.586 43.1H379v-43.1M154.054 1H31.813l122.241 81.575V1m193.554 0H225.362v81.573L347.608 1",
        fill: "#1966ca"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M98.365 189.591H65L1 232.3v22.269l97.365-64.975m55.689 0h-15.643L1 281.293V283h30.813l122.241-81.575v-11.834m86.948 0h-15.64v11.835L347.608 283H379v-1.321l-138-92.088m73.422 0h-33.369L379 254.953v-22.269l-64.576-43.093M1 29.433v22.273l64 42.708h33.378L1 29.433m378-.387l-97.96 65.368h33.374L379 51.319V29.046M31.813 1H1v1.707l137.424 91.707h15.63V82.575L31.813 1M379 1h-31.392L225.362 82.573v11.841h15.627L379 2.321V1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M138.411 189.591H98.365L1 254.566v26.727l137.411-91.7m142.642 0H241l138 92.088v-26.728l-97.947-65.362M1 2.707v26.726l97.378 64.981h40.046L1 2.707m378-.386L240.989 94.414h40.051L379 29.046V2.321",
        fill: "#de0000"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M167.448 171.709H1v17.882h153.054V283h13.394V171.709m211.552 0H211.968V283h13.394v-93.409H379v-17.882M167.448 1h-13.394v93.414H1v17.877h166.448V1m57.914 0h-13.394v111.291H379V94.414H225.362V1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        fill: "#de0000",
        d: "M211.968 1h-44.52v111.291H1v59.418h166.448V283h44.52V171.709H379v-59.418H211.968V1"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M347.608 283H225.362v1h123.744l-1.5-1M380 189.591h-1v43.093l1 .668v-43.761M0 51.039v43.375h1V51.706l-1-.667m380-.388l-1 .668v43.1h1V50.651M154.054 0H30.314l1.5 1h122.24V0m195.052 0H225.362v1h122.246l1.5-1",
        fill: "#175ebd"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M379 281.679V283h-31.392l1.5 1H380v-1.653l-1-.668m0-48.995v22.269l1 .667v-22.268l-1-.668M0 28.766v22.273l1 .667V29.433l-1-.667m380-.387l-1 .667v22.273l1-.668V28.379M30.314 0H0v2.04l1 .667V1h30.813l-1.5-1M380 0h-30.894l-1.5 1H379v1.321l1-.668V0",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M379 254.953v26.726l1 .668V255.62l-1-.667M0 2.04v26.726l1 .667V2.707L0 2.04m380-.387l-1 .668v26.725l1-.667V1.653",
        fill: "#ca0000"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M225.362 283h-13.394v1h13.394v-1M380 171.709h-1v17.882h1v-17.882M1 94.414H0v17.877h1V94.414m379 0h-1v17.877h1V94.414M167.448 0h-13.394v1h13.394V0m57.914 0h-13.394v1h13.394V0",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M154.054 283H31.813l-1.5 1h123.74v-1M1 189.591H0v43.374l1-.668v-42.706",
        fill: "#175ebd"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M1 281.293l-1 .667V284h30.314l1.5-1H1v-1.707m0-49l-1 .668v22.268l1-.667V232.3",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React123.createElement("path", { fill: "#ca0000", d: "M1 254.566l-1 .667v26.727l1-.667v-26.727" }),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M167.448 283h-13.394v1h13.394v-1M1 171.709H0v17.882h1v-17.882",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React123.createElement(
      "path",
      {
        d: "M211.968 283h-44.52v1h44.52v-1M1 112.291H0v59.418h1v-59.418m379 0h-1v59.418h1v-59.418M211.968 0h-44.52v1h44.52V0",
        fill: "#ca0000"
      }
    )
  );
}

// src/components/icons/flags/Hkd.tsx
import React124 from "react";
function Hkd(props) {
  return /* @__PURE__ */ React124.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React124.createElement("path", { fill: "#de0000", d: "M379.5.627H.5v282.746h379V.627" }),
    /* @__PURE__ */ React124.createElement(
      "path",
      {
        d: "M181.294 142.116c-44.213-22.87-22.053-83.854 24.177-80.689-14.476 7.441-7.338 16.334-4.046 25.835 4.241 12.239-3.64 21.28-13.111 27.14-10.385 6.429-12.616 19.441-4.975 27.68-12.866-8.772-14.793-28.9-6.672-41.808l-1.047-.66c-8.184 13.022-6.546 32.908 5.674 42.502zm1.111 5.532c-35.384 35.012-86.569-4.862-69.305-47.868 2.612 16.066 13.272 12.02 23.325 11.81 12.949-.26 19.118 10.023 21.779 20.84 2.916 11.857 14.608 17.992 24.8 13.266-12.308 9.535-32.052 5.161-41.831-6.54l-.949.8c9.864 11.792 29.286 16.361 42.181 7.692zm-42.194-15.312l2.182-4.745-4.852 1.913-3.831-3.532.323 5.2-4.55 2.549 5.049 1.3 1.022 5.122 2.8-4.408 5.185.617zm48.016 15.488c22.806 44.248-30.571 81.129-66.434 51.787 16.114 2.314 15.439-9.07 18.257-18.721 3.625-12.436 15.287-15.239 26.407-14.53 12.191.763 21.54-8.556 20.082-19.7 5.415 14.6-4.677 32.136-18.766 37.957l.47 1.14c14.215-5.875 24.391-23.036 19.984-37.933zm-27.254 35.663l-3.869-3.5.371 5.2-4.52 2.6 5.063 1.252 1.067 5.107 2.759-4.431 5.19.557-3.366-3.992 2.133-4.759zm27.923-39.63c49.028-8.6 68.357 53.337 29.661 78.834 7.01-14.69-4.061-17.434-12.436-23-10.792-7.162-9.994-19.127-6.012-29.533 4.359-11.408-1.747-23.1-12.822-25.032 15.552-.821 29.288 14.03 30.644 29.21l1.232-.107c-1.38-15.319-14.729-30.147-30.263-30.372zm25.933 36.636l-4.491 2.652 5.078 1.194 1.135 5.087 2.7-4.461 5.19.5-3.4-3.948 2.079-4.789-4.813 2.025-3.909-3.459zm-28.687-41.254c8.317-49.076 73.158-46.539 84.216-1.539-11.5-11.52-17.792-2.015-25.839 4.016-10.367 7.769-21.369 3.009-29.841-4.226-9.285-7.935-22.362-6.091-27.88 3.693 4.432-14.93 23.021-22.894 37.791-19.088l.307-1.2c-14.896-3.832-33.338 3.786-38.754 18.344zm43.207-12.161l.988 5.122 2.827-4.384 5.181.636-3.3-4.04 2.211-4.726-4.862 1.888-3.817-3.561.294 5.215-4.564 2.519zm-46.484-29.822l5.176.617-3.312-4.027 2.182-4.74-4.853 1.913-3.826-3.542.318 5.2-4.559 2.549 5.053 1.311 1.018 5.117z",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React124.createElement("path", { d: "M1 283V1h378v282H1M380 0H0v284h380V0", fill: "#ca0000" })
  );
}

// src/components/icons/flags/Huf.tsx
import React125 from "react";
function Huf(props) {
  return /* @__PURE__ */ React125.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React125.createElement(
      "polyline",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#DE0000",
        points: "0,94.7 380,94.7 380,0 0,0 0,94.7"
      }
    ),
    /* @__PURE__ */ React125.createElement(
      "polyline",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#FFFFFF",
        points: "0,189.3 380,189.3 380,94.7 0,94.7 0,189.3"
      }
    ),
    /* @__PURE__ */ React125.createElement(
      "polyline",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#01A616",
        points: "0,284 380,284 380,189.3 0,189.3 0,284"
      }
    ),
    /* @__PURE__ */ React125.createElement(
      "polyline",
      {
        fill: "#CA0000",
        points: "380,0 0,0 0,94.7 1,94.7 1,1 379,1 379,94.7 380,94.7 380,0"
      }
    ),
    /* @__PURE__ */ React125.createElement(
      "path",
      {
        fill: "#E8EBEE",
        d: "M1,94.7H0v94.7h1V94.7 M380,94.7L380,94.7h-1v94.7h1V94.7"
      }
    ),
    /* @__PURE__ */ React125.createElement(
      "polyline",
      {
        fill: "#019915",
        points: "380,189.3 380,189.3 379,189.3 379,283 1,283 1,189.3 0,189.3 0,284 380,284 380,189.3"
      }
    )
  );
}

// src/components/icons/flags/Isk.tsx
import React126 from "react";
function IskFlag(props) {
  return /* @__PURE__ */ React126.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      height: 50,
      width: 67,
      viewBox: "0 0 380 284",
      ...props
    },
    /* @__PURE__ */ React126.createElement(
      "path",
      {
        d: "M119.164 176.077H1V283h118.164V176.077M379 175.913l-191.645.164V283H379V175.913M119.164 1H1v106.918h118.164V1M379 1H187.355v106.918H379V1",
        fill: "#1966ca"
      }
    ),
    /* @__PURE__ */ React126.createElement(
      "path",
      {
        d: "M136.21 159.044H1v17.033h118.164V283h17.046V159.044m242.79 0H170.3V283h17.052V176.077L379 175.913v-16.869M136.21 1h-17.046v106.918H1v17.043h135.21V1m51.145 0H170.3v123.961H379v-17.043H187.355V1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React126.createElement(
      "path",
      {
        fill: "#de0000",
        d: "M170.303 0H136.21v124.961H1v34.083h135.21V283h34.093V159.044H379v-34.083H170.303V1"
      }
    ),
    /* @__PURE__ */ React126.createElement(
      "path",
      {
        d: "M380 175.912h-1V283H187.355v1H380V175.912M119.164 0H0v107.918h1V1h118.164V0M380 0H187.355v1H379v106.918h1V0",
        fill: "#175ebd"
      }
    ),
    /* @__PURE__ */ React126.createElement(
      "path",
      {
        d: "M187.355 283H170.3v1h17.052v-1M380 159.044h-1v16.869h1v-16.869M1 107.918H0v17.043h1v-17.043m379 0h-1v17.043h1v-17.043M136.21 0h-17.046v1h17.046V0m51.145 0H170.3v1h17.052V0",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React126.createElement("path", { fill: "#175ebd", d: "M1 176.077H0V284h119.164v-1H1V176.077" }),
    /* @__PURE__ */ React126.createElement(
      "path",
      {
        d: "M136.21 283h-17.046v1h17.046v-1M1 159.044H0v17.033h1v-17.033",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React126.createElement(
      "path",
      {
        d: "M170.3 283h-34.09v1h34.09v-1M1 124.961H0v34.083h1v-34.083m379 0h-1v34.083h1v-34.083M170.3 0h-34.09v1h34.09V0",
        fill: "#ca0000"
      }
    )
  );
}

// src/components/icons/flags/Jpy.tsx
import React127 from "react";
function Jpy(props) {
  return /* @__PURE__ */ React127.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React127.createElement("path", { fill: "#fff", d: "M379 1H1v282h378V1" }),
    /* @__PURE__ */ React127.createElement(
      "path",
      {
        d: "M275.193 142a85.193 85.193 0 10-85.188 85.2 85.19 85.19 0 0085.188-85.2z",
        fill: "#de0000",
        fillRule: "evenodd"
      }
    ),
    /* @__PURE__ */ React127.createElement("path", { d: "M1 283V1h378v282H1M380 0H0v284h380V0", fill: "#e8ebee" })
  );
}

// src/components/icons/flags/Nok.tsx
import React128 from "react";
function Nok(props) {
  return /* @__PURE__ */ React128.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React128.createElement(
      "path",
      {
        d: "M117.586 183.9H1V283h116.586v-99.1m261.414 0H201.224V283H379v-99.1M117.586 1H1v99.1h116.586V1M379 1H201.224v99.1H379V1",
        fill: "#de0000"
      }
    ),
    /* @__PURE__ */ React128.createElement(
      "path",
      {
        d: "M132.85 168.622H1V183.9h116.586V283h15.264V168.622m246.15 0H185.975V283h15.249v-99.1H379v-15.278M132.85 1h-15.264v99.1H1v15.282h131.85V1m68.374 0h-15.249v114.377H379V100.1H201.224V1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React128.createElement(
      "path",
      {
        fill: "#1966ca",
        d: "M185.975 1H132.85v114.377H1v53.245h131.85V283h53.125V168.622H379v-53.245H185.975V1"
      }
    ),
    /* @__PURE__ */ React128.createElement(
      "path",
      {
        d: "M1 183.9H0V284h117.586v-1H1v-99.1m379-15.282h-.008V183.9H379V283H201.224v1H380V168.622M117.586 0H0v100.1h1V1h116.586V0M380 0H201.224v1H379v99.1h.992v15.282H380V0",
        fill: "#ca0000"
      }
    ),
    /* @__PURE__ */ React128.createElement(
      "path",
      {
        d: "M132.85 283h-15.264v1h15.264v-1m68.374 0h-15.249v1h15.249v-1m178.768-114.378H379V183.9h.992v-15.278M1 100.1H0v83.8h1v-15.278H.035v-53.245H1V100.1m378.992 0H379v15.282h.992V100.1M132.85 0h-15.264v1h15.264V0m68.374 0h-15.249v1h15.249V0",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React128.createElement(
      "path",
      {
        d: "M185.975 283H132.85v1h53.125v-1M1 115.377H.035v53.245H1v-53.245m379 0h-1v53.245h1v-53.245M185.975 0H132.85v1h53.125V0",
        fill: "#175ebd"
      }
    )
  );
}

// src/components/icons/flags/Nzd.tsx
import React129 from "react";
function Nzd(props) {
  return /* @__PURE__ */ React129.createElement(
    "svg",
    {
      "data-name": "a1a269d6-4fce-4a3f-b565-fcf020846f75",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React129.createElement("defs", null, /* @__PURE__ */ React129.createElement(
      "linearGradient",
      {
        id: "prefix__a",
        y1: 223.608,
        x2: 181.187,
        y2: 223.608,
        gradientTransform: "matrix(1 0 0 -1 0 284)",
        gradientUnits: "userSpaceOnUse"
      },
      /* @__PURE__ */ React129.createElement("stop", { offset: 0, stopColor: "#f6f4f4" }),
      /* @__PURE__ */ React129.createElement("stop", { offset: 0.45, stopColor: "#f6f4f4" }),
      /* @__PURE__ */ React129.createElement("stop", { offset: 0.8, stopColor: "#f6f4f4" }),
      /* @__PURE__ */ React129.createElement("stop", { offset: 1, stopColor: "#f6f4f4" })
    ), /* @__PURE__ */ React129.createElement(
      "linearGradient",
      {
        id: "prefix__b",
        y1: 241.874,
        x2: 110.7,
        y2: 241.874,
        xlinkHref: "#prefix__a"
      }
    )),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M40.112 84.252H1v26.074l39.112-26.074M1 16.332v26.074h39.112L1 16.332M79.309 1h-54.3l54.3 36.2V1M165 1h-54.3v36.2L165 1m214 0H190v14.666l-40.112 26.74H190v41.846h-40.112L190 110.993v15.665h-23.5l-55.8-37.2v37.2H79.309v-37.2l-55.8 37.2H1V283h378V1",
        fill: "#1966ca"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M54.8 84.252H40.112L1 110.326v9.792l53.8-35.866m24.506 0h-6.88L8.814 126.658h14.692l55.8-37.2v-5.206m38.262 0H110.7v5.209l55.8 37.2h14.688l-63.617-42.409m32.317 0H135.2l54.8 36.533v-9.792l-40.112-26.741M1 6.54v9.792l39.112 26.074H54.8L1 6.54m189-.666l-54.8 36.532h14.688L190 15.666V5.874M25.006 1H10.314l62.111 41.406h6.884V37.2L25.006 1m154.679 0H165l-54.3 36.2v5.206h6.879L179.685 1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M72.429 84.252H54.8L1 120.118v6.54h7.814l63.615-42.406m62.771 0h-17.629l63.615 42.406H190v-5.873l-54.8-36.533M10.314 1H1v5.54l53.8 35.866h17.625L10.314 1M190 1h-10.315l-62.11 41.406H135.2L190 5.874V1",
        fill: "#de0000"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M190 76.389h-85.2v50.269H85.2V76.389H1v7.863h78.309v42.406H110.7V84.252H190v-7.863M85.2 1h-5.891v41.406H1v7.863h84.2V1m25.5 0h-5.9v49.269H190v-7.863h-79.3V1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        fill: "#de0000",
        d: "M104.801 1H85.204v49.269H1v26.12h84.204v50.269h19.597V76.389H190v-26.12h-85.199V1"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M1 84.252H0v26.741l1-.667V84.252M0 15.666v26.74h1V16.332l-1-.666M79.309 0h-55.8l1.5 1h54.3V0M166.5 0h-55.8v1H165l1.5-1M380 0H190v1h189v282H1V126.658H0V284h380V0",
        fill: "#175ebd"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M1 110.326l-1 .667v9.792l1-.667v-9.792M0 5.874v9.792l1 .666V6.54l-1-.666M23.506 0H8.814l1.5 1h14.692l-1.5-1m157.68 0H166.5L165 1h14.687l1.5-1",
        fill: "url(#prefix__a)"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M1 120.118l-1 .667v5.873h1v-6.54M8.814 0H0v5.874l1 .666V1h9.314l-1.5-1M190 0h-8.814l-1.5 1H190V0",
        fill: "#ca0000"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M1 76.389H0v7.863h1v-7.863m0-33.983H0v7.863h1v-7.863M85.2 0h-5.891v1H85.2V0m25.5 0h-5.9v1h5.9V0",
        fill: "url(#prefix__b)"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M1 50.269H0v26.12h1v-26.12M104.8 0H85.2v1h19.6V0",
        fill: "#ca0000"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M279.928 189.842l3.326-10.143-8.64-6.258 10.664.018 3.263-10.164 3.281 10.164 10.67-.018-8.639 6.258 3.326 10.143-8.626-6.293zm8.625-9.059l4.343 3.168-1.674-5.1 4.36-3.158-5.385.007-1.65-5.109-1.64 5.109-5.383-.007 4.361 3.158-1.674 5.1zM251.1 116.67l-8.626-6.293-8.626 6.293 3.326-10.142-8.639-6.257 10.666.015 3.262-10.164 3.28 10.164 10.669-.015-8.638 6.257zm-8.626-9.059l4.342 3.169-1.673-5.106 4.361-3.159-5.386.008-1.649-5.107-1.639 5.107-5.387-.008 4.362 3.159-1.674 5.106zm79.067-2.951l3.326-10.142-8.638-6.258 10.665.017 3.263-10.163 3.281 10.163 10.662-.017-8.639 6.258 3.326 10.142-8.627-6.292zm8.626-9.058l4.344 3.168-1.674-5.1 4.361-3.16-5.385.009-1.65-5.11-1.64 5.11-5.386-.009 4.361 3.16-1.674 5.1zM280.758 69.75l3.326-10.142-8.639-6.257 10.664.016 3.271-10.157 3.28 10.157 10.668-.016-8.639 6.258 3.318 10.139-8.623-6.29zm8.626-9.058l4.345 3.17-1.67-5.107 4.361-3.155-5.385.008-1.651-5.114-1.648 5.106-5.382-.008 4.361 3.16-1.674 5.1z",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React129.createElement(
      "path",
      {
        d: "M288.544 166.94l2.466 7.637 8.027-.013-6.5 4.708 2.5 7.624-6.484-4.73-6.484 4.73 2.5-7.624-6.5-4.708 8.024.013zm41.613-85.18l2.465 7.636 8.027-.013-6.5 4.709 2.5 7.623-6.485-4.73-6.484 4.73 2.5-7.623-6.5-4.709 8.025.013zm-87.694 12.009l2.465 7.636 8.027-.012-6.5 4.708 2.5 7.624-6.484-4.731-6.485 4.731 2.5-7.624-6.5-4.708 8.027.012zm46.919-46.919l2.466 7.636 8.026-.013-6.5 4.709 2.494 7.623-6.484-4.73-6.485 4.73 2.5-7.623-6.5-4.709 8.024.013z",
        fill: "#de0000",
        fillRule: "evenodd"
      }
    )
  );
}

// src/components/icons/flags/Php.tsx
import React130 from "react";
function Php(props) {
  return /* @__PURE__ */ React130.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React130.createElement("polyline", { fill: "#DE0000", points: "0,142 380,142 380,284 0,284 0,142" }),
    /* @__PURE__ */ React130.createElement("polyline", { fill: "#1966CA", points: "0,0 380,0 380,142 220.5,142 0,0" }),
    /* @__PURE__ */ React130.createElement("polyline", { fill: "#FFFFFF", points: "247.2,142 0,284 0,0 247.2,142" }),
    /* @__PURE__ */ React130.createElement(
      "path",
      {
        fill: "#FFDB00",
        d: "M33.4,256.7l-11-6.4l-9.4,8.6l2.7-12.4l-11.1-6.3l12.7-1.3l2.6-12.5L25,238l12.6-1.4l-9.5,8.5L33.4,256.7z\n		 M33.4,24.2l-11,6.4L13,22.1l2.7,12.4L4.6,40.8L17.3,42l2.6,12.5L25,42.9l12.6,1.4l-9.5-8.5L33.4,24.2z M185.1,152.3l7.5-10.3\n		l-7.5-10.2l12.1,4l7.4-10.3l-0.1,12.7l12.1,3.8l-12.1,3.9l0.1,12.7l-7.4-10.3L185.1,152.3z M85.1,112.2c0.7,0.1,1.4,0.2,2.1,0.3\n		l3.9-21.4l3.3,4.2L89.6,113c3,0.8,5.8,2,8.3,3.6l9.3-15.4l5.3-0.5L99.9,118c0.6,0.4,1.1,0.9,1.6,1.3l14.9-18.4l8.7-0.5l0.1,7.9\n		l-19.7,15.2c0.4,0.6,0.8,1.1,1.2,1.7l18.1-12.1l-0.7,5.3l-16.1,8.8c1.3,2.3,2.3,4.8,2.9,7.4l17.4-4.7l4.2,3.3l-21.1,3.8\n		c0.1,0.7,0.2,1.4,0.3,2.1l23.5-2.9l6.6,5.7l-5.4,5.7l-24.7-2.7c-0.1,0.7-0.2,1.4-0.3,2.1l21.4,3.9l-4.2,3.3l-17.7-4.8\n		c-0.7,2.7-1.7,5.2-3.1,7.5l15.7,8.8l0.7,5.3l-17.7-12c-0.4,0.6-0.8,1.1-1.3,1.7l18.8,14.4l0.7,8.7l-7.9,0.3L101.2,165\n		c-0.5,0.5-1.1,0.9-1.7,1.3l12.6,17.7l-5.3-0.6l-9.3-15.8c-2.5,1.5-5.1,2.6-8,3.4l4.8,17.3l-3.3,4.2l-3.9-21.1\n		c-0.7,0.1-1.4,0.2-2.1,0.3l3,23.5l-5.7,6.6l-5.7-5.4l2.5-24.8c-0.7-0.1-1.4-0.2-2.1-0.3L73.3,193l-3.3-4.2l4.7-17.8\n		c-2.9-0.7-5.6-1.9-8.1-3.4l-9,15.6l-5.3,0.6l12.3-17.6c-0.6-0.4-1.1-0.8-1.7-1.3l-14.6,18.6l-8.7,0.6l-0.2-7.9l19.4-15.6\n		c-0.4-0.5-0.9-1.1-1.3-1.7l-17.9,12.4l0.7-5.3l16-9.1c-1.4-2.4-2.4-4.9-3.1-7.6l-17.3,4.8l-4.2-3.3l21.1-3.9\n		c-0.1-0.7-0.2-1.4-0.3-2.1l-23.5,3l-6.6-5.7l5.4-5.7l24.8,2.5c0.1-0.7,0.2-1.4,0.3-2.1l-21.4-3.7l4.2-3.3l17.7,4.7\n		c0.7-2.8,1.8-5.4,3.2-7.8L40.5,118l-0.7-5.3l17.7,12.1c0.4-0.6,0.8-1.1,1.3-1.7l-18.8-14.5l-0.7-8.7l7.9-0.3L63,118.9\n		c0.5-0.5,1.1-0.9,1.7-1.3L52.1,99.8l5.3,0.6l9.2,15.9c2.5-1.5,5.2-2.6,8.1-3.3l-4.7-17.4l3.3-4.2l3.8,21.1c0.7-0.1,1.4-0.2,2.1-0.3\n		l-2.9-23.5l5.7-6.6l5.7,5.4L85.1,112.2z"
      }
    ),
    /* @__PURE__ */ React130.createElement(
      "polyline",
      {
        fill: "#CA0000",
        points: "380,142 379,142 379,283 1.7,283 0,284 380,284 380,142"
      }
    ),
    /* @__PURE__ */ React130.createElement(
      "polyline",
      {
        fill: "#175EBD",
        points: "380,0 0,0 1.7,1 379,1 379,142 380,142 380,0"
      }
    ),
    /* @__PURE__ */ React130.createElement("polyline", { fill: "#E8EBEE", points: "0,0 0,284 1.7,283 1,283 1,1 1.7,1 0,0" })
  );
}

// src/components/icons/flags/Pln.tsx
import React131 from "react";
function Pln(props) {
  return /* @__PURE__ */ React131.createElement(
    "svg",
    {
      height: 50,
      width: 67,
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      ...props
    },
    /* @__PURE__ */ React131.createElement("path", { fill: "#fff", d: "M379 1H1v141.002h378V1" }),
    /* @__PURE__ */ React131.createElement("path", { fill: "#de0000", d: "M379 142.002H1V283h378V142.002" }),
    /* @__PURE__ */ React131.createElement("path", { fill: "#e8ebee", d: "M380 0H0v142.002h1V1h378v141.002h1V0" }),
    /* @__PURE__ */ React131.createElement(
      "path",
      {
        fill: "#ca0000",
        d: "M380 142.002h-1V283H1V142.002H0V284h380V142.002"
      }
    )
  );
}

// src/components/icons/flags/Ron.tsx
import React132 from "react";
function Ron(props) {
  return /* @__PURE__ */ React132.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React132.createElement(
      "polyline",
      {
        fill: "#DE0000",
        points: "253.3,0 380,0 380,284 253.3,284 253.3,0"
      }
    ),
    /* @__PURE__ */ React132.createElement(
      "polyline",
      {
        fill: "#1966CA",
        points: "-0.2,0 126.5,0 126.5,284 -0.2,284 -0.2,0"
      }
    ),
    /* @__PURE__ */ React132.createElement(
      "polyline",
      {
        fill: "#FFDB00",
        points: "126.5,0 253.3,0 253.3,284 126.5,284 126.5,0"
      }
    ),
    /* @__PURE__ */ React132.createElement(
      "polyline",
      {
        fill: "#CA0000",
        points: "380,0 253.3,0 253.3,1 379,1 379,283 253.3,283 253.3,284 380,284 380,0"
      }
    ),
    /* @__PURE__ */ React132.createElement(
      "polyline",
      {
        fill: "#175EBD",
        points: "126.5,0 0,0 0,284 126.5,284 126.5,283 1,283 1,1 126.5,1 126.5,0"
      }
    ),
    /* @__PURE__ */ React132.createElement(
      "path",
      {
        fill: "#E8CA00",
        d: "M253.3,283H126.5v1h126.7V283 M253.3,0H126.5v1h126.7V0"
      }
    )
  );
}

// src/components/icons/flags/Rub.tsx
import React133 from "react";
function Rub(props) {
  return /* @__PURE__ */ React133.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React133.createElement("path", { fill: "#fff", d: "M379 1H1v93.667h378V1" }),
    /* @__PURE__ */ React133.createElement("path", { fill: "#1966ca", d: "M379 94.667H1v94.666h378V94.667" }),
    /* @__PURE__ */ React133.createElement("path", { fill: "#de0000", d: "M379 189.333H1V283h378v-93.667" }),
    /* @__PURE__ */ React133.createElement("path", { fill: "#e8ebee", d: "M380 0H0v94.667h1V1h378v93.667h1V0" }),
    /* @__PURE__ */ React133.createElement(
      "path",
      {
        d: "M1 94.667H0v94.666h1V94.667m379 0h-1v94.666h1V94.667",
        fill: "#175ebd"
      }
    ),
    /* @__PURE__ */ React133.createElement(
      "path",
      {
        fill: "#ca0000",
        d: "M380 189.333h-1V283H1v-93.667H0V284h380v-94.667"
      }
    )
  );
}

// src/components/icons/flags/Sek.tsx
import React134 from "react";
function Sek(props) {
  return /* @__PURE__ */ React134.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React134.createElement(
      "path",
      {
        d: "M132.806 168.271H1V283h131.806V168.271m246.194 0H185.918V283H379V168.271M132.806 1H1v114.025h131.806V1M379 1H185.918v114.025H379V1",
        fill: "#1966ca"
      }
    ),
    /* @__PURE__ */ React134.createElement(
      "path",
      {
        fill: "#ffdb00",
        d: "M185.918 1h-53.112v114.025H1v53.246h131.806V283h53.112V168.271H379v-53.246H185.918V1"
      }
    ),
    /* @__PURE__ */ React134.createElement(
      "path",
      {
        d: "M1 168.271H0V284h132.806v-1H1V168.271m379 0h-1V283H185.918v1H380V168.271M132.806 0H0v115.025h1V1h131.806V0M380 0H185.918v1H379v114.025h1V0",
        fill: "#175ebd"
      }
    ),
    /* @__PURE__ */ React134.createElement(
      "path",
      {
        d: "M185.918 283h-53.112v1h53.112v-1M1 115.025H0v53.246h1v-53.246m379 0h-1v53.246h1v-53.246M185.918 0h-53.112v1h53.112V0",
        fill: "#e8ca00"
      }
    )
  );
}

// src/components/icons/flags/Sgd.tsx
import React135 from "react";
function Sgd(props) {
  return /* @__PURE__ */ React135.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React135.createElement("path", { fill: "#de0000", d: "M379 1H1v141h378V1" }),
    /* @__PURE__ */ React135.createElement(
      "path",
      {
        fill: "#fff",
        d: "M379 142H1v141h378V142M61.964 71a50.457 50.457 0 0148.364-50.41 53.4 53.4 0 100 100.82A50.457 50.457 0 0161.964 71z"
      }
    ),
    /* @__PURE__ */ React135.createElement(
      "path",
      {
        fill: "#fff",
        d: "M94.821 57.805l-2.757-8.985-2.755 8.986-8.979-.019 7.276 5.531-2.792 8.973 7.253-5.567 7.254 5.564-2.795-8.972 7.275-5.534-8.98.023zM160.718 57.782l-8.979.023-2.758-8.985-2.754 8.986-8.98-.019 7.277 5.531-2.792 8.973 7.252-5.567 7.254 5.564-2.795-8.972 7.275-5.534zM125.013 42.193l7.276-5.534-8.98.022-2.758-8.985-2.754 8.986-8.98-.019 7.277 5.531-2.792 8.973 7.252-5.567 7.254 5.565-2.795-8.972zM102.714 82.619l-2.755 8.985-8.98-.019 7.278 5.532-2.792 8.973 7.252-5.568 7.254 5.565-2.795-8.972 7.275-5.534-8.98.023-2.757-8.985zM141.089 91.604l-2.758-8.985-2.755 8.985-8.979-.019 7.277 5.532-2.792 8.973 7.252-5.568 7.254 5.565-2.795-8.972 7.275-5.534-8.979.023z"
      }
    ),
    /* @__PURE__ */ React135.createElement("path", { fill: "#ca0000", d: "M380 0H0v142h1V1h378v141h1V0" }),
    /* @__PURE__ */ React135.createElement("path", { fill: "#e8ebee", d: "M380 142h-1v141H1V142H0v142h380V142" })
  );
}

// src/components/icons/flags/Thb.tsx
import React136 from "react";
function Thb(props) {
  return /* @__PURE__ */ React136.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React136.createElement(
      "polyline",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#FFFFFF",
        points: "0,0 380,0 380,284 0,284 0,0"
      }
    ),
    /* @__PURE__ */ React136.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#DE0000",
        d: "M0,284h380v-47.3H0V284 M380,47.3V0H0v47.3H380"
      }
    ),
    /* @__PURE__ */ React136.createElement(
      "polyline",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        fill: "#1966CA",
        points: "0,94.7 380,94.7 380,189.4 0,189.4 0,94.7"
      }
    ),
    /* @__PURE__ */ React136.createElement(
      "path",
      {
        fill: "#E8EBEE",
        d: "M1,189.4H0v47.3h1V189.4 M380,189.4h-1v47.3h1V189.4 M1,47.3H0v47.4h1V47.3 M380,47.3h-1v47.4h1V47.3"
      }
    ),
    /* @__PURE__ */ React136.createElement(
      "path",
      {
        fill: "#CA0000",
        d: "M380,236.7L380,236.7h-1V283H1v-46.3H0V284h380V236.7 M380,0H0v47.3h1V1h378v46.3h1V0"
      }
    ),
    /* @__PURE__ */ React136.createElement(
      "path",
      {
        fill: "#175EBD",
        d: "M1,94.7H0v94.7h1V94.7 M380,94.7L380,94.7h-1v94.7h1V94.7"
      }
    )
  );
}

// src/components/icons/flags/Usd.tsx
import React137 from "react";
function Usd(props) {
  return /* @__PURE__ */ React137.createElement(
    "svg",
    {
      height: 50,
      width: 67,
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      ...props
    },
    /* @__PURE__ */ React137.createElement(
      "path",
      {
        d: "M379 240.314H1v21.838h378v-21.838m0-43.69H1v21.843h378v-21.843m0-43.7H1v21.848h378v-21.848m0-43.7H215.84v21.853H379v-21.849m0-43.69H215.84v21.848H379V65.538m0-43.695H215.84V43.69H379V21.843",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React137.createElement(
      "path",
      {
        d: "M379 262.152H1V283h378v-20.848m0-43.685H1v21.847h378v-21.847m0-43.7H1v21.852h378v-21.847m0-43.691H215.84v21.843H379v-21.843m0-43.695H215.84v21.842H379V87.386m0-43.7H215.84v21.852H379V43.69M379 1H215.84v20.843H379V1",
        fill: "#de0000"
      }
    ),
    /* @__PURE__ */ React137.createElement("path", { fill: "#1966ca", d: "M215.84 1H1v151.924h214.84V1" }),
    /* @__PURE__ */ React137.createElement(
      "path",
      {
        d: "M199.344 43.089l-1.967-6.052-1.961 6.052h-6.355l5.142 3.732-1.962 6.047 5.136-3.738 5.142 3.738-1.961-6.047 5.14-3.732zM21.637 16.148l5.142-3.737h-6.355l-1.962-6.042-1.962 6.042h-6.359l5.142 3.742-1.967 6.047 5.146-3.737L23.6 22.2zm35.785 0l5.142-3.742h-6.355l-1.967-6.037-1.961 6.042h-6.355l5.142 3.737L49.1 22.2l5.141-3.737 5.143 3.737zm35.785 0l5.142-3.737h-6.36l-1.962-6.042-1.961 6.042h-6.36l5.147 3.742-1.967 6.047 5.141-3.737 5.142 3.737zm35.785 0l5.142-3.742h-6.36l-1.962-6.042-1.966 6.042h-6.355l5.141 3.742-1.961 6.052 5.141-3.737 5.142 3.737zM40.486 122.77l5.141-3.738h-6.354l-1.967-6.046-1.961 6.046H28.99l5.141 3.738-1.966 6.046 5.147-3.737 5.136 3.737zm-3.174-1.033zm2.465-28.759l5.137-3.738h-6.351L36.6 83.2l-1.965 6.04h-6.359l5.146 3.738-1.966 6.045 5.144-3.737 5.142 3.737zm.186-29.641L45.1 59.6h-6.35l-1.967-6.046-1.962 6.046h-6.359l5.138 3.737-1.961 6.046 5.141-3.733 5.141 3.733zm124.809-47.189l5.142-3.737h-6.355l-1.967-6.042-1.961 6.042h-6.355l5.141 3.742-1.961 6.047 5.142-3.737 5.136 3.737zm35.786 0l5.14-3.737h-6.354l-1.967-6.042-1.961 6.042h-6.355l5.142 3.742-1.962 6.047 5.136-3.737 5.142 3.737zM75.743 63.341l5.141-3.741H74.53l-1.962-6.046L70.6 59.6h-6.353l5.146 3.737-1.967 6.042 5.142-3.733 5.141 3.733zm-.181 29.637L80.7 89.24h-6.356l-1.967-6.04-1.961 6.04h-6.355l5.146 3.738-1.966 6.045 5.141-3.737 5.141 3.737zm.709 29.792l5.142-3.738h-6.355l-1.958-6.046-1.967 6.046h-6.358l5.142 3.738-1.967 6.046 5.141-3.737 5.142 3.737zm-3.18-1.033zm38.96 1.033l5.147-3.738h-6.355l-1.962-6.046-1.966 6.046h-6.355l5.136 3.738-1.961 6.046 5.141-3.737 5.142 3.737zm-.709-29.792l5.141-3.738h-6.355l-1.961-6.04-1.967 6.04h-6.354l5.141 3.738-1.962 6.045 5.142-3.737 5.142 3.737zm.186-29.637l5.141-3.737h-6.355l-1.966-6.046-1.962 6.042h-6.354l5.141 3.737-1.962 6.042 5.137-3.737 5.146 3.737zm-72-31.856l5.137-3.737h-6.351l-1.961-6.038-1.962 6.038h-6.36l5.147 3.737-1.967 6.041 5.142-3.732 5.136 3.732zm35.78 0l5.142-3.737H74.1l-1.968-6.038-1.961 6.038h-6.355l5.146 3.737L67 37.526l5.141-3.732 5.142 3.732zm72 31.852l5.141-3.733H146.1l-1.957-6.046-1.972 6.042h-6.355l5.142 3.737-1.966 6.042 5.141-3.737 5.146 3.737zm-.186 29.641l5.142-3.738h-6.355l-1.957-6.04-1.967 6.042h-6.354l5.141 3.732-1.961 6.051 5.136-3.737 5.146 3.737zm.709 29.792l5.142-3.738h-6.355l-1.962-6.046-1.961 6.046h-6.36l5.141 3.738-1.961 6.046 5.136-3.737 5.147 3.737zm35.785 0l5.142-3.738h-6.355l-1.967-6.046-1.962 6.046h-6.354l5.141 3.738-1.961 6.046 5.141-3.737 5.142 3.737zm-3.18-1.033zm2.471-28.759l5.141-3.738H181.7l-1.963-6.04-1.966 6.042h-6.355l5.141 3.738-1.967 6.045 5.142-3.737 5.142 3.737zm.186-29.641l5.136-3.733h-6.355l-1.961-6.046-1.957 6.042H171.6l5.141 3.737-1.962 6.042 5.142-3.733 5.142 3.733zm-72-31.852l5.142-3.737h-6.355l-1.962-6.038-1.966 6.038H99.6l5.142 3.737-1.962 6.041 5.136-3.732 5.142 3.732zm35.785 0l5.142-3.737h-6.355L143.7 21.71l-1.961 6.038h-6.355l5.142 3.737-1.967 6.041 5.141-3.732 5.147 3.732zm35.785 0l5.137-3.737h-6.355l-1.962-6.038-1.961 6.038h-6.36l5.142 3.737-1.967 6.041 5.146-3.732 5.142 3.732zM22.591 138.106l5.142-3.732h-6.35l-1.966-6.047-1.962 6.047H11.1l5.142 3.732-1.962 6.041 5.137-3.732 5.141 3.732zm-.709-29.793l5.141-3.737h-6.354l-1.962-6.046-1.967 6.046h-6.354l5.146 3.737-1.967 6.042 5.142-3.737 5.142 3.737zm.186-29.635l5.146-3.733h-6.355l-1.966-6.051-1.967 6.051h-6.354l5.141 3.733-1.962 6.042 5.142-3.733 5.136 3.733zm35.785 0l5.141-3.733h-6.349l-1.967-6.051-1.967 6.051h-6.355l5.144 3.733-1.964 6.042 5.142-3.733 5.141 3.733zm-.186 29.635l5.142-3.737h-6.355l-1.967-6.046-1.962 6.046h-6.354l5.141 3.737-1.961 6.042 5.136-3.737 5.146 3.737zm.714 29.793l5.137-3.732h-6.355l-1.963-6.047-1.961 6.047H46.88l5.141 3.732-1.961 6.041 5.141-3.732 5.142 3.732zm35.78 0l5.142-3.732h-6.355l-1.967-6.047-1.961 6.047h-6.355l5.141 3.732-1.961 6.041 5.141-3.732 5.142 3.732zm-.709-29.793l5.141-3.737h-6.359l-1.962-6.046-1.961 6.046h-6.36l5.146 3.737-1.962 6.042 5.137-3.737 5.142 3.737zm.186-29.635l5.141-3.733h-6.355l-1.966-6.051-1.958 6.051h-6.363l5.141 3.733-1.962 6.042 5.142-3.733L95.6 84.72zm-72-31.857l5.142-3.732h-6.35l-1.967-6.052-1.963 6.052h-6.359l5.142 3.732-1.967 6.047 5.146-3.738 5.138 3.738zm35.785 0l5.142-3.732h-6.356l-1.967-6.052-1.961 6.052h-6.355l5.136 3.732-1.962 6.047 5.141-3.738 5.142 3.738zm72 31.857l5.137-3.733h-6.351l-1.966-6.051-1.967 6.051h-6.354l5.141 3.733-1.963 6.042 5.141-3.733 5.141 3.733zm-.186 29.635l5.141-3.737h-6.355l-1.966-6.046-1.967 6.046h-6.354l5.141 3.737-1.961 6.042 5.141-3.737 5.142 3.737zm.709 29.793l5.137-3.732h-6.35l-1.962-6.047-1.966 6.047h-6.355l5.141 3.732-1.966 6.041 5.141-3.732 5.142 3.732zm35.785 0l5.137-3.732h-6.35l-1.967-6.047-1.962 6.047h-6.354l5.141 3.732-1.961 6.041 5.136-3.732 5.142 3.732zm35.786 0l5.14-3.732H200.3l-1.962-6.047-1.966 6.047h-6.35l5.136 3.732-1.962 6.041 5.142-3.732 5.142 3.732zm-36.5-29.793l5.141-3.737H163.8l-1.962-6.046-1.967 6.046h-6.354l5.146 3.737-1.961 6.042 5.136-3.737 5.137 3.737zm35.785 0l5.141-3.737h-6.354l-1.962-6.046-1.967 6.046h-6.354l5.141 3.737-1.962 6.042 5.137-3.737 5.142 3.737zM165.2 78.678l5.141-3.733h-6.351l-1.962-6.051-1.966 6.051h-6.355l5.141 3.733-1.962 6.042 5.142-3.733 5.141 3.733zm35.784 0l5.142-3.733h-6.355l-1.962-6.051-1.965 6.051h-6.355l5.141 3.733-1.962 6.042 5.141-3.733 5.142 3.733zM93.207 46.821l5.142-3.732h-6.355l-1.967-6.052-1.961 6.052h-6.36l5.147 3.732-1.967 6.047 5.141-3.738 5.142 3.738zm35.785 0l5.142-3.732h-6.36l-1.962-6.052-1.966 6.052h-6.355l5.141 3.732-1.961 6.047 5.141-3.738 5.142 3.738zm35.78 0l5.142-3.732h-6.355l-1.967-6.052-1.966 6.052h-6.35l5.141 3.732-1.961 6.047 5.144-3.738 5.136 3.738z",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React137.createElement(
      "path",
      {
        d: "M1 240.314H0v21.838h1v-21.838m379 0h-1v21.838h1v-21.838M1 196.624H0v21.843h1v-21.843m379 0h-1v21.843h1v-21.843m-379-43.7H0v21.848h1v-21.848m379 0h-1v21.848h1v-21.848m0-43.7h-1v21.853h1v-21.849m0-43.69h-1v21.848h1V65.538m0-43.695h-1V43.69h1V21.843",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React137.createElement(
      "path",
      {
        d: "M380 262.152h-1V283H1v-20.848H0V284h380v-21.848M1 218.467H0v21.847h1v-21.847m379 0h-1v21.847h1v-21.847M1 174.772H0v21.852h1v-21.852m379 0h-1v21.852h1v-21.852m0-43.691h-1v21.843h1v-21.843m0-43.695h-1v21.842h1V87.386m0-43.7h-1v21.852h1V43.69M380 0H215.84v1H379v20.843h1V0",
        fill: "#ca0000"
      }
    ),
    /* @__PURE__ */ React137.createElement("path", { fill: "#175ebd", d: "M215.84 0H0v152.924h1V1h214.84V0" })
  );
}

// src/components/icons/flags/Xdr.tsx
import React138 from "react";
function Xdr(props) {
  return /* @__PURE__ */ React138.createElement(
    "svg",
    {
      "data-name": "f7b74e6a-79ab-4bc5-ba7a-8edebb339609",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React138.createElement("path", { fill: "#1966ca", d: "M379.5.627H.5v282.746h379V.627" }),
    /* @__PURE__ */ React138.createElement("path", { d: "M1 283V1h378v282H1M380 0H0v284h380V0", fill: "#175ebd" }),
    /* @__PURE__ */ React138.createElement(
      "g",
      {
        style: {
          isolation: "isolate"
        },
        fill: "#fff",
        "data-name": "XDR"
      },
      /* @__PURE__ */ React138.createElement("path", { d: "M77.947 97.8h15.36c6 10.439 12 20.639 18.119 31.078L129.785 97.8h15.239c-8.639 13.319-17.279 26.519-25.8 39.838 8.759 14.159 17.759 28.438 26.519 42.358h-15.12c-6.72-11.039-13.319-22.319-20.039-33.478C104.106 157.68 97.626 168.96 91.147 180h-15.12l26.759-42.718zM157.865 180V97.683h28.679c27.719 0 35.038 18.839 35.038 40.918 0 22.319-8.52 41.4-36 41.4zm13.8-72.476v62.4h12.6c7.92 0 23.279-2.519 23.279-30.6 0-28.559-14.039-31.8-22.919-31.8zM281.223 142.081c6.6 2.039 8.88 7.919 10.8 14.519l6.72 23.4h-13.56c-2.16-7.319-4.32-14.639-6.36-22.079-2.76-9.96-7.559-12.479-14.88-12.479h-11.159V180h-13.439V97.683h30.6c17.4 0 25.679 9.84 25.679 23.159-.002 9.358-4.802 18.239-14.401 21.239zm-13.679-6.48c6.119 0 14.159-2.76 14.159-14.039 0-9.84-6.12-13.92-14.279-13.92h-14.64V135.6z" })
    )
  );
}

// src/components/icons/flags/Zar.tsx
import React139 from "react";
function Zar(props) {
  return /* @__PURE__ */ React139.createElement(
    "svg",
    {
      "data-name": "Layer 1",
      viewBox: "0 0 380 284",
      height: 50,
      width: 67,
      ...props
    },
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        d: "M379 170.114l-158.119.141L53.167 283h35.117L228.4 189.021l150.6.31v-19.217M88.053 1h-34.57l167.076 112.667 158.441-.1V94.593H227.973L88.053 1",
        fill: "#fff"
      }
    ),
    /* @__PURE__ */ React139.createElement("path", { fill: "#de0000", d: "M379 1H88.053l139.92 93.593H379V1" }),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        fill: "#ffd500",
        d: "M1 34.728v23.169l125.757 83.983L1 226.339v22.698L160.337 141.88 1 34.728"
      }
    ),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        fill: "#00701d",
        d: "M53.483 1H1v33.728L160.337 141.88 1 249.037V283h52.167l167.714-112.745L379 170.114v-56.545l-158.441.098L53.483 1"
      }
    ),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        fill: "#1966ca",
        d: "M228.401 189.021L88.284 283H379v-93.669l-150.599-.31"
      }
    ),
    /* @__PURE__ */ React139.createElement("path", { d: "M1 57.897v168.442l125.757-84.459L1 57.897" }),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        fill: "#e8ebee",
        d: "M380 0H0l52.087.059.095.064h34.559L86.558 0 380 .123V284 0"
      }
    ),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        d: "M380 170.113h-1v19.217h1v-19.22m0-75.52h-1v18.976h1V94.593M86.741.123H52.182l1.3.877h34.57L86.741.123",
        fill: "#e8ebee"
      }
    ),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        fill: "#ca0000",
        d: "M86.558 0l.183.123L88.053 1H379v93.593h1V.123L86.558 0"
      }
    ),
    /* @__PURE__ */ React139.createElement("path", { fill: "#e8c400", d: "M0 34.055v23.174l1 .668V34.728l-1-.673" }),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        d: "M380 113.569h-1v56.545h1v-56.545M0 0v34.055l1 .673V1h52.483l-1.3-.877-.096-.064L0 0",
        fill: "#00671b"
      }
    ),
    /* @__PURE__ */ React139.createElement("path", { fill: "#e8ebee", d: "M233.571 283.883H0V284h380l-146.429-.117" }),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        fill: "#e8ebee",
        d: "M88.284 283H53.167l-1.139.766H0v.117h233.571l-146.429-.117 1.142-.766"
      }
    ),
    /* @__PURE__ */ React139.createElement("path", { fill: "#e8c400", d: "M1 226.339l-1 .672v22.699l1-.673v-22.698" }),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        fill: "#00671b",
        d: "M1 249.037l-1 .673v34.056h52.028l1.139-.766H1v-33.963"
      }
    ),
    /* @__PURE__ */ React139.createElement(
      "path",
      {
        fill: "#175ebd",
        d: "M379 189.331V283H88.284l-1.142.766 146.429.117L380 284v-94.667l-1-.002"
      }
    ),
    /* @__PURE__ */ React139.createElement("path", { d: "M0 57.229v169.782l1-.672V57.897l-1-.668" })
  );
}

// src/components/icons/flags/Flags.tsx
function Flags({ currency, ...rest }) {
  switch (currency) {
    case "ISK": {
      return /* @__PURE__ */ React140.createElement(IskFlag, { ...rest });
    }
    case "EUR": {
      return /* @__PURE__ */ React140.createElement(Eur, { ...rest });
    }
    case "USD": {
      return /* @__PURE__ */ React140.createElement(Usd, { ...rest });
    }
    case "GBP": {
      return /* @__PURE__ */ React140.createElement(Gbp, { ...rest });
    }
    case "PLN": {
      return /* @__PURE__ */ React140.createElement(Pln, { ...rest });
    }
    case "JPY": {
      return /* @__PURE__ */ React140.createElement(Jpy, { ...rest });
    }
    case "NOK": {
      return /* @__PURE__ */ React140.createElement(Nok, { ...rest });
    }
    case "SEK": {
      return /* @__PURE__ */ React140.createElement(Sek, { ...rest });
    }
    case "DKK": {
      return /* @__PURE__ */ React140.createElement(Dkk, { ...rest });
    }
    case "CHF": {
      return /* @__PURE__ */ React140.createElement(Chf, { ...rest });
    }
    case "CAD": {
      return /* @__PURE__ */ React140.createElement(Cad, { ...rest });
    }
    case "AUD": {
      return /* @__PURE__ */ React140.createElement(Aud, { ...rest });
    }
    case "ZAR": {
      return /* @__PURE__ */ React140.createElement(Zar, { ...rest });
    }
    case "HKD": {
      return /* @__PURE__ */ React140.createElement(Hkd, { ...rest });
    }
    case "NZD": {
      return /* @__PURE__ */ React140.createElement(Nzd, { ...rest });
    }
    case "RUB": {
      return /* @__PURE__ */ React140.createElement(Rub, { ...rest });
    }
    case "SGD": {
      return /* @__PURE__ */ React140.createElement(Sgd, { ...rest });
    }
    case "CNY": {
      return /* @__PURE__ */ React140.createElement(Cny, { ...rest });
    }
    case "XDR": {
      return /* @__PURE__ */ React140.createElement(Xdr, { ...rest });
    }
    case "THB": {
      return /* @__PURE__ */ React140.createElement(Thb, { ...rest });
    }
    case "HUF": {
      return /* @__PURE__ */ React140.createElement(Huf, { ...rest });
    }
    case "PHP": {
      return /* @__PURE__ */ React140.createElement(Php, { ...rest });
    }
    case "RON": {
      return /* @__PURE__ */ React140.createElement(Ron, { ...rest });
    }
    case "CZK": {
      return /* @__PURE__ */ React140.createElement(Czk, { ...rest });
    }
    default: {
      return /* @__PURE__ */ React140.createElement(DefaultFlag, { ...rest });
    }
  }
}

// src/components/icons/flags/mini-currency-calculator/CurrencyIcon.tsx
import React146 from "react";

// src/components/icons/flags/mini-currency-calculator/EUR.tsx
import styled26 from "@emotion/styled";
import React141 from "react";
var SVG = styled26.svg`
  height: ${(props) => props.isMobile ? "28px" : "18px"};
  width: ${(props) => props.isMobile ? "28px" : "18px"};
  .st0 {
    fill-rule: evenodd;
    clip-rule: evenodd;
    fill: #1966ca;
  }
  .st1 {
    fill-rule: evenodd;
    clip-rule: evenodd;
    fill: #ffd500;
  }
  .st2 {
    fill: #165cb5;
  }
`;
function EUR() {
  const isMobile = useMedia("sm");
  return /* @__PURE__ */ React141.createElement(
    SVG,
    {
      isMobile,
      xmlns: "http://www.w3.org/2000/svg",
      x: "0",
      y: "0",
      enableBackground: "new 0 0 18 18",
      version: "1.1",
      viewBox: "0 0 18 18",
      xmlSpace: "preserve",
      "aria-hidden": true
    },
    /* @__PURE__ */ React141.createElement(
      "path",
      {
        d: "M2 0h14c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V2C0 .9.9 0 2 0",
        className: "st0"
      }
    ),
    /* @__PURE__ */ React141.createElement(
      "path",
      {
        d: "M6 2.8l.2.7H7l-.5.5.2.7-.7-.4-.5.4.1-.7-.6-.5h.7l.3-.7zm6 10.5l.2.7h.8l-.6.5.2.7-.6-.4-.6.4.2-.7-.6-.5h.7l.3-.7zm-6 0l.3.7H7l-.6.5.2.7-.6-.4-.6.4.2-.7L5 14h.7l.3-.7zm6-10.5l.2.7h.8l-.5.5.2.7-.7-.4-.6.4.3-.7-.7-.5h.7l.3-.7zm2.3 8.3l.2.7h.7l-.6.5.2.7-.6-.4-.5.4.2-.7-.6-.5h.7l.3-.7zm0-6.1l.2.7h.7l-.6.5.2.7-.6-.4-.6.4.2-.7-.6-.5h.7l.4-.7zM3.9 11.1l.1.7h.7l-.6.5.4.7-.6-.4-.6.4.2-.7-.6-.5h.7l.3-.7zm11.1-3l.2.7h.8l-.6.5.2.7-.6-.4-.5.4.2-.7-.7-.5h.7l.3-.7zm-12 0l.2.7H4l-.5.5.1.7-.6-.4-.5.4.2-.7-.7-.5h.7l.3-.7zM3.9 5l.1.7h.7l-.4.5.2.7-.6-.4-.6.4.2-.7-.6-.4h.7l.3-.8zM9 14.1l.2.7h.8l-.6.5.2.7-.6-.4-.5.4.2-.7-.7-.5h.7l.3-.7zM9 2l.2.7h.8l-.5.5.2.7-.7-.4-.5.4.2-.7-.7-.5h.7L9 2z",
        className: "st1"
      }
    ),
    /* @__PURE__ */ React141.createElement(
      "path",
      {
        d: "M2 17.5c-.8 0-1.5-.7-1.5-1.5V2C.5 1.2 1.2.5 2 .5h14c.8 0 1.5.7 1.5 1.5v14c0 .8-.7 1.5-1.5 1.5H2M16 0H2C.9 0 0 .9 0 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V2c0-1.1-.9-2-2-2",
        className: "st2"
      }
    )
  );
}

// src/components/icons/flags/mini-currency-calculator/GBP.tsx
import styled27 from "@emotion/styled";
import React142 from "react";
var SVG2 = styled27.svg`
  height: ${(props) => props.isMobile ? "28px" : "18px"};
  width: ${(props) => props.isMobile ? "28px" : "18px"};
`;
function GBP() {
  const isMobile = useMedia("sm");
  return /* @__PURE__ */ React142.createElement(
    SVG2,
    {
      isMobile,
      xmlns: "http://www.w3.org/2000/svg",
      "data-name": "Layer 2",
      viewBox: "0 0 18 18",
      "aria-hidden": true
    },
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#1966ca",
        fillRule: "evenodd",
        d: "M15.9 15.6H18V2a2.006 2.006 0 00-2-2H2a2.006 2.006 0 00-2 2v14a2.006 2.006 0 002 2h13.9v-2.4"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#fff",
        d: "M18 14.6L12.4 9 18 3.4V2a2.006 2.006 0 00-2-2h-1L9 5.8 3 0H2a2.006 2.006 0 00-2 2v1.4L5.7 9 0 14.6V16a2.353 2.353 0 00.2.8A1.975 1.975 0 002 18h1l6-5.7 6 5.7h1a2.153 2.153 0 001.8-1.2 1.077 1.077 0 00.2-.8v-1.4"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#de0000",
        d: "M16.749 17.851a2.01 2.01 0 001.117-1.15 1.383 1.383 0 00.034-.2L10.3 9l7.6-7.5a2 2 0 00-1-1.2L9 7.8 1.2.2C1.1.3 1 .3.9.4a1.548 1.548 0 00-.8 1.1L7.8 9 .1 16.6l.006.02a2.24 2.24 0 00.175.38l.01.018a2.044 2.044 0 00.187.26c.034.04.063.083.1.12l.032.031a2.017 2.017 0 00.642.42L9 10.2z"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#fff",
        fillRule: "evenodd",
        d: "M6.8 12v6h4.4v-6H18V6.1h-6.7V0H6.8v6.1H0V12h6.8"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#de0000",
        fillRule: "evenodd",
        d: "M7.6 10.9L7.6 18 10.4 18 10.4 10.9 18 10.9 18 7.2 10.4 7.2 10.4 0 9 0 7.6 0 7.6 7.2 0 7.2 0 10.9 7.6 10.9"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#165cb5",
        d: "M14.474 17.5H11.2v.5H15l-.526-.5M18 12h-.5v2.1l.5.5V12M0 3.4v2.7h.5V3.891L0 3.4m18 0l-.5.5v2.2h.5V3.4M6.8 0H3l.517.5H6.8V0M15 0h-3.7v.5h3.183L15 0"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#e5e5e5",
        d: "M1.6.041A2.008 2.008 0 000 2v1.4l.5.491V2v-.107L.1 1.5A1.548 1.548 0 01.9.4c.1-.1.2-.1.3-.2l.372.362A1.512 1.512 0 012 .5h1.517L3 0H2a1.972 1.972 0 00-.4.041zM16.777.158a2.114 2.114 0 00-.205-.064A1.975 1.975 0 0016 0h-1l-.517.5H16a1.487 1.487 0 01.571.113L16.9.3a2 2 0 011 1.2l-.4.4v2l.5-.5V2a1.973 1.973 0 00-.208-.869 2.017 2.017 0 00-1.015-.973zM16.732 17.833l-.38-.375A1.462 1.462 0 0116 17.5h-1.526L15 18h1a1.74 1.74 0 00.732-.167zM17.9 16.581A1.965 1.965 0 0018 16v-1.4l-.5-.5v2l.4.4c0 .036 0 .073-.006.109z"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#c70000",
        d: "M17.5 16.1a1.5 1.5 0 01-1.145 1.356l.38.375.017.018h.014a1.951 1.951 0 00.36-.194c.02-.014.036-.031.055-.046a2.021 2.021 0 00.254-.217c.032-.034.059-.072.089-.107a2.074 2.074 0 00.161-.212c.029-.045.052-.094.077-.141a2.009 2.009 0 00.106-.225c.011-.031.019-.063.029-.095s.006-.073.006-.109zM.117 1.354A1.3 1.3 0 01.1 1.5l.4.393A1.5 1.5 0 011.572.562L1.2.2 1.184.179A2.011 2.011 0 00.117 1.354zM17.9 1.5a2 2 0 00-1-1.2l-.329.313A1.5 1.5 0 0117.5 1.9z"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#e5e5e5",
        d: "M11.2 17.5h-.8v.5h.8v-.5m6.8-6.6h-.5V12h.5v-1.1M.5 6.1H0v1.1h.5V6.1m17.5 0h-.5v1.1h.5V6.1M7.6 0h-.8v.5h.8V0m3.7 0h-.9v.5h.9V0"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#165cb5",
        d: "M1.255 17.848v.005A1.984 1.984 0 002 18a1.915 1.915 0 01-.745-.152M6.8 17.5H3.526L3 18h3.8v-.5M.5 12H0v2.6l.5-.492V12"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#e5e5e5",
        d: "M1.648 17.458l-.395.39A1.915 1.915 0 002 18h1l.526-.5H2a1.462 1.462 0 01-.352-.042M.5 14.108L0 14.6V16a1.622 1.622 0 00.122.578l.39-.385A1.539 1.539 0 01.5 16v-1.892"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#c70000",
        d: "M.512 16.193l-.39.385L.1 16.6a2.18 2.18 0 00.407.727 2.019 2.019 0 00.742.526v-.005l.4-.39a1.5 1.5 0 01-1.137-1.265"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#e5e5e5",
        d: "M7.6 17.5h-.8v.5h.8v-.5M.5 10.9H0V12h.5v-1.1"
      }
    ),
    /* @__PURE__ */ React142.createElement(
      "path",
      {
        fill: "#c70000",
        d: "M10.4 17.5H7.6v.5h2.8v-.5M.5 7.2H0v3.7h.5V7.2m17.5 0h-.5v3.7h.5V7.2M10.4 0H7.6v.5h2.8V0"
      }
    )
  );
}

// src/components/icons/flags/mini-currency-calculator/ISK.tsx
import styled28 from "@emotion/styled";
import React143 from "react";
var SVG3 = styled28.svg`
  height: ${(props) => props.isMobile ? "28px" : "18px"};
  width: ${(props) => props.isMobile ? "28px" : "18px"};
  .st0 {
    fill-rule: evenodd;
    clip-rule: evenodd;
    fill: #1966ca;
  }
  .st1 {
    fill-rule: evenodd;
    clip-rule: evenodd;
    fill: #ffffff;
  }
  .st2 {
    fill-rule: evenodd;
    clip-rule: evenodd;
    fill: #de0000;
  }
  .st3 {
    fill: #165cb5;
  }
  .st4 {
    fill: #e5e5e5;
  }
  .st5 {
    fill: #c70000;
  }
`;
function ISK() {
  const isMobile = useMedia("sm");
  return /* @__PURE__ */ React143.createElement(
    SVG3,
    {
      isMobile,
      xmlns: "http://www.w3.org/2000/svg",
      x: "0",
      y: "0",
      enableBackground: "new 0 0 18 18",
      version: "1.1",
      viewBox: "0 0 18 18",
      xmlSpace: "preserve",
      "aria-hidden": true
    },
    /* @__PURE__ */ React143.createElement(
      "path",
      {
        d: "M2 0h14c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2H2c-1.1 0-2-.9-2-2V2C0 .9.9 0 2 0",
        className: "st0"
      }
    ),
    /* @__PURE__ */ React143.createElement(
      "path",
      {
        d: "M6.6 18L10.9 18 10.9 11.2 18 11.2 18 6.8 10.9 6.8 10.9 0 6.6 0 6.6 6.8 0 6.8 0 11.2 6.6 11.2 6.6 18",
        className: "st1"
      }
    ),
    /* @__PURE__ */ React143.createElement(
      "path",
      {
        d: "M0 10.1L7.7 10.1 7.7 18 9.9 18 9.9 10.1 18 10.1 18 7.9 9.9 7.9 9.9 0 7.7 0 7.7 7.9 0 7.9 0 10.1",
        className: "st2"
      }
    ),
    /* @__PURE__ */ React143.createElement(
      "path",
      {
        d: "M18 11.1h-.5V16c0 .8-.7 1.5-1.5 1.5h-5.1v.5H16c1.1 0 2-.9 2-2v-4.9M6.6 0H2C.9 0 0 .9 0 2v4.8h.5V2C.5 1.2 1.2.5 2 .5h4.6V0M16 0h-5.1v.5H16c.8 0 1.5.7 1.5 1.5v4.8h.5V2c0-1.1-.9-2-2-2",
        className: "st3"
      }
    ),
    /* @__PURE__ */ React143.createElement(
      "path",
      {
        d: "M10.9 17.5h-1v.5h1v-.5m7.1-7.4h-.5v1.1h.5v-1.1M.5 6.8H0v1.1h.5V6.8m17.5 0h-.5v1.1h.5V6.8M7.7 0H6.6v.5h1.1V0m3.2 0h-1v.5h1V0",
        className: "st4"
      }
    ),
    /* @__PURE__ */ React143.createElement(
      "path",
      {
        d: "M.5 11.2H0V16c0 1.1.9 2 2 2h4.6v-.5H2c-.8 0-1.5-.7-1.5-1.5v-4.8",
        className: "st3"
      }
    ),
    /* @__PURE__ */ React143.createElement(
      "path",
      {
        d: "M7.7 17.5H6.6v.5h1.1v-.5M.5 10.1H0v1.1h.5v-1.1",
        className: "st4"
      }
    ),
    /* @__PURE__ */ React143.createElement(
      "path",
      {
        d: "M9.9 17.5H7.7v.5h2.2v-.5M.5 7.9H0v2.2h.5V7.9m17.5 0h-.5v2.2h.5V7.9M9.9 0H7.7v.5h2.2V0",
        className: "st5"
      }
    )
  );
}

// src/components/icons/flags/mini-currency-calculator/PLN.tsx
import styled29 from "@emotion/styled";
import React144 from "react";
var SVG4 = styled29.svg`
  height: ${(props) => props.isMobile ? "28px" : "18px"};
  width: ${(props) => props.isMobile ? "28px" : "18px"};
`;
function PLN() {
  const isMobile = useMedia("sm");
  return /* @__PURE__ */ React144.createElement(
    SVG4,
    {
      isMobile,
      xmlns: "http://www.w3.org/2000/svg",
      "data-name": "Layer 1",
      viewBox: "0 0 18 18",
      "aria-hidden": true
    },
    /* @__PURE__ */ React144.createElement("rect", { width: "18", height: "18", fill: "#fff", opacity: "0.1", rx: "2" }),
    /* @__PURE__ */ React144.createElement(
      "path",
      {
        fill: "#de0000",
        fillRule: "evenodd",
        d: "M0 9.026V16a2.006 2.006 0 002 2h14a2.006 2.006 0 002-2V9.026z"
      }
    ),
    /* @__PURE__ */ React144.createElement(
      "path",
      {
        d: "M16 .5A1.5 1.5 0 0117.5 2v14a1.5 1.5 0 01-1.5 1.5H2A1.5 1.5 0 01.5 16V2A1.5 1.5 0 012 .5h14m0-.5H2a2.006 2.006 0 00-2 2v14a2.006 2.006 0 002 2h14a2.006 2.006 0 002-2V2a2.006 2.006 0 00-2-2z",
        opacity: "0.1"
      }
    )
  );
}

// src/components/icons/flags/mini-currency-calculator/USD.tsx
import styled30 from "@emotion/styled";
import React145 from "react";
var SVG5 = styled30.svg`
  height: ${(props) => props.isMobile ? "28px" : "18px"};
  width: ${(props) => props.isMobile ? "28px" : "18px"};
  .st0 {
    fill-rule: evenodd;
    clip-rule: evenodd;
    fill: #ffffff;
  }
  .st1 {
    fill: #de0000;
  }
  .st2 {
    fill: #1966ca;
  }
  .st3 {
    fill: #ffffff;
  }
  .st4 {
    fill-rule: evenodd;
    clip-rule: evenodd;
    fill: #de0000;
  }
  .st5 {
    fill: #bfbfbf;
  }
  .st6 {
    fill: #a60000;
  }
  .st7 {
    fill: #134c97;
  }
`;
function USD() {
  const isMobile = useMedia("sm");
  return /* @__PURE__ */ React145.createElement(
    SVG5,
    {
      isMobile,
      xmlns: "http://www.w3.org/2000/svg",
      x: "0",
      y: "0",
      enableBackground: "new 0 0 18 18",
      version: "1.1",
      viewBox: "0 0 18 18",
      xmlSpace: "preserve",
      "aria-hidden": true
    },
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M3 0h13.3c.9 0 1.7.8 1.7 1.7v14.6c0 .9-.8 1.7-1.7 1.7H1.7C.8 18 0 17.2 0 16.3V3c0-1.7 1.3-3 3-3",
        className: "st0"
      }
    ),
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M18 2.8v1.4H0V2.8h18M0 5.5h18v1.4H0V5.5m0 2.8h18v1.4H0V8.3m0 2.8h18v1.4H0v-1.4m0 2.7h18v1.4H0v-1.4M18 1.4H1.2C1.2.6 1.8 0 2.6 0h14.1c.7 0 1.3.6 1.3 1.4",
        className: "st1"
      }
    ),
    /* @__PURE__ */ React145.createElement("path", { d: "M1.4 0h8.3v9.7H0V1.4C0 .6.6 0 1.4 0", className: "st2" }),
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M8.6 2.7l-.1-.4-.1.4H8l.3.3-.1.3.3-.2.3.2-.1-.3.3-.3h-.4M1.9 1l.3-.2h-.4L1.7.4l-.1.4h-.4l.3.2-.1.4.3-.2.3.2-.1-.4m2.3 0l.3-.2h-.4L4 .4l-.2.4h-.4l.4.2-.2.4.4-.2.3.2-.1-.4m2.3 0l.3-.2h-.4L6.2.4l-.1.4h-.4L6 1l-.1.4.3-.2.3.2V1m2.2 0L9 .8h-.4L8.5.4l-.1.4H8l.3.2-.1.4.3-.2.3.2-.1-.4M.8 4l.3-.2H.7l-.1-.4-.1.4H.1l.3.2-.1.4.3-.2.3.2L.8 4m0 1.9l.3-.2H.7l-.1-.4-.1.4H.1l.3.2-.2.4.4-.3.3.2-.1-.3m0 1.9l.3-.2H.8l-.2-.4-.1.3H.1l.3.2-.1.5.3-.2.3.2-.1-.4m2.3 0l.3-.2H3l-.1-.4-.1.3h-.4l.3.2-.1.5.3-.2.3.2-.1-.4m0-1.9l.3-.2H3l-.2-.4-.1.4h-.4l.3.2-.1.4.4-.3.3.2-.1-.3m0-1.9l.3-.2H3l-.1-.4-.2.4h-.4l.4.2-.2.4.3-.2.3.2V4M.8 2l.3-.2H.7l-.1-.4-.2.4H0l.4.2-.2.4.3-.2.3.2V2m4.5 2l.3-.2h-.3l-.2-.4-.1.4h-.4l.3.2-.1.4.3-.2.3.2-.1-.4m0 1.9l.3-.2h-.4l-.1-.4-.1.4h-.4l.3.2-.1.4.3-.3.3.2-.1-.3m.1 1.9l.3-.2h-.4l-.1-.4-.2.3h-.4l.3.2-.1.5.3-.2.3.2v-.4m2.2 0l.4-.3h-.4l-.2-.3-.1.3h-.4l.3.2-.1.5.3-.2.3.2-.1-.4m0-1.9l.3-.2h-.4l-.1-.4-.1.4h-.4l.3.2-.2.4.4-.3.3.2-.1-.3m0-1.9l.3-.2h-.4l-.1-.4-.1.4h-.4l.3.2-.1.4.3-.2.3.2-.1-.4M3 2l.3-.2H3l-.2-.4-.1.4h-.4l.3.2-.1.4.3-.2.3.2L3 2m2.3 0l.3-.2h-.4l-.1-.4-.1.4h-.4l.3.2-.1.4.3-.2.3.2-.1-.4m2.3 0l.3-.2h-.4l-.1-.4-.2.4h-.4l.4.2-.2.4.3-.2.3.2V2M2 8.8l.3-.2h-.4l-.1-.5-.2.4h-.4l.3.2-.1.4.3-.2.3.2v-.3m-.1-1.9l.3-.2h-.4l-.1-.5-.1.4h-.4l.3.2-.1.4.3-.2.3.2-.1-.3m0-1.9l.3-.2h-.3l-.2-.4-.1.4h-.4l.3.2-.1.4.3-.2.3.2-.1-.4m2.3 0l.3-.2h-.4L4 4.4l-.1.4h-.4l.3.2-.1.4.3-.3.3.2-.1-.3m0 1.9l.3-.2h-.4L4 6.2l-.1.4h-.4l.3.2-.1.4L4 7l.3.2-.1-.3m0 1.9l.3-.2h-.3L4 8.1l-.1.4h-.4l.3.2-.1.4.3-.2.3.2-.1-.3m2.3 0l.3-.2h-.4l-.1-.5-.1.4h-.4l.3.2-.1.4.3-.2.3.2-.1-.3m2.3 0l.3-.2h-.4l-.1-.5-.2.4H8l.3.2-.1.4.3-.2.3.2v-.3M6.5 6.9l.3-.2h-.4l-.2-.5-.1.4h-.4l.3.3-.1.3.3-.2.3.2v-.3m2.2 0l.3-.2h-.4l-.1-.5-.1.4H8l.3.2-.1.4.3-.2.3.2-.1-.3M6.5 5l.3-.2h-.4l-.1-.4-.2.4h-.4l.4.2-.2.4.3-.2.3.2V5m2.2 0l.3-.2h-.3l-.2-.4-.1.4H8l.3.2-.1.4.3-.2.3.2-.1-.4M1.9 3l.3-.2h-.4l-.1-.5-.1.4h-.4l.3.3-.1.3.3-.2.3.2-.1-.3m2.3 0l.3-.2h-.4L4 2.3l-.2.4h-.4l.4.3-.2.3.4-.2.3.2-.1-.3m2.3 0l.3-.2h-.4l-.2-.5-.1.4h-.4L6 3l-.1.3.3-.2.3.2V3",
        className: "st3"
      }
    ),
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M.1 16.5H18c0 .8-.7 1.5-1.5 1.5h-15c-.7.1-1.4-.4-1.5-1.1 0-.1 0-.2.1-.4",
        className: "st4"
      }
    ),
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M18 15.1h-.5v1.4h.5v-1.4m0-2.7h-.5v1.4h.5v-1.4m0-2.8l-.5.1v1.4h.5V9.6m0-2.8h-.5v1.5h.5V6.8m0-2.7h-.5v1.4h.5V4.1M0 2.9v13.3c0 .2 0 .8.1 1v-.7h.4v-1.3H0v-1.4h.5v-1.4H0v-1.3h.5V9.6H0V2.9m18-1.6h-.5v1.5h.5V1.3",
        className: "st5"
      }
    ),
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M.5 13.8H0v1.4h.5v-1.4m17.5 0h-.5v1.4h.5v-1.4M.5 11.1H0v1.4h.5v-1.4m17.5 0h-.5v1.4h.5v-1.4m0-2.8h-.5v1.4h.5V8.3m0-2.8h-.5v1.4h.5V5.5m0-2.7h-.5v1.4h.5V2.8M16.6 0h-7v.5h7c.5 0 .8.4.9.9h.5c0-.8-.6-1.4-1.4-1.4",
        className: "st6"
      }
    ),
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M9.7 0H1.3C1.1 0 1 .1.8.1.3.4 0 .8 0 1.4v8.3h.5V8l-.2.2.1-.4-.3-.3h.4V6.1l-.3.2.1-.4-.2-.2h.4V4.2l-.2.2.1-.4-.3-.2h.4V2.2l-.3.2.2-.4-.4-.2h.4l.1-.2v-.2c0-.5.4-.8.9-.9h.3V.4v.1h2.2V.4v.1h2.2V.4v.1h2.2V.4v.1h1.4V0",
        className: "st7"
      }
    ),
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M.5 7.5H.1l.3.2-.1.5.2-.2v-.5m0-1.9l-.4.1.3.2-.2.4.2-.2.1-.5m0-1.9l-.4.1.3.2-.1.4.2-.2v-.5m0-2.1l-.1.2H0l.4.2-.2.4.3-.2v-.6M1.7.4l.1.1-.1-.1m6.8 0l.1.1-.1-.1",
        className: "st5"
      }
    ),
    /* @__PURE__ */ React145.createElement(
      "path",
      {
        d: "M18 16.5h-.5v.2c0 .5-.4.8-.9.9H1.3c-.5 0-.7-.4-.8-.9v-.2H0v.3c0 .7.7 1.3 1.4 1.2h15.1c.8 0 1.4-.6 1.5-1.3v-.2",
        className: "st6"
      }
    )
  );
}

// src/components/icons/flags/mini-currency-calculator/CurrencyIcon.tsx
function CurrencyIcon({ icon }) {
  switch (icon) {
    case "ISK":
      return /* @__PURE__ */ React146.createElement(ISK, null);
    case "EUR":
      return /* @__PURE__ */ React146.createElement(EUR, null);
    case "USD":
      return /* @__PURE__ */ React146.createElement(USD, null);
    case "GBP":
      return /* @__PURE__ */ React146.createElement(GBP, null);
    case "PLN":
      return /* @__PURE__ */ React146.createElement(PLN, null);
    default:
      return /* @__PURE__ */ React146.createElement(React146.Fragment, null);
  }
}

// src/components/icons/flags/mini-currency-calculator/GengiArrow.tsx
import React147 from "react";
function GengiArrow() {
  return /* @__PURE__ */ React147.createElement("svg", { width: "21", height: "10", viewBox: "0 0 21 10" }, /* @__PURE__ */ React147.createElement("g", { fill: "none", fillRule: "evenodd", stroke: "none", strokeWidth: "1" }, /* @__PURE__ */ React147.createElement("g", { fill: "#1E3D5A", fillRule: "nonzero", transform: "translate(-340 -1814)" }, /* @__PURE__ */ React147.createElement("path", { d: "M351.517 1814.469l8.983 4.533-9.016 4.467.014-4.001-10.5-.038-.5-.002.004-1 .5.002 10.499.038.016-4z" }))));
}

// src/components/icons/flags/mobile/MobileFlags.tsx
import React172 from "react";

// src/components/icons/flags/mobile/Aud.tsx
import React148 from "react";
function Aud2(props) {
  return /* @__PURE__ */ React148.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React148.createElement("defs", null, /* @__PURE__ */ React148.createElement(
    "linearGradient",
    {
      id: "prefix__a",
      y1: 0.011,
      x2: 1,
      y2: 0.011,
      gradientTransform: "scale(-44.615 44.615) rotate(21 -.254 -1.719)",
      gradientUnits: "userSpaceOnUse"
    },
    /* @__PURE__ */ React148.createElement("stop", { offset: 0, stopColor: "#f6f4f4" }),
    /* @__PURE__ */ React148.createElement("stop", { offset: 0.45, stopColor: "#f6f4f4" }),
    /* @__PURE__ */ React148.createElement("stop", { offset: 0.8, stopColor: "#f6f4f4" }),
    /* @__PURE__ */ React148.createElement("stop", { offset: 1, stopColor: "#f6f4f4" })
  ), /* @__PURE__ */ React148.createElement(
    "linearGradient",
    {
      id: "prefix__c",
      y1: 0.011,
      x2: 1,
      y2: 0.011,
      gradientTransform: "scale(-42.225 42.225) rotate(21 -.17 -1.696)",
      xlinkHref: "#prefix__a"
    }
  ), /* @__PURE__ */ React148.createElement(
    "linearGradient",
    {
      id: "prefix__b",
      y1: -0.074,
      x2: 1,
      y2: -0.074,
      gradientTransform: "scale(-44.615 44.615) rotate(21 -.254 -1.719)",
      gradientUnits: "userSpaceOnUse"
    },
    /* @__PURE__ */ React148.createElement("stop", { offset: 0, stopColor: "#dddbdb" }),
    /* @__PURE__ */ React148.createElement("stop", { offset: 0.45, stopColor: "#dddbdb" }),
    /* @__PURE__ */ React148.createElement("stop", { offset: 0.8, stopColor: "#dddbdb" }),
    /* @__PURE__ */ React148.createElement("stop", { offset: 1, stopColor: "#dddbdb" })
  ), /* @__PURE__ */ React148.createElement(
    "linearGradient",
    {
      id: "prefix__d",
      y1: -0.066,
      y2: -0.066,
      gradientTransform: "scale(-42.225 42.225) rotate(21 -.17 -1.696)",
      xlinkHref: "#prefix__b"
    }
  )), /* @__PURE__ */ React148.createElement("path", { fill: "#1966ca", fillRule: "evenodd", d: "M0 0h28v28H0V0" }), /* @__PURE__ */ React148.createElement(
    "path",
    {
      d: "M2.606 8.307h-.679L1 8.925v.965l1.606-1.07v-.513m3.772 0H5.7v.513l5.5 3.667h1.45l-6.272-4.18m3.187 0H8.117l5.4 3.6v-.965L9.565 8.307M1 2.6v.966l.927.618h.679v-.516L1 2.6M11.15 1H9.7l-4 2.667v.514h.678L11.15 1m2.369 0h-.631L8.117 4.181h1.448l3.954-2.637V1",
      fill: "url(#prefix__a)"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      fill: "#de0000",
      d: "M13.519 0h-.869L4.153 5.665 0 2.896v1.158l3.284 2.19L0 8.433v1.158l4.153-2.768 8.497 5.664h.869v-.579L5.022 6.244 13.519.579V0"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      d: "M13.519 7.531h-8.4v4.956H3.187V7.531H1v.776h1.606v4.18H5.7v-4.18h7.818v-.776M3.187 1h-.581v3.181H1v.775h2.187V1M5.7 1h-.581v3.956h8.4v-.775H5.7V1",
      fill: "url(#prefix__c)"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      fill: "#de0000",
      fillRule: "evenodd",
      d: "M5.119 4.956V0H3.187v4.956H0v2.575h3.187v4.956h1.932V7.531h8.4V4.956h-8.4"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      fill: "#fff",
      fillRule: "evenodd",
      d: "M21.533 16.319l.258.803.788-.299-.467.702.725.43-.839.073.115.835-.58-.611-.581.611.115-.835-.838-.073.724-.43-.467-.702.788.299.259-.803zM16.85 8.905l.257.803.789-.299-.466.702.724.43-.84.073.117.834-.581-.61-.58.61.115-.834-.839-.073.724-.43-.466-.702.788.299.258-.803zM21.533 3.832l.258.802.788-.298-.467.702.725.43-.839.073.115.834-.58-.61-.581.61.115-.834-.838-.073.724-.43-.467-.702.788.298.259-.802zM25.695 7.656l.259.802.788-.298-.467.702.725.43-.839.073.115.835-.581-.611-.58.611.115-.835-.839-.073.724-.43-.466-.702.789.298.257-.802zM23.406 11.414l.204.5.538.039-.412.349.128.524-.458-.285-.459.285.129-.524-.413-.349.539-.039.204-.5zM4.26 15.768l.542 1.684 1.656-.627-.98 1.474L7 19.202l-1.763.153.242 1.753-1.219-1.282-1.218 1.282.242-1.753-1.763-.153 1.522-.903-.979-1.474 1.655.627.541-1.684z"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      d: "M2.606 0H0v1.931L1 2.6V1h1.606V0m8.6 0H5.7v1h4l1.5-1M28 0H13.519v1H27v26H1V9.89l-1 .667V28h28V0",
      fill: "#165cb5"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      d: "M1 8.925l-1 .666v.966l1-.667v-.965m-.81-.618H0v.126l.19-.126M0 4.054v.127h.19L0 4.054m0-2.123V2.9l1 .667V2.6l-1-.669M13.519.579L12.888 1h.631V.579M12.65 0H11.2L9.7 1h1.45l1.5-1",
      fill: "url(#prefix__b)"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      d: "M1 8.307H.19L0 8.433v1.158l1-.666v-.618M0 2.9v1.154l.19.127H1v-.618L0 2.9M13.519 0h-.869l-1.5 1h1.738l.631-.421V0",
      fill: "#c70000"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      d: "M1 7.531H0v.776h1v-.776m0-3.35H0v.775h1v-.775M3.187 0h-.581v1h.581V0M5.7 0h-.581v1H5.7V0",
      fill: "url(#prefix__d)"
    }
  ), /* @__PURE__ */ React148.createElement(
    "path",
    {
      d: "M1 4.956H0v2.575h1V4.956M5.119 0H3.187v1h1.932V0",
      fill: "#c70000"
    }
  ));
}

// src/components/icons/flags/mobile/Cad.tsx
import React149 from "react";
function Cad2(props) {
  return /* @__PURE__ */ React149.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React149.createElement("path", { fill: "#fff", fillRule: "evenodd", d: "M0 0h28v28H0V0" }), /* @__PURE__ */ React149.createElement(
    "path",
    {
      d: "M8 14.121l2.741 2.379c.5.436.113.881-.128 1.3l3.145-.387-.1 3.146h.677l-.146-3.146 3.146.387c-.226-.451-.5-1-.025-1.4L20 14.121c-.258-.1-.561-.141-.621-.431a5.453 5.453 0 01.473-2.06 4.194 4.194 0 01-1.717.343c-.253-.026-.4-.677-.5-.9l-1.414 1.5c-.376.4-.52.214-.413-.3l.663-3.183-.947.535c-.279.157-.371.174-.528-.148L14 7.443l-1.02 2.04c-.158.316-.173.323-.471.16l-.928-.506.568 3.149c.091.5 0 .679-.4.246l-1.331-1.46a1.933 1.933 0 01-.532.92 5.034 5.034 0 01-1.694-.339 5.73 5.73 0 01.551 1.979c-.05.329-.443.374-.743.489zM0 0h6v28H0V0M22 0h6v28h-6V0",
      fill: "#de0000",
      fillRule: "evenodd"
    }
  ), /* @__PURE__ */ React149.createElement("path", { d: "M22 27H6v1h16v-1m0-27H6v1h16V0", fill: "#e5e5e5" }), /* @__PURE__ */ React149.createElement("path", { fill: "#c70000", d: "M6 0H0v28h6v-1H1V1h5V0M28 0h-6v1h5v26h-5v1h6V0" }));
}

// src/components/icons/flags/mobile/Chf.tsx
import React150 from "react";
function Chf2(props) {
  return /* @__PURE__ */ React150.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React150.createElement("path", { fill: "#de0000", fillRule: "evenodd", d: "M0 28h28V0H0v28" }), /* @__PURE__ */ React150.createElement(
    "path",
    {
      fill: "#fff",
      fillRule: "evenodd",
      d: "M7 16.1h4.9V21h4.201v-4.9H21v-4.2h-4.899V7H11.9v4.9H7v4.2z"
    }
  ), /* @__PURE__ */ React150.createElement("path", { d: "M1 27V1h26v26H1M28 0H0v28h28V0", fill: "#c70000" }));
}

// src/components/icons/flags/mobile/Cny.tsx
import React151 from "react";
function Cny2(props) {
  return /* @__PURE__ */ React151.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#4c4c4c", fillRule: "evenodd", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React151.createElement("path", { fill: "#de0000", fillRule: "evenodd", d: "M0 28h28V0H0v28" }), /* @__PURE__ */ React151.createElement(
    "path",
    {
      d: "M7 3.563l1 3.081 3.237-.006-2.622 1.9 1.006 3.077L7 9.705l-2.612 1.908 1.006-3.077-2.623-1.9 3.237.006zm8.78 10.213l-1.03.349.018 1.088-.651-.873-1.028.353.628-.888-.653-.869 1.038.324.626-.89.013 1.087zm2.861-3.355l-.893.622.32 1.04-.867-.656-.889.625.356-1.028-.87-.653 1.088.022.351-1.029.317 1.04zm-.257-4.835l-.628.888.654.869-1.039-.323-.624.89-.014-1.087-1.04-.32 1.03-.348-.023-1.088.65.872zM14.8 2.159l-.01 1.087 1.03.346-1.038.322L14.771 5l-.626-.889-1.039.327.652-.872-.631-.885 1.03.35z",
      fill: "#ffd500",
      fillRule: "evenodd"
    }
  ), /* @__PURE__ */ React151.createElement("path", { d: "M1 27V1h26v26H1M28 0H0v28h28V0", fill: "#c70000" }));
}

// src/components/icons/flags/mobile/Czk.tsx
import React152 from "react";
function Czk2(props) {
  return /* @__PURE__ */ React152.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React152.createElement(
    "polyline",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#DE0000",
      points: "0,28 28,28 28,14 0,14 0,28"
    }
  ), /* @__PURE__ */ React152.createElement(
    "polyline",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#FFFFFF",
      points: "0,14 28,14 28,0 0,0 0,14"
    }
  ), /* @__PURE__ */ React152.createElement(
    "polyline",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#1966CA",
      points: "21,14 0,0 0,28 21,14"
    }
  ), /* @__PURE__ */ React152.createElement(
    "polyline",
    {
      fill: "#C70000",
      points: "28,14 27,14 27,27 1.5,27 0,28 28,28 28,14"
    }
  ), /* @__PURE__ */ React152.createElement("polyline", { fill: "#E5E5E5", points: "28,0 0,0 1.5,1 27,1 27,14 28,14 28,0" }), /* @__PURE__ */ React152.createElement("polyline", { fill: "#165CB5", points: "0,0 0,28 1.5,27 1,27 1,1 1.5,1 0,0" }));
}

// src/components/icons/flags/mobile/DefaultFlag.tsx
import React153 from "react";
function DefaultFlag2(props) {
  return /* @__PURE__ */ React153.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React153.createElement("path", { fill: "#1966ca", fillRule: "evenodd", d: "M0 0h28v28H0V0" }), /* @__PURE__ */ React153.createElement("path", { d: "M1 27V1h26v26H1M28 0H0v28h28V0", fill: "#165cb5" }));
}

// src/components/icons/flags/mobile/Dkk.tsx
import React154 from "react";
function Dkk2(props) {
  return /* @__PURE__ */ React154.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React154.createElement("path", { fill: "#de0000", d: "M0 0h28v28H0V0" }), /* @__PURE__ */ React154.createElement(
    "path",
    {
      fill: "#fff",
      d: "M8.629 0h5.236v11.34H28v5.25H13.865V28H8.629V16.59H0v-5.25h8.629V0"
    }
  ), /* @__PURE__ */ React154.createElement(
    "path",
    {
      d: "M1 16.59H0V28h8.629v-1H1V16.59m27 0h-1V27H13.865v1H28V16.59M8.629 0H0v11.34h1V1h7.629V0M28 0H13.865v1H27v10.34h1V0",
      fill: "#c70000"
    }
  ), /* @__PURE__ */ React154.createElement(
    "path",
    {
      d: "M13.865 27H8.629v1h5.236v-1M1 11.34H0v5.25h1v-5.25m27 0h-1v5.25h1v-5.25M13.865 0H8.629v1h5.236V0",
      fill: "#e5e5e5"
    }
  ));
}

// src/components/icons/flags/mobile/Eur.tsx
import React155 from "react";
function Eur2(props) {
  return /* @__PURE__ */ React155.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React155.createElement("path", { fill: "#1966ca", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React155.createElement(
    "path",
    {
      d: "M9.7 5.174l.358 1 1.059.033-.838.648.3 1.017-.876-.6-.875.6.3-1.017-.846-.655 1.059-.033zm8.528 14.933l.358 1 1.059.033-.838.648.3 1.018-.875-.6-.875.6.295-1.018-.836-.648 1.058-.033zm-8.528 0l.358 1 1.059.033-.838.648.3 1.018-.876-.6-.875.6.3-1.018-.838-.648 1.051-.04zm8.527-14.933l.358 1 1.059.033-.838.648.3 1.017-.875-.6-.875.6.295-1.017-.84-.655 1.058-.033zm3.165 11.805l.358 1 1.059.032-.838.648.3 1.017-.876-.6-.875.6.3-1.017-.836-.648 1.058-.032zm0-8.639l.358 1 1.059.033-.838.649.3 1.016-.876-.6-.875.6.3-1.016-.836-.649 1.058-.033zM6.571 16.979l.358 1 1.058.032-.837.648.3 1.017-.876-.6-.875.6.3-1.017-.837-.648 1.058-.032zM22.583 12.7l.358 1 1.059.026-.837.649.3 1.017-.876-.6-.875.6.3-1.017-.837-.649 1.059-.032zm-17.166 0l.357 1 1.059.032-.833.643.3 1.017-.875-.6-.875.6.3-1.017-.85-.649 1.059-.032zm1.154-4.36l.358 1 1.058.033-.837.649.3 1.016-.876-.6-.875.6.3-1.016-.837-.649L6.22 9.34zm7.394 12.942l.358 1 1.059.034-.838.648.3 1.017-.876-.6-.875.6.3-1.017-.838-.648 1.059-.034zm0-17.259l.358 1 1.059.032-.838.648.3 1.018-.876-.6-.875.6.3-1.018-.838-.648 1.059-.032z",
      fill: "#ffd500",
      fillRule: "evenodd"
    }
  ), /* @__PURE__ */ React155.createElement("path", { d: "M1 27V1h26v26H1M28 0H0v28h28V0", fill: "#165cb5" }));
}

// src/components/icons/flags/mobile/Gbp.tsx
import React156 from "react";
function Gbp2(props) {
  return /* @__PURE__ */ React156.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React156.createElement("path", { fill: "#1966ca", fillRule: "evenodd", d: "M28 0H0v28h28V0" }), /* @__PURE__ */ React156.createElement(
    "path",
    {
      fill: "#fff",
      d: "M24.389 28H28v-4.932L18.59 14 28 4.932V0h-3.611L14.226 9.794 4.063 0H0v4.495L9.862 14 0 23.505V28h4.063l10.163-9.794L24.389 28"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      fill: "#de0000",
      d: "M27.116 28H28v-2.303L15.862 14 28 2.303V0h-.884l-12.89 12.422L1.336 0H0v1.867L12.59 14 0 26.133V28h1.336l12.89-12.423L27.116 28"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      fill: "#fff",
      fillRule: "evenodd",
      d: "M13.979 28h3.711v-9.374H28V9.375H17.69V0h-6.938v9.375H0v9.251h10.752V28h3.227"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      fill: "#de0000",
      fillRule: "evenodd",
      d: "M13.979 28h2.409V16.888H28v-5.776H16.388V0h-4.332v11.112H0v5.776h12.056V28h1.923"
    }
  ), /* @__PURE__ */ React156.createElement("path", { fill: "#e5e5e5", d: "M28 0H0 28v28V0" }), /* @__PURE__ */ React156.createElement(
    "path",
    {
      d: "M23.351 27H17.69v1h6.7l-1.038-1M28 18.626h-1v3.479l1 .963v-4.442m0-13.694L27 5.9v3.48h1V4.932M0 4.5v4.88h1V5.459L0 4.5M10.752 0H4.063L5.1 1h5.651V0m13.638 0h-6.7v1h5.661l1.038-1",
      fill: "#165cb5"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      d: "M26.079 27h-2.728l1.038 1h2.727l-1.037-1M27 22.105v2.629l1 .963v-2.629l-1-.963M28 2.3l-1 .963V5.9l1-.963V2.3M0 1.867V4.5l1 .964V2.83l-1-.963M4.063 0H1.336l1.038 1H5.1L4.063 0m23.053 0h-2.727l-1.038 1h2.728l1.037-1",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      d: "M27 24.734V27h-.921l1.037 1H28v-2.3l-1-.963M1.336 0H0v1.867l1 .963V1h1.374L1.336 0M28 0h-.884l-1.037 1H27v2.266l1-.966V0",
      fill: "#c70000"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      d: "M17.69 27h-1.3v1h1.3v-1M28 16.888h-1v1.738h1v-1.738M1 9.375H0v1.737h1V9.375m27 0h-1v1.737h1V9.375M12.056 0h-1.3v1h1.3V0m5.634 0h-1.3v1h1.3V0",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      d: "M10.752 27H5.1l-1.037 1h6.689v-1M1 18.626H0v4.879l1-.964v-3.915",
      fill: "#165cb5"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      d: "M5.1 27H2.374l-1.038 1h2.727L5.1 27M1 22.541l-1 .964v2.628l1-.964v-2.628",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React156.createElement("path", { fill: "#c70000", d: "M1 25.169l-1 .964V28h1.336l1.038-1H1v-1.831" }), /* @__PURE__ */ React156.createElement(
    "path",
    {
      d: "M12.056 27h-1.3v1h1.3v-1M1 16.888H0v1.738h1v-1.738",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React156.createElement(
    "path",
    {
      d: "M16.388 27h-4.332v1h4.332v-1M1 11.112H0v5.776h1v-5.776m27 0h-1v5.776h1v-5.776M16.388 0h-4.332v1h4.332V0",
      fill: "#c70000"
    }
  ));
}

// src/components/icons/flags/mobile/Hkd.tsx
import React157 from "react";
function Hkd2(props) {
  return /* @__PURE__ */ React157.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React157.createElement("path", { fill: "#de0000", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React157.createElement(
    "path",
    {
      d: "M13.142 14.011c-4.359-2.254-2.175-8.267 2.383-7.955-1.427.734-.723 1.611-.4 2.548.419 1.206-.358 2.1-1.292 2.675a1.772 1.772 0 00-.491 2.729 3.15 3.15 0 01-.657-4.122l-.1-.065a3.3 3.3 0 00.557 4.19zm.109.546c-3.488 3.452-8.535-.479-6.833-4.72.258 1.584 1.309 1.185 2.3 1.165 1.277-.026 1.885.988 2.147 2.055a1.771 1.771 0 002.445 1.307 3.151 3.151 0 01-4.124-.644l-.094.078a3.305 3.305 0 004.159.759zm-4.16-1.51l.215-.467-.478.188-.378-.348.032.513-.448.251.5.128.1.505.276-.435.511.061zm4.734 1.527c2.248 4.363-3.014 8-6.55 5.106 1.589.228 1.522-.894 1.8-1.846.358-1.226 1.508-1.5 2.6-1.433a1.772 1.772 0 001.98-1.941 3.151 3.151 0 01-1.85 3.742l.046.112a3.3 3.3 0 001.974-3.74zm-2.687 3.516l-.381-.345.036.513-.445.257.5.123.105.5.272-.438.512.055-.332-.394.21-.469zm2.753-3.907c4.834-.848 6.74 5.258 2.924 7.772.692-1.448-.4-1.719-1.225-2.267-1.065-.707-.99-1.888-.59-2.912a1.772 1.772 0 00-1.265-2.468 3.15 3.15 0 013.022 2.88l.121-.01a3.3 3.3 0 00-2.987-2.995zm2.557 3.617l-.443.262.5.117.112.5.266-.44.512.049-.335-.388.205-.472-.475.2-.385-.341zm-2.828-4.072c.819-4.839 7.212-4.588 8.3-.152-1.134-1.136-1.754-.2-2.548.4-1.021.766-2.106.3-2.942-.417a1.773 1.773 0 00-2.749.364 3.15 3.15 0 013.726-1.882l.03-.118a3.3 3.3 0 00-3.817 1.805zm4.26-1.2l.1.505.279-.432.511.063-.327-.4.219-.466-.479.186-.377-.351.029.514-.45.248zm-4.58-2.94l.511.061-.327-.4.215-.467-.478.189-.377-.349.031.513-.45.251.5.129.1.5z",
      fill: "#fff"
    }
  ), /* @__PURE__ */ React157.createElement("path", { d: "M1 27V1h26v26H1M28 0H0v28h28V0", fill: "#c70000" }));
}

// src/components/icons/flags/mobile/Huf.tsx
import React158 from "react";
function Huf2(props) {
  return /* @__PURE__ */ React158.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React158.createElement(
    "polyline",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#DE0000",
      points: "0,9.3 28,9.3 28,0 0,0 0,9.3"
    }
  ), /* @__PURE__ */ React158.createElement(
    "polyline",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#FFFFFF",
      points: "0,18.7 28,18.7 28,9.3 0,9.3 0,18.7"
    }
  ), /* @__PURE__ */ React158.createElement(
    "polyline",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#01A616",
      points: "0,28 28,28 28,18.7 0,18.7 0,28"
    }
  ), /* @__PURE__ */ React158.createElement(
    "polyline",
    {
      fill: "#C70000",
      points: "28,0 0,0 0,9.3 1,9.3 1,1 27,1 27,9.3 28,9.3 28,0"
    }
  ), /* @__PURE__ */ React158.createElement("path", { fill: "#E5E5E5", d: "M1,9.3H0v9.3h1V9.3 M28,9.3L28,9.3h-1v9.3h1V9.3" }), /* @__PURE__ */ React158.createElement(
    "polyline",
    {
      fill: "#019514",
      points: "28,18.7 28,18.7 27,18.7 27,27 1,27 1,18.7 0,18.7 0,28 28,28 28,18.7"
    }
  ));
}

// src/components/icons/flags/mobile/Isk.tsx
import React159 from "react";
function Isk(props) {
  return /* @__PURE__ */ React159.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React159.createElement("path", { fill: "#1966ca", fillRule: "evenodd", d: "M0 28h28V0H0v28" }), /* @__PURE__ */ React159.createElement(
    "path",
    {
      fill: "#fff",
      fillRule: "evenodd",
      d: "M7.295 28h6.72V17.359L28 17.344V10.64H14.015V0h-6.72v10.64H0v6.719h7.295V28"
    }
  ), /* @__PURE__ */ React159.createElement(
    "path",
    {
      fill: "#de0000",
      fillRule: "evenodd",
      d: "M0 15.681h8.975V28h3.36V15.681H28V12.32H12.335V0h-3.36v12.32H0v3.361"
    }
  ), /* @__PURE__ */ React159.createElement(
    "path",
    {
      d: "M28 17.344h-1V27H14.015v1H28V17.344M7.3 0H0v10.64h1V1h6.3V0M28 0H14.015v1H27v9.64h1V0",
      fill: "#165cb5"
    }
  ), /* @__PURE__ */ React159.createElement(
    "path",
    {
      d: "M14.015 27h-1.68v1h1.68v-1M28 15.681h-1v1.664h1v-1.664M1 10.64H0v1.68h1v-1.68m27 0h-1v1.68h1v-1.68M8.975 0H7.3v1h1.68V0m5.04 0h-1.68v1h1.68V0",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React159.createElement("path", { fill: "#165cb5", d: "M1 17.359H0V28h7.295v-1H1v-9.641" }), /* @__PURE__ */ React159.createElement(
    "path",
    {
      d: "M8.975 27H7.3v1h1.68v-1M1 15.681H0v1.678h1v-1.678",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React159.createElement(
    "path",
    {
      d: "M12.335 27h-3.36v1h3.36v-1M1 12.32H0v3.361h1V12.32m27 0h-1v3.361h1V12.32M12.335 0h-3.36v1h3.36V0",
      fill: "#c70000"
    }
  ));
}

// src/components/icons/flags/mobile/Jpy.tsx
import React160 from "react";
function Jpy2(props) {
  return /* @__PURE__ */ React160.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React160.createElement("path", { fill: "#fff", d: "M28 28H0V0h28v28" }), /* @__PURE__ */ React160.createElement(
    "path",
    {
      d: "M20.864 14A6.865 6.865 0 1014 20.865 6.863 6.863 0 0020.864 14z",
      fill: "#de0000",
      fillRule: "evenodd"
    }
  ), /* @__PURE__ */ React160.createElement("path", { d: "M1 27V1h26v26H1M28 0H0v28h28V0", fill: "#e5e5e5" }));
}

// src/components/icons/flags/mobile/Nok.tsx
import React161 from "react";
function Nok2(props) {
  return /* @__PURE__ */ React161.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React161.createElement("path", { fill: "#de0000", d: "M.001 0h28v28h-28V0" }), /* @__PURE__ */ React161.createElement(
    "path",
    {
      fill: "#fff",
      d: "M7.125 0h8.246v9.869H28v8.263H15.371V28H7.125v-9.868H0V9.869h7.125V0"
    }
  ), /* @__PURE__ */ React161.createElement(
    "path",
    {
      fill: "#1966ca",
      d: "M8.63 0h5.238v11.375h14.136v5.25H13.868V28H8.63V16.625H.004v-5.25H8.63V0"
    }
  ), /* @__PURE__ */ React161.createElement(
    "path",
    {
      d: "M1 18.132H0V28h7.125v-1H1v-8.868M7.125 0H0v9.869h1V1h6.125V0M28 0H15.371v1H27v8.869h1v1.506V0",
      fill: "#c70000"
    }
  ), /* @__PURE__ */ React161.createElement(
    "path",
    {
      d: "M8.63 27H7.125v1H8.63v-1M1 9.869H0v8.263h1v-1.507H0v-5.25h1V9.869m27 0h-1v1.506h1V9.869M8.63 0H7.125v1H8.63V0m6.741 0h-1.5v1h1.5V0",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React161.createElement(
    "path",
    {
      fill: "#c70000",
      d: "M28.001 16.625H28v1.507h-.999V27h-11.63v1h12.63V16.625"
    }
  ), /* @__PURE__ */ React161.createElement(
    "path",
    {
      d: "M15.371 27h-1.5v1h1.5v-1M28 16.625h-1v1.507h1v-1.507",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React161.createElement(
    "path",
    {
      d: "M13.868 27H8.63v1h5.238v-1M1 11.375H0v5.25h1v-5.25m27 0h-1v5.25h1v-5.25M13.868 0H8.63v1h5.238V0",
      fill: "#165cb5"
    }
  ));
}

// src/components/icons/flags/mobile/Nzd.tsx
import React162 from "react";
function Nzd2(props) {
  return /* @__PURE__ */ React162.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React162.createElement("defs", null, /* @__PURE__ */ React162.createElement(
    "linearGradient",
    {
      id: "prefix__a",
      y1: 0.011,
      x2: 1,
      y2: 0.011,
      gradientTransform: "scale(-44.615 44.615) rotate(21 -.254 -1.719)",
      gradientUnits: "userSpaceOnUse"
    },
    /* @__PURE__ */ React162.createElement("stop", { offset: 0, stopColor: "#f6f4f4" }),
    /* @__PURE__ */ React162.createElement("stop", { offset: 0.45, stopColor: "#f6f4f4" }),
    /* @__PURE__ */ React162.createElement("stop", { offset: 0.8, stopColor: "#f6f4f4" }),
    /* @__PURE__ */ React162.createElement("stop", { offset: 1, stopColor: "#f6f4f4" })
  ), /* @__PURE__ */ React162.createElement(
    "linearGradient",
    {
      id: "prefix__c",
      y1: 0.011,
      x2: 1,
      y2: 0.011,
      gradientTransform: "scale(-42.225 42.225) rotate(21 -.17 -1.696)",
      xlinkHref: "#prefix__a"
    }
  ), /* @__PURE__ */ React162.createElement(
    "linearGradient",
    {
      id: "prefix__b",
      y1: -0.074,
      x2: 1,
      y2: -0.074,
      gradientTransform: "scale(-44.615 44.615) rotate(21 -.254 -1.719)",
      gradientUnits: "userSpaceOnUse"
    },
    /* @__PURE__ */ React162.createElement("stop", { offset: 0, stopColor: "#dddbdb" }),
    /* @__PURE__ */ React162.createElement("stop", { offset: 0.45, stopColor: "#dddbdb" }),
    /* @__PURE__ */ React162.createElement("stop", { offset: 0.8, stopColor: "#dddbdb" }),
    /* @__PURE__ */ React162.createElement("stop", { offset: 1, stopColor: "#dddbdb" })
  ), /* @__PURE__ */ React162.createElement(
    "linearGradient",
    {
      id: "prefix__d",
      y1: -0.066,
      y2: -0.066,
      gradientTransform: "scale(-42.225 42.225) rotate(21 -.17 -1.696)",
      xlinkHref: "#prefix__b"
    }
  )), /* @__PURE__ */ React162.createElement("path", { fill: "#1966ca", fillRule: "evenodd", d: "M0 0h28v28H0V0" }), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M2.606 8.307h-.678L1 8.925v.965l1.606-1.07v-.513m3.772 0H5.7v.513l5.5 3.667h1.45l-6.272-4.18m3.187 0H8.117l5.4 3.6v-.965L9.565 8.307M1 2.6v.966l.927.618h.679v-.516L1 2.6M11.15 1H9.7l-4 2.667v.514h.678L11.15 1m2.369 0h-.631L8.117 4.181h1.448l3.954-2.637V1",
      fill: "url(#prefix__a)"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      fill: "#de0000",
      d: "M13.519 0h-.869L4.153 5.665 0 2.896v1.158l3.284 2.19L0 8.433v1.159l4.153-2.769 8.497 5.664h.869v-.579L5.022 6.244 13.519.579V0"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M13.519 7.531h-8.4v4.956H3.187V7.531H1v.776h1.606v4.18H5.7v-4.18h7.818v-.776M3.187 1h-.581v3.181H1v.775h2.187V1M5.7 1h-.581v3.956h8.4v-.775H5.7V1",
      fill: "url(#prefix__c)"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      fill: "#de0000",
      fillRule: "evenodd",
      d: "M5.119 4.956V0H3.187v4.956H0v2.575h3.187v4.956h1.932V7.531h8.4V4.956h-8.4"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M21.445 16.584l.228.705h.741l-.6.435.231.7-.6-.438-.6.438.23-.7-.6-.435h.741zm3.842-7.865l.228.705h.741l-.6.435.23.7-.6-.437-.6.437.231-.7-.6-.435h.742zm-8.1 1.109l.228.705h.741l-.6.435.23.7-.6-.436-.6.436.23-.7-.6-.435h.741zM21.522 5.5l.228.7h.741l-.6.435.23.7-.6-.436-.6.436.23-.7-.6-.435h.749z",
      fill: "#fff",
      fillRule: "evenodd"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M21.445 16.584l-.267.086.227.7.063.2h.946v-.281l-.165-.228-.6.435-.166.12.294.9.267-.087.166-.227-.6-.438-.166-.121-.764.559.166.227.267.087.23-.7.064-.2-.767-.555-.164.228v.281h.946l.291-.9-.268-.086-.267.086.267-.086-.267-.086-.165.51h-1.406l1.139.824-.166.509-.272.829 1.137-.831.433.317.7.514-.438-1.338.435-.314.7-.51h-1.406l-.164-.51-.268-.829-.267.829.267.086m3.842-7.865l-.267.087.227.705.063.2h.946v-.288l-.157-.223-.6.436-.165.12.293.9.267-.087.165-.227-.6-.437-.166-.121-.764.558.165.227.267.087.231-.7.064-.194-.765-.562-.165.228V9.7h.946l.29-.9-.267-.087-.267.087.267-.087-.267-.086-.165.51H23.449l1.139.825-.167.509-.271.828 1.137-.829.433.316.7.511-.435-1.335.434-.315.7-.51h-1.4l-.165-.51-.267-.827-.267.829.267.086m-8.1 1.109l-.267.086.228.705.063.2h.946v-.281l-.165-.228-.6.435-.166.12.294.9.267-.088.166-.227-.6-.436-.166-.121-.764.557.166.227.267.088.23-.7.064-.2-.767-.555-.164.228v.281h.946l.291-.9-.268-.086-.267.086.267-.086-.267-.086-.165.51h-1.405l1.138.825-.166.509-.271.827 1.136-.828.434.315.7.513-.438-1.336.435-.315.7-.51h-1.406l-.164-.51-.268-.829-.267.829.267.086M21.522 5.5l-.267.086.228.705.063.2h.946V6.2l-.165-.228-.6.435-.166.12.294.9.267-.088.166-.227-.6-.436-.166-.121-.764.557.166.227.267.088.23-.7.064-.2-.766-.555-.165.228v.281h.946l.291-.9-.269-.081-.267.086.267-.086-.267-.086-.165.51h-1.405l1.139.825-.167.509-.271.827 1.136-.828.434.315.7.513-.438-1.336.435-.315.7-.51h-1.406l-.164-.51-.268-.829-.267.829.267.086",
      fill: "#fff"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M21.445 16.584l.228.705h.741l-.6.435.231.7-.6-.438-.6.438.23-.7-.6-.435h.741zm3.842-7.865l.228.705h.741l-.6.435.23.7-.6-.437-.6.437.231-.7-.6-.435h.742zm-8.1 1.109l.228.705h.741l-.6.435.23.7-.6-.436-.6.436.23-.7-.6-.435h.741zM21.522 5.5l.228.7h.741l-.6.435.23.7-.6-.436-.6.436.23-.7-.6-.435h.749z",
      fill: "#de0000",
      fillRule: "evenodd"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M2.606 0H0v1.931L1 2.6V1h1.606V0m8.6 0H5.7v1h4l1.5-1M28 0H13.519v1H27v8.14h.124L27 9.23V27H1V9.89l-1 .667V28h28V0",
      fill: "#165cb5"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M1 8.925l-1 .667v.965l1-.667v-.965m-.81-.618H0v.126l.19-.126M0 4.054v.127h.19L0 4.054m0-2.123V2.9l1 .667V2.6l-1-.669M13.519.579L12.888 1h.631V.579M12.65 0H11.2L9.7 1h1.45l1.5-1",
      fill: "url(#prefix__b)"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M1 8.307H.19L0 8.433v1.159l1-.667v-.618M0 2.9v1.154l.19.127H1v-.618L0 2.9M13.519 0h-.869l-1.5 1h1.738l.631-.421V0",
      fill: "#c70000"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M1 7.531H0v.776h1v-.776m0-3.35H0v.775h1v-.775M3.187 0h-.581v1h.581V0M5.7 0h-.581v1H5.7V0",
      fill: "url(#prefix__d)"
    }
  ), /* @__PURE__ */ React162.createElement(
    "path",
    {
      d: "M1 4.956H0v2.575h1V4.956M5.119 0H3.187v1h1.932V0",
      fill: "#c70000"
    }
  ), /* @__PURE__ */ React162.createElement("path", { fill: "#e5e5e5", d: "M27.124 9.14H27v.09l.124-.09" }));
}

// src/components/icons/flags/mobile/Php.tsx
import React163 from "react";
function Php2(props) {
  return /* @__PURE__ */ React163.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React163.createElement("polyline", { fill: "#DE0000", points: "0,14 28,14 28,28 0,28 0,14" }), /* @__PURE__ */ React163.createElement("polyline", { fill: "#1966CA", points: "0,0 28,0 28,14 16.2,14 0,0" }), /* @__PURE__ */ React163.createElement("polyline", { fill: "#FFFFFF", points: "24.4,14 0,28 0,0 24.4,14" }), /* @__PURE__ */ React163.createElement(
    "path",
    {
      fill: "#FFDB00",
      d: "M3.3,25.3l-1.1-0.6l-0.9,0.8l0.3-1.2l-1.1-0.6l1.2-0.1L2,22.3l0.5,1.1l1.2-0.1l-0.9,0.8L3.3,25.3 M3.3,2.4\n		L2.2,3L1.3,2.2l0.3,1.2L0.5,4l1.2,0.1L2,5.4l0.5-1.1l1.2,0.1L2.8,3.5L3.3,2.4 M18.3,15l0.7-1l-0.7-1l1.2,0.4l0.7-1l0,1.3l1.2,0.4\n		l-1.2,0.4l0,1.3l-0.7-1L18.3,15 M8.4,11.1l0.2,0L9,9l0.3,0.4l-0.5,1.7c0.3,0.1,0.6,0.2,0.8,0.4l0.9-1.5l0.5-0.1l-1.2,1.7l0.2,0.1\n		l1.5-1.8l0.9,0l0,0.8l-1.9,1.5l0.1,0.2l1.8-1.2l-0.1,0.5l-1.6,0.9c0.1,0.2,0.2,0.5,0.3,0.7l1.7-0.5l0.4,0.3L11,13.5l0,0.2l2.3-0.3\n		L14,14l-0.5,0.6L11,14.3l0,0.2l2.1,0.4l-0.4,0.3l-1.7-0.5c-0.1,0.3-0.2,0.5-0.3,0.7l1.5,0.9l0.1,0.5l-1.7-1.2l-0.1,0.2l1.9,1.4\n		l0.1,0.9l-0.8,0L10,16.3l-0.2,0.1l1.2,1.7l-0.5-0.1l-0.9-1.6c-0.2,0.1-0.5,0.3-0.8,0.3l0.5,1.7L9,19l-0.4-2.1l-0.2,0l0.3,2.3\n		l-0.6,0.7l-0.6-0.5l0.2-2.4l-0.2,0L7.2,19l-0.3-0.4l0.5-1.8c-0.3-0.1-0.6-0.2-0.8-0.3l-0.9,1.5l-0.5,0.1l1.2-1.7l-0.2-0.1l-1.4,1.8\n		l-0.9,0.1l0-0.8l1.9-1.5l-0.1-0.2l-1.8,1.2L4,16.4l1.6-0.9c-0.1-0.2-0.2-0.5-0.3-0.8l-1.7,0.5l-0.4-0.3l2.1-0.4l0-0.2l-2.3,0.3\n		L2.2,14l0.5-0.6l2.4,0.2l0-0.2l-2.1-0.4l0.4-0.3l1.7,0.5c0.1-0.3,0.2-0.5,0.3-0.8L4,11.6l-0.1-0.5l1.7,1.2l0.1-0.2l-1.9-1.4\n		L3.9,9.8l0.8,0l1.6,1.9l0.2-0.1L5.1,9.8l0.5,0.1l0.9,1.6c0.2-0.1,0.5-0.3,0.8-0.3L6.9,9.4L7.2,9l0.4,2.1l0.2,0L7.5,8.7l0.6-0.7\n		l0.6,0.5L8.4,11.1"
    }
  ), /* @__PURE__ */ React163.createElement(
    "polyline",
    {
      fill: "#E5E5E5",
      points: "28,0 0,0 0,0 28,0 28,14 27,14 28,14 28,28 28,28 28,0"
    }
  ), /* @__PURE__ */ React163.createElement(
    "polyline",
    {
      fill: "#C70000",
      points: "28,14 27,14 27,27 1.7,27 0,28 28,28 28,14"
    }
  ), /* @__PURE__ */ React163.createElement("polyline", { fill: "#165CB5", points: "28,0 0,0 1.7,1 27,1 27,14 28,14 28,0" }), /* @__PURE__ */ React163.createElement(
    "polyline",
    {
      fill: "#E5E5E5",
      points: "0,0 0,28 1.7,27 1,27 1,24 0.5,23.7 1,23.6 1,4.1 0.5,4 1,3.7 1,1 1.7,1 0,0"
    }
  ), /* @__PURE__ */ React163.createElement(
    "path",
    {
      fill: "#E5C500",
      d: "M1,23.6l-0.5,0.1L1,24V23.6 M1,3.7L0.5,4L1,4.1V3.7"
    }
  ));
}

// src/components/icons/flags/mobile/Pln.tsx
import React164 from "react";
function Pln2(props) {
  return /* @__PURE__ */ React164.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React164.createElement("path", { fill: "#fff", d: "M0 0h28v14H0V0" }), /* @__PURE__ */ React164.createElement("path", { fill: "#de0000", d: "M0 14h28v14H0V14" }), /* @__PURE__ */ React164.createElement("path", { fill: "#e5e5e5", d: "M28 0H0v14h1V1h26v13h1V0" }), /* @__PURE__ */ React164.createElement("path", { fill: "#c70000", d: "M28 14h-1v13H1V14H0v14h28V14" }));
}

// src/components/icons/flags/mobile/Ron.tsx
import React165 from "react";
function Ron2(props) {
  return /* @__PURE__ */ React165.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React165.createElement("polyline", { fill: "#DE0000", points: "18.7,0 28,0 28,28 18.7,28 18.7,0" }), /* @__PURE__ */ React165.createElement("polyline", { fill: "#1966CA", points: "0,0 9.3,0 9.3,28 0,28 0,0" }), /* @__PURE__ */ React165.createElement("polyline", { fill: "#FFDB00", points: "9.3,0 18.7,0 18.7,28 9.3,28 9.3,0" }), /* @__PURE__ */ React165.createElement(
    "polyline",
    {
      fill: "#C70000",
      points: "28,0 18.7,0 18.7,1 27,1 27,27 18.7,27 18.7,28 28,28 28,0"
    }
  ), /* @__PURE__ */ React165.createElement(
    "polyline",
    {
      fill: "#165CB5",
      points: "9.3,0 0,0 0,28 9.3,28 9.3,27 1,27 1,1 9.3,1 9.3,0"
    }
  ), /* @__PURE__ */ React165.createElement("path", { fill: "#E5C500", d: "M18.7,27H9.3v1h9.3V27 M18.7,0H9.3v1h9.3V0" }));
}

// src/components/icons/flags/mobile/Sek.tsx
import React166 from "react";
function Sek2(props) {
  return /* @__PURE__ */ React166.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React166.createElement("path", { fill: "#1966ca", d: "M0 0h28v28H0z" }), /* @__PURE__ */ React166.createElement(
    "path",
    {
      fill: "#ffdb00",
      d: "M8.629 0h5.236v11.341H28v5.249H13.865V28H8.629V16.59H0v-5.249h8.629V0z"
    }
  ), /* @__PURE__ */ React166.createElement("path", { d: "M27 1v26H1V1h26m1-1H0v28h28V0z", opacity: 0.1 }));
}

// src/components/icons/flags/mobile/Sgd.tsx
import React167 from "react";
function Sgd2(props) {
  return /* @__PURE__ */ React167.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React167.createElement("path", { fill: "#de0000", d: "M0 0h28v28H0V0" }), /* @__PURE__ */ React167.createElement(
    "path",
    {
      fill: "#fff",
      d: "M0 14h28v14H0V14M6.109 7a4.975 4.975 0 014.768-4.97 5.265 5.265 0 100 9.94A4.975 4.975 0 016.109 7z"
    }
  ), /* @__PURE__ */ React167.createElement(
    "path",
    {
      fill: "#fff",
      d: "M9.792 7.127l-.715-.548-.715.548.275-.884-.717-.546.885.002.272-.886.272.886.885-.002-.717.546.275.884zM15.403 7.127l-.715-.548-.714.548.275-.884-.718-.546.886.002.271-.886.272.886.886-.002-.718.546.275.884zM12.601 5.044l-.715-.548-.716.548.276-.884-.717-.545.885.002.271-.887.272.886.885-.002-.717.546.276.884zM10.842 10.459l-.715-.548-.715.548.276-.884-.718-.545.885.001.272-.885.271.885.886-.002-.718.546.276.884zM14.354 10.459l-.715-.548-.716.548.276-.884-.718-.545.886.001.271-.885.272.885.885-.002-.717.546.276.884z"
    }
  ), /* @__PURE__ */ React167.createElement("path", { fill: "#c70000", d: "M28 0H0v14h1V1h26v13h1V0" }), /* @__PURE__ */ React167.createElement("path", { fill: "#e5e5e5", d: "M28 14h-1v13H1V14H0v14h28V14" }));
}

// src/components/icons/flags/mobile/Thb.tsx
import React168 from "react";
function Thb2(props) {
  return /* @__PURE__ */ React168.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React168.createElement(
    "polyline",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#FFFFFF",
      points: "0,28 28,28 28,0 0,0 0,28"
    }
  ), /* @__PURE__ */ React168.createElement(
    "path",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#DE0000",
      d: "M0,28h28v-4.7H0V28 M28,4.7V0H0v4.7H28"
    }
  ), /* @__PURE__ */ React168.createElement(
    "polyline",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      fill: "#1966CA",
      points: "0,18.7 28,18.7 28,9.3 0,9.3 0,18.7"
    }
  ), /* @__PURE__ */ React168.createElement(
    "path",
    {
      fill: "#E5E5E5",
      d: "M1,18.7H0v4.7h1V18.7 M28,18.7h-1v4.7h1V18.7 M1,4.7H0v4.7h1V4.7 M28,4.7h-1v4.7h1V4.7"
    }
  ), /* @__PURE__ */ React168.createElement(
    "path",
    {
      fill: "#C70000",
      d: "M28,23.3L28,23.3h-1V27H1v-3.7H0V28h28V23.3 M28,0H0v4.7h1V1h26v3.7h1V0"
    }
  ), /* @__PURE__ */ React168.createElement("path", { fill: "#165CB5", d: "M1,9.3H0v9.3h1V9.3 M28,9.3L28,9.3h-1v9.3h1V9.3" }));
}

// src/components/icons/flags/mobile/Usd.tsx
import React169 from "react";
function Usd2(props) {
  return /* @__PURE__ */ React169.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React169.createElement("path", { fill: "#fff", d: "M0 0h28v28H0V0" }), /* @__PURE__ */ React169.createElement(
    "path",
    {
      d: "M0 25.846h28V28H0v-2.154M0 0h28v2.153H0V0m0 4.308h28v2.153H0V4.308m0 4.308h28v2.153H0V8.616m0 4.307h28v2.154H0v-2.154m0 4.308h28v2.155H0v-2.155m0 4.308h28v2.154H0v-2.154",
      fill: "#de0000"
    }
  ), /* @__PURE__ */ React169.createElement("path", { fill: "#1966ca", d: "M0 0h14.114v15.077H0V0" }), /* @__PURE__ */ React169.createElement(
    "path",
    {
      d: "M12.488 4.248l-.194-.6-.193.6h-.627l.507.368-.193.6.506-.368.507.368-.194-.6.507-.368h-.626M2.023 1.592l.508-.368H1.9L1.71.628l-.193.6H.89l.507.369-.194.6.507-.377.507.368-.194-.6m3.529 0l.507-.369h-.627l-.194-.6-.194.6h-.626l.507.369-.194.6.507-.368.507.368-.193-.6m3.528 0l.506-.368H8.96l-.194-.6-.194.6h-.626l.507.369-.194.6.507-.368.507.368-.193-.6m3.527 0l.507-.368h-.626l-.194-.6-.193.6h-.627l.507.369-.193.6.506-.368.507.368-.194-.6M3.881 12.1l.508-.369h-.627l-.193-.595-.194.595h-.626l.506.369-.193.6.506-.368.507.368-.194-.6m-.069-2.933l.506-.367h-.626L3.5 8.2l-.194.6h-.628l.507.369-.193.6L3.5 9.4l.506.368-.193-.6m.017-2.923l.507-.369H3.71l-.193-.6-.194.6H2.7l.508.369-.194.6.507-.369.507.369-.194-.6m3.528 0l.507-.369h-.631l-.193-.6-.194.6h-.626l.506.369-.193.6.506-.369.508.369-.194-.6M7.34 9.167l.507-.367H7.22l-.193-.6-.194.6h-.627l.507.369-.193.6.506-.369.508.368-.194-.6m.07 2.932l.507-.369H7.29l-.19-.591-.194.595h-.63l.507.369-.193.6.506-.368.504.364-.194-.6m3.528 0l.507-.369h-.627l-.194-.595-.193.595H9.8l.507.369-.193.6.507-.368.506.368-.193-.6m-.07-2.937l.507-.369h-.627l-.193-.6-.194.6h-.623l.507.369-.194.6.507-.368.507.368-.193-.6m.018-2.922l.507-.369h-.627l-.193-.6-.194.6h-.627l.507.369-.193.6.507-.368.507.368-.194-.6M3.788 3.1l.506-.368h-.626l-.193-.6-.194.6h-.627l.507.368-.193.6.506-.368.507.368-.193-.6m3.527 0l.507-.368H7.2L7 2.141l-.193.6h-.625l.507.368L6.5 3.7l.5-.368.507.368-.194-.6m3.529 0l.506-.368h-.626l-.194-.6-.193.6H9.71l.507.368-.194.6.507-.368.507.368-.193-.6M2.118 13.616l.507-.368H2l-.194-.6-.193.6H.984l.507.368-.193.6.507-.368.507.368-.194-.6m-.07-2.937l.507-.368h-.627l-.194-.6-.193.6H.914l.507.368-.193.595.506-.368.507.368-.193-.595m.018-2.922l.507-.368h-.627l-.194-.6-.193.6H.932l.507.368-.193.6.506-.368.507.368-.193-.6m3.528 0l.506-.368h-.625l-.194-.6-.194.6H4.46l.507.368-.193.6.507-.368.507.368-.194-.6m-.018 2.922l.507-.368h-.627l-.194-.6-.194.6h-.626l.507.368-.194.595.507-.368.507.368-.193-.595m.07 2.937l.506-.368h-.626l-.193-.6-.194.6h-.627l.507.368-.194.6.508-.368.506.368-.193-.6m3.528 0l.506-.368h-.626l-.194-.6-.193.6h-.626l.506.368-.193.6.506-.368.507.368-.193-.6m3.528 0l.507-.368h-.627l-.193-.6-.194.6h-.627l.507.368-.194.6.508-.368.507.368-.194-.6M9.1 10.679l.506-.368h-.622l-.193-.6-.194.6H7.97l.508.368-.194.595.507-.368.506.368-.197-.595m3.527 0l.508-.368h-.627l-.194-.6-.193.6H11.5l.507.368-.193.595.506-.368.507.368-.194-.595M9.122 7.757l.507-.368H9l-.193-.6-.194.6h-.625l.507.368-.193.6.507-.368.507.368-.194-.6m3.528 0l.507-.368h-.627l-.193-.6-.194.6h-.626l.506.368-.193.6.507-.368.507.368-.194-.6M2.023 4.616l.508-.368H1.9l-.194-.6-.193.6H.89l.507.368-.194.6.507-.368.507.368-.194-.6m3.529 0l.507-.368h-.627l-.194-.6-.194.6h-.626l.507.368-.194.6.507-.368.507.368-.193-.6m3.528 0l.506-.368H8.96l-.194-.6-.194.6h-.626l.507.368-.194.6.507-.368.507.368-.193-.6",
      fill: "#fff"
    }
  ), /* @__PURE__ */ React169.createElement(
    "path",
    {
      d: "M1 23.693H0v2.153h1v-2.153m27 0h-1v2.153h1v-2.153M1 19.386H0v2.153h1v-2.153m27 0h-1v2.153h1v-2.153M1 15.077H0v2.154h1v-2.154m27 0h-1v2.154h1v-2.154m0-4.308h-1v2.154h1v-2.154m0-4.308h-1v2.155h1V6.461m0-4.308h-1v2.155h1V2.153",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React169.createElement(
    "path",
    {
      d: "M28 25.846h-1V27H1v-1.154H0V28h28v-2.154M1 21.539H0v2.154h1v-2.154m27 0h-1v2.154h1v-2.154M1 17.231H0v2.155h1v-2.155m27 0h-1v2.155h1v-2.155m0-4.308h-1v2.154h1v-2.154m0-4.307h-1v2.153h1V8.616m0-4.308h-1v2.153h1V4.308M28 0H14.114v1H27v1.153h1V0",
      fill: "#c70000"
    }
  ), /* @__PURE__ */ React169.createElement(
    "path",
    {
      d: "M14.114 0H0v15.077h1v-1.818l-.016-.011H1v-2.875l-.086-.062H1V7.438l-.068-.049H1V4.328l-.11-.08H1V1.3l-.11-.08H1V1h.589L1.71.628 1.831 1h3.286l.121-.372.121.372h3.286l.121-.372.121.372h3.286l.121-.372.121.372h1.7V0",
      fill: "#165cb5"
    }
  ), /* @__PURE__ */ React169.createElement(
    "path",
    {
      d: "M1 13.248H.984l.016.011v-.011M1 10.311H.914l.086.062v-.062m0-2.922H.932L1 7.438v-.049m0-3.141H.89l.11.08v-.08m0-3.024H.89L1 1.3v-.08m.71-.6L1.589 1h.242L1.71.628m3.528 0L5.117 1h.242L5.238.628m3.528 0L8.645 1h.242L8.766.628m3.528 0L12.173 1h.242l-.121-.372",
      fill: "#e5e5e5"
    }
  ));
}

// src/components/icons/flags/mobile/Xdr.tsx
import React170 from "react";
function Xdr2(props) {
  return /* @__PURE__ */ React170.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React170.createElement("path", { fill: "#1966ca", fillRule: "evenodd", d: "M0 0h28v28H0V0" }), /* @__PURE__ */ React170.createElement(
    "path",
    {
      d: "M5.155 10.683H6.4c.485.844.97 1.668 1.465 2.512l1.484-2.512h1.231c-.7 1.076-1.4 2.143-2.085 3.22.708 1.144 1.435 2.3 2.143 3.423H9.413c-.544-.892-1.077-1.8-1.62-2.7-.523.9-1.047 1.814-1.571 2.705H5l2.163-3.452zM11.614 17.326v-6.653h2.318c2.24 0 2.832 1.523 2.832 3.307 0 1.8-.689 3.346-2.91 3.346zm1.115-5.857v5.043h1.019c.64 0 1.881-.2 1.881-2.473 0-2.308-1.134-2.57-1.852-2.57zM21.584 14.262c.533.165.718.64.873 1.174l.543 1.89h-1.1a90.795 90.795 0 01-.514-1.784c-.223-.8-.611-1.009-1.2-1.009h-.9v2.793H18.2v-6.653h2.474a1.815 1.815 0 012.075 1.872 1.716 1.716 0 01-1.165 1.717zm-1.105-.524a1.046 1.046 0 001.144-1.138 1.033 1.033 0 00-1.154-1.125h-1.184v2.259z",
      fill: "#fff"
    }
  ), /* @__PURE__ */ React170.createElement("path", { d: "M1 27V1h26v26H1M28 0H0v28h28V0", fill: "#165cb5" }));
}

// src/components/icons/flags/mobile/Zar.tsx
import React171 from "react";
function Zar2(props) {
  return /* @__PURE__ */ React171.createElement("svg", { viewBox: "0 0 28 28", height: 28, width: 28, ...props }, /* @__PURE__ */ React171.createElement("path", { fill: "#fff", fillRule: "evenodd", d: "M0 .012h28v27.976H0V.012" }), /* @__PURE__ */ React171.createElement("path", { fill: "#de0000", d: "M22.477 9.326L8.534 0 28 .012v9.314h-5.523" }), /* @__PURE__ */ React171.createElement(
    "path",
    {
      fill: "#ffd500",
      d: "M0 24.619v-2.238l12.497-8.393L0 5.642V3.357l15.808 10.631L0 24.619"
    }
  ), /* @__PURE__ */ React171.createElement(
    "path",
    {
      fill: "#00701d",
      d: "M0 3.357V0l5.135.006 16.61 11.201 6.255-.01v5.574l-6.223.015L5.129 27.977H0v-3.358l15.808-10.631L0 3.357"
    }
  ), /* @__PURE__ */ React171.createElement(
    "path",
    {
      fill: "#1966ca",
      d: "M8.591 27.977l13.928-9.341 5.481.031V28l-19.409-.023"
    }
  ), /* @__PURE__ */ React171.createElement("path", { d: "M0 5.642l12.497 8.346L0 22.381V5.642" }), /* @__PURE__ */ React171.createElement(
    "path",
    {
      fill: "#e5e5e5",
      d: "M28 0H0l5.135.006.01.006h3.407L8.534 0 28 .012V28 0"
    }
  ), /* @__PURE__ */ React171.createElement(
    "path",
    {
      d: "M28 16.771h-1v1.887l1 .006v-1.9m0-7.445h-1V11.2h1V9.326M8.552.012H5.145L6.609 1h3.42L8.552.012",
      fill: "#e5e5e5"
    }
  ), /* @__PURE__ */ React171.createElement(
    "path",
    {
      fill: "#c70000",
      d: "M8.534 0l.018.012L10.029 1H27v8.326h1V.012L8.534 0"
    }
  ), /* @__PURE__ */ React171.createElement("path", { fill: "#e5bf00", d: "M0 3.357v2.285l1 .668V4.03l-1-.673" }), /* @__PURE__ */ React171.createElement(
    "path",
    {
      d: "M28 11.2h-1v5.576h1V11.2M0 0v3.357l1 .673V1h5.609L5.145.012l-.01-.006L0 0",
      fill: "#00651a"
    }
  ), /* @__PURE__ */ React171.createElement(
    "path",
    {
      fill: "#e5e5e5",
      d: "M18.299 27.988H0V28h28l-9.701-.012M10.047 27H6.582l-1.453.977H0v.011h18.299l-9.708-.011L10.047 27"
    }
  ), /* @__PURE__ */ React171.createElement("path", { fill: "#e5bf00", d: "M1 21.709l-1 .672v2.238l1-.672v-2.238" }), /* @__PURE__ */ React171.createElement(
    "path",
    {
      fill: "#00651a",
      d: "M1 23.947l-1 .672v3.358h5.129L6.582 27H1v-3.053"
    }
  ), /* @__PURE__ */ React171.createElement(
    "path",
    {
      fill: "#165cb5",
      d: "M27 18.661V27H10.047l-1.456.977 9.708.011L28 28v-9.333l-1-.006"
    }
  ), /* @__PURE__ */ React171.createElement("path", { d: "M0 5.642v16.739l1-.672V6.31l-1-.668" }));
}

// src/components/icons/flags/mobile/MobileFlags.tsx
function MobileFlags({ currency, ...rest }) {
  switch (currency) {
    case "ISK": {
      return /* @__PURE__ */ React172.createElement(Isk, { ...rest });
    }
    case "EUR": {
      return /* @__PURE__ */ React172.createElement(Eur2, { ...rest });
    }
    case "USD": {
      return /* @__PURE__ */ React172.createElement(Usd2, { ...rest });
    }
    case "GBP": {
      return /* @__PURE__ */ React172.createElement(Gbp2, { ...rest });
    }
    case "PLN": {
      return /* @__PURE__ */ React172.createElement(Pln2, { ...rest });
    }
    case "JPY": {
      return /* @__PURE__ */ React172.createElement(Jpy2, { ...rest });
    }
    case "NOK": {
      return /* @__PURE__ */ React172.createElement(Nok2, { ...rest });
    }
    case "SEK": {
      return /* @__PURE__ */ React172.createElement(Sek2, { ...rest });
    }
    case "DKK": {
      return /* @__PURE__ */ React172.createElement(Dkk2, { ...rest });
    }
    case "CHF": {
      return /* @__PURE__ */ React172.createElement(Chf2, { ...rest });
    }
    case "CAD": {
      return /* @__PURE__ */ React172.createElement(Cad2, { ...rest });
    }
    case "AUD": {
      return /* @__PURE__ */ React172.createElement(Aud2, { ...rest });
    }
    case "ZAR": {
      return /* @__PURE__ */ React172.createElement(Zar2, { ...rest });
    }
    case "HKD": {
      return /* @__PURE__ */ React172.createElement(Hkd2, { ...rest });
    }
    case "NZD": {
      return /* @__PURE__ */ React172.createElement(Nzd2, { ...rest });
    }
    case "SGD": {
      return /* @__PURE__ */ React172.createElement(Sgd2, { ...rest });
    }
    case "CNY": {
      return /* @__PURE__ */ React172.createElement(Cny2, { ...rest });
    }
    case "XDR": {
      return /* @__PURE__ */ React172.createElement(Xdr2, { ...rest });
    }
    case "RON": {
      return /* @__PURE__ */ React172.createElement(Ron2, { ...rest });
    }
    case "HUF": {
      return /* @__PURE__ */ React172.createElement(Huf2, { ...rest });
    }
    case "PHP": {
      return /* @__PURE__ */ React172.createElement(Php2, { ...rest });
    }
    case "CZK": {
      return /* @__PURE__ */ React172.createElement(Czk2, { ...rest });
    }
    case "THB": {
      return /* @__PURE__ */ React172.createElement(Thb2, { ...rest });
    }
    default: {
      return /* @__PURE__ */ React172.createElement(DefaultFlag2, { ...rest });
    }
  }
}

// src/components/icons/socialMedia/Spotify.tsx
import React173 from "react";
var Spotify = () => {
  return /* @__PURE__ */ React173.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "24",
      height: "24",
      viewBox: "0 0 24 24"
    },
    /* @__PURE__ */ React173.createElement(
      "path",
      {
        fill: "#1E3D5A",
        d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12A12 12 0 0012 0zm5 18.1a.9.9 0 01-1.27.08 8.37 8.37 0 00-4.27-1.78 8.4 8.4 0 00-4.57.52.9.9 0 11-.76-1.63 10.07 10.07 0 015.58-.67 10.05 10.05 0 015.2 2.21.9.9 0 01.09 1.27zm1.19-3.78a1 1 0 01-1.41.09 9.35 9.35 0 00-4.75-2 9.33 9.33 0 00-5.11.59 1 1 0 11-.85-1.81 11.21 11.21 0 016.25-.76 11.22 11.22 0 015.79 2.45 1 1 0 01.09 1.44h-.01zm1.58-3.62a1.15 1.15 0 01-1.62.08 10.62 10.62 0 00-5.55-2.35 10.61 10.61 0 00-6 .72 1.15 1.15 0 01-1-2.07 13 13 0 017.29-.93 13 13 0 016.81 2.92 1.15 1.15 0 01.08 1.62l-.01.01z"
      }
    )
  );
};

// src/components/icons/NotFound.tsx
import React174 from "react";
function NotFound({ className }) {
  return /* @__PURE__ */ React174.createElement(
    "svg",
    {
      className,
      id: "prefix__b48d11bd-edef-44e5-b622-00da69e86340",
      xmlns: "http://www.w3.org/2000/svg",
      x: 0,
      y: 0,
      viewBox: "0 0 92 97",
      xmlSpace: "preserve"
    },
    /* @__PURE__ */ React174.createElement("style", null, ".prefix__st2{fill:none;stroke:#163e63;stroke-width:2}"),
    /* @__PURE__ */ React174.createElement("g", { opacity: 0.6 }, /* @__PURE__ */ React174.createElement("g", { id: "prefix__a1c82103-fa64-46ba-bde6-c3ec1fb128cc" }, /* @__PURE__ */ React174.createElement("g", { id: "prefix__e4120f49-a5fe-4129-9ffa-1b4100a16ba9" }, /* @__PURE__ */ React174.createElement("g", { id: "prefix__ef97e4aa-5ee2-4fa0-a682-304d39904d32" }, /* @__PURE__ */ React174.createElement(
      "path",
      {
        id: "prefix__b285ac04-46ff-4e34-9540-3f4fe0b3e745",
        fill: "none",
        stroke: "#163e63",
        strokeWidth: 2,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        d: "M69 73.7L91 96"
      }
    ), /* @__PURE__ */ React174.createElement(
      "ellipse",
      {
        id: "prefix__bf3baf16-4f85-4f16-9778-c6c36311a228",
        className: "prefix__st2",
        cx: 41.5,
        cy: 42.4,
        rx: 40.5,
        ry: 41.4
      }
    )))), /* @__PURE__ */ React174.createElement(
      "path",
      {
        className: "prefix__st2",
        d: "M34.7 28.3s14.1-4.5 15.6 3c1.7 8.2-13.5 9.4-10.5 21.2"
      }
    ), /* @__PURE__ */ React174.createElement("circle", { cx: 40.9, cy: 59.2, r: 1.8, fill: "#163e63" }))
  );
}

// src/components/icons/authentication/ElectronicId.tsx
import React175 from "react";

// src/components/icons/authentication/colors.ts
var IconColorBlue = "#20446a";
var IconColorWhite = "#ffffff";
function getHexFromColor(color) {
  switch (color) {
    case "white":
      return IconColorWhite;
    default:
      return IconColorBlue;
  }
}

// src/components/icons/authentication/ElectronicId.tsx
var ElectronicId = ({ color, ...rest }) => {
  const hex = getHexFromColor(color);
  return /* @__PURE__ */ React175.createElement("svg", { viewBox: "0 0 24 30", ...rest }, /* @__PURE__ */ React175.createElement("g", { stroke: hex, fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React175.createElement(
    "path",
    {
      d: "M18.333333 1H4C2.343146 1 1 2.343146 1 4v22c0 1.656854 1.343146 3 3 3h16c1.656854 0 3-1.343146 3-3V5.829065L18.333333 1z",
      strokeLinecap: "round"
    }
  ), /* @__PURE__ */ React175.createElement("g", { transform: "translate(4 10)" }, /* @__PURE__ */ React175.createElement("rect", { x: ".5", y: ".5", width: "15", height: "15", rx: "3" }), /* @__PURE__ */ React175.createElement("path", { d: "M5.5 0v5H15M10.5.5v4m-10.035714 6H15.5m-10 .1v4.918823M10.5 10.6v4.918823" }))));
};

// src/components/icons/authentication/FaceId.tsx
import React176 from "react";
var FaceId = ({ color, ...rest }) => {
  const hex = getHexFromColor(color);
  return /* @__PURE__ */ React176.createElement("svg", { viewBox: "0 0 30 30", ...rest }, /* @__PURE__ */ React176.createElement("g", { fill: "none", fillRule: "evenodd", stroke: hex, strokeLinecap: "round" }, /* @__PURE__ */ React176.createElement("path", { strokeWidth: "2", d: "M9 8v3" }), /* @__PURE__ */ React176.createElement("path", { d: "M9 21c1.6 1.3333333 3.6 2 6 2s4.4-.6666667 6-2M15 9v6c0 1.1045695-.8954305 2-2 2" }), /* @__PURE__ */ React176.createElement("path", { strokeWidth: "2", d: "M21 8v3" }), /* @__PURE__ */ React176.createElement("path", { d: "M1 7V5c0-2.209139 1.790861-4 4-4h2M1 23v2c0 2.209139 1.790861 4 4 4h2M29 7V5c0-2.209139-1.790861-4-4-4h-2m6 22v2c0 2.209139-1.790861 4-4 4h-2" })));
};

// src/components/icons/authentication/Fingerprint.tsx
import React177 from "react";
var Fingerprint = ({ color, ...rest }) => {
  const hex = getHexFromColor(color);
  return /* @__PURE__ */ React177.createElement("svg", { viewBox: "0 0 27 32", ...rest }, /* @__PURE__ */ React177.createElement(
    "g",
    {
      fill: "none",
      fillRule: "evenodd",
      stroke: hex,
      strokeLinecap: "round",
      strokeLinejoin: "round"
    },
    /* @__PURE__ */ React177.createElement("path", { d: "M22 2.9169876C19.5730296 1.6897732 16.898123 1 14.077849 1 11.194853 1 8.4653415 1.7222935 6 3m9 19c1.2873965 2.0197364 3.8901307 3.5310225 7 2.822849" }),
    /* @__PURE__ */ React177.createElement("path", { d: "M1 21.09238c.821944-3.3196866 2.0959936-5.6902703 4.2043636-7.6507836 5.085278-4.7295754 13.467796-4.514397 18.5567142.2137293 1.6518963 1.5359528 1.6518963 4.027529 0 5.5598592-1.6555364 1.538126-4.3324966 1.538126-5.986577.002898-1.7843974-1.662017-4.6921426-1.662017-6.4830923-.002898C9.5397723 20.8467722 8.5969757 27.815796 17.3850055 31" }),
    /* @__PURE__ */ React177.createElement("path", { d: "M6.737048 27c-1.710735-4.5776568-.1046055-7.8549092 1.3867485-9.6611884 3.1926455-3.8698694 9.31497-4.4624864 13.6451998-1.3222722.083539.0553984.154729.1166282.2310037.1815027m5-5.6217596C23.8424006 7.6187854 19.3537816 6 14.1688214 6 8.8830947 6 4.1583514 7.9454283 1 11" })
  ));
};

// src/components/icons/authentication/Pin.tsx
import React178 from "react";
var Pin = ({ color, ...rest }) => {
  const hex = getHexFromColor(color);
  return /* @__PURE__ */ React178.createElement("svg", { viewBox: "0 0 18 23", ...rest }, /* @__PURE__ */ React178.createElement("g", { fill: hex, fillRule: "evenodd" }, /* @__PURE__ */ React178.createElement("circle", { cx: "2", cy: "2", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "2", cy: "8", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "2", cy: "14", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "9", cy: "2", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "9", cy: "8", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "9", cy: "14", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "9", cy: "21", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "16", cy: "2", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "16", cy: "8", r: "2" }), /* @__PURE__ */ React178.createElement("circle", { cx: "16", cy: "14", r: "2" })));
};

// src/components/icons/authentication/Username.tsx
import React179 from "react";
var Username = ({ color, ...rest }) => {
  const hex = getHexFromColor(color);
  return /* @__PURE__ */ React179.createElement("svg", { viewBox: "0 0 26 22", ...rest }, /* @__PURE__ */ React179.createElement("g", { fill: "none", fillRule: "evenodd", transform: "translate(1 1)" }, /* @__PURE__ */ React179.createElement("rect", { width: "24", height: "4", stroke: hex, rx: "2" }), /* @__PURE__ */ React179.createElement("rect", { width: "24", height: "4", y: "8", stroke: hex, rx: "2" }), /* @__PURE__ */ React179.createElement("rect", { width: "12", height: "4", y: "17", fill: hex, rx: "2" })));
};

// src/components/icons/carouselControls/LeftArrow.tsx
import React180 from "react";
var LeftArrow = () => {
  return /* @__PURE__ */ React180.createElement(
    "svg",
    {
      width: "50",
      height: "50",
      viewBox: "0 0 50 50",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React180.createElement("g", { id: "Type=Left" }, /* @__PURE__ */ React180.createElement(
      "path",
      {
        id: "Path Copy 4",
        d: "M30.958 37.4443L16.958 24.9999L30.958 12.5554",
        stroke: "#163E63",
        strokeWidth: "2"
      }
    ))
  );
};

// src/components/icons/carouselControls/RightArrow.tsx
import React181 from "react";
var RightArrow = () => {
  return /* @__PURE__ */ React181.createElement(
    "svg",
    {
      width: "50",
      height: "50",
      viewBox: "0 0 50 50",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React181.createElement("g", { id: "Type=Right" }, /* @__PURE__ */ React181.createElement(
      "path",
      {
        id: "Path Copy 4",
        d: "M19.042 37.4443L33.042 24.9999L19.042 12.5554",
        stroke: "#163E63",
        strokeWidth: "2"
      }
    ))
  );
};

// src/components/icons/carouselControls/Exit.tsx
import React182 from "react";
var Exit = () => {
  return /* @__PURE__ */ React182.createElement(
    "svg",
    {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React182.createElement("g", { id: "Group 34" }, /* @__PURE__ */ React182.createElement(
      "rect",
      {
        id: "Rectangle 68",
        width: "1.98992",
        height: "31.8947",
        transform: "matrix(0.708277 0.705935 -0.708277 0.705935 22.5906 0.0795135)",
        fill: "#163E63"
      }
    ), /* @__PURE__ */ React182.createElement(
      "rect",
      {
        id: "Rectangle 69",
        width: "1.98992",
        height: "31.8947",
        transform: "matrix(0.708277 -0.705935 0.708277 0.705935 0 1.40457)",
        fill: "#163E63"
      }
    ))
  );
};

// src/components/icons/bullet/Positive.tsx
import React183 from "react";
var PositiveSvg = `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M18.6709 6.98923L10.4258 16.1084L6.06359 11.618' stroke='%2300A651' stroke-width='2' stroke-linecap='square'/></svg>`;
var Positive = () => {
  return /* @__PURE__ */ React183.createElement(
    "svg",
    {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React183.createElement(
      "path",
      {
        d: "M18.6709 6.98923L10.4258 16.1084L6.06359 11.618",
        stroke: "%2300A651",
        strokeWidth: "2",
        strokeLinecap: "square"
      }
    )
  );
};

// src/components/icons/bullet/Negative.tsx
import React184 from "react";
var NegativeSvg = `<svg width='24' height='25' viewBox='0 0 24 25' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M18 7L6 18.8571' stroke='%23FF0000' stroke-width='2' stroke-linecap='square'/><path d='M18 19L6 7.14291' stroke='%23FF0000' stroke-width='2' stroke-linecap='square'/></svg>`;
var Negative = () => {
  return /* @__PURE__ */ React184.createElement(
    "svg",
    {
      width: "24",
      height: "25",
      viewBox: "0 0 24 25",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React184.createElement(
      "path",
      {
        d: "M18 7L6 18.8571",
        stroke: "%23FF0000",
        strokeWidth: "2",
        strokeLinecap: "square"
      }
    ),
    /* @__PURE__ */ React184.createElement(
      "path",
      {
        d: "M18 19L6 7.14291",
        stroke: "%23FF0000",
        strokeWidth: "2",
        strokeLinecap: "square"
      }
    )
  );
};

// src/components/icons/bullet/Bullet.tsx
import React185 from "react";
var BulletSvg = `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><circle cx='12' cy='12' r='4' fill='%23163E63'/></svg>`;
var Bullet = () => {
  return /* @__PURE__ */ React185.createElement(
    "svg",
    {
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React185.createElement("circle", { cx: "12", cy: "12", r: "4", fill: "%23163E63" })
  );
};

// src/components/icons/bullet/LightBullet.tsx
import React186 from "react";
var LightBulletSvg = `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><circle cx='12' cy='12' r='4' fill='%23DEE4E8'/></svg>`;
var LightBullet = /* @__PURE__ */ React186.createElement(
  "svg",
  {
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  },
  /* @__PURE__ */ React186.createElement("circle", { cx: "12", cy: "12", r: "4", fill: "#DEE4E8" })
);

// src/components/icons/bullet/PositivePerson.tsx
import React187 from "react";
var PositivePersonSvg = `<svg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'><rect width='60' height='60' rx='30' fill='%23489575' fill-opacity='0.1'/><path fill-rule='evenodd' clip-rule='evenodd' d='M31.3333 24.6667C31.3333 27.6119 28.9452 30 26 30C23.0547 30 20.6666 27.6119 20.6666 24.6667C20.6666 21.7214 23.0547 19.3333 26 19.3333C28.9452 19.3333 31.3333 21.7214 31.3333 24.6667Z' stroke='%23489575' stroke-width='1.06667' stroke-linecap='round' stroke-linejoin='round'/><path d='M18.0869 40.6667C18.0869 36.235 16.5921 31.3333 26.0005 31.3333C35.409 31.3333 33.9129 36.235 33.9129 40.6667' stroke='%23489575' stroke-width='1.06667' stroke-linecap='round' stroke-linejoin='round'/><path d='M45.1093 21.5095L39.9178 27.2513L37.1712 24.424' stroke='%23489575' stroke-width='1.06667' stroke-linecap='square'/></svg>`;
var PositivePerson = () => {
  return /* @__PURE__ */ React187.createElement(
    "svg",
    {
      width: "60",
      height: "60",
      viewBox: "0 0 60 60",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React187.createElement("rect", { width: "60", height: "60", rx: "30", fill: "#489575", fillOpacity: "0.1" }),
    /* @__PURE__ */ React187.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M31.3333 24.6667C31.3333 27.6119 28.9452 30 26 30C23.0547 30 20.6666 27.6119 20.6666 24.6667C20.6666 21.7214 23.0547 19.3333 26 19.3333C28.9452 19.3333 31.3333 21.7214 31.3333 24.6667Z",
        stroke: "#489575",
        strokeWidth: "1.06667",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React187.createElement(
      "path",
      {
        d: "M18.0869 40.6667C18.0869 36.235 16.5921 31.3333 26.0005 31.3333C35.409 31.3333 33.9129 36.235 33.9129 40.6667",
        stroke: "#489575",
        strokeWidth: "1.06667",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React187.createElement(
      "path",
      {
        d: "M45.1093 21.5095L39.9178 27.2513L37.1712 24.424",
        stroke: "#489575",
        strokeWidth: "1.06667",
        strokeLinecap: "square"
      }
    )
  );
};

// src/components/icons/bullet/NegativePerson.tsx
import React188 from "react";
var NegativePersonSvg = `<svg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'><rect width='60' height='60' rx='30' fill='%23FF0000' fill-opacity='0.1'/><path fill-rule='evenodd' clip-rule='evenodd' d='M31.194 25.2239C31.194 27.8614 29.0554 30 26.4178 30C23.7803 30 21.6417 27.8614 21.6417 25.2239C21.6417 22.5863 23.7803 20.4478 26.4178 20.4478C29.0554 20.4478 31.194 22.5863 31.194 25.2239Z' stroke='%23FF0000' stroke-width='0.955224' stroke-linecap='round' stroke-linejoin='round'/><path d='M19.3315 39.5522C19.3315 35.5836 17.9928 31.194 26.4183 31.194C34.8438 31.194 33.504 35.5836 33.504 39.5522' stroke='%23FF0000' stroke-width='0.955224' stroke-linecap='round' stroke-linejoin='round'/><path d='M43.3721 20.5512L36.789 27.0558' stroke='%23FF0000' stroke-width='0.955224' stroke-linecap='square'/><path d='M43.3721 27.1342L36.789 20.6296' stroke='%23FF0000' stroke-width='0.955224' stroke-linecap='square'/></svg>`;
var NegativePerson = () => {
  return /* @__PURE__ */ React188.createElement(
    "svg",
    {
      width: "60",
      height: "60",
      viewBox: "0 0 60 60",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React188.createElement("rect", { width: "60", height: "60", rx: "30", fill: "#FF0000", fillOpacity: "0.1" }),
    /* @__PURE__ */ React188.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M31.194 25.2239C31.194 27.8614 29.0554 30 26.4178 30C23.7803 30 21.6417 27.8614 21.6417 25.2239C21.6417 22.5863 23.7803 20.4478 26.4178 20.4478C29.0554 20.4478 31.194 22.5863 31.194 25.2239Z",
        stroke: "#FF0000",
        strokeWidth: "0.955224",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React188.createElement(
      "path",
      {
        d: "M19.3315 39.5522C19.3315 35.5836 17.9928 31.194 26.4183 31.194C34.8438 31.194 33.504 35.5836 33.504 39.5522",
        stroke: "#FF0000",
        strokeWidth: "0.955224",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ React188.createElement(
      "path",
      {
        d: "M43.3721 20.5512L36.789 27.0558",
        stroke: "#FF0000",
        strokeWidth: "0.955224",
        strokeLinecap: "square"
      }
    ),
    /* @__PURE__ */ React188.createElement(
      "path",
      {
        d: "M43.3721 27.1342L36.789 20.6296",
        stroke: "#FF0000",
        strokeWidth: "0.955224",
        strokeLinecap: "square"
      }
    )
  );
};

// src/components/icons/index.tsx
var getIconByName = (iconName) => {
  if (!iconName) {
    return null;
  }
  const iconNameLowerCased = iconName?.toLowerCase();
  switch (iconNameLowerCased) {
    case "facebook":
      return /* @__PURE__ */ React189.createElement(Facebook, null);
    case "youtube":
      return /* @__PURE__ */ React189.createElement(Youtube, null);
    case "twitter":
      return /* @__PURE__ */ React189.createElement(TwitterX, null);
    case "house":
      return /* @__PURE__ */ React189.createElement(HouseLine, null);
    case "contract":
      return /* @__PURE__ */ React189.createElement(Contract, null);
    case "document":
      return /* @__PURE__ */ React189.createElement(Document, null);
    case "invoice":
      return /* @__PURE__ */ React189.createElement(Invoice, null);
    case "wallet":
      return /* @__PURE__ */ React189.createElement(Wallet, null);
    case "ibudalan":
      return /* @__PURE__ */ React189.createElement(Ibudalan, null);
    case "bilalan":
      return /* @__PURE__ */ React189.createElement(Bilalan, null);
    case "sparnadur":
      return /* @__PURE__ */ React189.createElement(Sparnadur, null);
    case "lifeyrissparnadur":
      return /* @__PURE__ */ React189.createElement(Lifeyrissparnadur, null);
    case "serignasparnadur":
      return /* @__PURE__ */ React189.createElement(Sereignasparnadur, null);
    case "fyrirtaeki":
      return /* @__PURE__ */ React189.createElement(Fyrirtaeki, null);
    case "fyrirtaekiblue":
      return /* @__PURE__ */ React189.createElement(FyrirtaekiBlue, null);
    case "kreditkort":
      return /* @__PURE__ */ React189.createElement(Kreditkort, null);
    case "radgjof":
      return /* @__PURE__ */ React189.createElement(Radgjof, null);
    case "atm":
      return /* @__PURE__ */ React189.createElement(Atm, null);
    case "service":
      return /* @__PURE__ */ React189.createElement(Service, null);
    case "clock":
      return /* @__PURE__ */ React189.createElement(Clock, null);
    case "downarrow":
      return /* @__PURE__ */ React189.createElement(DownArrow, null);
    case "web":
      return /* @__PURE__ */ React189.createElement(Web, null);
    case "visa":
      return /* @__PURE__ */ React189.createElement(Visa, null);
    case "info":
      return /* @__PURE__ */ React189.createElement(Info, null);
    case "anaegjuvogin":
      return /* @__PURE__ */ React189.createElement(Anaegjuvogin, null);
    case "fyrstasaeti":
      return /* @__PURE__ */ React189.createElement(FyrstaSaeti, null);
    case "sjalfsafgreidsla":
      return /* @__PURE__ */ React189.createElement(Sjalfsafgreidsla, null);
    case "greidslumat":
      return /* @__PURE__ */ React189.createElement(Greidslumat, null);
    case "timapantanir":
      return /* @__PURE__ */ React189.createElement(Timapantanir, null);
    case "downloaddocument":
      return /* @__PURE__ */ React189.createElement(NewDocument, null);
    case "phone":
      return /* @__PURE__ */ React189.createElement(Phone, null);
    case "tree":
      return /* @__PURE__ */ React189.createElement(Tree, null);
    case "bluespotify":
      return /* @__PURE__ */ React189.createElement(BlueSpotify, null);
    case "blueyoutube":
      return /* @__PURE__ */ React189.createElement(BlueYoutube, null);
    case "rafmagnsbill":
      return /* @__PURE__ */ React189.createElement(ElectricCar, null);
    case "landsbankinn":
      return /* @__PURE__ */ React189.createElement(LandsbankinnIcon, null);
    case "car":
      return /* @__PURE__ */ React189.createElement(Car, null);
    case "pcaf":
      return /* @__PURE__ */ React189.createElement(Pcaf, null);
    case "paper":
      return /* @__PURE__ */ React189.createElement(Paper, null);
    case "electriccar":
      return /* @__PURE__ */ React189.createElement(ElectonicCar, null);
    case "aircontract":
      return /* @__PURE__ */ React189.createElement(AirContract, null);
    case "bicycle":
      return /* @__PURE__ */ React189.createElement(Bicycle, null);
    case "foot":
      return /* @__PURE__ */ React189.createElement(Foot, null);
    case "food":
      return /* @__PURE__ */ React189.createElement(Food, null);
    case "download":
      return /* @__PURE__ */ React189.createElement(Download, null);
    case "mail":
      return /* @__PURE__ */ React189.createElement(Mail, null);
    case "instagram":
      return /* @__PURE__ */ React189.createElement(Instagram, null);
    case "tiktok":
      return /* @__PURE__ */ React189.createElement(TikTok, null);
    case "linkedin":
      return /* @__PURE__ */ React189.createElement(LinkedIn, null);
    case "personal":
      return /* @__PURE__ */ React189.createElement(PersonalTWP, null);
    case "corporate":
      return /* @__PURE__ */ React189.createElement(CorporateTWP, null);
    case "documentinfo":
      return /* @__PURE__ */ React189.createElement(DocumentInfo, null);
    case "tryggingar":
      return /* @__PURE__ */ React189.createElement(Tryggingar, null);
    default:
      return null;
  }
};
var Icon3 = ({ name }) => {
  return getIconByName(name);
};

// src/components/buttons/IconLink.tsx
var Host5 = styled31(animated9.a, {
  shouldForwardProp: shouldForwardProp("a")
})`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  padding: 20px 30px;
  position: relative;
  cursor: pointer;
  span {
    transition: transform 200ms ease-in-out;
  }
  &:hover,
  &:focus {
    span {
      transform: translateY(-6px);
    }
  }
  @media ${device.tablet} {
    padding: 30px 10px;
  }
  @media ${device.desktop} {
    padding: 30px;
  }
`;
var IconWrap = styled31.span`
  display: block;
  margin-bottom: 13px;
  @media ${device.tablet} {
    > svg {
      width: 100%;
    }
  }
`;
var HitArea = styled31.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
`;
var Text2 = styled31.span`
  display: block;
  text-align: center;
  font-size: 14px;
  @media ${device.tablet} {
    font-size: 14px;
  }
  @media ${device.desktop} {
    font-size: 16px;
  }
`;
var IconLink = forwardRef4(function IconLink2({ text, icon, as, ...rest }, ref) {
  const internalRef = useRef2(null);
  function dispatchEvent(name) {
    const obj = internalRef.current?.querySelector("object");
    if (obj && obj.contentDocument) {
      const svg = obj.contentDocument.querySelector("svg");
      if (svg) {
        svg.dispatchEvent(new Event(name));
      }
    }
  }
  return /* @__PURE__ */ React190.createElement(Host5, { as, ref, ...rest }, /* @__PURE__ */ React190.createElement(
    HitArea,
    {
      onMouseEnter: () => dispatchEvent("mouseenter"),
      onMouseLeave: () => dispatchEvent("mouseleave")
    }
  ), /* @__PURE__ */ React190.createElement(IconWrap, { ref: internalRef }, /* @__PURE__ */ React190.createElement(Icon3, { name: icon ?? "facebook" })), /* @__PURE__ */ React190.createElement(Text2, null, text));
});

// src/components/buttons/PlayButton.tsx
import styled32 from "@emotion/styled";
import React191 from "react";
var Overlay = styled32.a`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  circle {
    fill: rgba(255, 255, 255, 0);
    stroke: rgba(255, 255, 255, 1);
    transition: fill 175ms ease-in-out;
  }

  path {
    stroke: rgba(255, 255, 255, 1);
    transition: 175ms ease-in-out;
    transition-property: stroke, fill;
  }

  path + path {
    stroke: none;
    opacity: 0;
    transition: opacity 175ms ease-in-out;
  }

  &:hover circle {
    fill: rgba(255, 255, 255, 1);
  }

  &:hover path + path {
    opacity: 1;
  }
`;
var PlayButton = (props) => {
  return /* @__PURE__ */ React191.createElement(Overlay, { ...props }, /* @__PURE__ */ React191.createElement(
    "svg",
    {
      width: "124",
      height: "124",
      viewBox: "0 0 124 124",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg"
    },
    /* @__PURE__ */ React191.createElement("circle", { cx: "61.8", cy: "61.8", r: "60.8", strokeWidth: "1.6" }),
    /* @__PURE__ */ React191.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M44.9599 95.6422v-68.304l51.8322 34.152-51.8322 34.152z"
      }
    ),
    /* @__PURE__ */ React191.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M44.9599 95.6422v-68.304l51.8322 34.152-51.8322 34.152z",
        fill: "url(#paint0_linear)"
      }
    ),
    /* @__PURE__ */ React191.createElement("defs", null, /* @__PURE__ */ React191.createElement(
      "linearGradient",
      {
        id: "paint0_linear",
        x1: "10.8079",
        y1: "18.4981",
        x2: "10.8079",
        y2: "104.482",
        gradientUnits: "userSpaceOnUse"
      },
      /* @__PURE__ */ React191.createElement("stop", { stopColor: "#2F6183" }),
      /* @__PURE__ */ React191.createElement("stop", { offset: "1", stopColor: "#15324C" })
    ))
  ));
};

// src/components/buttons/SvgButton.tsx
import styled33 from "@emotion/styled";
import React192 from "react";
var Host6 = styled33.a`
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;
var SrOnly = styled33.span`
  ${sronly}
`;
var SvgButton = ({ title, children, target, ...rest }) => {
  return /* @__PURE__ */ React192.createElement(Host6, { target, title, ...rest }, children, /* @__PURE__ */ React192.createElement(SrOnly, null, title));
};

// src/components/buttons/TableButton.tsx
import styled34 from "@emotion/styled";
import React193 from "react";
var MyButton2 = styled34(Button)`
  min-height: 40px;
  max-height: 40px;
  ${typography.numbers2};
`;
var TableButton = function TableButton2(props) {
  const { ...rest } = props;
  return /* @__PURE__ */ React193.createElement(MyButton2, { ...rest });
};

// src/components/buttons/TableDetailButton.tsx
import { useTheme as useTheme3 } from "@emotion/react";
import styled35 from "@emotion/styled";
import React194, { forwardRef as forwardRef5 } from "react";
var BorderedButton = styled35(({ ...rest }) => /* @__PURE__ */ React194.createElement(Button, { bordered: true, ...rest }))`
  min-width: 100%;
  min-height: 52px;
  div {
    font-size: 16px;
  }
`;
var DetailButton = styled35(BorderedButton)`
  margin-bottom: 12px;
  background-color: white;
  border: 1px solid rgb(22, 62, 99);
  color: rgb(30, 61, 90);
  a {
    margin-top: 55px;
  }
  @media ${device.tablet} {
    margin-top: 0px;
    a {
      margin-top: 0px;
    }
  }
`;
var TableDetailButton = forwardRef5(
  function TableButton3(props, _) {
    const theme2 = useTheme3();
    const { ...rest } = props;
    return /* @__PURE__ */ React194.createElement(React194.Fragment, null, /* @__PURE__ */ React194.createElement(DetailButton, { ...rest, isSmall: true, bg: theme2.colors.primary }));
  }
);

// src/components/buttons/TableMenuButton.tsx
import styled36 from "@emotion/styled";
import React195 from "react";
var Button3 = styled36.button`
  ${typography.tableNumbers};
`;
var TableMenuButton = function TableMenuButton2(props) {
  const { ...rest } = props;
  return /* @__PURE__ */ React195.createElement(Button3, { ...rest });
};

// src/components/buttons/TextCardButton.tsx
import styled37 from "@emotion/styled";
import React196 from "react";
var ArrowLine = styled37.span`
  display: inline-block;
  width: 22px;
  height: 1px;
  background-color: ${(props) => props.theme.colors.primary};

  transition: transform 300ms ease-in-out;
  transform-origin: right center;
  transform: ${(props) => props.isHover ? "scaleX(1.5)" : "scaleX(1)"};
`;
var Host7 = styled37.a`
  position: relative;
  display: flex;
  align-items: center;

  padding-top: 12px;
  padding-bottom: 12px;
  padding-right: 85px;

  cursor: pointer;

  font-weight: 500;

  &:hover {
    ${ArrowLine} {
      transform: scaleX(1.5);
    }
  }
`;
var Text3 = styled37.span``;
var Arrow2 = styled37.span`
  position: absolute;
  right: 15px;

  display: flex;
  align-items: center;
`;
var ArrowOdd = styled37.span`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 3px 0 3px 6px;
  border-color: ${(props) => `transparent transparent transparent ${props.theme.colors.primary}`};
`;
var TextCardButton = ({ title, isHover, as, ...rest }) => {
  return /* @__PURE__ */ React196.createElement(Host7, { as, ...rest }, /* @__PURE__ */ React196.createElement(Text3, null, title), /* @__PURE__ */ React196.createElement(Arrow2, null, /* @__PURE__ */ React196.createElement(ArrowLine, { isHover }), /* @__PURE__ */ React196.createElement(ArrowOdd, null)));
};

// src/components/divider/Divider.tsx
import styled38 from "@emotion/styled";
var fnCall = (variable, props) => {
  return typeof variable === "function" ? variable(props) : variable;
};
var Divider = styled38.div`
  width: ${(props) => props.width ?? "27px"};
  height: 1px;
  background-color: ${(props) => fnCall(props.color, props) ?? props.theme.colors.foreground};
  margin: ${(props) => props.margin ?? "16px 0"};
`;

// src/components/grid/Column.tsx
import { css as css7 } from "@emotion/react";
import styled39 from "@emotion/styled";
import { animated as animated10 } from "react-spring";
function columnStyles(props) {
  const offsetLeft = typeof props.offset === "object" ? props.offset.left : props.offset;
  const offsetRight = props.offsetRight ? props.offsetRight : typeof props.offset === "object" && props.offset.right;
  const gutter = props.gutter !== void 0 ? Number(props.gutter) : props.theme.gutter;
  return css7`
    align-self: ${props.align ?? "stretch"};

    width: ${!props.width ? "100" : props.width * 100}%;

    margin-left: ${(offsetLeft || 0) * 100}%;
    margin-right: ${(offsetRight || 0) * 100}%;

    margin-top: ${props.spaceMobile ? "80px" : "0px"};
    padding-left: ${gutter / 2}px;
    padding-right: ${gutter / 2}px;

    @media ${device.tablet} {
      margin-top: 0;
    }
  `;
}
function breakpointStyles(props) {
  const output = [];
  for (const breakPointName in props.theme.breakpoints) {
    const currentPropThemeOfBreakPoint = { ...props }[breakPointName] ?? null;
    if (currentPropThemeOfBreakPoint) {
      const breakPointProps = {
        ...props,
        ...typeof currentPropThemeOfBreakPoint === "object" ? currentPropThemeOfBreakPoint : { width: currentPropThemeOfBreakPoint }
      };
      const currentBreakPoint = { ...props.theme.breakpoints }[breakPointName] ?? null;
      output.push(
        breakPointName !== "sm" ? css7`
              @media (min-width: ${currentBreakPoint?.width ?? 0}px) {
                ${columnStyles(breakPointProps)}
              }
            ` : css7`
              ${columnStyles(breakPointProps)}
            `
      );
    }
  }
  return output;
}
var Column = styled39(animated10.div, {
  shouldForwardProp: shouldForwardProp("div")
})`
  flex: none;
  ${columnStyles}
  ${breakpointStyles}
`;

// src/components/grid/Row.tsx
import styled40 from "@emotion/styled";
import React197 from "react";
var Row = styled40.div`
  display: flex;
  flex-wrap: ${(props) => props.wrap === false ? "nowrap" : "wrap"};
  flex-direction: ${(props) => props.reverse === true ? "row-reverse" : "row"};
  align-items: ${(props) => props.valign || "stretch"};
  justify-content: ${(props) => props.align || "flex-start"};

  margin-left: ${(props) => (props.gutter !== void 0 ? Number(props.gutter) : props.theme.gutter) / -2}px;
  margin-right: ${(props) => (props.gutter !== void 0 ? Number(props.gutter) : props.theme.gutter) / -2}px;
`;
var TrailRow = ({
  children,
  ...rest
}) => {
  const { ref, itemsWithStyleProp } = useRevealTrail(children);
  return /* @__PURE__ */ React197.createElement(Row, { ref, ...rest }, itemsWithStyleProp);
};

// src/components/logo/Logo.tsx
import React198 from "react";
var Logo = ({ color, ...rest }) => /* @__PURE__ */ React198.createElement("svg", { width: "24px", height: "24px", viewBox: "0 0 24 24", version: "1.1", ...rest }, /* @__PURE__ */ React198.createElement("title", null, "Landsbankinn Logo"), /* @__PURE__ */ React198.createElement("g", { stroke: "none", strokeWidth: "1", fill: "none", fillRule: "evenodd" }, /* @__PURE__ */ React198.createElement("g", { transform: "translate(-117.000000, -17.000000)" }, /* @__PURE__ */ React198.createElement("g", null, /* @__PURE__ */ React198.createElement("g", { transform: "translate(111.000000, 0.000000)" }, /* @__PURE__ */ React198.createElement(
  "path",
  {
    d: "M27.9482285,40.7999976 C29.7472636,40.7999976 30.2509934,39.5158513 29.8911864,38.374388 C29.459418,37.0902417 26.0772319,27.3164618 26.0772319,27.3164618 C26.0772319,27.3164618 22.6230845,37.6609734 22.1913161,38.9451196 C21.471702,41.0853634 18.449323,40.3006074 19.1689371,37.9463392 C19.8885511,35.592071 24.134274,22.3225596 24.7099652,20.6817061 C25.4295793,18.5414623 26.3650775,17.5426819 28.5239197,17.399999 L17.9455932,17.399999 C16.0745967,17.399999 14.7792914,18.6841452 14.347523,20.1823159 C13.9157545,21.6804865 9.16630181,36.2341442 8.73453338,37.6609734 C8.15884214,39.3731684 7.07942107,40.3719488 6,40.7286561 C7.5831509,40.7999976 27.9482285,40.7999976 27.9482285,40.7999976",
    fill: !color || color === "red" ? "#FF0000" : "#1e3d5a"
  }
))))));

// src/components/modal/Modal.tsx
import styled41 from "@emotion/styled";
import React200, { useContext as useContext2, useEffect as useEffect7, useRef as useRef4, useState as useState9 } from "react";
import { createPortal } from "react-dom";
import { animated as animated11, useSpring as useSpring8 } from "react-spring";

// src/contexts/LayoutContext.tsx
import React199, { createContext as createContext2, useState as useState7 } from "react";
var DEFAULT_BUBBLE = {
  open: false,
  active: "Front",
  defaultServiceSelected: void 0
};
var LayoutContext = createContext2({
  isMobile: false,
  isIOS: false,
  isEdge: false,
  bubble: DEFAULT_BUBBLE,
  setBubble: () => void 0,
  isSubnav: false,
  setIsSubnav: () => void 0,
  isNavigationOpen: false,
  setIsNavigationOpen: () => void 0,
  lastScrollPosition: 0,
  setLastScrollPosition: () => void 0,
  scrollDisabled: false,
  setScrollDisabled: () => void 0,
  isIE: false
});

// src/hooks/useEventListener.ts
import { useEffect as useEffect5, useRef as useRef3 } from "react";
function useEventListener(eventName, handler, element) {
  const savedHandler = useRef3();
  useEffect5(() => {
    const targetElement = element?.current || window;
    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }
    if (savedHandler.current !== handler) {
      savedHandler.current = handler;
    }
    const eventListener = (event) => {
      if (savedHandler?.current) {
        savedHandler.current(event);
      }
    };
    targetElement.addEventListener(eventName, eventListener);
    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, handler]);
}
var useEventListener_default = useEventListener;

// src/hooks/useClickOutside.ts
function useOnClickOutside(ref, handler, mouseEvent = "mousedown") {
  useEventListener_default(mouseEvent, (event) => {
    const el = ref?.current;
    if (!el || el.contains(event.target)) {
      return;
    }
    handler(event);
  });
}
var useClickOutside_default = useOnClickOutside;

// src/hooks/useLockedBody.ts
import { useEffect as useEffect6, useLayoutEffect, useState as useState8 } from "react";
var useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect6;
function useLockedBody(initialLocked = false) {
  const [locked, setLocked] = useState8(initialLocked);
  useIsomorphicLayoutEffect(() => {
    if (!locked) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    const root = document.getElementById("__next");
    const scrollBarWidth = root ? root.offsetWidth - root.scrollWidth : 0;
    if (scrollBarWidth) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      document.body.style.overflow = originalOverflow;
      if (scrollBarWidth) {
        document.body.style.paddingRight = originalPaddingRight;
      }
    };
  }, [locked]);
  useEffect6(() => {
    if (locked !== initialLocked) {
      setLocked(initialLocked);
    }
  }, [initialLocked]);
  return [locked, setLocked];
}
var useLockedBody_default = useLockedBody;

// src/components/modal/Modal.tsx
var CloseButton = styled41.button`
  position: absolute;
  top: 16px;
  right: 16px;
  color: black;
  padding: 15px 19px;
  cursor: pointer;
`;
var Host8 = styled41(animated11.div)`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  overflow-y: auto;
  background: rgba(30, 61, 90, 0.2);
  @supports (backdrop-filter: blur(3px)) {
    backdrop-filter: blur(3px);
    background: rgba(30, 61, 90, 0.2);
  }
`;
var ContentContainer = styled41(Container)`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
var Content2 = styled41.div`
  width: 100%;
`;
var Dialog = ({ children }) => {
  if (typeof window === "undefined") {
    return null;
  }
  const root = document.getElementById("portal");
  return root ? createPortal(children, root) : null;
};
var Modal = ({ visible: vis, onClose, children }) => {
  const [visible, setVisible] = useState9(vis);
  const { setScrollDisabled } = useContext2(LayoutContext);
  const contentRef = useRef4(null);
  const closeRef = useRef4(null);
  const { opacity } = useSpring8({
    opacity: visible ? 1 : 0
  });
  const onCloseClick = () => {
    onClose?.();
  };
  useClickOutside_default(contentRef, onCloseClick);
  useLockedBody_default(visible);
  useEffect7(() => {
    setScrollDisabled(visible);
  }, [setScrollDisabled, visible]);
  useEffect7(() => {
    const onKeyUp = (e) => {
      if (e.key === "Escape") {
        onClose?.();
      }
    };
    document.addEventListener("keyup", onKeyUp, false);
    return () => {
      document.removeEventListener("keyup", onKeyUp, false);
    };
  }, [onClose]);
  useEffect7(() => {
    setTimeout(() => {
      setVisible(vis);
    }, 175);
  }, [vis]);
  if (!visible) {
    return null;
  }
  return /* @__PURE__ */ React200.createElement(Dialog, null, /* @__PURE__ */ React200.createElement(
    Host8,
    {
      style: {
        pointerEvents: visible ? "auto" : "none",
        opacity
      }
    },
    /* @__PURE__ */ React200.createElement(ContentContainer, null, /* @__PURE__ */ React200.createElement(Content2, { ref: contentRef }, children)),
    /* @__PURE__ */ React200.createElement(
      CloseButton,
      {
        ref: closeRef,
        tabIndex: 0,
        "aria-label": "Close Modal",
        onClick: onCloseClick
      },
      /* @__PURE__ */ React200.createElement(CloseThick, null)
    )
  ));
};

// src/components/dialog/Dialog.tsx
import styled42 from "@emotion/styled";
import { useResource as useResource3 } from "@landsbankinn/l16n";
import React201, { useEffect as useEffect8, useMemo as useMemo2, useRef as useRef5 } from "react";
import { createPortal as createPortal2 } from "react-dom";
var Host9 = styled42.div`
  background-color: ${theme.colors.background};
  position: fixed;
  overflow: scroll;
  top: 0;
  left: 0;
  min-width: 100vw;
  min-height: 100vh;
  z-index: 101;

  @media ${device.desktop} {
    background: rgba(21, 46, 72, 0.9);
  }
`;
var AbsoluteContainer = styled42.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 101;
  background: white;
  @media ${device.desktop} {
    margin: 90px 130px 90px 130px;
    width: calc(100% - 260px);
  }
`;
var Container2 = styled42.div`
  margin-left: 44px;
  margin-right: 44px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  min-height: 100vh;
  flex: 1;
  padding-bottom: 144px;

  @media ${device.desktop} {
    margin-left: 100px;
    margin-right: 100px;
  }
`;
var Header2 = styled42.div`
  position: fixed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background-color: ${theme.shades.primary2};
  z-index: 101;
  padding: 23px 44px 25px 44px;
`;
var Title2 = styled42.h2`
  ${typography.body}
  cursor: pointer;
  font-weight: bold;
  ${(props) => props.hidden ? sronly : ""}
`;
var HeaderBackButton = styled42.button`
  cursor: pointer;
  width: 30px;
`;
var ChildContainer = styled42.div`
  padding-top: ${(props) => {
  if (!props.noHeader) {
    return props.removeConatinerTopPadding ? "72px;" : "156px;";
  }
  return "20px;";
}};
`;
function Dialog2({
  children,
  onClose,
  title,
  removeContainarTopPadding
}) {
  const { r } = useResource3();
  const titleRef = useRef5(null);
  const root = useMemo2(() => {
    if (typeof window !== "undefined") {
      return document.getElementById("portal");
    }
  }, []);
  useEffect8(() => {
    if (typeof window !== "undefined") {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [root]);
  useEffect8(() => {
    if (titleRef && titleRef.current) {
      titleRef.current.focus();
    }
  }, [titleRef]);
  if (!root) {
    return null;
  }
  return createPortal2(
    /* @__PURE__ */ React201.createElement(Host9, { role: "dialog", "aria-modal": true, "data-cy": "overlayDialog" }, /* @__PURE__ */ React201.createElement(AbsoluteContainer, null, onClose !== void 0 ? /* @__PURE__ */ React201.createElement(Header2, null, /* @__PURE__ */ React201.createElement(Title2, { tabIndex: -1, ref: titleRef, hidden: title.hidden }, title.label), /* @__PURE__ */ React201.createElement(HeaderBackButton, { onClick: onClose }, /* @__PURE__ */ React201.createElement(CloseThick, { "aria-label": r(Dialog_res_default.closeAriaLabel) }))) : /* @__PURE__ */ React201.createElement(Title2, { tabIndex: -1, ref: titleRef, hidden: true }, title.label), /* @__PURE__ */ React201.createElement(Container2, null, /* @__PURE__ */ React201.createElement(
      ChildContainer,
      {
        removeConatinerTopPadding: removeContainarTopPadding,
        noHeader: onClose === void 0
      },
      children
    )))),
    root
  );
}

// src/components/tabs/SelectorTabs.tsx
import styled64 from "@emotion/styled";
import { rgba as rgba8 } from "polished";
import React228, { createRef, useEffect as useEffect13, useRef as useRef8 } from "react";
import { animated as animated14, useSpring as useSpring10 } from "react-spring";

// src/components/form/checkbox/Checkbox.tsx
import { css as css8 } from "@emotion/react";
import styled44 from "@emotion/styled";
import { rgba } from "polished";
import React203, { forwardRef as forwardRef6 } from "react";

// src/components/form/Label.tsx
import styled43 from "@emotion/styled";
import React202 from "react";
import { animated as animated12, config, useTransition } from "react-spring";
import useMeasure2 from "react-use-measure";
var Label = styled43.label`
  ${({ size }) => size === "small" ? typography.label : typography.tableNumbers}
  font-weight: 400;
  text-align: ${({ size }) => {
  return size === "small" ? "center" : "left";
}};
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: ${({ disabled }) => disabled ? 0.3 : 1};
  transition: color 0.1s ease-in;
`;
var ErrorLabel = styled43(Label)`
  position: relative;
  text-align: right;
  text-wrap: wrap;
  margin-bottom: 36px;
  display: block;
`;
var AnimatedErrorLabel = ({
  visible = false,
  id,
  errorMessage
}) => {
  const [innerRef, inner] = useMeasure2();
  const errorTransition = useTransition(visible, {
    enter: () => async (next) => {
      await next({ height: inner.height, opacity: 1 });
    },
    leave: () => async (next) => {
      await next({ opacity: 0, height: 0 });
    },
    update: { height: inner.height },
    config: { ...config.stiff, clamp: true }
  });
  return errorTransition(
    (style, show) => show && /* @__PURE__ */ React202.createElement(animated12.div, { style }, /* @__PURE__ */ React202.createElement(ErrorLabel, { id, ref: innerRef }, errorMessage))
  );
};

// src/components/form/checkbox/Checkbox.tsx
var CheckboxHost = styled44.div`
  position: relative;
  display: flex;
  flex-direction: column;
  & > * + * {
    margin-top: 8px;
  }
`;
var CheckboxInput = styled44.input`
  ${dynamicInput};
`;
var dynamicLabel = (disabled) => css8`
  position: relative;
  width: 100%;
  cursor: pointer;
  white-space: pre-wrap;
  ${disabled ? "pointer-events: none;" : ""}
  &:hover > div {
    background-image: url('data:image/svg+xml;utf8,<svg width="16px" height="15px" viewBox="0 0 16 15" version="1.1" xmlns="http://www.w3.org/2000/svg" ><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="Assets" transform="translate(-347.000000, -1288.000000)" stroke="%231e3d5a" stroke-width="2"><polyline id="icon-check-hover" transform="translate(355.000000, 1295.000000) scale(-1, 1) rotate(-180.000000) translate(-355.000000, -1295.000000) " points="348 1295.46154 353.5 1289 362 1301"></polyline></g></g></svg>');
    background-repeat: no-repeat;
    background-size: 12px;
    background-position: center;
  }
`;
var CheckboxContainer = styled44.div`
  margin-right: 12px;
  min-width: 24px;
  width: 24px;
  height: 24px;
  border-radius: 1px;

  box-shadow: 0 0 0 1px
    ${(props) => !props.disabled ? props.isValid === false ? props.theme?.colors.warning : rgba(props.theme?.colors.foreground ?? "", 0.3) : "#a0a0a0"};
  background-color: ${(props) => props.checked ? props.disabled ? "#a0a0a0" : props.theme?.colors.primary : props.theme?.colors.background};

  ${(props) => props.isValid === false && `background-color: ${rgba(props.theme.colors.warning, 0.1)}`};
`;
var Label2 = styled44.label`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  ${(props) => dynamicLabel(props.disabled)};
  ${typography.numbers2};
  color: ${({ theme: theme2 }) => theme2.colors.foreground};
  box-shadow: 0 0 0 1px
    ${({ variant, theme: theme2 }) => variant === "fullBorder" || variant === "bottomBorder" ? rgba(theme2?.colors.foreground ?? "", 0.8) : "transparent"};
  height: 64px;
  padding-left: 15px;
  padding-right: 15px;
  background-color: ${({ theme: theme2, transparentBackground }) => transparentBackground ? "transparent" : theme2.colors.background};

  ${({ theme: theme2, isValid }) => isValid !== void 0 && isValid === false && `box-shadow: 0 0 0 2px ${theme2?.colors.warning}`};

  &:hover {
    ${({ variant, theme: theme2 }) => variant === "fullBorder" && `
        box-shadow: 0 0 0 1px ${rgba(theme2?.colors.foreground ?? "", 0.5)};
      `};
    & > div {
      background-color: transparent;
    }
  }

  &:active {
    box-shadow: 0 0 0 2px
      ${(props) => props.transparentBackground ? "transparent" : props.theme?.shades.primary3};
  }
`;
var Checkbox = forwardRef6(function Cbox({
  checked,
  onChange,
  id,
  label: label2,
  disabled,
  align = "left",
  variant = "fullBorder",
  errorMessage,
  transparentBackground,
  describedBy,
  ...rest
}, ref) {
  return /* @__PURE__ */ React203.createElement(CheckboxHost, { ...rest }, /* @__PURE__ */ React203.createElement(
    Label2,
    {
      htmlFor: id,
      checked,
      disabled,
      align,
      isValid: rest.isValid,
      variant,
      transparentBackground
    },
    /* @__PURE__ */ React203.createElement(
      CheckboxContainer,
      {
        checked,
        disabled,
        align,
        isValid: rest.isValid
      },
      checked && /* @__PURE__ */ React203.createElement(CheckboxActive, { disabled })
    ),
    label2
  ), errorMessage && /* @__PURE__ */ React203.createElement(ErrorLabel, null, errorMessage), /* @__PURE__ */ React203.createElement(
    CheckboxInput,
    {
      type: "checkbox",
      id,
      checked,
      "aria-disabled": disabled,
      "aria-describedby": describedBy,
      tabIndex: 0,
      onChange: () => {
        if (!disabled) onChange(!checked);
      },
      ref
    }
  ));
});

// src/components/form/contact-input/EmaiInput.tsx
import styled46 from "@emotion/styled";
import React206, { forwardRef as forwardRef8 } from "react";

// src/components/form/Input.tsx
import { css as css9 } from "@emotion/react";
import styled45 from "@emotion/styled";
import { rgba as rgba2 } from "polished";
import React205, {
  forwardRef as forwardRef7,
  useContext as useContext3,
  useEffect as useEffect9,
  useMemo as useMemo3,
  useState as useState11
} from "react";
import { v4 as getUUID } from "uuid";

// src/validation/Form.tsx
import * as React204 from "react";
import { useImmer } from "use-immer";
var FormContext = React204.createContext({
  inputs: [],
  addInput: () => {
  },
  removeInput: () => {
  },
  updateInput: () => {
  },
  isInputGroupValid: () => true,
  validate: true,
  hasSubmitted: false
});
var Form = ({ children, ...rest }) => {
  const [hasSubmitted, setHasSubmitted] = React204.useState(false);
  const [{ inputs, validate }, setState] = useImmer({
    inputs: [],
    validate: false
  });
  const addInput = (input3) => {
    setState((draft) => {
      draft.inputs.push(input3);
    });
  };
  const removeInput = (name) => {
    setState((draft) => {
      draft.inputs = draft.inputs.filter((x) => x.name !== name);
    });
  };
  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const errors = [];
    validateInputs(errors);
    setHasSubmitted(true);
    if (errors.length > 0) {
      setTimeout(() => errors[0].onFocus());
    } else if (rest.onSubmit) {
      rest.onSubmit(e);
    }
    return false;
  };
  const validateInputs = (errors) => {
    inputs.forEach((input3) => {
      input3.validators.forEach((validator) => {
        if (!validator.validation(input3.value)) {
          errors.push({
            onFocus: input3.onFocus,
            message: validator.message,
            isValid: false
          });
        }
      });
    });
    setState((draft) => {
      draft.validate = true;
      draft.inputs = draft.inputs.map((input3) => {
        let errorMessage;
        input3.isValid = input3.validators.reduce(
          (current, validator) => {
            if (!validator.validation(input3.value)) {
              if (errorMessage === void 0) {
                errorMessage = validator.message;
              }
              return false;
            }
            return current;
          },
          true
        );
        input3.invalidMessge = errorMessage;
        return input3;
      });
    });
  };
  const updateInput = (input3) => {
    setState((draft) => {
      const index = draft.inputs.findIndex((x) => x.name === input3.name);
      if (index > -1) {
        draft.inputs[index] = input3;
      }
    });
  };
  const isInputGroupValid = (validationGroup) => {
    return inputs.filter((x) => x.validationGroup === validationGroup).reduce((current, item) => current && item.isValid, true);
  };
  return /* @__PURE__ */ React204.createElement(
    FormContext.Provider,
    {
      value: {
        inputs,
        validate,
        removeInput,
        addInput,
        updateInput,
        isInputGroupValid,
        hasSubmitted
      }
    },
    /* @__PURE__ */ React204.createElement("form", { ...rest, onSubmit, style: { width: "100%" } }, children)
  );
};

// src/components/form/Input.tsx
var InputHost = styled45.div`
  display: flex;
  flex-direction: column;
  position: relative;
  & > * + * {
    margin-top: 8px;
  }
`;
var inputCss = ({
  inputSize,
  variant,
  theme: theme2,
  isValid,
  Icon: Icon5
}) => css9`
  width: 100%;
  height: ${inputSize === "small" ? "40px" : "64px"};
  color: ${theme2.shades.primary5};

  padding: 20px 16px;

  ${Icon5 && `
    padding-left: 58px;
  `}

  ${variant === "fullBorder" && `box-shadow: 0 0 0 1px
     ${rgba2(theme2.colors.foreground, 0.7)}`};

  ${variant === "bottomBorder" && `border-bottom: 1px solid
     ${rgba2(theme2?.colors.foreground, 0.7)}`};

  &:hover {
    box-shadow: 0 0 0 1px ${theme2?.colors.foreground};

    ${variant === "fullBorder" && `box-shadow: 0 0 0 1px
     ${theme2?.colors.foreground}`};

    ${variant === "bottomBorder" && `border-bottom: 1px solid
     ${rgba2(theme2.colors.foreground, 0.3)}`};

    &::placeholder {
      color: ${theme2.shades.primary5};
    }
  }

  &:focus + label {
    color: ${rgba2(theme2.shades.primary5, 0.6)};
  }

  &:active,
  &:focus {
    box-shadow: 0 0 0 2px ${theme2.shades.primary3};
  }

  ${isValid === false && `
    box-shadow: 0 0 0 2px ${theme2.colors.secondary};
  `}

  &::placeholder {
    color: ${rgba2(theme2.shades.primary5, 0.6)};
  }

  &:disabled {
    opacity: 0.5;
    background-color: ${rgba2(theme2.colors.foreground ?? "", 0.05)};
  }

  transition: box-shadow 0.1s ease-in;
`;
var BaseInput = styled45.input`
  ${inputCss};
  ${(props) => props.inputSize === "small" ? typography.numbers : props.inputSize === "big" ? typography.input2 : typography.body};
`;
var InputContainer = styled45.div`
  position: relative;

  & > svg {
    position: absolute;
    left: 24px;
    top: 50%;
    margin: 0;
    height: 17px;
    width: 17px;
    transform: translate(0, -50%);
  }
`;
var SuffixLabel = styled45.span`
  position: absolute;
  ${typography.body};
  top: 52%;
  transform: translateY(-50%);
  right: 16px;
  opacity: ${(props) => props.isActive ? "30%" : "100%"};
  margin-top: 0px;
  color: ${({ theme: theme2 }) => rgba2(theme2.shades.primary5, 1)};
  height: 24px;

  text-decoration: none;
  &:hover {
    color: ${({ theme: theme2 }) => rgba2(theme2.shades.primary5, 0.6)};
  }
`;
var Input = forwardRef7(function Input2({
  inputRef,
  isValid,
  label: label2,
  errorMessage,
  variant = "fullBorder",
  labelVariant = "aboveInput",
  textAlign = "left",
  errorMessageVariant = "belowInput",
  hackSuffix,
  placeholder,
  Icon: Icon5,
  ...rest
}, ref) {
  const formContext = useContext3(FormContext);
  const [visited, setVisited] = useState11(false);
  const [focused, setFocused] = useState11(false);
  const [uuid2, setUuid] = useState11("");
  const [errorLabelId, setErrorLabelId] = useState11("");
  const showValidState = useMemo3(() => {
    return !focused && formContext.validate && !isValid || !focused && visited && !!rest.value && rest.value !== hackSuffix;
  }, [visited, isValid, focused, rest.value, hackSuffix, formContext.validate]);
  useEffect9(() => {
    setUuid(getUUID());
    setErrorLabelId(getUUID());
  }, []);
  return /* @__PURE__ */ React205.createElement(InputHost, { ref: rest.ref ?? ref }, label2 && labelVariant === "aboveInput" && /* @__PURE__ */ React205.createElement(
    Label,
    {
      size: rest.inputSize,
      htmlFor: rest.id ?? uuid2,
      disabled: rest.disabled,
      isValid: showValidState ? isValid : void 0
    },
    label2
  ), /* @__PURE__ */ React205.createElement(InputContainer, null, Icon5 && /* @__PURE__ */ React205.createElement(Icon5, { stroke: "#163E63" }), /* @__PURE__ */ React205.createElement(
    BaseInput,
    {
      ref: inputRef,
      id: rest.id ?? uuid2,
      isValid: showValidState ? isValid : void 0,
      "aria-invalid": !isValid,
      visited,
      "aria-describedby": errorLabelId,
      variant,
      labelVariant,
      style: { textAlign },
      placeholder,
      ...rest,
      onFocus: (e) => {
        setFocused(true);
        if (rest.onFocus) {
          rest.onFocus(e);
        }
      },
      Icon: Icon5,
      onBlur: (e) => {
        setVisited(true);
        setFocused(false);
        if (rest.onBlur) {
          rest.onBlur(e);
        }
      }
    }
  ), hackSuffix && /* @__PURE__ */ React205.createElement(SuffixLabel, { isActive: focused }, hackSuffix)), /* @__PURE__ */ React205.createElement(
    AnimatedErrorLabel,
    {
      visible: !!errorMessage && errorMessageVariant === "belowInput",
      id: errorLabelId,
      errorMessage
    }
  ));
});

// src/components/form/contact-input/EmaiInput.tsx
var Host10 = styled46.div``;
var EmailInput = forwardRef8(
  function EmailInput2({ value = "", ...rest }, ref) {
    return /* @__PURE__ */ React206.createElement(Host10, null, /* @__PURE__ */ React206.createElement(Input, { value, ...rest, ref }));
  }
);

// src/components/form/contact-input/Kennitalanput.tsx
import styled47 from "@emotion/styled";
import React207 from "react";
import { PatternFormat } from "react-number-format";
var Host11 = styled47.div``;
var FormattedInput = styled47(Input)`
  text-align: center;
  width: 100%;
`;
function KennitalaInput({
  inputSize,
  input: input3,
  value = "",
  ...rest
}) {
  return /* @__PURE__ */ React207.createElement(Host11, null, /* @__PURE__ */ React207.createElement(
    PatternFormat,
    {
      customInput: input3 ?? FormattedInput,
      displayType: "input",
      format: value && value?.toString().length > 6 ? "######-####" : "##########",
      inputSize,
      value,
      ...rest
    }
  ));
}

// src/components/form/contact-input/PhoneInput.tsx
import styled48 from "@emotion/styled";
import React208 from "react";
import { PatternFormat as PatternFormat2 } from "react-number-format";
var Host12 = styled48.div``;
var FormattedInput2 = styled48(Input)`
  text-align: center;
  width: 100%;
`;
function PhoneInput({
  inputSize,
  input: input3,
  value = "",
  ...rest
}) {
  return /* @__PURE__ */ React208.createElement(Host12, null, /* @__PURE__ */ React208.createElement(
    PatternFormat2,
    {
      customInput: input3 ?? FormattedInput2,
      displayType: "input",
      format: value && value.toString().trim().length > 3 ? "###-####" : "#######",
      inputSize,
      value,
      ...rest
    }
  ));
}

// src/components/form/contact-input/TextInput.tsx
import styled49 from "@emotion/styled";
import React209, { useState as useState12 } from "react";
import { v4 as getUUID2 } from "uuid";
var TextArea = styled49.textarea`
  ${inputCss};
  padding: 20px 20px 12px 15px;
  ${(props) => props.inputSize === "small" ? typography.numbers(props) : props.inputSize === "big" ? typography.input(props) : typography.input2(props)};

  resize: none;
  outline: none;

  ::placeholder {
    color: ${(props) => props.theme.shades.primary2};
  }
  ${(props) => props.setHeight ? `height:${props.setHeight}` : ""}
`;
function TextInput({
  label: label2,
  inputSize,
  id,
  isValid,
  errorMessage,
  placeholder,
  setHeight,
  variant,
  value = "",
  ...rest
}) {
  const [uuid2] = useState12(getUUID2());
  return /* @__PURE__ */ React209.createElement(InputHost, null, label2 && /* @__PURE__ */ React209.createElement(Label, { size: inputSize, htmlFor: id ?? uuid2 }, label2), /* @__PURE__ */ React209.createElement(
    TextArea,
    {
      id: id ?? uuid2,
      isValid: isValid !== false,
      variant,
      inputSize,
      setHeight,
      placeholder,
      value,
      ...rest
    }
  ), isValid === false && errorMessage && /* @__PURE__ */ React209.createElement(ErrorLabel, { size: inputSize }, errorMessage));
}

// src/components/form/date-input/DateInput.tsx
import styled50 from "@emotion/styled";
import { useResource as useResource4 } from "@landsbankinn/l16n";
import { is, enGB } from "date-fns/locale";
import React210, { useState as useState13 } from "react";
import ReactDatePicker, { registerLocale } from "react-datepicker";
registerLocale("enGB", enGB);
registerLocale("is", is);
var InputHost2 = styled50.div`
  display: flex;
  position: relative;
  flex-direction: column;

  & input {
    overflow: hidden;
    width: 100%;
  }

  .react-datepicker-popper {
    z-index: 10;
  }

  .react-datepicker {
    font-size: 12.8px;
    border-radius: 4.8px;
  }

  .react-datepicker__month {
    margin: 6.4px;
  }

  .react-datepicker__month-text {
    line-height: 27.2px;
  }

  .react-datepicker__day {
    width: 27.2px;
    line-height: 27.2px;
    margin: 2.656px;
  }

  .react-datepicker__day-name {
    width: 27.2px;
    line-height: 27.2px;
    margin: 2.656px;
  }

  .react-datepicker__current-month {
    font-size: 15px;
  }

  .todayHiglighting {
    border-radius: 0.3px;
  }

  svg:hover path {
    fill: #cee1ea;
  }
`;
var IconContainer = styled50.div`
  position: absolute;
  z-index: 9;
  right: 21px;
  top: 50%;
  pointer-events: none;
  > svg {
    height: 20px;
    width: 20px;
  }

  @media ${device.tablet} {
    > svg {
      height: inherit;
      width: inherit;
    }
  }
`;
var DateInput = ({
  value,
  onChange,
  placeholder,
  disabled,
  filterDate,
  includeDates,
  ref,
  popperPlacement,
  ...rest
}) => {
  const [hover, setHover] = useState13(false);
  const [active, setActive] = useState13(false);
  const { locale } = useResource4();
  return /* @__PURE__ */ React210.createElement(
    InputHost2,
    {
      ref,
      ...rest,
      onMouseEnter: () => setHover(true),
      onMouseLeave: () => setHover(false)
    },
    /* @__PURE__ */ React210.createElement(
      ReactDatePicker,
      {
        disabled,
        selected: value,
        placeholderText: placeholder,
        onChange: (val) => onChange(val),
        onFocus: () => setActive(true),
        onBlur: () => setActive(false),
        filterDate,
        includeDates,
        popperPlacement: popperPlacement ?? void 0,
        customInput: /* @__PURE__ */ React210.createElement(Input, { ...rest }),
        dateFormat: locale === "en" ? "dd/MM/yyyy" : "dd.MM.yyyy",
        locale: locale === "en" ? "enGB" : "is"
      }
    ),
    /* @__PURE__ */ React210.createElement(IconContainer, null, /* @__PURE__ */ React210.createElement(Datepicker, { active, hover }))
  );
};

// src/components/form/dropdown/Dropdown.tsx
import styled51 from "@emotion/styled";
import { ResizeObserver as ResizeObserver2 } from "@juggle/resize-observer";
import { rgba as rgba3 } from "polished";
import React211, {
  forwardRef as forwardRef9,
  useContext as useContext4,
  useEffect as useEffect10,
  useMemo as useMemo4,
  useState as useState14
} from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import useMeasure3 from "react-use-measure";
import { v4 as getUUID3 } from "uuid";
var InputHost3 = styled51.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;

  & > * + * {
    margin-top: 8px;
  }

  &:hover > label {
    color: ${({ theme: theme2 }) => rgba3(theme2.shades.primary5 ?? "", 1)};
  }
`;
var prefix = "react-select";
var ReactSelect = styled51(Select)`
  .${prefix}__control {
    border-width: 0;
    ${({ variant, theme: theme2 }) => variant === "fullBorder" && `box-shadow: 0 0 0 1px
        ${rgba3(theme2?.colors.foreground ?? "", 0.7)};
        `};

    ${({ variant, theme: theme2, currentTheme }) => variant === "bottomBorder" && `border-bottom: 1px solid
        ${rgba3(mapThemeColorStringToValue(theme2, currentTheme), 0.7)}`};

    ${({ theme: theme2, isValid }) => isValid === false && `box-shadow: 0 0 0 2px ${theme2?.colors.secondary}`};

    border-radius: 0;
    background-color: ${(props) => props.fill ? rgba3(mapThemeColorStringToValue(props.theme, props.currentTheme), 0.3) : "#fff"};
    .${prefix}__single-value {
      width: 100%;
      top: initial;
      bottom: initial;
      transform: none;
      margin-left: 0;
      margin-right: 0;
      text-align: ${({ inputSize }) => inputSize !== "small" ? "left" : "center"};
      color: ${({ theme: theme2 }) => theme2.shades.primary5};
      ${(props) => props.inputSize === "small" ? typography.numbers : props.inputSize === "big" ? typography.input2 : typography.body};
    }
    &::before {
      content: '';
      width: 12px;
      height: 12px;
      background: ${(props) => `linear-gradient(
        to bottom right,
        transparent 0%,
        transparent 50%,
        ${rgba3(
  mapThemeColorStringToValue(props.theme, props.currentTheme),
  0.3
)} 50%,
        ${rgba3(
  mapThemeColorStringToValue(props.theme, props.currentTheme),
  0.3
)} 100%
      )`};
      top: -6px;
      left: ${({ indicatorPosition }) => indicatorPosition === "right" ? `calc(75% - 6px)` : indicatorPosition === "left" ? `calc(25% - 6px)` : `calc(50% - 6px)`};
      transform: rotate(224deg);
      position: absolute;
      display: ${(props) => props.fill ? "block" : "none"};
    }

    &:active {
      box-shadow: 0 0 0 2px ${({ theme: theme2 }) => theme2.shades.primary3};
    }
  }
  .${prefix}__menu {
    min-width: 200px;
    text-align: left;
    z-index: 99;
  }

  .${prefix}__indicator-separator {
    display: none;
  }
  .${prefix}__indicator {
    width: 0;
    height: 0;
    border: solid;
    border-width: 0 1px 1px 0;
    transform: rotate(45deg);
    border-color: ${({ isValid }) => isValid === false ? theme.colors.secondary : theme.colors.foreground};

    padding: 5px;
    margin-right: 19px;
  }
  .${prefix}__indicator svg {
    display: none;
  }
  .${prefix}__value-container {
    height: ${({ inputSize }) => inputSize === "small" ? "40px" : "64px"};
    padding-left: 15px;
    justify-content: flex-start;
    ${typography.numbers2};
    color: ${({ theme: theme2 }) => theme2.shades.primary5};
  }
  .${prefix}__placeholder {
    color: ${({ theme: theme2 }) => rgba3(theme2.shades.primary5 ?? "", 0.5)};
    display: none;
    margin: 0;
    bottom: 12px;
    top: initial;
    transform: none;
    ${(props) => props.isValid === false && `
          color: ${theme?.colors.secondary};
          
        `}
  }
  .${prefix}__control--is-disabled {
    ${({ variant, theme: theme2 }) => variant === "fullBorder" && `box-shadow: 0 0 0 1px
        ${rgba3(theme2?.colors.foreground ?? "", 0.3)};
        `};
    background-color: ${({ theme: theme2 }) => rgba3(theme2.colors.foreground, 0.05)};
  }
`;
var ReactCreateble = styled51(CreatableSelect)`
  .${prefix}__control {
    border: none;

    border-bottom: 1px solid
      ${(props) => mapThemeColorStringToValue(props.theme, props.currentTheme)};
    border-radius: 0;
    background-color: ${(props) => props.fill ? rgba3(mapThemeColorStringToValue(props.theme, props.currentTheme), 0.3) : "transparent"};
  }
  .${prefix}__menu {
    min-width: 200px;
    text-align: left;
    z-index: 99;
  }
  .${prefix}__indicator-separator {
    display: none;
  }
  .${prefix}__indicator {
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6px 6px 0 6px;
    border-color: rgb(30, 60, 90) transparent transparent transparent;
    padding: 0;
    margin-right: 5px;
  }

  .${prefix}__indicator svg {
    display: none;
  }
  .${prefix}__value-container {
    justify-content: center;
    padding: 8px;
    ${typography.numbers};
    & > div:last-child {
      padding: 0px;
      margin: 0px;
    }
  }
`;
var Dropdown = forwardRef9(function Ddown({
  options,
  theme: theme2 = "primary",
  fill,
  onChange,
  value,
  buildOption,
  placeholder,
  isOptionDisabled,
  label: label2,
  createble,
  onCreateOption,
  formatCreateLabel,
  isValidNewOption,
  indicatorPosition = "center",
  isValid,
  labelVariant = "aboveInput",
  variant = "fullBorder",
  inputSize,
  errorMessage,
  isDisabled,
  components,
  noTranslate
}, ref) {
  const [uuid2, setUuid] = useState14("");
  const [visited, setVisited] = useState14(false);
  const [hostRef, bounds] = useMeasure3({ polyfill: ResizeObserver2 });
  const [selectedValue, setSelectedValue] = useState14(value);
  const formContext = useContext4(FormContext);
  const showValidState = useMemo4(() => {
    return formContext.validate && !isValid || visited && selectedValue !== void 0;
  }, [visited, isValid, selectedValue, formContext.validate]);
  useEffect10(() => {
    setUuid(getUUID3());
  }, []);
  useEffect10(() => {
    setSelectedValue(value);
  }, [value]);
  const body2 = useMemo4(() => {
    if (typeof window !== "undefined") {
      return document?.body;
    }
    return void 0;
  }, []);
  useEffect10(() => {
    if (options.length === 1) {
      let newOption = buildOption(options[0], true, 0);
      if ("options" in newOption && !("value" in newOption)) {
        newOption = buildOption(newOption.options, true, 0);
      }
      if ("value" in newOption && newOption?.value !== selectedValue?.value) {
        onChange(newOption);
      }
    }
  }, [buildOption, onChange, options, selectedValue]);
  function computeIsDisabled(options2, isDisabled2) {
    if (isDisabled2 !== void 0) {
      return isDisabled2;
    }
    return options2.length === 1 && typeof options2[0] === "object" && !("options" in options2[0]);
  }
  return /* @__PURE__ */ React211.createElement(InputHost3, { ref: hostRef }, label2 && labelVariant === "aboveInput" && /* @__PURE__ */ React211.createElement(Label, { size: inputSize, htmlFor: uuid2 }, label2), !createble ? /* @__PURE__ */ React211.createElement(React211.Fragment, null, /* @__PURE__ */ React211.createElement(
    ReactSelect,
    {
      ref,
      indicatorPosition,
      placeholder: labelVariant === "aboveInput" ? placeholder : void 0,
      fill,
      currentTheme: theme2,
      options: options.map((x) => buildOption(x, true, bounds.width)),
      classNamePrefix: (noTranslate ? "notranslate " : "") + prefix,
      value: selectedValue === void 0 ? null : buildOption(selectedValue, false, bounds.width),
      onChange: (val) => {
        if (val && "value" in val) {
          const item = options.find(
            (x) => buildOption(x, true, bounds.width)?.["value"] === val?.["value"]
          );
          onChange(val);
          setSelectedValue(item);
        }
      },
      instanceId: `id-${uuid2}`,
      isValid: showValidState ? isValid : void 0,
      isSearchable: false,
      isDisabled: computeIsDisabled(options, isDisabled),
      isOptionDisabled,
      variant,
      onFocus: () => setVisited(true),
      inputSize,
      openMenuOnFocus: true,
      components,
      menuPortalTarget: body2,
      styles: {
        menuPortal: (base2) => ({ ...base2, zIndex: 9999 })
      },
      menuPosition: "fixed"
    }
  ), errorMessage && /* @__PURE__ */ React211.createElement(
    AnimatedErrorLabel,
    {
      visible: isValid === false,
      id: uuid2,
      errorMessage
    }
  )) : /* @__PURE__ */ React211.createElement(
    ReactCreateble,
    {
      instanceId: `id-${uuid2}`,
      placeholder,
      fill,
      currentTheme: theme2,
      options: options.map((x) => buildOption(x, true, bounds.width)),
      classNamePrefix: prefix,
      value: buildOption(selectedValue, false, bounds.width),
      onChange: (val) => {
        onChange(val);
      },
      formatCreateLabel,
      onCreateOption,
      createOptionPosition: "first",
      isValidNewOption
    }
  ));
});

// src/components/form/dropdown/MobileDropdown.tsx
import styled52 from "@emotion/styled";
import { rgba as rgba4 } from "polished";
import React212, { useRef as useRef6, useState as useState15 } from "react";
var Button4 = styled52.button`
  font-size: 16px;
  line-height: 24px;
  cursor: pointer;
  font-weight: 500;
  background: ${(props) => props.inset ? props.theme.shades.primary1 : "#fff"};
  position: relative;
  width: 100%;

  border: ${(props) => props.bordered ? `1px solid ${props.theme.colors.primary}` : "none"};

  text-align: start;
  padding: ${(props) => props.bordered ? " 21px 20px 17px 12px" : " 15px 20px 15px 12px"};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
var Ul = styled52.ul`
  outline: none;
`;
var ListItem = styled52.li`
  border-bottom: 1px solid rgba(30, 61, 90, 0.2);
  &:hover {
    box-shadow: 0 3px 0 0 ${(props) => rgba4(props.theme.colors.primary, 1)};
    border-color: transparent;
    cursor: pointer;
  }
`;
var ListButton = styled52.button`
  ${typography.body}
  width: 100%;
  text-align: start;
  padding: ${(props) => props.hasSubtext ? "14px 0 13px 0" : "23px 0 22px 0"};
  line-height: 18px;
  cursor: pointer;
  font-weight: bold;
  opacity: ${(props) => props.selected ? "0.3" : "1"};
`;
var SubText = styled52.span`
  ${typography.bodySmall}
  opacity: ${(props) => props.selected ? "1" : "0.6"};
`;
var MobileDropdown = ({
  onChange,
  options,
  value,
  placeholder,
  dialogTitle,
  bordered,
  inset
}) => {
  const [open, setOpen] = useState15(false);
  const ref = useRef6(null);
  const buttonRef = useRef6(null);
  return /* @__PURE__ */ React212.createElement(React212.Fragment, null, /* @__PURE__ */ React212.createElement(
    Button4,
    {
      bordered,
      ref: buttonRef,
      inset,
      onClick: () => setOpen(true),
      "aria-haspopup": "listbox"
    },
    options.find((i) => i.value === value)?.label ?? placeholder,
    /* @__PURE__ */ React212.createElement("svg", { width: "16", height: "10", viewBox: "0 0 16 10" }, /* @__PURE__ */ React212.createElement(
      "path",
      {
        fill: "none",
        fillRule: "evenodd",
        stroke: "#163E63",
        strokeWidth: "2",
        d: "M14.545455.909091L8 8.272727 1.454545.909091"
      }
    ))
  ), open && /* @__PURE__ */ React212.createElement(Dialog2, { onClose: () => setOpen(false), title: { label: dialogTitle } }, /* @__PURE__ */ React212.createElement(Ul, { ref, tabIndex: -1, role: "listbox" }, options.map((option) => /* @__PURE__ */ React212.createElement(ListItem, { key: option.value, role: "option" }, /* @__PURE__ */ React212.createElement(
    ListButton,
    {
      hasSubtext: !!option.subText,
      selected: option.value === value,
      onClick: () => {
        onChange(option.value);
        setOpen(false);
        buttonRef?.current?.focus();
      }
    },
    /* @__PURE__ */ React212.createElement("p", null, option.label),
    option.subText && /* @__PURE__ */ React212.createElement(SubText, null, option.subText)
  ))))));
};

// src/components/form/dropdown/AsyncDropdown.tsx
import styled53 from "@emotion/styled";
import { useResource as useResource5 } from "@landsbankinn/l16n";
import { rgba as rgba5 } from "polished";
import React213, { useState as useState16 } from "react";
import { AsyncPaginate, useComponents } from "react-select-async-paginate";
import { v4 as getUUID4 } from "uuid";

// src/components/form/dropdown/AsyncDropdown.css.ts
var customStyles = (menuPlacement, menuTheme, isValid) => {
  return {
    input: () => ({ width: "100%" }),
    container: () => ({
      position: menuPlacement === "top" ? "relative" : void 0,
      width: "100%"
    }),
    control: (state) => ({
      alignItems: "center",
      backgroundColor: "hsl(0,0%,100%)",
      cursor: "pointer",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "spaceBetween",
      minHeight: "64px",
      outline: 0,
      position: "relative",
      transition: "all 100ms",
      boxSizing: "border-box",
      border: isValid === false ? "none" : "1px solid " + theme.colors.foreground,
      boxShadow: isValid === false ? "0 0 0 2px " + theme.colors.secondary : void 0,
      width: "100%",
      fontSize: 18,
      paddingLeft: 4,
      height: "100%",
      color: theme.shades.primary5,
      letterSpacing: "0.28px"
    }),
    singleValue: () => ({
      marginLeft: 0,
      marginRight: 0,
      color: theme.shades.primary5,
      maxWidth: "calc(100% - 8px)",
      overflow: "hidden",
      position: "absolute",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      top: "50%",
      transform: "translateY(-50%)",
      boxSizing: "border-box"
    }),
    menu: () => ({
      backgroundColor: "hsl(0,0%,100%)",
      marginBottom: menuPlacement !== "top" ? "8px" : null,
      border: "none",
      width: "100%",
      zIndex: 1,
      boxSizing: "border-box",
      borderTopLeftRadius: menuPlacement !== "top" ? 0 : null,
      borderTopRightRadius: menuPlacement !== "top" ? 0 : null,
      borderBottomLeftRadius: menuPlacement === "top" ? 0 : null,
      borderBottomRightRadius: menuPlacement === "top" ? 0 : null
    }),
    menuList: () => ({
      marginTop: "10px",
      position: "absolute",
      backgroundColor: "hsl(0, 0%, 100%)",
      borderRadius: "4px",
      boxShadow: "0 0 0 1px hsla(0,0%,0%,0.1), 0 4px 11px hsla(0,0%,0%,0.1)",
      marginBottom: menuPlacement !== "top" ? "8px" : null,
      border: "none",
      width: "inherit",
      zIndex: 1,
      maxHeight: "250px",
      overflowX: "hidden",
      overflowY: "auto"
    }),
    option: (styles, state) => ({
      ...styles,
      cursor: "pointer",
      color: state.isSelected ? theme.colors.background : theme.shades.primary5
    }),
    dropdownIndicator: () => ({
      width: 0,
      height: 0,
      border: "solid",
      borderWidth: "0 1px 1px 0",
      transform: "rotate(45deg)",
      borderColor: isValid === false ? theme.colors.secondary : theme.colors.foreground,
      padding: "5px",
      marginRight: "19px",
      "& svg": { display: "none" }
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: "flex"
    })
  };
};

// src/components/form/dropdown/AsyncDropdown.res.ts
var AsyncDropdown_res_default = {
  loading: {
    is: "Hle\xF0...",
    en: "Loading...",
    pl: "\u0141adowanie..."
  },
  nothingFound: {
    is: "Ekkert fannst.",
    en: "Nothing found.",
    pl: "Nic nie znaleziono"
  }
};

// src/components/form/dropdown/AsyncDropdown.tsx
var InputHost4 = styled53.div`
  display: flex;
  position: relative;
  flex-direction: column;
  width: 100%;

  & > * + * {
    margin-top: 8px;
  }

  &:hover > label {
    color: ${({ theme: theme2 }) => rgba5(theme2.shades.primary5 ?? "", 1)};
  }
`;
var LabelHost = styled53(Label)`
  letter-spacing: 0.25px;
`;
function AsyncDropdown({
  isValid,
  validationMessage,
  placeholder = "",
  label: label2,
  className,
  isOpen,
  onBlur,
  value,
  openMenuOnFocus,
  labelVariant,
  styles,
  components: defaultComponents = { IndicatorSeparator: null },
  selectRef,
  ...selectProps
}) {
  const { r } = useResource5();
  const [uuid2] = useState16(getUUID4());
  const components = useComponents(defaultComponents);
  return /* @__PURE__ */ React213.createElement(InputHost4, null, label2 && labelVariant === "aboveInput" && /* @__PURE__ */ React213.createElement(LabelHost, { size: "medium", htmlFor: uuid2 }, label2), /* @__PURE__ */ React213.createElement(
    AsyncPaginate,
    {
      selectRef,
      isLoading: true,
      className,
      classNamePrefix: "select",
      value: value ? value : { value: -1, label: placeholder },
      components,
      placeholder,
      loadingMessage: () => r(AsyncDropdown_res_default.loading),
      inputId: "inputId",
      name: "inputId",
      styles: styles ? styles : customStyles("top", void 0, isValid),
      defaultOptions: true,
      openMenuOnFocus: openMenuOnFocus === void 0 ? true : openMenuOnFocus,
      menuIsOpen: isOpen,
      onBlur: () => {
        if (onBlur) {
          onBlur();
        }
      },
      noOptionsMessage: () => r(AsyncDropdown_res_default.nothingFound),
      ...selectProps
    }
  ), validationMessage && /* @__PURE__ */ React213.createElement(
    AnimatedErrorLabel,
    {
      visible: isValid === false,
      id: "inputId",
      errorMessage: validationMessage
    }
  ));
}

// src/components/form/number-input/MoneyInput.tsx
import React215 from "react";

// src/components/form/number-input/NumberFormatInput.tsx
import styled54 from "@emotion/styled";
import { useResource as useResource6 } from "@landsbankinn/l16n";
import {
  getDecimalSeparatorByLocale,
  getThousandSeparatorByLocale
} from "@landsbankinn/utils";
import React214, { useState as useState17 } from "react";
import { NumericFormat } from "react-number-format";
var FormattedInput3 = styled54(Input)`
  width: 100%;
`;
var InputContainer2 = styled54.div`
  position: relative;
`;
function NumberFormatInput({
  inputSize,
  input: input3,
  suffix,
  ...rest
}) {
  const { locale } = useResource6();
  const [focused, setFocused] = useState17(false);
  return /* @__PURE__ */ React214.createElement(InputContainer2, null, /* @__PURE__ */ React214.createElement(
    NumericFormat,
    {
      customInput: input3 ?? FormattedInput3,
      thousandSeparator: getThousandSeparatorByLocale(locale),
      decimalSeparator: getDecimalSeparatorByLocale(locale),
      displayType: "input",
      inputSize,
      allowEmptyFormatting: true,
      valueIsNumericString: locale === "is",
      hackSuffix: suffix,
      onFocus: (e) => {
        setFocused(true);
        if (rest.onFocus) {
          rest.onFocus(e);
        }
      },
      onBlur: (e) => {
        setFocused(false);
        if (rest.onBlur) {
          rest.onBlur(e);
        }
      },
      ...rest
    }
  ));
}

// src/components/form/number-input/MoneyInput.tsx
var MoneyInput = ({
  onChangeValue,
  currency = "ISK",
  value,
  ...rest
}) => {
  return /* @__PURE__ */ React215.createElement(
    NumberFormatInput,
    {
      isAllowed: ({ floatValue }) => {
        return floatValue === void 0 || floatValue >= 0 && floatValue < 1e9;
      },
      onValueChange: ({ floatValue }) => {
        onChangeValue(floatValue ?? 0);
      },
      displayType: "input",
      suffix: currency,
      value,
      decimalScale: currency === "ISK" ? 0 : 2,
      ...rest
    }
  );
};

// src/components/form/number-input/MonthInput.tsx
import React216 from "react";
var DEFAULT_MIN = 0;
var DEFAULT_MAX = 99;
var MonthInput = ({
  value,
  onChangeValue,
  max = DEFAULT_MAX,
  min = DEFAULT_MIN,
  hideSuffix,
  suffix = "m\xE1n.",
  ...rest
}) => {
  return /* @__PURE__ */ React216.createElement(
    NumberFormatInput,
    {
      isAllowed: ({ floatValue }) => {
        const usedMin = isNaN(+min) ? DEFAULT_MIN : +min;
        const usedMax = isNaN(+max) ? DEFAULT_MAX : +max;
        return floatValue === void 0 || floatValue >= usedMin && floatValue <= usedMax;
      },
      onValueChange: ({ floatValue }) => {
        if (floatValue) {
          onChangeValue(floatValue);
        }
      },
      onBlur: () => {
        if (value !== void 0 && value !== null && value < min) {
          onChangeValue(Number(min));
        }
      },
      value,
      decimalScale: 0,
      hackSuffix: !hideSuffix ? suffix : void 0,
      allowNegative: false,
      min,
      ...rest
    }
  );
};

// src/components/form/number-input/NumberInput.tsx
import styled55 from "@emotion/styled";
import { rgba as rgba6 } from "polished";
import React217 from "react";
var Host13 = styled55.div``;
var MinusButton = styled55.button`
  border: 1px solid ${(props) => rgba6(props.theme.colors.primary, 0.3)};
  border-radius: 100px;
  height: 30px;
  width: 30px;
  color: transparent;
  display: inline-block;
  position: absolute;
  cursor: pointer;
  left: 20px;
  top: 50%;
  z-index: 10;
  cursor: pointer;

  &::after {
    content: '';
    display: block;
    border-bottom: 1px solid ${(props) => rgba6(props.theme.colors.primary, 0.3)};
    position: absolute;
    top: 50%;
    left: calc(50% - 4px);
    width: 9px;
  }
`;
var PlusButton = styled55(MinusButton)`
  left: initial;
  right: 20px;
  &::before {
    content: '';
    display: block;
    border-bottom: 1px solid ${(props) => rgba6(props.theme.colors.primary, 0.3)};
    position: absolute;
    top: 50%;
    left: calc(50% - 4px);
    width: 9px;
    transform: rotate(90deg);
  }
`;
var NumberHost = styled55.div`
  position: relative;
`;
function NumberInput({
  allowNegatives,
  onChange,
  value,
  ...rest
}) {
  function changeValue(newVal) {
    if (allowNegatives || newVal >= 0) {
      onChange(newVal);
    }
  }
  return /* @__PURE__ */ React217.createElement(Host13, null, /* @__PURE__ */ React217.createElement(NumberHost, null, /* @__PURE__ */ React217.createElement(
    MinusButton,
    {
      onClick: (e) => {
        e.preventDefault();
        const nn = value - 1;
        if (allowNegatives || nn >= 0) {
          changeValue(nn);
        }
      }
    },
    "-"
  ), /* @__PURE__ */ React217.createElement(
    NumberFormatInput,
    {
      customInput: Input,
      displayType: "input",
      value,
      textAlign: "center",
      labelVariant: "aboveInput",
      variant: "noBorder",
      onValueChange: (values) => {
        if (values.floatValue !== void 0 && values.floatValue > -1) {
          changeValue(values.floatValue);
        }
      },
      ...rest
    }
  ), /* @__PURE__ */ React217.createElement(
    PlusButton,
    {
      onClick: (e) => {
        e.preventDefault();
        changeValue(value + 1);
      }
    },
    "+"
  )));
}

// src/components/form/number-input/PercentageInput.tsx
import React218 from "react";
var PercentageInput = ({
  onChangeValue,
  suffix = "%",
  ...rest
}) => {
  return /* @__PURE__ */ React218.createElement(
    NumberFormatInput,
    {
      isAllowed: ({ floatValue }) => {
        return floatValue === void 0 || floatValue >= 0 && floatValue < 100;
      },
      onValueChange: ({ floatValue }) => {
        if (floatValue) {
          onChangeValue(floatValue);
        }
      },
      hackSuffix: suffix,
      ...rest
    }
  );
};

// src/components/form/number-input/AccountInput.tsx
import styled56 from "@emotion/styled";
import * as React220 from "react";
import { PatternFormat as PatternFormat3 } from "react-number-format";

// src/validation/useInputValidation.ts
import * as React219 from "react";
import { v4 as getUUID5 } from "uuid";
var useInputValidation = (value, validators, deps = [], validationGroup) => {
  const validationContext = React219.useContext(FormContext);
  const [isValid, setIsValid] = React219.useState(void 0);
  const [isFocused, setIsFocused] = React219.useState(
    void 0
  );
  const [isDirty, setIsDirty] = React219.useState(false);
  const [message, setMessage] = React219.useState(void 0);
  const [inputId] = React219.useState(getUUID5());
  const [contextInput, setContextInput] = React219.useState(
    void 0
  );
  const ref = React219.useRef(null);
  const cb = React219.useCallback(() => validators, deps);
  const setFocus = React219.useCallback(() => {
    ref.current?.focus();
  }, [ref]);
  React219.useEffect(() => {
    validationContext.addInput({
      value,
      validationGroup,
      validators: cb(),
      name: inputId,
      isValid: true,
      onFocus: setFocus
    });
    const onBlur = () => {
      setIsFocused(false);
    };
    const onFocus = () => {
      setIsFocused(true);
    };
    ref.current?.addEventListener("blur", onBlur);
    ref.current?.addEventListener("focus", onFocus);
    return () => {
      validationContext.removeInput(inputId);
      ref.current?.removeEventListener("blur", onBlur);
      ref.current?.removeEventListener("focus", onFocus);
    };
  }, []);
  React219.useEffect(() => {
    const { isValid: isValid2, newMessage } = validate();
    setIsValid(isValid2);
    if (!isFocused || validationContext.hasSubmitted) {
      setMessage(newMessage);
    }
    validationContext.updateInput({
      validationGroup,
      value,
      isValid: isValid2,
      validators: cb(),
      name: inputId,
      onFocus: setFocus
    });
  }, [value, cb, isFocused, validationContext.hasSubmitted]);
  React219.useEffect(() => {
    setContextInput(validationContext.inputs.find((x) => x.name === inputId));
  }, [validationContext.inputs, inputId]);
  function validate() {
    if ((value === 0 || value) && !isDirty) {
      setIsDirty(true);
    }
    if (!validationContext.hasSubmitted && !isDirty) {
      return { isValid: true };
    }
    let message2;
    const isValid2 = validators.reduce((current, validator) => {
      if (!current) {
        return false;
      }
      if (!validator.validation(value)) {
        message2 = validator.message;
        return false;
      }
      return true;
    }, true);
    if (message2) {
      message2 = message2.replace(" ", " ");
    }
    return {
      newMessage: message2,
      isValid: isValid2
    };
  }
  const isContextInputValid = contextInput !== void 0 ? contextInput.isValid : true;
  const contextMessage = contextInput !== void 0 ? contextInput.invalidMessge : void 0;
  return [
    ref,
    // fugly hack
    isValid !== false && isContextInputValid,
    message || contextMessage || ""
  ];
};

// src/validation/useEmailValidation.ts
var re = (
  // the 'i' from the end makes the string case-insensitive, meaning that A-Z will be handled by the a-z rule
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-z\-\d]+\.)+[a-z]{2,}))$/i
);
var useEmailValidation = (email) => {
  return useInputValidation(email, [
    {
      validation: (v) => v !== void 0 && re.test(String(v).toLowerCase()),
      message: "Netfang er ekki \xE1 r\xE9ttu formi."
    }
  ]);
};

// src/validation/useKennitalaValidation.ts
var useKennitalaValidation = (email) => {
  return useInputValidation(email, [
    {
      validation: (v) => v !== void 0 && v.length === 10,
      message: "Kennitala er ekki \xE1 r\xE9ttu formi"
    }
  ]);
};

// src/validation/usePhoneValidation.ts
var usePhoneValidation = (email) => {
  return useInputValidation(email, [
    {
      validation: (v) => v !== void 0 && v.replaceAll("-", "").length === 7,
      // including the dash.
      message: "S\xEDman\xFAmer er ekki \xE1 r\xE9ttu formi"
    }
  ]);
};

// src/components/form/number-input/AccountInput.tsx
var FormattedInput4 = styled56(Input)`
  text-align: center;
  width: 100%;
`;
var onlyNumber = /^\d+$/;
var AccountInput = ({ onChange, ...rest }) => {
  const [bankNr, setBankNr] = React220.useState("");
  const [ledger, setLedger] = React220.useState("");
  const [accountNr, setAccountNr] = React220.useState("");
  const [bankNrRef, isBankNrValid] = useInputValidation(bankNr, [
    {
      validation: (val) => val.length === 4
    }
  ]);
  const [ledgerRef, isLedgerValid] = useInputValidation(ledger, [
    {
      validation: (val) => val.length === 2
    }
  ]);
  const [accountNrRef, isAccountNumberValid] = useInputValidation(accountNr, [
    {
      validation: (val) => {
        return val.length === 6;
      }
    }
  ]);
  React220.useEffect(() => {
    onChange(`${bankNr}${ledger}${accountNr}`);
  }, [bankNr, ledger, accountNr]);
  const onBankNrChange = (value) => {
    if (value.length <= 4) {
      setBankNr(value);
      if (value.length === 4) {
        setTimeout(() => {
          ledgerRef.current?.focus();
        }, 0);
      }
    }
  };
  const onLedgerChange = (value) => {
    if (value.length <= 2) {
      setLedger(value);
      if (value.length === 2) {
        setTimeout(() => {
          accountNrRef.current?.focus();
        }, 0);
      }
      if (value.length === 0) {
        setTimeout(() => {
          bankNrRef.current?.focus();
        }, 0);
      }
    }
  };
  const onAccountNrChange = (value) => {
    if (value.length <= 6) {
      setAccountNr(value);
    }
    if (value.length === 0) {
      setTimeout(() => {
        ledgerRef.current?.focus();
      }, 0);
    }
  };
  const onPaste = (event) => {
    const clipboardData = event.clipboardData.getData("Text").trim();
    const accountNumber = clipboardData.replace(/\D/g, "");
    if (accountNumber.length === 12 && onlyNumber.test(accountNumber)) {
      const bankNr2 = accountNumber.slice(0, 4);
      const ledger2 = accountNumber.slice(4, 6);
      const accountNr2 = accountNumber.slice(6, 12);
      setBankNr(bankNr2);
      setLedger(ledger2);
      setAccountNr(accountNr2);
      accountNrRef.current?.focus();
    }
  };
  const onEnter = (event) => {
    if (event.key === "Enter") {
      const length = accountNr.length;
      if (length < 6 && length !== 0) {
        setAccountNr(`${"0".repeat(6 - length)}${accountNr}`);
      }
    }
    return true;
  };
  return /* @__PURE__ */ React220.createElement(Row, null, /* @__PURE__ */ React220.createElement(Column, { md: { width: 4.5 / 12 } }, /* @__PURE__ */ React220.createElement(
    PatternFormat3,
    {
      ...rest,
      id: "bankNr",
      customInput: FormattedInput4,
      label: "Banki",
      onValueChange: ({ value }) => onBankNrChange(value),
      value: bankNr,
      onBlur: () => {
        if (bankNr.length === 3) {
          setBankNr(`0${bankNr}`);
        }
      },
      onPaste,
      isValid: isBankNrValid,
      inputRef: bankNrRef,
      format: "####"
    }
  )), /* @__PURE__ */ React220.createElement(Column, { md: { width: 3 / 12 } }, /* @__PURE__ */ React220.createElement(
    PatternFormat3,
    {
      ...rest,
      customInput: FormattedInput4,
      id: "hbNr",
      inputRef: ledgerRef,
      label: "H\xF6fu\xF0b\xF3k",
      value: ledger,
      onBlur: () => {
        if (ledger.length === 1) {
          setLedger(`0${ledger}`);
        }
      },
      onValueChange: ({ value }) => onLedgerChange(value),
      onPaste,
      isValid: isLedgerValid,
      format: "##"
    }
  )), /* @__PURE__ */ React220.createElement(Column, { md: { width: 4.5 / 12 } }, /* @__PURE__ */ React220.createElement(
    PatternFormat3,
    {
      ...rest,
      customInput: FormattedInput4,
      id: "accountNr",
      value: accountNr,
      label: "Reikningsn\xFAmer",
      onKeyDown: onEnter,
      onValueChange: ({ value }) => onAccountNrChange(value),
      inputRef: accountNrRef,
      onBlur: () => {
        if (accountNr && accountNr.length < 6 && accountNr.length !== 0) {
          setAccountNr(`${"0".repeat(6 - accountNr.length)}${accountNr}`);
        }
      },
      onPaste,
      isValid: isAccountNumberValid,
      format: "######",
      errorMessage: isBankNrValid === false || isLedgerValid === false || isAccountNumberValid === false ? "Reikningsn\xFAmer ekki \xE1 r\xE9ttu formi" : void 0
    }
  )));
};

// src/components/form/radio-button/Radio.tsx
import styled57 from "@emotion/styled";
import { rgba as rgba7 } from "polished";
import React221, { forwardRef as forwardRef10, useState as useState20 } from "react";
import { animated as animated13, useSpring as useSpring9 } from "react-spring";
var RadiogroupHost = styled57.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  & > * + * {
    margin-top: 8px;
  }
`;
function Radiogroup({ children, errorMessage }) {
  return /* @__PURE__ */ React221.createElement(RadiogroupHost, { role: "radiogroup" }, children, errorMessage && /* @__PURE__ */ React221.createElement(ErrorLabel, null, errorMessage));
}
var Radio = styled57.input`
  opacity: 0;
  position: absolute;
  transform: translateX(-50000px);

  &:focus + label {
    outline: rgba(77, 97, 171, 0.5) auto 3px;
  }
`;
var Circle2 = styled57("span", {
  shouldForwardProp: shouldForwardProp("span")
})`
  border: 1px solid
    ${({ theme: theme2, checked, currentTheme }) => checked ? "transparent" : rgba7(mapThemeColorStringToValue(theme2, currentTheme), 0.3)};
  height: 24px;
  width: 24px;
  left: 0px;
  top: calc(50% - 12px);
  position: absolute;
  border-radius: 100px;
  background: ${({ theme: theme2 }) => theme2.colors.background};
`;
var AnimatedCircle = styled57(animated13.span, {
  shouldForwardProp: shouldForwardProp("span")
})`
  border-color: ${({ theme: theme2, checked, currentTheme }) => checked ? mapThemeColorStringToValue(theme2, currentTheme) : "transparent"};
  height: 22px;
  border-style: solid;
  width: 22px;
  left: 0px;
  top: calc(50% - 11px);
  position: absolute;
  border-radius: 100px;
`;
var Label3 = styled57.label`
  cursor: pointer;
  ${typography.numbers2};
  color: ${({ theme: theme2 }) => theme2.colors.foreground};
  position: relative;
  padding-left: 39px;
  padding-top: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  &:hover > span > span {
    background: ${({ theme: theme2, checked, currentTheme }) => checked ? "transparent" : mapThemeColorStringToValue(theme2, currentTheme)};
    border: 8px solid
      ${({ theme: theme2, checked, currentTheme }) => !checked ? theme2.colors.background : mapThemeColorStringToValue(theme2, currentTheme)};
  }
  & > span {
    border-color: ${({ theme: theme2, isValid, currentTheme }) => isValid === false ? theme2.colors.warning : rgba7(mapThemeColorStringToValue(theme2, currentTheme), 0.3)};
    ${({ isValid, theme: theme2 }) => isValid === false && `background-color: ${rgba7(theme2.colors.warning, 0.1)}`};
  }
  &:hover > span {
    border-color: ${({ theme: theme2, currentTheme }) => mapThemeColorStringToValue(theme2, currentTheme)};
  }
`;
var RadioHost = styled57.div`
  height: 64px;
  padding-left: 15px;
  box-shadow: 0 0 0 1px
    ${({ variant, theme: theme2 }) => variant === "fullBorder" || variant === "bottomBorder" ? rgba7(theme2?.colors.foreground ?? "", 0.8) : "transparent"};
  background-color: ${({ theme: theme2 }) => theme2.colors.background};
  ${({ theme: theme2, isValid }) => isValid !== void 0 && isValid === false && `box-shadow: 0 0 0 2px ${theme2?.colors.warning}`};
  &:hover {
    ${({ variant, theme: theme2 }) => variant === "fullBorder" && `box-shadow: 0 0 0 1px ${rgba7(theme2?.colors.foreground ?? "", 0.5)}`};
    & > span {
      border-color: ${(props) => props.theme?.colors.foreground};
    }
  }

  &:active {
    box-shadow: 0 0 0 2px ${(props) => props.theme?.shades.primary3};
  }
`;
var RadioButton = forwardRef10(
  function RButton({
    label: label2,
    checked,
    onChange,
    theme: theme2 = "primary",
    className,
    variant = "fullBorder",
    isValid
  }, ref) {
    const [focused, setFocused] = useState20(false);
    const animatingCircle = useSpring9({
      borderWidth: checked || focused ? "8px" : "0px",
      config: { duration: 150 }
    });
    return /* @__PURE__ */ React221.createElement(RadioHost, { variant, isValid }, /* @__PURE__ */ React221.createElement(
      Label3,
      {
        currentTheme: theme2,
        checked,
        className,
        htmlFor: `radio_${label2}_${theme2}`,
        "aria-checked": checked,
        isValid,
        onMouseEnter: () => {
          setFocused(true);
        },
        onMouseLeave: () => {
          setFocused(false);
        }
      },
      /* @__PURE__ */ React221.createElement(Circle2, { currentTheme: theme2, checked }, /* @__PURE__ */ React221.createElement(
        AnimatedCircle,
        {
          currentTheme: theme2,
          checked,
          style: { ...animatingCircle }
        }
      )),
      label2
    ), /* @__PURE__ */ React221.createElement(
      Radio,
      {
        id: `radio_${label2}_${theme2}`,
        type: "radio",
        checked,
        onChange: (e) => {
          onChange(e.currentTarget.checked);
        },
        ref
      }
    ));
  }
);

// src/components/form/radio-button/CheckboxRadio.tsx
import { css as css10 } from "@emotion/react";
import styled58 from "@emotion/styled";
import React222 from "react";
var CheckboxHost2 = styled58.div`
  position: relative;
`;
var CheckboxInput2 = styled58.input`
  ${dynamicInput}
`;
var dynamicLabel2 = (props) => css10`
  position: relative;
  padding-left: 45px;

  cursor: pointer;
  white-space: pre-wrap;
  display: block;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0px;
    width: 28px;
    height: 28px;
    border-radius: 1px;
    background-color: ${props.theme.colors.background};
    border: 1px solid ${props.disabled ? "#a0a0a0" : props.theme.colors.primary};
    border-width: ${props.bordered ? "1px" : "0px"};
    border-radius: 1px;
    background-color: ${props.checked ? props.disabled ? "#a0a0a0" : props.theme.colors.primary : props.theme.colors.background};
  }
  &:hover::before {
    background-image: url('data:image/svg+xml;utf8,<svg width="16px" height="15px" viewBox="0 0 16 15" version="1.1" xmlns="http://www.w3.org/2000/svg" ><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="Assets" transform="translate(-347.000000, -1288.000000)" stroke="%2372DCEB" stroke-width="2"><polyline id="icon-check-hover" transform="translate(355.000000, 1295.000000) scale(-1, 1) rotate(-180.000000) translate(-355.000000, -1295.000000) " points="348 1295.46154 353.5 1289 362 1301"></polyline></g></g></svg>');
    background-repeat: no-repeat;
    background-size: 16px;
    background-position: center;
  }
`;
var Label4 = styled58.label`
  ${dynamicLabel2}
  ${typography.body}
  line-height: 28px;
  height: 28px;
`;
var CheckboxSvg = styled58(CheckboxActive)`
  position: absolute;
  left: 0;
  top: 0;
`;
var CheckboxRadio = ({
  checked,
  onChange,
  id,
  label: label2,
  bordered,
  disabled,
  ...rest
}) => {
  return /* @__PURE__ */ React222.createElement(CheckboxHost2, { ...rest }, /* @__PURE__ */ React222.createElement(
    CheckboxInput2,
    {
      id: `radio_${label2}_${id}`,
      type: "radio",
      checked,
      disabled,
      onChange: () => {
        if (onChange) {
          onChange(!checked);
        }
      }
    }
  ), /* @__PURE__ */ React222.createElement(
    Label4,
    {
      htmlFor: `radio_${label2}_${id}`,
      checked,
      bordered,
      disabled: disabled ?? false
    },
    checked && /* @__PURE__ */ React222.createElement(CheckboxSvg, { height: "28px", width: "28px" }),
    label2
  ));
};

// src/components/form/slider/Slider.tsx
import { Slider as BaseSlider } from "@base-ui/react/slider";
import styled59 from "@emotion/styled";
import React223 from "react";
var SliderControl = styled59(BaseSlider.Control)`
  box-sizing: content-box;
  width: 100%;
  padding-block: 15px;
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;

  &[data-disabled] {
    opacity: 0.5;
  }
`;
var SliderTrack = styled59(BaseSlider.Track)`
  width: 100%;
  height: 2px;
  background-color: ${(props) => mapThemeColorStringToValue(props.theme, props.currentTheme)};
`;
var SliderIndicator = styled59(BaseSlider.Indicator)`
  background-color: ${(props) => mapThemeColorStringToValue(props.theme, props.currentTheme)};
`;
var SliderThumb = styled59(BaseSlider.Thumb)`
  height: 16px;
  width: 16px;
  background-color: ${(props) => mapThemeColorStringToValue(props.theme, props.currentTheme)};
  border-radius: 50%;
  cursor: grab;

  &:has(:focus-visible) {
    outline: rgb(22, 62, 99) solid 4px;
    outline-offset: 4px;
  }

  &[data-disabled] {
    cursor: not-allowed;
  }
`;
var Slider = ({
  theme: theme2 = "primary",
  value,
  defaultValue,
  step = 1,
  min = 0,
  max = 100,
  ariaLabel,
  isDisabled = false,
  onChange,
  onSliderRelease,
  getAriaValueText
}) => {
  return /* @__PURE__ */ React223.createElement(
    BaseSlider.Root,
    {
      thumbAlignment: "edge",
      value,
      defaultValue,
      step,
      min,
      max,
      disabled: isDisabled,
      onValueChange: onChange,
      onValueCommitted: onSliderRelease
    },
    /* @__PURE__ */ React223.createElement(SliderControl, null, /* @__PURE__ */ React223.createElement(SliderTrack, { currentTheme: theme2 }, /* @__PURE__ */ React223.createElement(SliderIndicator, { currentTheme: theme2 }), /* @__PURE__ */ React223.createElement(
      SliderThumb,
      {
        currentTheme: theme2,
        "aria-label": ariaLabel,
        getAriaValueText
      }
    )))
  );
};

// src/components/form/switch/Switch.tsx
import styled60 from "@emotion/styled";
import React224 from "react";
import ReactSwitch from "react-switch";
var SwitchLabel = styled60(Label)`
  ${(props) => props.isSmall ? typography.body : typography.h4};
  align-items: center;
`;
var Host14 = styled60.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
function Switch({
  onChange,
  checked,
  label: label2,
  id,
  color = theme.colors.primary,
  height = 32,
  width = 64,
  isSmall = false,
  className
}) {
  return /* @__PURE__ */ React224.createElement(Host14, { className }, /* @__PURE__ */ React224.createElement(SwitchLabel, { isSmall, htmlFor: id }, label2), /* @__PURE__ */ React224.createElement(
    ReactSwitch,
    {
      id,
      onChange,
      checked,
      onHandleColor: theme.colors.background,
      offHandleColor: isSmall ? theme.colors.background : color,
      onColor: color,
      offColor: isSmall ? "#a3b3c2" : theme.colors.background,
      height,
      width,
      checkedIcon: false,
      uncheckedIcon: false
    }
  ));
}

// src/components/form/tagFilterCheckbox/TagFilterCheckbox.tsx
import styled61 from "@emotion/styled";
import React225 from "react";
var CheckboxInput3 = styled61.input`
  ${dynamicInput};
`;
var TagButton = styled61.div`
  cursor: pointer;
  border-radius: 6px;
  border: ${(props) => props.checked ? "none" : "1px solid rgba(30, 60, 90,0.2)"};
  background-color: ${(props) => props.checked ? props.theme.colors.primary : "#ffff"};
  color: ${(props) => props.checked ? "#ffff" : props.theme.colors.primary};
  height: 48px;
  display: flex;
  font-size: 14px;
  align-items: center;
  justify-content: center;

  padding-top: 3px;
  @media ${device.tablet} {
    font-size: 16px;
    border-radius: 24px;
  }
`;
var TagFilterCheckbox = ({
  id,
  checked,
  disabled,
  label: label2,
  onChange,
  className
}) => {
  return /* @__PURE__ */ React225.createElement(React225.Fragment, null, /* @__PURE__ */ React225.createElement(
    CheckboxInput3,
    {
      type: "checkbox",
      id,
      checked,
      disabled,
      onChange: () => onChange(!checked)
    }
  ), /* @__PURE__ */ React225.createElement("label", { htmlFor: id }, /* @__PURE__ */ React225.createElement(TagButton, { checked, className }, label2)));
};

// src/components/form/text/FormDescription.tsx
import styled62 from "@emotion/styled";
import React226 from "react";
var Host15 = styled62.div`
  ${typography.body}
`;
function FormDescription({ children }) {
  return /* @__PURE__ */ React226.createElement(Host15, null, children);
}

// src/components/form/text/FormTitle.tsx
import styled63 from "@emotion/styled";
import React227 from "react";
var Host16 = styled63.h2`
  ${typography.h2}
`;
var CustomDivider = styled63(Divider)`
  margin: 31px 0 25px;
  background-color: currentColor;

  @media ${device.tablet} {
    margin: 10% 0;
  }

  @media ${device.pageLimit} {
    margin: 31px 0 25px;
  }
`;
function FormTitle({ text }) {
  return /* @__PURE__ */ React227.createElement(React227.Fragment, null, /* @__PURE__ */ React227.createElement(Host16, null, text), /* @__PURE__ */ React227.createElement(CustomDivider, null));
}

// src/components/tabs/SelectorTabs.tsx
var ButtonGroup = styled64.div`
  position: relative;
  width: 100%;
  display: flex;

  & > * + *:not(:last-child) {
    margin-left: ${({ tabSpacing }) => tabSpacing};
  }

  @media ${device.tablet} {
    border-bottom: ${({ variant, theme: theme2 }) => variant === "block" ? "0px;" : `1px solid ${rgba8(theme2.colors.foreground, 0.3)}`};
    flex-direction: row;
  }
  justify-content: space-between;

  @media print {
    border-bottom: none;
  }
`;
var TabHost = styled64.div`
  display: flex;
  gap: 32px;
`;
var Tab = styled64.button`
  padding-bottom: 26px;
  text-align: left;
  display: flex;
  align-items: center;

  ${typography.h5};
  ${({ block, fontVariant }) => (block || fontVariant === "small") && typography.bodySmall};
  ${({ fontVariant }) => fontVariant === "secondary" && typography.body};
  ${({ selected }) => !selected && `@media print {
    display: none;
  }`}
  ${({ selected, fontVariant }) => !selected && fontVariant === "secondary" && "opacity: 0.6;"}
  ${({ selected, fontVariant }) => selected && fontVariant === "small" && "font-weight: 500;"}
  ${({ fontVariant }) => fontVariant === "small" && "padding-bottom: 16px"};

  ${({ block, selected, theme: theme2 }) => block && `
      color: ${selected ? theme2.colors.primary : "white"};
      background: ${selected ? "white" : "rgb(30, 105, 176)"};
      justify-content: center;
      padding: 11px 10px 8px 10px;
      font-weight: bold;
      min-height: auto;
      width: 100%;
    `}

  cursor: pointer;

  @media ${device.tablet} {
    margin-top: 0;
  }

  & > svg {
    margin-right: 10px;
  }
`;
var Underline = styled64(animated14.div, {
  shouldForwardProp: (prop) => prop !== "lineColor"
})`
  width: 100%;
  height: 4px;
  background: ${({ theme: theme2, lineColor }) => mapThemeColorStringToValue(theme2, lineColor)};
  position: absolute;
  bottom: -1px;
  left: 0;
  @media print {
    display: none;
  }
`;
function SelectorTabs({
  items,
  selectedItemId,
  onSelectTab,
  mobileLabel,
  mobileInset,
  mobileVariant = "dropdown",
  variant = "underline",
  fontVariant = "primary",
  lineColor = "foreground",
  tabSpacing = "24px",
  titleElement,
  ...rest
}) {
  const [elRefs, setElRefs] = React228.useState([]);
  const isMobile = useMedia("sm");
  const ref = useRef8(null);
  const [underline, spring] = useSpring10(() => ({
    from: {
      x: 0,
      width: 0
    }
  }));
  React228.useEffect(() => {
    setElRefs(
      (elRefs2) => Array(items.length).fill(null).map((_, i) => elRefs2[i] || createRef())
    );
  }, [items.length]);
  const selectedRef = React228.useMemo(() => {
    return elRefs ? elRefs[items.findIndex((x) => x.id === selectedItemId)] : null;
  }, [elRefs, selectedItemId, items]);
  const reset2 = React228.useCallback(
    ({ immediate = false }) => {
      if (ref.current) {
        const host = ref.current;
        const item = selectedRef;
        const hostRect = host.getBoundingClientRect();
        if (item) {
          const itemRect = item.getBoundingClientRect();
          spring.start({
            to: {
              x: itemRect.x - hostRect.x,
              width: itemRect.width
            },
            immediate
          });
        } else {
          spring.start({ to: { width: 0 } });
        }
      }
    },
    [selectedRef, spring]
  );
  const onMouseOver = React228.useCallback(
    (e, id) => {
      if (ref.current) {
        const item = e.currentTarget.getBoundingClientRect();
        const host = ref.current.getBoundingClientRect();
        spring.start({
          x: item.x - host.x,
          width: item.width,
          onRest: () => {
            onSelectTab(id);
          }
        });
      }
    },
    [spring]
  );
  useEffect13(() => {
    reset2({ immediate: true });
  }, [reset2, selectedRef]);
  return /* @__PURE__ */ React228.createElement(ButtonGroup, { ref, tabSpacing, ...rest }, titleElement, /* @__PURE__ */ React228.createElement(React228.Fragment, null, !isMobile || variant === "block" || mobileVariant === "tabs" ? /* @__PURE__ */ React228.createElement(React228.Fragment, null, /* @__PURE__ */ React228.createElement(TabHost, null, items.map((x, index) => /* @__PURE__ */ React228.createElement(
    Tab,
    {
      key: x.id,
      selected: x.id === selectedItemId,
      onClick: (e) => {
        onMouseOver(e, x.id);
      },
      block: variant === "block",
      ref: (ref2) => {
        if (ref2) elRefs[index] = ref2;
      },
      fontVariant
    },
    x.icon,
    x.label
  ))), variant === "underline" && /* @__PURE__ */ React228.createElement(Underline, { style: underline, lineColor })) : /* @__PURE__ */ React228.createElement(
    MobileDropdown,
    {
      inset: mobileInset,
      bordered: !mobileInset,
      dialogTitle: mobileLabel ?? "",
      options: items.map((i) => ({ value: i.id, label: i.label })),
      value: selectedItemId,
      onChange: (val) => onSelectTab(val)
    }
  )));
}

// src/components/text/Text.tsx
import styled65 from "@emotion/styled";
import React229 from "react";
var TextHost = styled65.p`
  ${(props) => typography[props.variant]}
`;
var Text4 = ({
  as = "p",
  variant = "body",
  children
}) => {
  return /* @__PURE__ */ React229.createElement(TextHost, { as, variant }, children);
};

// src/components/tooltip/Tooltip.tsx
import styled66 from "@emotion/styled";
import {
  arrow,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles
} from "@floating-ui/react";
import React230, { forwardRef as forwardRef11, useRef as useRef9, useState as useState21 } from "react";
var DEFAULT_PLACEMENT = "top";
var Title3 = styled66.h3`
  ${typography.h2}
  margin-top: 80px;
  @media ${device.tablet} {
    ${typography.h4}
    margin-top: 0px;
  }
`;
var Text5 = styled66.p``;
var Link = styled66.a`
  position: relative;
  z-index: 1;
  cursor: pointer;
  display: inline-block;
`;
var ButtonText = styled66.span`
  position: relative;
  z-index: 1;
`;
var BorderBottom = styled66.div`
  position: absolute;
  bottom: 2px;
  left: 0;
  z-index: 0;
  height: 4px;
  width: 100%;

  background-color: ${(props) => props.theme.shades.primary2};
  transform-origin: bottom;
`;
var QuestionMark = styled66.div`
  background: ${(props) => props.theme.colors.background};
  border-radius: 100px;
  height: 20px;
  width: 20px;
  text-align: center;
  ${typography.bodySmall}
  line-height: 24px;
`;
var TooltipContentHost = styled66("div", {
  shouldForwardProp: shouldForwardProp("div")
})`
  padding: 44px;
  width: 500px;
  background: ${(props) => props.bg};
  z-index: 3;
  pointer-events: auto;
`;
var TooltipContent = forwardRef11(
  function TooltipContent2({
    bg,
    title,
    copy,
    floatingStyles,
    transitionStyles,
    getFloatingProps,
    arrowRef,
    context
  }, ref) {
    return /* @__PURE__ */ React230.createElement(
      TooltipContentHost,
      {
        ref,
        style: { ...floatingStyles, ...transitionStyles },
        bg,
        ...getFloatingProps()
      },
      /* @__PURE__ */ React230.createElement(Title3, null, title),
      /* @__PURE__ */ React230.createElement(Divider, { margin: "23px 0 20px" }),
      /* @__PURE__ */ React230.createElement(Text5, null, copy),
      /* @__PURE__ */ React230.createElement(
        FloatingArrow,
        {
          ref: arrowRef,
          context,
          width: 32,
          height: 16,
          fill: bg
        }
      )
    );
  }
);
var Tooltip = ({
  title,
  copy,
  placement = DEFAULT_PLACEMENT,
  useQuestionMark,
  bg = theme.shades.primary2
}) => {
  const [isOpen, setIsOpen] = useState21(false);
  const isMobile = useMedia("sm");
  const arrowRef = useRef9(null);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(16),
      shift({
        padding: 8
      }),
      arrow({ element: arrowRef })
    ]
  });
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: { opacity: 0 }
  });
  const role = useRole(context, {
    role: "tooltip"
  });
  const hover = useHover(context, {
    delay: {
      open: 100,
      close: 100
    },
    enabled: !isMobile
  });
  const click = useClick(context, {
    enabled: isMobile
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    role,
    hover,
    click
  ]);
  return /* @__PURE__ */ React230.createElement(React230.Fragment, null, /* @__PURE__ */ React230.createElement(Link, { ref: refs.setReference, ...getReferenceProps() }, useQuestionMark ? /* @__PURE__ */ React230.createElement(QuestionMark, null, "?") : /* @__PURE__ */ React230.createElement(React230.Fragment, null, /* @__PURE__ */ React230.createElement(ButtonText, null, title), /* @__PURE__ */ React230.createElement(BorderBottom, null))), isMounted && !isMobile && /* @__PURE__ */ React230.createElement(FloatingPortal, null, /* @__PURE__ */ React230.createElement(
    TooltipContent,
    {
      ref: refs.setFloating,
      bg,
      title,
      copy,
      floatingStyles,
      transitionStyles,
      getFloatingProps,
      arrowRef,
      context
    }
  )), isMounted && isMobile && /* @__PURE__ */ React230.createElement(Dialog2, { onClose: () => setIsOpen(false), title: { label: title ?? "" } }, /* @__PURE__ */ React230.createElement(Title3, null, title), /* @__PURE__ */ React230.createElement(Divider, { margin: "23px 0 20px" }), /* @__PURE__ */ React230.createElement(Text5, null, copy)));
};

// src/components/links/DottedLink.tsx
import styled67 from "@emotion/styled";
import React231 from "react";
var DottedHost = styled67.a`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  border-bottom: 1px dashed ${({ theme: theme2 }) => theme2.shades.primary5};

  height: 32px;
  padding-bottom: 4px;

  & > p {
    font-size: 16px !important;
    line-height: 32px;
  }
`;
var ArrowHost = styled67.span``;
var DottedLink = ({ text, ...rest }) => {
  return /* @__PURE__ */ React231.createElement(DottedHost, { ...rest }, /* @__PURE__ */ React231.createElement(Text4, { variant: "bodySmallBold" }, text), /* @__PURE__ */ React231.createElement(ArrowHost, null, /* @__PURE__ */ React231.createElement(SimpleArrow, null)));
};

// src/components/definition-list/DefinitionList.tsx
import styled68 from "@emotion/styled";
import { rgba as rgba9 } from "polished";
import React232 from "react";
var List2 = styled68.dl`
  ${typography.tableNumbers}
  line-height: 24px;
`;
var ItemHost = styled68.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme: theme2 }) => rgba9(theme2.colors.primary, 0.1)};
  padding-bottom: ${({ theme: theme2 }) => theme2.baseline * 2}px;
  padding-top: ${({ theme: theme2 }) => theme2.baseline * 1}px;
  margin-top: ${({ theme: theme2 }) => theme2.baseline * 1}px;
`;
var HeaderItemHost = styled68(ItemHost)`
  border-bottom: 3px solid ${({ theme: theme2 }) => theme2.colors.primary};
`;
var FooterItemHost = styled68(ItemHost)`
  border-bottom: none;
`;
var HeaderItemTitle = styled68.h2`
  ${typography.h5}
`;
var FooterItemContent = styled68.dd`
  font-weight: 500;
`;
var DefinitionList = ({
  children
}) => {
  return /* @__PURE__ */ React232.createElement(List2, null, children);
};
var DefinitionListItem = ({
  title,
  children
}) => {
  return /* @__PURE__ */ React232.createElement(ItemHost, null, /* @__PURE__ */ React232.createElement("dt", null, title), /* @__PURE__ */ React232.createElement("dd", null, children));
};
var DefinitionListHeader = ({ title, children }) => {
  return /* @__PURE__ */ React232.createElement(HeaderItemHost, null, /* @__PURE__ */ React232.createElement(HeaderItemTitle, null, title), /* @__PURE__ */ React232.createElement("div", null, children));
};
var DefinitionListFooter = ({ title, children }) => {
  return /* @__PURE__ */ React232.createElement(FooterItemHost, null, /* @__PURE__ */ React232.createElement("dt", null, title), /* @__PURE__ */ React232.createElement(FooterItemContent, null, children));
};

// src/components/table/Table.tsx
import styled69 from "@emotion/styled";
import { rgba as rgba10 } from "polished";
import React233 from "react";
var Host17 = styled69.div`
  overflow-y: auto;
  margin-left: ${(props) => `-${props.theme.breakpoints.sm.gutter}px`};
  margin-right: ${(props) => `-${props.theme.breakpoints.sm.gutter}px`};
  ${mobileGutterMargin};
  padding: ${(props) => `0 ${props.theme.breakpoints.sm.gutter}px`};
  ${mobileGutterPadding};
  @media ${device.tablet} {
    margin-left: ${(props) => `-${props.theme.breakpoints.md.gutter}px`};
    margin-right: ${(props) => `-${props.theme.breakpoints.md.gutter}px`};
    padding: ${(props) => `0 ${props.theme.breakpoints.md.gutter}px`};
  }
  @media ${device.desktop} {
    overflow-y: unset;
    margin-left: ${(props) => `-${props.theme.breakpoints.lg.gutter}px`};
    margin-right: ${(props) => `-${props.theme.breakpoints.lg.gutter}px`};
    padding: ${(props) => `0 ${props.theme.breakpoints.lg.gutter}px`};
  }
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;
var TableHost = styled69.table`
  width: 100%;
  text-align: right;
  border-collapse: separate;

  th {
    &:first-of-type td {
      text-align: left;
      ${(props) => !props.isZebra && `padding-left: 0px;`}

      @media ${device.tablet} {
        ${(props) => !props.isZebra && !props.isSelectable ? `padding-left: 0px;` : `padding-left: 10px;`}
      }
    }
    &:last-child {
      text-align: right;
      ${(props) => !props.isZebra && `padding-right: 0px;`}

      @media ${device.tablet} {
        ${(props) => !props.isZebra && !props.isSelectable ? `padding-right: 0px;` : `padding-right: 10px;`}
      }
    }
  }

  td {
    z-index: 2;

    &:after {
      content: '';

      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;

      height: 2px;
      width: 100%;

      transition: background-color 100ms ease-in;
    }

    ${(props) => props.compact ? "padding: 8px 0" : void 0};
    transition: border-color 100ms ease-in;
    border-bottom: 1px solid ${(props) => rgba10(props.theme.colors.primary, 0.1)};
    padding-bottom: ${(props) => props.compact ? "12px;" : "8px"};

    &:first-of-type {
      text-align: left;
      ${(props) => !props.isZebra && `padding-left: 0px;`}

      @media ${device.tablet} {
        ${(props) => !props.isZebra && !props.isSelectable ? `padding-left: 0px;` : `padding-left: 11px;`}
      }
    }

    &:last-child {
      text-align: right;
      ${(props) => !props.isZebra && `padding-right: 0px;`}

      @media ${device.tablet} {
        ${(props) => !props.isZebra && !props.isSelectable ? `padding-right: 0px;` : `padding-right: 11px;`}
      }
    }
  }

  tr:nth-of-type(2n) {
    td {
      ${(props) => {
  if (props.isZebra)
    return `
          background-color: ${mapThemeShadeStringToValue(
      props.theme,
      props.colorTheme
    )};
        `;
}}
    }
  }

  tbody tr:hover td {
    ${(props) => {
  if (props.isClickable && props.colorTheme !== void 0)
    return `
        z-index: 2;
        
        cursor: pointer;
        padding-bottom:${props.compact ? "11px" : "7px"};
        border-bottom: 2px solid ${props.colorTheme !== "primary" ? mapThemeShadeStringToValue(props.theme, props.colorTheme, 3) : "#cadbf0"};            
      `;
}}
  }

  tfoot tr:hover {
    background-color: unset;
  }
`;
var Table = ({
  children,
  isZebra = false,
  isClickable = false,
  isSelectable = false,
  colorTheme = "primary",
  ...rest
}) => {
  return /* @__PURE__ */ React233.createElement(Host17, { className: "hostTarget" }, /* @__PURE__ */ React233.createElement(
    TableHost,
    {
      isZebra,
      isClickable,
      isSelectable,
      colorTheme,
      ...rest
    },
    children
  ));
};

// src/components/table/Tbody.tsx
import styled70 from "@emotion/styled";
import React234 from "react";
var Host18 = styled70.tbody``;
var Tbody = ({ children, ...rest }) => {
  return /* @__PURE__ */ React234.createElement(Host18, { ...rest }, children);
};

// src/components/table/Td.tsx
import styled71 from "@emotion/styled";
import { rgba as rgba11 } from "polished";
import React235, { forwardRef as forwardRef12 } from "react";
var Host19 = styled71.td`
  padding: 11px 11px;
  text-align: ${(props) => props.align ? props.align + " !important" : "left"};
  border-bottom: ${(props) => `1px solid ${rgba11(props.theme.colors.primary, 0.1)}`};
  cursor: ${({ isClickable }) => isClickable ? "pointer" : "default"} !important;
`;
var Td = forwardRef12(function Td2({ align, children, colSpan, isClickable, ...rest }, ref) {
  return /* @__PURE__ */ React235.createElement(
    Host19,
    {
      ref,
      align,
      colSpan,
      isClickable,
      ...rest
    },
    children
  );
});

// src/components/table/Th.tsx
import styled72 from "@emotion/styled";
import React236 from "react";
var Host20 = styled72.th`
  padding: 12px 10px 5px;
  ${typography.bodySmall}
  text-align: ${(props) => props.align ? props.align + " !important" : "left"};
  border-bottom: ${(props) => `3px solid ${props.theme.shades.primary5}`};
`;
var Th = ({ align, children, ...rest }) => {
  return /* @__PURE__ */ React236.createElement(Host20, { align, ...rest }, children);
};

// src/components/table/Thead.tsx
import styled73 from "@emotion/styled";
import React237 from "react";
var Host21 = styled73.thead`
  tr {
    height: 40px;
    vertical-align: bottom;
  }
`;
var Thead = ({ children, ...rest }) => {
  return /* @__PURE__ */ React237.createElement(Host21, { ...rest }, children);
};

// src/components/table/Tfoot.tsx
import styled74 from "@emotion/styled";
import React238 from "react";
var Footer = styled74.tfoot`
  tr {
    td {
      border: none;
      padding: inherit;
    }
    td:last-child {
      font-weight: 500;
    }
  }
`;
var Tfoot = ({ children, ...rest }) => {
  return /* @__PURE__ */ React238.createElement(Footer, { ...rest }, children);
};

// src/components/table/Tr.tsx
import styled75 from "@emotion/styled";
import { rgba as rgba12 } from "polished";
import React239, { forwardRef as forwardRef13 } from "react";
var Host22 = styled75.tr`
  min-height: 68px;

  &:last-child td {
    border-color: ${(props) => rgba12(props.theme.colors.foreground, 0.5)};
  }

  &:first-of-type td {
    &:before {
      top: 0px;
    }
  }

  th:first-of-type {
    ${({ firstColumnTitle }) => firstColumnTitle && typography.h5}
  }

  td {
    border-color: ${(props) => rgba12(props.theme.colors.primary, 0.1)};
    transition: border-color 100ms ease-in;

    &:before {
      content: '';

      position: absolute;
      top: 0;
      left: 0;
      right: 0;

      height: 1px;
      width: 100%;

      transition: border-color 100ms ease-in;
    }

    &:after {
      content: '';

      position: absolute;
      bottom: -1px;
      left: 0;
      right: 0;

      height: 2px;
      width: 100%;
    }

    ${(props) => {
  if (props.active && props.colorTheme !== void 0)
    return `
        
        background-color: ${props.colorTheme !== "primary" ? mapThemeShadeStringToValue(props.theme, props.colorTheme) : "#f0f4fb"};
        
        position: relative;


        &:before {
          content: '';

          position: absolute;
          top: -1px;
          left: 0;
          right: 0;

          height: 1px;
          width: 100%;

          background-color: ${props.colorTheme !== "primary" ? mapThemeShadeStringToValue(props.theme, props.colorTheme) : "#f0f4fb"};
        }

          &:after {
            content: '';

            position: absolute;
            bottom: -1px;
            left: 0;
            right: 0;

            height: 2px;
            width: 100%;

            background-color: ${props.colorTheme !== "primary" ? mapThemeShadeStringToValue(props.theme, props.colorTheme, 3) : "#cadbf0"};
          }    
      `;
}};
  }
`;
var Tr = forwardRef13(function Tr2({ children, firstColumnTitle = true, ...rest }, ref) {
  return /* @__PURE__ */ React239.createElement(Host22, { ref, firstColumnTitle, ...rest }, children);
});

// src/components/table/TableWithImages.tsx
import styled76 from "@emotion/styled";
import React240 from "react";
var Host23 = styled76(Reveal)`
  ${standardTable("White", true)}

  width: 100%;
  table thead th {
    font-weight: bold;
    border-bottom: 1px solid ${(props) => props.theme.colors.primary};
    padding-bottom: 10px;

    font-family: ${(props) => props.theme.font.family};
    font-size: 12px;
    padding-left: 6px;
    text-align: left;
    @media ${device.tablet} {
      font-size: ${typography.sizes.bodySmallSize}px;
      padding-left: 0;
      text-align: center;
    }
  }
  table tbody tr > td {
    border-bottom: 1px solid ${(props) => props.theme.colors.primary};
    padding: 20px 0px;
  }
  > div > div > table {
    width: ${(props) => props.theme.pageWidth}px;
  }
  td {
    font-size: 24px;
    font-weight: 500;
    text-align: center;
    border-bottom: 1px solid ${(props) => props.theme.colors.primary};
    font-family: ${(props) => props.theme.font.familySlab};
    @media ${device.tablet} {
      font-size: ${typography.sizes.numbersMSize}px;
    }
  }

  tr > td:nth-of-type(2) {
    text-align: left;
  }

  tr > td:nth-of-type(2) > div {
    min-height: 135px;
    display: flex;
    align-items: center;
  }

  tr > td:nth-of-type(2) * {
    font-size: 14px;
  }

  tr > td:nth-of-type(1) * {
    font-size: 12px;
    @media ${device.tablet} {
      padding-right: inherit;
    }
  }

  tr > td:nth-of-type(2) strong {
    font-weight: 700;
    padding-bottom: 5px;
    display: block;
  }

  table tbody tr > td:first-child {
    width: 155px;
    padding-right: 12px;
    box-sizing: content-box;
    @media ${device.tablet} {
      padding-right: 88px;
    }
  }

  table thead th:first-child:after {
    content: initial;
  }
  tfoot td {
    border: 0;
    font-size: ${typography.sizes.bodySmallSize}px;
    padding-top: 15px;
    font-family: ${(props) => props.theme.font.family};
    font-weight: 400;
  }
`;
function TableWithImages({ children }) {
  return /* @__PURE__ */ React240.createElement(Host23, null, children);
}

// src/components/table/TableExpander.tsx
import styled77 from "@emotion/styled";
import { useResource as useResource7 } from "@landsbankinn/l16n";
import React241 from "react";

// src/components/table/Table.res.tsx
var Table_res_default = {
  showAllPayments: {
    is: "S\xFDna allar grei\xF0slur",
    en: "Show all payments",
    pl: "Poka\u017C wszystkie raty"
  }
};

// src/components/table/TableExpander.tsx
var TD = styled77.td`
  padding: 36px 0px !important;
  text-align: left !important;
  background: transparent !important;
`;
var Button5 = styled77.button`
  ${typography.body}
  font-weight: 500;
  cursor: pointer;
`;
var ArrowButtonNoBox = styled77(ArrowButton)`
  height: auto;
  width: auto;
  display: inline-block;
  border: 0;
  margin-right: 20px;
  & > span {
    border: none;
  }
`;
function TableExpander({ colSpan, onExpand }) {
  const { r } = useResource7();
  return /* @__PURE__ */ React241.createElement("tr", null, /* @__PURE__ */ React241.createElement(TD, { colSpan }, /* @__PURE__ */ React241.createElement(Button5, { onClick: onExpand }, /* @__PURE__ */ React241.createElement(ArrowButtonNoBox, null), " ", r(Table_res_default.showAllPayments))));
}

// src/components/table/MenuButton.tsx
import styled78 from "@emotion/styled";
import React242 from "react";
import useDropdownMenu from "react-accessible-dropdown-menu-hook";
import { animated as animated15, useSpring as useSpring11 } from "react-spring";
var Host24 = styled78.div`
  position: relative;
  cursor: default;
`;
var Button6 = styled78.button`
  position: relative;
  background-color: rgb(231, 235, 239);
  background-image: none;
  width: 39px;
  height: 29px;
  color: white;
  :hover {
    background-color: #d0d8e0;
    cursor: pointer;
  }
  :after {
    content: '...';
    ${typography.bodySmallBold}
    color: rgb(22, 62, 99);
    position: absolute;
    top: -2px;
    height: 29px;
    left: 12px;
  }
`;
var Menu = styled78(animated15.div)`
  position: absolute;
  top: -1px;
  right: -2px;
  padding: 36px 10px 23px;
  background: ${(props) => props.theme.colors.background};
  z-index: 5;
  width: 39px;
  transform-origin: 100% 0;
  border-radius: 2px;
  border: 1px solid rgba(6, 15, 25, 0.01);
  box-shadow:
    0px 1px 2px 0px rgba(6, 15, 25, 0.2),
    0px 4px 6px 0px rgba(6, 15, 25, 0.1);
`;
var Dots = styled78.button`
  position: relative;
  background-image: none;
  width: 0;
  height: 0;
  z-index: 50;
  :hover {
    cursor: pointer;
  }
  :after {
    content: '...';
    ${typography.bodySmallBold}
    color: rgb(22, 62, 99, 0.3);
    position: absolute;
    top: -16px;
    height: 29px;
    height: 29px;
    right: 12px;
  }
  :hover::after {
    color: rgb(22, 62, 99);
  }
  outline: none;
`;
var LI = styled78(animated15.li)`
  white-space: nowrap;
  position: relative;
  padding: 6px 14px;
  * {
    ${typography.tableNumbers}
  }
  &:hover {
    background: rgb(30, 105, 176, 0.2);
    border-radius: 4px;
  }
  &:active,
  &.focus {
    background: ${(props) => props.theme.graphs?.[0]};
    border-radius: 4px;
    color: ${(props) => props.theme.colors.background};
  }
  button {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
  }
  a {
    width: 100%;
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    align-items: center;
  }
`;
var MenuButton = ({
  id,
  items,
  deactivateOtherMenuButtons
}) => {
  const { buttonProps, itemProps, isOpen, setIsOpen } = useDropdownMenu(
    items.length
  );
  const stylesMenu = useSpring11({
    config: { friction: 20 },
    transform: isOpen ? "scale(1)" : "scale(0.3)",
    width: isOpen ? 230 : 0
  });
  const styles = useSpring11({
    opacity: isOpen ? 1 : 0
  });
  const childrenWithProps = React242.Children.map(items, (child, index) => {
    if (React242.isValidElement(child)) {
      return React242.cloneElement(child, {
        ...itemProps[index]
      });
    }
    return child;
  });
  const attachedFunctionComponent = (elem) => {
    if (elem) {
      elem.deactivateDropdown = () => {
        setIsOpen(false);
      };
    }
  };
  return /* @__PURE__ */ React242.createElement(
    Host24,
    {
      onClick: (e) => {
        e.preventDefault();
      }
    },
    /* @__PURE__ */ React242.createElement(
      Button6,
      {
        ...buttonProps,
        onClick: (e) => {
          e.stopPropagation();
          if (deactivateOtherMenuButtons) {
            const otherMenu = document.getElementById(
              "ActionMenuButton"
            );
            if (otherMenu) {
              otherMenu.deactivateDropdown?.();
            }
          }
          buttonProps.onClick?.(e);
        },
        "aria-controls": `${id}menu`,
        id: `${id}`
      }
    ),
    isOpen && /* @__PURE__ */ React242.createElement(React242.Fragment, null, /* @__PURE__ */ React242.createElement(
      Menu,
      {
        style: stylesMenu,
        role: "menu",
        id: "ActionMenuButton",
        ref: attachedFunctionComponent
      },
      /* @__PURE__ */ React242.createElement("ul", { id: `${id}menu`, "aria-labelledby": `${id}` }, childrenWithProps && childrenWithProps.map((item, index) => /* @__PURE__ */ React242.createElement(
        LI,
        {
          style: styles,
          role: "none",
          key: `tableMenu_${id}_${index}`
        },
        item
      )))
    ), /* @__PURE__ */ React242.createElement(Dots, null))
  );
};

// src/components/table/Drawer.tsx
import styled79 from "@emotion/styled";
import * as React243 from "react";
var DrawerRow = styled79(Tr)`
  white-space: nowrap;
  pointer-events: none;

  & > td {
    pointer-events: auto;
    cursor: default !important;
  }
`;
var StyledDiv = styled79.div`
  background-color: rgb(244, 247, 247);
  padding-top: 48px;
  padding-bottom: 56px;
  width: -moz-available; /* WebKit-based browsers will ignore this. */
  width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
  width: fill-available;
  margin-left: ${(props) => `-${props.theme.breakpoints.sm.gutter}px`};
  margin-right: ${(props) => `-${props.theme.breakpoints.sm.gutter}px`};
  padding-left: ${(props) => `${props.theme.breakpoints.sm.gutter}px`};
  padding-right: ${(props) => `${props.theme.breakpoints.sm.gutter}px`};
  @media ${device.tablet} {
    margin-left: ${(props) => `-${props.theme.breakpoints.md.gutter}px`};
    margin-right: ${(props) => `-${props.theme.breakpoints.md.gutter}px`};
    padding-left: ${(props) => `${props.theme.breakpoints.md.gutter}px`};
    padding-right: ${(props) => `${props.theme.breakpoints.md.gutter}px`};
  }
  @media ${device.desktop} {
    margin-left: -50%;
    margin-right: -50%;
    padding-left: 50%;
    padding-right: 50%;
  }
`;
var Ss = styled79.div`
  max-width: auto;
  margin: auto;
`;
var Drawer = ({ children }) => {
  return /* @__PURE__ */ React243.createElement(DrawerRow, null, /* @__PURE__ */ React243.createElement("td", { colSpan: 100, style: { padding: 0, boxShadow: "none" } }, /* @__PURE__ */ React243.createElement(StyledDiv, null, /* @__PURE__ */ React243.createElement(Ss, null, children))));
};

// src/components/table/PrismicTable.tsx
import styled80 from "@emotion/styled";
import React244 from "react";
var ZebraHost = styled80.div``;
var ZebraTableWrapper = styled80(Reveal, {
  shouldForwardProp: shouldForwardProp("div")
})`
  ${(props) => zebraTable(props.background, props.colorTheme)}
`;
var StandardHost = styled80.div``;
var StandardTableWrapper = styled80(Reveal, {
  shouldForwardProp: shouldForwardProp("div")
})`
  ${(props) => standardTable(props.background, props.noHeadingUnderline)}

  @media ${device.tablet} {
    padding: 90px 0;
  }
`;
var SmallTableWrapper = styled80(Reveal, {
  shouldForwardProp: shouldForwardProp("div")
})`
  ${(props) => smallTable(props.background)}
`;
var Copy = styled80.div`
  margin-bottom: 60px;

  ${device.tablet} {
    max-width: 70%;
  }
`;
var PrismicTable = ({
  background,
  layout,
  theme: theme2 = "primary",
  children,
  copy,
  noHeadingUnderline = true
}) => {
  if (layout === "Zebra") {
    return /* @__PURE__ */ React244.createElement(ZebraHost, null, copy && /* @__PURE__ */ React244.createElement(Copy, null, copy), /* @__PURE__ */ React244.createElement(ZebraTableWrapper, { background, colorTheme: theme2 }, children));
  }
  if (layout === "Small") {
    return /* @__PURE__ */ React244.createElement(StandardHost, null, /* @__PURE__ */ React244.createElement(SmallTableWrapper, { background }, children));
  } else {
    return /* @__PURE__ */ React244.createElement(StandardHost, null, !!copy && /* @__PURE__ */ React244.createElement(Row, null, /* @__PURE__ */ React244.createElement(
      Column,
      {
        lg: { width: 7 / 12, offset: 1 / 12 },
        md: { width: 8 / 12 }
      },
      /* @__PURE__ */ React244.createElement(Copy, null, copy)
    )), /* @__PURE__ */ React244.createElement(Row, null, /* @__PURE__ */ React244.createElement(Column, { lg: { width: 10 / 12, offset: 1 / 12 } }, /* @__PURE__ */ React244.createElement(
      StandardTableWrapper,
      {
        background,
        noHeadingUnderline
      },
      children
    ))));
  }
};

// src/components/table/TableWithLargeText.tsx
import styled81 from "@emotion/styled";
import React245 from "react";
var Host25 = styled81(Reveal)`
  ${standardTable("Gray", true)}

  width: 100%;
  padding: 0px 20px;
  table {
    table-layout: fixed;
  }
  & > h2 {
    font-size: 18px;
    font-weight: 500;
    text-align: left;
    font-family: ${(props) => props.theme.font.familySlab};
    margin-bottom: 30px;
  }

  table thead th {
    border-bottom: 3px solid ${(props) => props.theme.colors.primary};
    padding-bottom: 10px;
    text-align: right;
    font-family: ${(props) => props.theme.font.family};
    font-size: 14px;

    padding-left: 0px;
    @media ${device.tablet} {
      font-size: ${typography.sizes.bodySmallSize}px;
      padding-left: 0;
      min-width: 100px;
    }
    &:nth-of-type(1) {
      font-size: 14px;
      font-weight: 400;
      text-align: left;
      font-family: ${(props) => props.theme.font.family};
      width: 250px;
      &:after {
        display: none;
      }
    }
    @media ${device.tablet} {
      &:nth-of-type(1) {
        font-size: 18px;
        font-weight: 500;
        text-align: left;
        font-family: ${(props) => props.theme.font.familySlab};
        &:after {
          display: none;
        }
      }
    }
  }
  table tbody tr {
    > td {
      border-bottom: 1px solid rgba(30, 61, 90, 0.1);
      padding-top: 10px;
      padding-bottom: 3px;
      text-align: right;
      width: 25%;
    }
    &:first-child {
      td {
        padding-top: 20px;
      }
    }
  }
  table tbody tr:last-child td {
    border-color: rgba(30, 61, 90, 0.1);
    padding-bottom: 3px;
  }
  > div > div > table {
    width: ${(props) => props.theme.pageWidth}px;
  }
  td {
    font-size: 14px;
    font-weight: 400;
    text-align: center;
    border-bottom: 1px solid ${(props) => props.theme.colors.primary};
    font-family: ${(props) => props.theme.font.family};

    @media ${device.tablet} {
      font-size: 14px;
    }
  }

  tr > td:nth-of-type(1) {
    text-align: left;
  }

  tfoot td {
    border: 0;
    font-size: ${typography.sizes.bodySmallSize}px;
    padding-top: 11px;
    font-family: ${(props) => props.theme.font.family};
    font-weight: 600;
    text-align: right;
    &:nth-of-type(1) {
      text-align: left;
    }
  }
`;
function TableWithLargeText({ children }) {
  return /* @__PURE__ */ React245.createElement(Host25, null, children);
}

// src/components/footer/Footer.tsx
import styled82 from "@emotion/styled";
import React246 from "react";
var Host26 = styled82.footer`
  display: flex;
  flex-flow: row nowrap;

  padding: 65px 0 100px;
  background-color: ${(props) => props.theme.colors.backgroundAlt};
  font-size: 14px;
  position: relative;
  z-index: 1;
  @media print {
    display: none;
  }
`;
var Wrapper = styled82.div`
  display: flex;
  flex-flow: column-reverse nowrap;

  @media ${device.desktop} {
    flex-flow: row nowrap;
  }
`;
var TopWrapper = styled82(Wrapper)`
  flex-flow: column nowrap;
  margin: 0 0 30px;
`;
var First = styled82.div`
  margin-bottom: 2em;
  min-width: 30%;
`;
var Last = styled82.div`
  ${typography.body}

  display: flex;
  flex-flow: column wrap;
  align-items: flex-start;

  @media ${device.desktop} {
    flex-flow: row wrap;
    justify-content: ${(props) => props.counter === 2 ? "flex-start" : "space-between"};
    width: 100%;

    > a + a {
      margin-left: ${(props) => props.counter === 2 ? "70px" : "unset"};
    }
  }

  > * {
    margin-bottom: 20px;
    @media ${device.tablet} {
      margin-bottom: 0;
    }
  }
`;
var Text6 = styled82.div`
  margin-bottom: 30px;
  p {
    font-size: 14px;
    line-height: ${`calc(27 / ${typography.sizes.bodySize})`};
  }

  strong {
    font-weight: 500;
  }

  a {
    text-decoration: none;
    @media (pointer: fine) {
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;
var Social = styled82.div`
  display: flex;
  align-items: center;

  > * {
    margin-right: 8px;
  }
`;
var Groups = styled82.div`
  display: flex;
  flex-flow: row wrap;

  margin-bottom: 30px;

  @media ${device.tablet} {
    flex-flow: row nowrap;
    margin-left: 0;
  }
  @media ${device.desktop} {
    margin-bottom: 0;
    width: 100%;
    justify-content: ${(props) => props.counter === 2 ? "flex-start" : "space-between"};
  }

  > div + div {
    margin-left: ${(props) => props.counter === 2 ? "60px" : "unset"};
  }
`;
var Footer2 = ({ text, social, groups, shortcuts }) => {
  let counter = 0;
  if (shortcuts) {
    counter = React246.Children.count(shortcuts);
  }
  return /* @__PURE__ */ React246.createElement(Host26, { "data-nosnippet": true, role: "contentinfo" }, /* @__PURE__ */ React246.createElement(Container, null, /* @__PURE__ */ React246.createElement(TopWrapper, null, /* @__PURE__ */ React246.createElement(First, null, /* @__PURE__ */ React246.createElement(Social, null, social)), /* @__PURE__ */ React246.createElement(Last, { counter }, shortcuts)), /* @__PURE__ */ React246.createElement(Wrapper, null, /* @__PURE__ */ React246.createElement(First, null, /* @__PURE__ */ React246.createElement(Text6, null, text)), /* @__PURE__ */ React246.createElement(Groups, { counter }, groups))));
};

// src/components/footer/FooterGroup.tsx
import styled83 from "@emotion/styled";
import React247 from "react";
var Host27 = styled83.div`
  margin-bottom: 30px;
  width: 100%;

  line-height: ${`calc(27 / ${typography.sizes.bodySize})`};

  @media ${device.mobile} {
    width: calc(50% - 25px);
    margin-right: 25px;
  }

  @media ${device.tablet} {
    width: auto;
    margin-right: 35px;
  }

  @media ${device.pageLimit} {
    margin-right: 42px;
  }

  &:last-child {
    margin-right: 0;
  }
`;
var Title4 = styled83.div`
  font-weight: 500;
`;
var List3 = styled83.div``;
var FooterGroup = ({ title, children }) => {
  return /* @__PURE__ */ React247.createElement(Host27, null, /* @__PURE__ */ React247.createElement(Title4, null, title), /* @__PURE__ */ React247.createElement(List3, null, children));
};

// src/components/footer/GroupLink.tsx
import styled84 from "@emotion/styled";
import React248, { forwardRef as forwardRef14 } from "react";
var Link2 = styled84.a`
  display: block;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;
var GroupLink = forwardRef14(function GroupLink2({ title, as, ...rest }, ref) {
  return /* @__PURE__ */ React248.createElement(Link2, { as, ...rest, ref, title }, title);
});

// src/components/footer/SoicalIcon.tsx
import styled85 from "@emotion/styled";
import React249 from "react";
var Link3 = styled85.a`
  opacity: 1;
  transition: opacity 200ms ease-in-out;

  &:hover {
    opacity: 0.7;
  }
`;
var SrOnly2 = styled85.span`
  ${sronly}
`;
var SocialLink = ({ children, title, as, ...rest }) => {
  return /* @__PURE__ */ React249.createElement(Link3, { as, ...rest, title }, children, /* @__PURE__ */ React249.createElement(SrOnly2, null, title));
};

// src/components/portal/Portal.tsx
import styled86 from "@emotion/styled";
import React250, { useEffect as useEffect14 } from "react";
import { createPortal as createPortal3 } from "react-dom";
var Host28 = styled86.div`
  background-color: ${theme.colors.background};
  position: fixed;
  top: 0;
  left: 0;
  min-width: 100vw;
  min-height: 100vh;
  z-index: 101;
`;
var AbsoluteContainer2 = styled86.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 101;
`;
var StyledButton = styled86(Button)`
  margin-top: auto;
`;
var Container3 = styled86.div`
  margin-left: 44px;
  margin-right: 44px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  min-height: 100vh;
  flex: 1;
  padding-bottom: 144px;
`;
var ChildContainer2 = styled86.div``;
function Portal({ children, titleRef, onClose, isOpen }) {
  useEffect14(() => {
    if (typeof window !== "undefined") {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);
  useEffect14(() => {
    if (titleRef?.current) {
      titleRef.current.focus();
    }
  }, [titleRef]);
  if (typeof window === "undefined") {
    return null;
  }
  const root = document.getElementById("portal");
  if (!root) {
    return null;
  }
  return createPortal3(
    /* @__PURE__ */ React250.createElement(Host28, { role: "dialog", "aria-modal": true, "data-cy": "overlayDialog" }, /* @__PURE__ */ React250.createElement(AbsoluteContainer2, null, /* @__PURE__ */ React250.createElement(Container3, null, /* @__PURE__ */ React250.createElement(ChildContainer2, null, children), /* @__PURE__ */ React250.createElement(
      StyledButton,
      {
        onClick: onClose,
        bordered: true,
        bg: theme.colors.primary,
        title: "Til baka"
      }
    )))),
    root
  );
}

// src/components/sliderList/SliderList.tsx
import styled88 from "@emotion/styled";
import React251, {
  Children as Children3,
  cloneElement as cloneElement2,
  useCallback as useCallback3,
  useEffect as useEffect15,
  useRef as useRef10
} from "react";
import { animated as animated16, useSpring as useSpring12 } from "react-spring";

// src/components/sliderList/SliderListItem.tsx
import styled87 from "@emotion/styled";
var ITEM_GUTTER = 14;
var SliderListItem = styled87.li`
  padding-left: ${ITEM_GUTTER}px;
  padding-right: ${ITEM_GUTTER}px;
  padding-bottom: 6px;
  margin-bottom: -8px;
  transition: opacity 175ms ease-in-out;
  transition-property: opacity, color;
  list-style: none;
  cursor: pointer;
  &:hover {
    opacity: 1 !important;
  }
  ${({ disableEvents }) => disableEvents && `pointer-events: none`}
`;

// src/components/sliderList/SliderList.tsx
var List4 = styled88.ul`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 16px;

  &:hover .li {
    opacity: 0.5;
  }
`;
var Underline2 = styled88(animated16.li)`
  width: 100px;
  height: 2px;
  background: #20446a;
  position: absolute;
  bottom: -8px;
  left: 0;
  list-style: none;
`;
var SliderList = ({
  children
}) => {
  const ref = useRef10(null);
  const [underline, spring] = useSpring12(() => ({
    x: 0,
    width: 0
  }));
  const reset2 = useCallback3(
    ({ immediate = false }) => {
      if (ref.current) {
        const host = ref.current;
        const activeIndex = Children3.toArray(children).findIndex(
          (node) => React251.isValidElement(node) && node.props.active
        );
        const item = host.children[activeIndex];
        if (activeIndex >= 0 && item) {
          const hostRect = host.getBoundingClientRect();
          const itemRect = item.getBoundingClientRect();
          spring.start({
            x: itemRect.x - hostRect.x + ITEM_GUTTER,
            width: itemRect.width - ITEM_GUTTER * 2,
            immediate
          });
        } else {
          spring.start({ width: 0 });
        }
      }
    },
    [children, spring]
  );
  const onMouseOver = React251.useCallback(
    (e) => {
      if (ref.current) {
        const item = e.currentTarget.getBoundingClientRect();
        const host = ref.current.getBoundingClientRect();
        spring.start({
          x: item.x - host.x + ITEM_GUTTER,
          width: item.width - ITEM_GUTTER * 2
        });
      }
    },
    [spring]
  );
  const onMouseOut = () => {
    reset2({ immediate: false });
  };
  useEffect15(() => {
    setTimeout(() => {
      reset2({ immediate: true });
    }, 10);
  }, [reset2]);
  return /* @__PURE__ */ React251.createElement(List4, { ref }, Children3.map(children, (child) => {
    if (!React251.isValidElement(child)) {
      return null;
    }
    const eventHandlers = {
      onMouseOver: (e) => {
        onMouseOver(e);
      },
      onMouseOut: () => {
        onMouseOut();
      },
      onFocus: (e) => {
        onMouseOver(e);
      },
      onBlur: () => onMouseOut()
    };
    return cloneElement2(child, eventHandlers);
  }), /* @__PURE__ */ React251.createElement(Underline2, { style: underline }));
};

// src/hooks/usePagination.ts
function usePagination({
  boundaryCount = 1,
  count = 1,
  page,
  disabled = false,
  hideNextButton = false,
  hidePrevButton = false,
  onChange,
  showFirstButton = false,
  showLastButton = false,
  siblingCount = 1
}) {
  const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };
  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(
    Math.max(count - boundaryCount + 1, boundaryCount + 1),
    count
  );
  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      count - boundaryCount - siblingCount * 2 - 1
    ),
    // Greater than startPages
    boundaryCount + 2
  );
  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : count - 1
  );
  const itemList = [
    ...showFirstButton ? ["first"] : [],
    ...hidePrevButton ? [] : ["previous"],
    ...startPages,
    // Start ellipsis
    ...siblingsStart > boundaryCount + 2 ? ["start-ellipsis"] : boundaryCount + 1 < count - boundaryCount ? [boundaryCount + 1] : [],
    // Sibling pages
    ...range(siblingsStart, siblingsEnd),
    // End ellipsis
    ...siblingsEnd < count - boundaryCount - 1 ? ["end-ellipsis"] : count - boundaryCount > boundaryCount ? [count - boundaryCount] : [],
    ...endPages,
    ...hideNextButton ? [] : ["next"],
    ...showLastButton ? ["last"] : []
  ];
  const buttonPage = (type) => {
    switch (type) {
      case "first":
        return 1;
      case "previous":
        return page - 1;
      case "next":
        return page + 1;
      case "last":
        return count;
      default:
        return null;
    }
  };
  const items = itemList.map((item) => {
    const itemIsANumber = typeof item === "number";
    const itemType = itemIsANumber ? "page" : item;
    const itemPage = itemIsANumber ? item : buttonPage(item);
    const itemSelected = itemIsANumber ? item === page : false;
    const itemDisabled = itemIsANumber ? disabled : disabled || item.indexOf("ellipsis") === -1 && (item === "next" || item === "last" ? page >= count : page <= 1);
    const itemAreaCurrent = itemIsANumber && item === page ? "true" : void 0;
    const itemOnClick = (event) => {
      onChange?.(event, itemPage);
    };
    return {
      onClick: itemOnClick,
      type: itemType,
      page: itemPage,
      selected: itemSelected,
      disabled: itemDisabled,
      "aria-current": itemAreaCurrent
    };
  });
  return {
    items
  };
}

// src/components/dropdown/DropDown.tsx
import styled89 from "@emotion/styled";
import React253, { useEffect as useEffect16, useMemo as useMemo5, useRef as useRef11, useState as useState22 } from "react";
import { animated as animated17, useSpring as useSpring13 } from "react-spring";

// src/contexts/TabsContext.tsx
import React252 from "react";
var emptyRect = {
  height: 0,
  width: 0,
  x: 0,
  y: 0,
  bottom: 0,
  left: 0,
  right: 0,
  top: 0,
  toJSON: () => {
  }
};
var TabsContext = React252.createContext({
  selectedTab: "",
  selectedTabRect: emptyRect,
  setSelectedTabRect: () => {
  },
  setSelectedTab: () => {
  },
  registerTab: () => {
  },
  registerRef: () => {
  },
  focusNextTab: () => {
  },
  focusPreviousTab: () => {
  }
});

// src/components/dropdown/DropDown.tsx
var Host29 = styled89.button`
  position: relative;
  height: 50px;
  background: ${(props) => props.transparent && !props.isOpen ? "transparent" : props.theme.colors.background};

  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-radius: ${(props) => props.isOpen ? "2px 2px 0px 0px" : "2px"};

  padding: 18px 17px 15px 28px;
  min-width: 169px;
  margin-right: ${(props) => props.marginRight}px;

  cursor: pointer;

  outline-offset: -0.4rem;
`;
var Text7 = styled89.span`
  margin-right: 19px;
  ${typography.body};
  font-weight: 500;
`;
var Icon4 = styled89.span`
  svg#lock path:nth-of-type(2) {
    display: ${(props) => props.isOpen && !props.transparent ? "none" : "block"};
  }
  svg#globe * {
    stroke: ${(props) => props.isOpen && props.transparent ? `${props.theme.shades.ternary5} !important;` : ""};
  }
`;
var Drawer2 = styled89(animated17.div)`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  position: absolute;
  top: 49px;
  left: 0px;
  right: 0px;
  padding: 25px 18px 25px 28px;
  z-index: 100;
  border-top: none;
  background-color: ${(props) => props.theme.colors.backgroundAlt};

  font-size: 16px;
  line-height: 24px;

  > p {
    white-space: nowrap;
    font-size: 14px;
  }

  > * {
    padding-bottom: 6px;
    margin-bottom: 25px;
    position: relative;
  }
  & > *:last-child {
    margin-bottom: 0px;
  }
`;
var DropDown = ({
  text,
  icon,
  transparent,
  id,
  children,
  a_lbl,
  marginRight = 0
}) => {
  const [selectedTab, setSelectedTab] = React253.useState("0");
  const [selectedTabRect, setSelectedTabRect] = React253.useState(emptyRect);
  const [focus, setFocus] = React253.useState(false);
  const [dropDownState, setDropDownState] = useState22(
    "Closed" /* Closed */
  );
  const handleHostClick = () => {
    if (dropDownState === "Open" /* Open */) {
      setDropDownState("Closed" /* Closed */);
    } else {
      setDropDownState("Open" /* Open */);
    }
  };
  const handleMouseEnter = () => {
    setDropDownState("Open" /* Open */);
  };
  const handleMouseLeave = () => {
    setDropDownState("Closed" /* Closed */);
  };
  const tabsArray = React253.useRef([]);
  const [refMap, setRefMap] = React253.useState({});
  const nodeRef = useRef11(null);
  function registerTab(tabId) {
    tabsArray.current.push(tabId);
  }
  function registerRef(tabId, ref) {
    setRefMap((map) => ({
      ...map,
      [tabId]: ref
    }));
  }
  const fadeIn = useSpring13({
    opacity: dropDownState === "Open" /* Open */ ? 1 : 0,
    transform: dropDownState === "Open" /* Open */ ? "translateY(0px)" : "translateY(-5px)"
  });
  const handleClickOutside = (e) => {
    if (nodeRef.current.contains(e.target)) {
      return;
    }
    setDropDownState("Closed" /* Closed */);
  };
  useEffect16(() => {
    if (dropDownState === "Open" /* Open */) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropDownState]);
  useMemo5(() => {
    if (dropDownState === "Open" /* Open */ && focus && refMap[0] !== void 0) {
      refMap[0]?.current?.focus();
      setFocus(false);
    }
  }, [dropDownState, refMap]);
  function focusNextTab() {
    const tabsCount = tabsArray.current.length;
    const currentSelectedIndex = tabsArray.current.findIndex(
      (i) => i == selectedTab
    );
    const indexOfNextTabBtn = (currentSelectedIndex + 1) % tabsCount;
    const idOfNextTab = tabsArray.current[indexOfNextTabBtn];
    const nextTabBtnRef = refMap[idOfNextTab];
    nextTabBtnRef?.current?.focus();
    setSelectedTab(idOfNextTab);
  }
  function focusPreviousTab() {
    const tabsCount = tabsArray.current.length;
    const currentSelectedIndex = tabsArray.current.findIndex(
      (i) => i == selectedTab
    );
    const indexOfPreviousTabBtn = (currentSelectedIndex - 1 + tabsCount) % tabsCount;
    const idOfPreviousTab = tabsArray.current[indexOfPreviousTabBtn];
    const previousTabRef = refMap[idOfPreviousTab];
    previousTabRef?.current?.focus();
    setSelectedTab(idOfPreviousTab);
  }
  const value = React253.useMemo(() => {
    return {
      selectedTab,
      selectedTabRect,
      setSelectedTabRect,
      setSelectedTab: (selected) => {
        setSelectedTab(selected);
      },
      registerTab,
      registerRef,
      focusNextTab,
      focusPreviousTab
    };
  }, [selectedTab, setSelectedTab, selectedTabRect]);
  return /* @__PURE__ */ React253.createElement(
    Host29,
    {
      id,
      role: "button",
      "aria-label": `${a_lbl ? a_lbl : text}`,
      "aria-haspopup": true,
      "aria-expanded": dropDownState === "Open" /* Open */ ? true : false,
      "aria-controls": `${id}-links`,
      onClick: handleHostClick,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onKeyDown: (event) => {
        if (event.key === "Escape") {
          refMap[selectedTab]?.current?.parentNode?.parentElement?.focus();
          setDropDownState("Closed" /* Closed */);
        }
      },
      isOpen: dropDownState === "Open" /* Open */,
      ref: nodeRef,
      transparent,
      marginRight
    },
    !!text && transparent ? dropDownState === "Open" /* Open */ ? /* @__PURE__ */ React253.createElement(Text7, null, text) : /* @__PURE__ */ React253.createElement(React253.Fragment, null) : /* @__PURE__ */ React253.createElement(Text7, null, text),
    /* @__PURE__ */ React253.createElement(
      Icon4,
      {
        isOpen: dropDownState === "Open" /* Open */,
        transparent
      },
      icon
    ),
    dropDownState === "Open" /* Open */ && /* @__PURE__ */ React253.createElement(
      Drawer2,
      {
        id: `${id}-links`,
        role: "menu",
        "aria-labelledby": id,
        style: { ...fadeIn }
      },
      /* @__PURE__ */ React253.createElement(TabsContext.Provider, { value }, children)
    )
  );
};

// src/components/dropdown/UnderlineLink.tsx
import styled90 from "@emotion/styled";
import composeRefs from "@seznam/compose-react-refs";
import React254, { useState as useState23 } from "react";
import { animated as animated18, useSpring as useSpring14 } from "react-spring";
var UnderlineHost = styled90.div`
  position: absolute;
  left: 0;
  bottom: 0px;
  height: 1px;
  background-color: rgba(22, 62, 99, 0.1);
  overflow: hidden;
  width: 28px;
  transition: all 200ms ease-in;
`;
var InnerLink = styled90.a`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
var UnderlineLink = React254.forwardRef(
  function UnderlineLink2({ children, id, href, ...rest }, ref) {
    const innerRef = React254.useRef(null);
    const context = React254.useContext(TabsContext);
    React254.useEffect(() => {
      context.registerTab(id, innerRef);
    }, []);
    React254.useEffect(() => {
      context.registerRef(id, innerRef);
    });
    React254.useLayoutEffect(() => {
      if (innerRef.current && context.selectedTab === id) {
        context.setSelectedTabRect(innerRef.current.getBoundingClientRect());
      }
    }, [innerRef.current, context.selectedTab]);
    function onKeyDown(ev) {
      const event = ev;
      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight": {
          context.focusNextTab();
          break;
        }
        case "ArrowLeft":
        case "ArrowUp": {
          context.focusPreviousTab();
          break;
        }
        default:
          break;
      }
    }
    const [hover, setHover] = useState23(false);
    const p = useSpring14({
      x: hover ? "0%" : "-100%",
      opacity: hover ? 1 : 0,
      from: { x: "-100%" }
    });
    const handleClick = (e, id2) => {
      context.setSelectedTab(id2);
      if (rest.onClick) rest.onClick(e);
    };
    return /* @__PURE__ */ React254.createElement(
      InnerLink,
      {
        ...rest,
        id: `menuitem-${id}`,
        onClick: (e) => handleClick(e, id),
        ref: composeRefs(innerRef, ref),
        role: "menuitem",
        tabIndex: context.selectedTab == id ? 0 : -1,
        href,
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(false),
        onKeyDown
      },
      children,
      /* @__PURE__ */ React254.createElement(UnderlineHost, null, /* @__PURE__ */ React254.createElement(
        animated18.div,
        {
          style: {
            backgroundColor: "#163e63",
            width: "100%",
            height: "100%",
            ...p
          }
        }
      ))
    );
  }
);

// src/components/pagination/Pagination.tsx
import styled91 from "@emotion/styled";
import { useResource as useResource8 } from "@landsbankinn/l16n";
import React255 from "react";

// src/components/pagination/Pagination.res.ts
var Pagination_res_default = {
  next: {
    is: "N\xE6st",
    en: "Next",
    pl: "Nast\u0119pna"
  },
  previous: {
    is: "Fyrri",
    en: "Last",
    pl: "Poprzednie"
  }
};

// src/components/pagination/Pagination.tsx
var PaginationContainer = styled91.div`
  display: flex;
  justify-content: center;
  margin: 60px 0;
  margin-top: ${(props) => props.top ? "0px" : "60px"};
`;
var Items = styled91.ul`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
var Item2 = styled91.li``;
var Dots2 = styled91.div`
  padding: 8px;
`;
var Page = styled91.button`
  font-weight: ${(props) => props.selected ? "bold" : "normal"};
  font-size: 19px;
  opacity: ${(props) => props.disabled ? 0.6 : 1};
  padding: 8px;
  cursor: pointer;
`;
var Button7 = styled91.button`
  border: 1px solid ${(props) => props.theme.colors.primary};
  color: ${(props) => props.theme.colors.primary};
  padding: 8px;
  margin: 0 8px;
  opacity: ${(props) => props.disabled ? 0.5 : 1};
  cursor: pointer;
`;
function Pagination(props) {
  const { items } = usePagination(props);
  const { r } = useResource8();
  return /* @__PURE__ */ React255.createElement(Items, null, items.map(({ page, type, ...item }, index) => {
    let children = null;
    if (type === "start-ellipsis" || type === "end-ellipsis") {
      children = /* @__PURE__ */ React255.createElement(Dots2, null, "\u2026");
    } else if (type === "page") {
      children = /* @__PURE__ */ React255.createElement(Page, { type: "button", ...item }, page);
    } else {
      children = /* @__PURE__ */ React255.createElement(Button7, { type: "button", ...item }, r(Pagination_res_default[type]));
    }
    return /* @__PURE__ */ React255.createElement(Item2, { key: index }, children);
  }));
}

// src/components/overlay-dialog/OverlayButton.tsx
import styled92 from "@emotion/styled";
import * as React256 from "react";
import { forwardRef as forwardRef15 } from "react";
var Host30 = styled92.button`
  margin-top: 0;
  height: 42px;
  display: inline-block;
  text-align: center;
  line-height: 1;
  cursor: pointer;
  -webkit-appearance: none;
  vertical-align: middle;
  border: solid 1px transparent;
  border-radius: 2px;
  padding: 0 20px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 400;
  transition: all 0.25s ease-out;
  text-decoration: none;
  background: #3c71ac;
  color: #fff;
  width: 273px;
  white-space: nowrap;

  &:hover,
  &:focus {
    background-color: #467fbe;
    outline: none;
  }
  &:active {
    background-color: #3c71ac;
    outline: none;
  }

  ${(props) => props.green ? `

    background: #2bab55;
    color: #fff;


    &:hover,
    &:focus {
    background-color: #37be63;
    outline: none;
    }

    &:active {
    background-color: #2bab55;
    outline: none;
    }

  ` : ``};
  ${(props) => props.small ? `width: 252px;` : ``};
  ${(props) => props.pill ? `
    border-radius: 21px;
    width: 135px;
    background-color: #fff;
    color: #37679c;
    font-size: 14px;
    outline: none;

    &:hover,
    &:focus {
    border: 1px solid #fff;
    color: #fff;
    }

    &:active {
    border: 1px solid #fff;
    color: #fff;
    }
  ` : ``};
  ${(props) => props.disabled ? `opacity: 0.25;
  cursor: not-allowed;
  pointer-events: none;` : ``};
`;
var OverlayButton = forwardRef15(function ButtonInner2({ isSubmit, pill, disabled, isGreen, onClick, children, size, dataId }, ref) {
  return /* @__PURE__ */ React256.createElement(
    Host30,
    {
      type: isSubmit ? "submit" : "button",
      disabled,
      green: isGreen,
      pill,
      small: size === "small",
      onClick,
      role: "button",
      "data-id": dataId,
      ref
    },
    children
  );
});

// src/components/overlay-dialog/OverlayDialog.tsx
import styled95 from "@emotion/styled";
import { useResource as useResource9 } from "@landsbankinn/l16n";
import React260 from "react";
import { v4 as uuid } from "uuid";

// src/components/overlay-dialog/DialogClose.tsx
import * as React257 from "react";
var DialogClose = () => /* @__PURE__ */ React257.createElement("svg", { height: "20", width: "20" }, /* @__PURE__ */ React257.createElement("g", null, /* @__PURE__ */ React257.createElement(
  "path",
  {
    fill: "#FFFFFF",
    d: "M13.6,10.4l5.6-5.6c1-1,1-2.6,0-3.5c-1-1-2.6-1-3.5,0l-5.6,5.6l-5.6-5.6c-1-1-2.7-1-3.7,0s-1,2.7,0,3.7\n			l5.6,5.6l-5.6,5.6c-1,1-1,2.6,0,3.5c1,1,2.6,1,3.5,0l5.6-5.6l5.6,5.6c1,1,2.7,1,3.7,0s1-2.7,0-3.7L13.6,10.4z"
  }
)));

// src/components/overlay-dialog/OverlayDialogActionCloseButton.tsx
import styled93 from "@emotion/styled";
import * as React258 from "react";
var Host31 = styled93.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 98px;

  ${(props) => props.withDivider ? `box-shadow: inset 0px 1px 0px 0px rgba(213, 213, 213, 0.5);` : ""};
`;
var CloseButton2 = styled93.div`
  height: 40px;
  width: 273px;
  border-radius: 2px;
  background-color: dialog-close-button-background;
  color: dialog-close-button-text;

  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  border: none;
  cursor: pointer;

  @media print {
    display: none;
  }

  &:hover,
  &:focus {
    background-color: dialog-close-button-background--hover;
  }

  &:active {
    background-color: dialog-close-button-background;
  }
`;
var OverlayDialogActionCloseButton = ({ onClose, withDivider, children }) => {
  return /* @__PURE__ */ React258.createElement(Host31, { withDivider }, /* @__PURE__ */ React258.createElement(CloseButton2, { onClick: onClose, "data-cy": "overlayDialogCloseButton" }, children));
};

// src/components/overlay-dialog/spinner/Bubbles.tsx
import styled94 from "@emotion/styled";
import * as React259 from "react";
var Size = /* @__PURE__ */ ((Size2) => {
  Size2["INLINE"] = "inline";
  Size2["SMALL"] = "small";
  Size2["MEDIUM_SMALL"] = "mediumSmall";
  Size2["MEDIUM"] = "medium";
  Size2["LARGE"] = "large";
  return Size2;
})(Size || {});
var Speed = /* @__PURE__ */ ((Speed2) => {
  Speed2["SLOW"] = "slow";
  Speed2["MEDIUM"] = "medium";
  return Speed2;
})(Speed || {});
var Color = /* @__PURE__ */ ((Color2) => {
  Color2["BLUE"] = "blue";
  Color2["ORANGE"] = "orange";
  Color2["WHITE"] = "white";
  return Color2;
})(Color || {});
var Bubble = styled94.span`
  ${(props) => props.size === "inline" /* INLINE */ ? "display:inline-flex;" : "display:flex;"};
  align-items: center;
  justify-content: center;

  & > span {
    height: 48px;
    width: 48px;
    background: white;
    border-radius: 100%;
    margin: 0 6px;
    animation: spinners-bouncedelay infinite both;
    animation-duration: 1s;
    animation-timing-function: ease-in-out;

    ${(props) => props.size === "inline" /* INLINE */ ? `height: 8px;
            width: 8px;
        margin: 0 3px;` : props.size === "small" /* SMALL */ ? `height: 24px;
        width: 24px;` : props.size === "mediumSmall" /* MEDIUM_SMALL */ ? `height: 38px;
        width: 38px;` : props.size === "medium" /* MEDIUM */ ? `height: 68px;
        width: 68px;` : props.size === "large" /* LARGE */ ? `height: 88px;
        width: 88px;` : ""};

    ${(props) => props.speed === "slow" /* SLOW */ ? `animation-duration: 1.6s;` : `animation-duration: 1.3s;`};

    ${(props) => props.color === "blue" /* BLUE */ ? `background-color: #85d7fb;` : props.color === "orange" /* ORANGE */ ? `background-color: #f58345;` : props.color === "white" /* WHITE */ ? `background-color: #ffffff;` : ``};
    ${(props) => props.centerWithin ? `margin: auto 6px;` : ""};
  }
  & > span:nth-of-type(1) {
    animation-delay: -0.32s !important;
  }
  & > span:nth-of-type(2) {
    animation-delay: -0.16s !important;
  }
  & > span:nth-of-type(3) {
  }
`;
function Bubbles({ size, speed, color, centerWithin }) {
  return /* @__PURE__ */ React259.createElement(
    Bubble,
    {
      size,
      speed,
      color,
      centerWithin,
      "data-cy": "bubbles"
    },
    /* @__PURE__ */ React259.createElement("span", null),
    /* @__PURE__ */ React259.createElement("span", null),
    /* @__PURE__ */ React259.createElement("span", null)
  );
}

// src/components/overlay-dialog/OverlayDialog.tsx
var Host32 = styled95.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  display: block;
  background: hsla(207, 65%, 9%, 0.8);
  z-index: 9000;
  ${(props) => props.usingIdp ? `
  background:#1c2e46;
  &:before{
    content:url("data:image/svg+xml,%3Csvg width='34' height='34' viewBox='0 0 34 34' xmlns='http://www.w3.org/2000/svg'%3E%3Ctitle%3ELandsbankinn%3C/title%3E%3Cpath d='M0 33.116c1.582-.507 3.114-1.97 3.874-4.38.632-2.016 7.25-22.687 7.9-24.783.656-2.096 2.402-3.88 5.077-3.926C19.53-.02 29.62.007 31.68.02c-3.03.2-4.387 1.64-5.38 4.673-.757 2.305-6.762 21.082-7.736 24.41-.973 3.317 3.26 4.464 4.232 1.402.612-1.907 5.462-16.516 5.462-16.516s4.71 13.856 5.318 15.69c.53 1.613-.257 3.424-2.747 3.436 0 0-28.633-.048-30.83 0z' fill='%234e5f73' /%3E%3C/svg%3E");
    background:#223750;
    display:flex;
    justify-content:center;
    align-items:center;
    position:sticky;
    height:67px;
    width:100%;
  }
  ` : ""};
`;
var Header3 = styled95.div`
  width: 100%;
  height: 56px;
  background-color: white;
  display: inline-block;
  text-align: left;

  &.success {
    background-color: rgba(0, 166, 81, 1);
    color: white;

    h2 {
      color: white;
      box-sizing: border-box;
      font-weight: 400;
    }
  }

  @media print {
    height: 0;
  }
  h2 {
    text-align: left;
    display: inline-block;
    margin: 0;
    padding: 18px 30px;
    font-weight: 500;
    font-size: 20px;
    outline: none;
    position: relative;
    border: 0;
    @media print {
      padding: 18px 10px;
    }
  }

  ${(props) => props.type === "green" ? `background-color: #00A651;
  color: white;` : props.type === "orange" ? ` background-color: #fda424 !important;
  color: #000 !important;` : `background-color: rgba(53, 102, 158, 0.05);`};
`;
var Dialog3 = styled95.div`
  position: relative;
  background: #ffffff;
  width: 900px;
  min-width: 900px;
  height: auto;
  min-height: 133px;
  overflow: hidden;
  border: none;
  border-radius: 2px;
  box-sizing: border-box;
  z-index: 9999;
  margin: auto;
  margin-top: 122px;
  margin-bottom: 40px;
  @media print {
    min-width: auto;
    width: auto;
  }

  /* IE hack */
  @media screen\0 {
    margin: 0;
  }

  ${(props) => props.size === 0 /* Narrow */ ? `
  
  width: 600px;
  min-width: 600px;` : ""};
  ${(props) => props.size === 3 /* Fullscreen */ ? `
  
  width: 98%;
  min-width: 98%;
  height: 98%;
  display: inline-flex;
  flex-direction: column;
  margin-top: auto;
  @media print {
    display: block;
  }` : ""};
  ${(props) => props.size === 2 /* Wide */ ? `
  
  width: 960px;
  min-width: 960px` : ""}
`;
var CloseButton3 = styled95.button`
  position: absolute;
  margin: 12px 10px;
  margin-left: 30px;
  height: 36px;
  width: 36px;
  padding: 0;
  line-height: 0;
  border: none;
  border-radius: 2px;
  background-color: #35669e;
  top: 0px;
  right: 0px;
  z-index: 11000;

  cursor: pointer;

  &:hover,
  &:focus {
    background-color: hsla(212, 50%, 51%, 1);
  }

  &:active {
    background-color: #35669e;
  }
  ${(props) => props.isSuccess ? `
  background-color: rgba(255, 255, 255, 0.2);

  &:hover,
  &:focus {
    background-color: rgba(255, 255, 255, 0.3);
  }

  &:active {
    background-color: rgba(255, 255, 255, 0.1);
  }
  ` : ""};
`;
var FullScreenChildren = styled95.div`
  height: 100%;
  overflow: auto;
  overflow-x: hidden;
  padding: 10px;

  ${(props) => props.borderlessFullscreen ? `padding: 0;` : ""};
`;
var DialogSize = /* @__PURE__ */ ((DialogSize2) => {
  DialogSize2[DialogSize2["Narrow"] = 0] = "Narrow";
  DialogSize2[DialogSize2["Normal"] = 1] = "Normal";
  DialogSize2[DialogSize2["Wide"] = 2] = "Wide";
  DialogSize2[DialogSize2["Fullscreen"] = 3] = "Fullscreen";
  return DialogSize2;
})(DialogSize || {});
function OverlayDialog({
  isOpen,
  isLoading,
  hideCloseButton,
  isSuccess,
  size = 1 /* Normal */,
  overflowContent,
  greyContent,
  onClose,
  title,
  hideHeader,
  greenHeader,
  orangeHeader,
  subHeader,
  buttonContainerDivider,
  usingIdp,
  borderlessFullscreen,
  children
}) {
  const { r } = useResource9();
  const headingId = uuid();
  const titleRef = React260.createRef();
  const closeBtnRef = React260.createRef();
  if (!isOpen) {
    return null;
  }
  return /* @__PURE__ */ React260.createElement(
    Host32,
    {
      "data-cy": "overlayDialog",
      usingIdp,
      onKeyDown: (e) => {
        if (e.key === "Escape") {
          onClose();
        }
      },
      "aria-labelledby": title.type === "visible" ? headingId : void 0,
      "aria-label": title.type === "hidden" ? "" : void 0,
      role: "dialog",
      "aria-modal": true
    },
    !isLoading && /* @__PURE__ */ React260.createElement(
      Dialog3,
      {
        size,
        greyContent,
        overflowContent,
        onClick: (e) => e.stopPropagation()
      },
      !hideHeader ? title.type === "visible" ? /* @__PURE__ */ React260.createElement(
        Header3,
        {
          success: isSuccess,
          type: greenHeader ? "green" : orangeHeader ? "orange" : "grey"
        },
        /* @__PURE__ */ React260.createElement("h2", { id: headingId, tabIndex: -1, ref: titleRef }, title.value),
        subHeader,
        /* @__PURE__ */ React260.createElement(
          CloseButton3,
          {
            isSuccess,
            ref: closeBtnRef,
            onClick: onClose,
            "aria-label": r(Dialog_res_default.closeAriaLabel)
          },
          /* @__PURE__ */ React260.createElement(DialogClose, null)
        )
      ) : /* @__PURE__ */ React260.createElement(
        CloseButton3,
        {
          isSuccess,
          ref: closeBtnRef,
          onClick: onClose,
          "aria-label": r(Dialog_res_default.closeAriaLabel)
        },
        /* @__PURE__ */ React260.createElement(DialogClose, null)
      ) : null,
      size === 3 /* Fullscreen */ && /* @__PURE__ */ React260.createElement(FullScreenChildren, { borderlessFullscreen }, children),
      size !== 3 /* Fullscreen */ && children,
      !hideCloseButton && /* @__PURE__ */ React260.createElement(
        OverlayDialogActionCloseButton,
        {
          onClose,
          withDivider: buttonContainerDivider
        },
        r(Dialog_res_default.closeAriaLabel)
      )
    ),
    isLoading && /* @__PURE__ */ React260.createElement(Bubbles, { size: "large" /* LARGE */, speed: "medium" /* MEDIUM */ })
  );
}

// src/components/overlay-dialog/OverlayFooter.tsx
import styled96 from "@emotion/styled";
import * as React261 from "react";
var Host33 = styled96.div`
  margin-top: 48px;
  padding: 36px;
  padding-bottom: 24px;
  ${(props) => props.divider ? `box-shadow: inset 0 2px 0 0 #efefef;` : ""};
`;
var LoadingContainer = styled96.div`
  display: flex;
  min-height: 40px;
`;
var FooterColumn = styled96(Column)`
  display: flex;
  ${(props) => props.count > 1 && !props.loading ? "justify-content:space-between;" : "justify-content:center"}
`;
function OverlayFooter({ loading, divider, children }) {
  const cnt = React261.Children.count(children);
  return /* @__PURE__ */ React261.createElement(Host33, { divider }, /* @__PURE__ */ React261.createElement(FooterColumn, { count: cnt, loading }, loading ? /* @__PURE__ */ React261.createElement(LoadingContainer, null, /* @__PURE__ */ React261.createElement(Bubbles, { size: "small" /* SMALL */, color: "blue" /* BLUE */ })) : children));
}

// src/components/overlay-dialog/OverlayHeader.tsx
import styled97 from "@emotion/styled";
import * as React262 from "react";
var HeaderType = /* @__PURE__ */ ((HeaderType2) => {
  HeaderType2[HeaderType2["Success"] = 0] = "Success";
  HeaderType2[HeaderType2["Grey"] = 1] = "Grey";
  HeaderType2[HeaderType2["Blue"] = 2] = "Blue";
  HeaderType2[HeaderType2["Orange"] = 3] = "Orange";
  return HeaderType2;
})(HeaderType || {});
var Host34 = styled97.div`
  display: flex;
  flex-direction: column;
  background-color: rgba(53, 102, 158, 0.05);
  align-items: center;
  color: black-100;
  padding: 36px 30px 24px;

  ${(props) => props.headerType === 0 /* Success */ ? `color:#fff;background-color:rgba(0, 166, 81, 1);` : props.headerType === 2 /* Blue */ ? `rgba(53,102,158,0.05);` : props.headerType === 1 /* Grey */ ? `rgba(240, 240, 240, 0.6);` : props.headerType === 3 /* Orange */ ? `background-color: #fda424;` : ""};
`;
var H3 = styled97.h3`
  font-size: 36px;
  line-height: 54px;
  font-weight: 400;
  text-align: center;
  margin: 0;
  padding: 0;
  color: rgba(0, 0, 0, 1);
`;
var SubHeader = styled97.div`
  display: flex;
  justify-content: center;
  & > * + * {
    margin-left: 14px;
  }
`;
function OverlayHeader({ title, headerType, children }) {
  return /* @__PURE__ */ React262.createElement(Host34, { headerType }, /* @__PURE__ */ React262.createElement(H3, { success: headerType === 0 /* Success */ }, title), /* @__PURE__ */ React262.createElement(SubHeader, null, children));
}

// src/components/details/NumberDetails.tsx
import styled98 from "@emotion/styled";
import React263 from "react";
var Host35 = styled98.div`
  padding: 12px 0 0;
  border-top: 1px solid rgba(21, 46, 72, 0.2);
  border-bottom: 1px solid rgba(21, 46, 72, 0.2);
`;
var Label5 = styled98.div`
  ${bodySmall};
  margin-bottom: 11px;
`;
var Bottom = styled98.div`
  display: flex;
  flex-flow: row wrap;
  align-items: baseline;
`;
var Number2 = styled98.span`
  ${numbers};
  ${responseFont(30, 40)};
  line-height: ${70 / 40};
  margin-right: 9px;
`;
var Subfix = styled98.span`
  ${body};
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: -0.05em;
`;
var NumberDetails = ({
  label: label2,
  number,
  subfix
}) => {
  return /* @__PURE__ */ React263.createElement(Host35, null, /* @__PURE__ */ React263.createElement(Label5, null, label2), /* @__PURE__ */ React263.createElement(Bottom, null, /* @__PURE__ */ React263.createElement(Number2, null, number), /* @__PURE__ */ React263.createElement(Subfix, null, subfix)));
};

// src/components/details/HeroNumberDetails.tsx
import styled99 from "@emotion/styled";
import React264 from "react";
var Host36 = styled99.div`
  padding: 15px 0 9px;
  border-top: 1px solid rgba(21, 46, 72, 0.2);
  border-bottom: 1px solid rgba(21, 46, 72, 0.2);
`;
var Label6 = styled99.div`
  font-size: 16px;
  line-height: ${24 / 16}em;
  font-weight: 500;
  margin-bottom: 7px;
`;
var Bottom2 = styled99.div`
  display: flex;
  flex-flow: row wrap;
  align-items: baseline;
`;
var Number3 = styled99.span`
  ${responseFont(40, 50)};
  font-weight: 500;
  line-height: ${58 / 50}em;
  margin-right: 13px;
`;
var Subfix2 = styled99.span`
  font-size: 16px;
  font-weight: 500;
  line-height: ${24 / 16}em;
  text-transform: uppercase;
`;
var Subtitle = styled99.span`
  font-size: 16px;
  line-height: ${24 / 16}em;
  font-weight: 400;
`;
function HeroNumberDetails({
  label: label2,
  number,
  subfix,
  subtitle
}) {
  return /* @__PURE__ */ React264.createElement(Host36, null, /* @__PURE__ */ React264.createElement(Label6, null, label2), /* @__PURE__ */ React264.createElement(Bottom2, null, /* @__PURE__ */ React264.createElement(Number3, null, number), /* @__PURE__ */ React264.createElement(Subfix2, null, subfix)), /* @__PURE__ */ React264.createElement(Subtitle, null, subtitle));
}

// src/components/notification-banner/NotificationBanner.tsx
import styled100 from "@emotion/styled";
import React265 from "react";
var Background = styled100.a`
  display: flex;
  flex-flow: row;
  box-sizing: border-box;
  background-color: ${(props) => props.color};
  pointer-events: ${(props) => props.href ? "auto" : "none"};
  justify-content: space-between;

  padding: 20px 28px;

  @media ${device.desktop} {
    flex-flow: row nowrap;
    padding-right: 0px;
  }
`;
var Title5 = styled100.span`
  display: inline;
  font-weight: bold;
  font-size: 16px;
`;
var ArrowContainer = styled100.div`
  display: inline-flex;
  padding-top: 7px;
  padding-left: 10px;
  @media ${device.desktop} {
    min-width: 50px;
  }
`;
var TextContainer = styled100.div`
  display: flex;
  flex-direction: column;
  @media ${device.desktop} {
    display: inline;
    flex-direction: row;
  }
  p {
    display: inline;
    font-size: 16px;
  }
  span {
    display: inline;
  }
`;
var NotificationBanner = ({
  color,
  title,
  copy,
  link
}) => {
  const bgColor = color.toLowerCase() == "blue" ? theme.shades.ternary2 : theme.shades.secondary3;
  return /* @__PURE__ */ React265.createElement(Background, { href: link, target: "_blank", color: bgColor }, /* @__PURE__ */ React265.createElement(TextContainer, null, title ? /* @__PURE__ */ React265.createElement(Title5, null, title, " | ") : null, copy), /* @__PURE__ */ React265.createElement(ArrowContainer, null, link && /* @__PURE__ */ React265.createElement(SimpleArrow, { width: "15", height: "7" })));
};
export {
  AccordionItem,
  AccordionList,
  AccountInput,
  AirContract,
  Appstore,
  Arrow,
  ArrowButton,
  ArrowDown,
  ArrowHouse,
  ArrowUp,
  AsyncDropdown,
  Atm,
  Aukakronur,
  Bicycle,
  Bilalan,
  BoxClose,
  BubbleClose,
  BubbleIcon,
  BubbleIconSmall,
  Bubbles,
  Bullet,
  BulletSvg,
  Button,
  ButtonArrow,
  ButtonPlus,
  Calendar,
  CalendarIconLarge,
  CancelContract,
  Car,
  CardButton,
  Checkbox,
  CheckboxActive,
  CheckboxRadio,
  Clock,
  Close,
  CloseIcon,
  CloseThick,
  CloseThin,
  Color,
  Column,
  Contact,
  Container,
  Contract,
  Corporate,
  CurrencyIcon,
  DateInput,
  Datepicker,
  DatepickerWithCheckmark,
  DefinitionList,
  DefinitionListFooter,
  DefinitionListHeader,
  DefinitionListItem,
  Dialog2 as Dialog,
  DialogSize,
  Divider,
  Document,
  DocumentInfo,
  DottedLink,
  Download,
  Drawer,
  DropDown,
  Dropdown,
  ElectonicCar,
  ElectricCar2 as ElectricCar,
  ElectronicId,
  EmailInput,
  ExclamationMark,
  Exit,
  ExpandArrow,
  ExpandPlus,
  FaceId,
  Facebook,
  Fingerprint,
  Flags,
  Food,
  Foot,
  Footer2 as Footer,
  FooterGroup,
  Form,
  FormButton,
  FormDescription,
  FormTitle,
  Fyrirtaeki,
  FyrirtaekiBlue,
  GasCar,
  GengiArrow,
  GoBackArrow,
  GroupLink,
  HeaderType,
  Headset,
  HeroNumberDetails,
  House,
  HouseLine,
  HybridCar,
  Ibudalan,
  Icon3 as Icon,
  IconLink,
  Info,
  InfoBubble,
  Input,
  Invoice,
  KennitalaInput,
  Kreditkort,
  Label,
  Language,
  LayoutContext,
  LeftArrow,
  Lifeyrissparnadur,
  LightBullet,
  LightBulletSvg,
  LoadingCircles,
  Lock,
  Logo,
  Ludur,
  Mail,
  MenuButton,
  MobileDropdown,
  MobileFlags,
  Modal,
  MoneyInput,
  MonthInput,
  Negative,
  NegativePerson,
  NegativePersonSvg,
  NegativeSvg,
  NotFound,
  Notification,
  NotificationBanner,
  NumberDetails,
  NumberFormatInput,
  NumberInput,
  OverlayButton,
  OverlayDialog,
  OverlayDialogActionCloseButton,
  OverlayFooter,
  OverlayHeader,
  Pagination,
  PaginationContainer,
  Paper,
  Pcaf,
  PercentageInput,
  Personal,
  Phone,
  PhoneInput,
  Pin,
  PlayButton,
  PlayStore,
  Portal,
  Positive,
  PositivePerson,
  PositivePersonSvg,
  PositiveSvg,
  PrismicTable,
  Question,
  Radgjof,
  RadioButton,
  Radiogroup,
  Reveal,
  RightArrow,
  Row,
  ScrollDownArrow,
  SearchIcon,
  Segment,
  SelectorTabs,
  Sereignasparnadur,
  Service,
  Share,
  Shield,
  SimpleArrow,
  Size,
  Skutla,
  SkutlaLarge,
  Slider,
  SliderList,
  SliderListItem,
  SmileIcon,
  SocialLink,
  Sparnadur,
  Speed,
  Spotify,
  SuccessCheck,
  SvgButton,
  Switch,
  SwitchIcon,
  Table,
  TableButton,
  TableDetailButton,
  TableExpander,
  TableHost,
  TableMenuButton,
  TableWithImages,
  TableWithLargeText,
  TagFilterCheckbox,
  Tbody,
  Td,
  Text4 as Text,
  TextCardButton,
  TextInput,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
  TrailRow,
  Tree,
  Tryggingar,
  UnderlineLink,
  Username,
  Visa,
  Wallet,
  Web,
  Youtube,
  base,
  container,
  device,
  dynamicInput,
  fit,
  fonts,
  getConfig,
  inViewConfig,
  legacyFonts,
  mapThemeColorStringToValue,
  mapThemeShadeStringToValue,
  mobileGutterMargin,
  mobileGutterPadding,
  reset,
  responseFont,
  shouldForwardProp,
  sizes,
  smallTable,
  sronly,
  standardTable,
  stickyFirstColumn,
  theme,
  toNonIcelandicLowerCase,
  typography,
  useClickOutside_default as useClickOutside,
  useCustomMedia,
  useEmailValidation,
  useEventListener_default as useEventListener,
  useInputValidation,
  useKennitalaValidation,
  useLockedBody_default as useLockedBody,
  useMedia,
  usePagination,
  usePhoneValidation,
  useReveal,
  useRevealTrail,
  useRevealTrailByRow,
  zebraTable
};
//# sourceMappingURL=index.mjs.map